import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Archive,
  AlertTriangle,
  BookOpen,
  Boxes,
  Camera,
  Check,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  Grid2X2,
  CircleHelp,
  List,
  Mail,
  Menu,
  MonitorCog,
  PackagePlus,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Settings,
  Shield,
  SlidersHorizontal,
  Tags,
  Upload,
  User,
  X,
} from "lucide-react";
import type { AppCustomer, BookMetadata, CatalogEdition, CatalogFormat, CustomerRequest, InventoryDefaults, InventoryItem, InventoryLocation, InventoryStats, Money, ProductType, ProductVariation, PublicStoreProfile, ReceivingScanRow, SquareLocation, StockPosition, StorefrontFulfillmentMethod, StorefrontPublicCart, UserSession } from "./shared/types";
import type { ModuleKey, ModuleRoute, OrganizationModuleView } from "./shared/modules";
import { getModuleDefinition, isModuleEnabledInMap, moduleForNavigation, moduleForRoute } from "./shared/modules";
import { aggregateScan } from "./shared/receiving";
import { isValidIsbn, normalizeIsbn } from "./shared/isbn";
import { defaultSupplierSearchProviders } from "./shared/schemas";
import { requestJson, requestText, StaffApiError, staffErrorMessage } from "./client/api";
import { InventorySettingsPage, LabelsPage, ReceivingOperationsPage } from "./phase4/Operations";
import { PosRegister as Phase5PosRegister } from "./phase5/PosRegister";
import { ReconciliationWorkspace } from "./phase8/ReconciliationWorkspace";
import { StoreCreditReturnsWorkspace } from "./phase9/StoreCreditReturnsWorkspace";
import { productBrand } from "./brand";
import "./styles.css";

type View = "inventory" | "receive" | "item" | "settings" | "supplier-search" | "imports" | "labels" | "returns" | "reconciliation" | "purchasing" | "help";
type AppRoute = "demo" | "scanner" | "storefront" | "cart" | "product" | "collection" | "account" | "inventory" | "receive" | "item" | "settings" | "pos" | "trade-ins" | "customers" | "orders" | "returns" | "reconciliation" | "supplier-search" | "imports" | "labels" | "reports" | "loyalty" | "storefront-admin" | "onboarding" | "purchasing" | "help";
type ImportFormat = "hardcover" | "softcover" | "new";
type StorefrontItem = Pick<InventoryItem, "id" | "title" | "author" | "description" | "isbn" | "category" | "priceMoney" | "imageUrl" | "stockStatus" | "variations"> & {
  publicId?: string;
  subtitle?: string;
  publisher?: string;
  available: boolean;
  availabilityLabel?: string;
  availableQuantity?: number | null;
  categoryId?: string;
  trackingMode?: "copy" | "quantity";
  signedCopy?: boolean;
  choices?: { variationId: string; copyId?: string; label: string; priceMoney: Money; availableQuantity: number }[];
  editionGroup?: {
    editionId: string;
    workId?: string;
    alternatives: { id: string; title: string; format?: string; priceMoney: Money; available: boolean; availabilityLabel: string }[];
  };
};
type PublicCartLine = { id?: string; variationId: string; copyId?: string; quantity: number; title: string; priceMoney?: Money; unitPriceAmount?: number; currency?: string; lineTotalAmount?: number; metadata?: Record<string, unknown> };
type PublicGiftCardLine = NonNullable<StorefrontPublicCart["giftCardLines"]>[number];
type StorefrontCollectionView = { id: string; name: string; slug: string; description?: string; imageUrl?: string; featured: boolean; items?: StorefrontItem[] };
type StorefrontStaffPickView = { id: string; variationId: string; staffDisplayName: string; recommendationText: string; featured: boolean; selectedAt: string };
type CustomerAccountView = {
  customer: AppCustomer;
  preferences: { emailMarketingOptIn: boolean; smsMarketingOptIn: boolean; availabilityNotificationsOptIn: boolean; deletionRequestedAt?: string };
};
type LoyaltyBalanceView = {
  enrolled?: boolean;
  balance: number;
  lifetimeEarned: number;
  lifetimeRedeemed: number;
  expiringPoints?: { points: number; expiresAt: string }[];
  currentLevel?: string;
  nextLevel?: { name: string; pointsNeeded: number };
  availableRewards: { id: string; name: string; pointsCost: number; rewardAmount: number; currency: string; rewardType: string }[];
};
type DetectedBarcode = { rawValue: string };
type BarcodeDetectorLike = {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
};
type BarcodeDetectorConstructor = new (options: { formats: string[] }) => BarcodeDetectorLike;
type SquareMatchResult = {
  exact: InventoryItem | null;
  similar: InventoryItem | null;
};
type ItemStockPosition = StockPosition & { locationName: string; locationCode: string; locationActive: boolean };
type ReceivingBatchRowResult = { title?: string; status: string; error?: string };
type ReceivingBatchResponse = {
  rows?: ReceivingBatchRowResult[];
  result?: { rows?: ReceivingBatchRowResult[] };
};
type SupplierSearchProvider = {
  id: string;
  name: string;
  enabled: boolean;
  mode: "link" | "ebay_api" | "google_books" | "serpapi_google_shopping" | "wholesale_fixture" | "wholesale_recorded_fixture" | "pubeasy_soap" | "ingram_ipage" | "direct_supplier_api";
  providerType: "local_inventory" | "incoming_inventory" | "wholesale" | "marketplace" | "metadata";
  capabilities: SourcingActionCapability[];
  credentialRequirements: string[];
  urlTemplate: string;
};
type SourcingActionCapability = "sell_local_copy" | "reserve_copy" | "transfer_location" | "view_reservation" | "view_open_po" | "reserve_incoming" | "add_to_draft_po" | "add_to_existing_po" | "create_po" | "attach_customer_special_order" | "create_customer_special_order" | "direct_to_customer" | "confirm_substitution" | "manual_refresh_availability" | "open_external_listing";
type HelpTopic = {
  id: string;
  title: string;
  category: string;
  routes: AppRoute[];
  roles?: UserSession["role"][];
  actionPath?: string;
  actionLabel?: string;
  summary: string;
  prerequisites?: string[];
  steps: string[];
  tips?: string[];
  troubleshooting?: string[];
  related?: string[];
};
type SupplierSearchResult = {
  providerId: string;
  providerName: string;
  providerType?: SupplierSearchProvider["providerType"];
  sourceCategory?: "current_store_inventory" | "other_location_inventory" | "reserved_inventory" | "incoming_inventory" | "wholesale_availability" | "marketplace_result";
  sourceName?: string;
  section?: "in_your_inventory" | "at_other_locations" | "already_on_order" | "book_suppliers" | "other_online_sources";
  title: string;
  author?: string;
  identifiers?: { isbn13?: string; isbn10?: string; upcEanGtin?: string; internalSku?: string; vendorSku?: string };
  editionId?: string | null;
  variationId?: string | null;
  supplierName?: string | null;
  priceMoney?: Money;
  listPriceMoney?: Money;
  wholesaleCostMoney?: Money;
  consumerRetailPriceMoney?: Money;
  shippingMoney?: Money;
  condition?: string;
  availability?: string;
  availabilityCategory?: string;
  availableQuantity?: number;
  approximateQuantity?: boolean;
  onHandQuantity?: number;
  reservedQuantity?: number;
  incomingQuantity?: number;
  onOrderToSupplierQuantity?: number | null;
  publicationStatus?: string | null;
  expectedAvailabilityDate?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  openPurchaseOrderId?: string | null;
  openPurchaseOrderNumber?: string | null;
  purchaseOrderLineId?: string | null;
  vendorId?: string | null;
  vendorAccountId?: string | null;
  vendorProductId?: string | null;
  vendorSku?: string | null;
  warehouseCode?: string | null;
  warehouseName?: string | null;
  returnability?: string | null;
  minimumOrderQuantity?: number | null;
  casePackQuantity?: number | null;
  shippingEligible?: boolean | null;
  directToCustomerEligible?: boolean | null;
  estimatedArrivalDate?: string | null;
  backorderEligible?: boolean | null;
  leadTimeDays?: number | null;
  warehouseLocation?: string | null;
  retrievedAt?: string;
  dataSourceAt?: string | null;
  cacheExpiresAt?: string | null;
  stale?: boolean;
  providerConfidence?: string | null;
  recommended?: boolean;
  recommendationScore?: number;
  recommendationReasons?: string[];
  freeFreightGapMoney?: Money | null;
  minimumOrderGapMoney?: Money | null;
  requiresConfirmation?: boolean;
  capabilities?: SourcingActionCapability[];
  warnings?: string[];
  imageUrl?: string;
  productUrl: string;
  resultType: "priced_offer" | "search_link";
  note?: string;
};
type MetadataRefreshCandidate = { id: string; title: string; authors: string[]; isbn?: string; sku?: string };
type MetadataBatchResult = { title: string; status: "refreshed" | "skipped" | "not_found" | "failed"; detail: string };
type MetadataBatchState = {
  total: number;
  processed: number;
  refreshed: number;
  skipped: number;
  notFound: number;
  failed: number;
  currentTitle?: string;
  results: MetadataBatchResult[];
};
const productTypeOptions: { value: ProductType; label: string }[] = [
  { value: "book", label: "Book" },
  { value: "movie", label: "Movie" },
  { value: "music", label: "Music" },
  { value: "game", label: "Game" },
  { value: "merchandise", label: "Merchandise" },
  { value: "other", label: "Other" },
];

function productTypeLabel(value?: ProductType): string {
  return productTypeOptions.find((option) => option.value === value)?.label ?? "Book";
}

function ExternalMetadataLinks({ links }: { links: NonNullable<BookMetadata["externalLinks"]> }) {
  const uniqueLinks = [...new Map(links.map((link) => [link.url, link])).values()].slice(0, 4);
  if (!uniqueLinks.length) return null;
  return (
    <div className="metadata-links">
      {uniqueLinks.map((link) => (
        <a key={link.url} href={link.url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>{link.label}</a>
      ))}
    </div>
  );
}
type CustomerRequestPricing = {
  foundPriceMoney: Money;
  shippingMoney: Money;
  taxMoney: Money;
  landedCostMoney: Money;
  customerPriceMoney: Money;
};
type CustomerRequestPrefill = {
  title: string;
  author?: string;
  isbn?: string;
  notes?: string;
  requestFormat: ImportFormat;
  metadata?: BookMetadata;
};
type AppSettings = {
  defaultReceivingLocation?: string;
  defaultLowStockThreshold: number;
  defaultCurrency: string;
  defaultImportPrices: Record<ImportFormat, Money>;
  customerRequestMinimumPrices: Record<ImportFormat, Money>;
  newItemLocationPolicy: string;
  autoImportImages: boolean;
  shippingEnabled: boolean;
  storefrontDefaultOnlineVisibility: boolean;
  storefrontDefaultPickupEnabled: boolean;
  storefrontDefaultShippingEnabled: boolean;
  flatRateShippingAmount: Money;
  freeShippingThresholdAmount?: Money;
  storefrontCategoryVisibility: Record<string, boolean>;
  supplierSearchProviders: SupplierSearchProvider[];
};

const demoGatewayEnabled = import.meta.env.VITE_PAPERBACKOS_DEMO_GATEWAY === "true";
const demoStorefrontUrl = import.meta.env.VITE_PAPERBACKOS_DEMO_STOREFRONT_URL || "/shop";
const demoAdminUrl = import.meta.env.VITE_PAPERBACKOS_DEMO_ADMIN_URL || "/app/inventory";

type PurchasingVendor = {
  id: string;
  name: string;
  legalName?: string | null;
  status: "active" | "inactive";
  orderingEmail?: string | null;
  orderingPhone?: string | null;
  orderingContactName?: string | null;
  paymentTerms?: string | null;
  defaultCurrency: string;
  defaultShippingMethod?: string | null;
  defaultBackorderPolicy: "allow" | "cancel" | "ship_available";
  minimumOrderAmount?: number | null;
  freeFreightThresholdAmount?: number | null;
  notes?: string | null;
  preferred: boolean;
  accounts?: PurchasingVendorAccount[];
  addresses?: PurchasingVendorAddress[];
  identifiers?: PurchasingPartyIdentifier[];
  products?: PurchasingVendorProduct[];
};
type PurchasingVendorAccount = {
  id: string;
  vendorId: string;
  accountNumber: string;
  accountName?: string | null;
  status: "active" | "inactive";
  orderingEmail?: string | null;
  paymentTerms?: string | null;
  currency: string;
  shippingMethod?: string | null;
  backorderPolicy: "allow" | "cancel" | "ship_available";
  minimumOrderAmount?: number | null;
  freeFreightThresholdAmount?: number | null;
  notes?: string | null;
};
type PurchasingVendorAddress = {
  id: string;
  vendorId: string;
  vendorAccountId?: string | null;
  addressType: "ship_to" | "bill_to" | "remit_to" | "ordering";
  label: string;
  contactName?: string | null;
  companyName?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  region?: string | null;
  postalCode?: string | null;
  country: string;
  phone?: string | null;
  email?: string | null;
  defaultForType: boolean;
  active: boolean;
};
type PurchasingPartyIdentifier = {
  id: string;
  vendorId: string;
  vendorAccountId?: string | null;
  addressId?: string | null;
  identifierType: "san" | "pubnet_id" | "vendor_account_id" | "x12_sender_id" | "x12_receiver_id" | "gln" | "other";
  identifierValue: string;
  qualifier?: string | null;
  notes?: string | null;
  active: boolean;
};
type PurchasingVendorProduct = {
  id: string;
  vendorId: string;
  vendorAccountId?: string | null;
  editionId?: string | null;
  variationId?: string | null;
  isbn13?: string | null;
  isbn10?: string | null;
  upcEanGtin?: string | null;
  internalSku?: string | null;
  vendorSku?: string | null;
  costAmount: number;
  currency: string;
  discountBasisPoints?: number | null;
  minimumOrderQuantity: number;
  casePackQuantity: number;
  returnability: "returnable" | "non_returnable" | "conditional" | "unknown";
  expectedLeadTimeDays?: number | null;
  preferred: boolean;
  effectiveAt?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
  active: boolean;
};
type PurchasingPoLine = {
  id: string;
  purchaseOrderId?: string;
  lineNumber?: number;
  vendorProductId?: string | null;
  editionId?: string | null;
  variationId?: string | null;
  isbn13?: string | null;
  isbn10?: string | null;
  upcEanGtin?: string | null;
  internalSku?: string | null;
  vendorSku?: string | null;
  titleSnapshot?: string | null;
  status?: string;
  requestedQuantity: number;
  acknowledgedQuantity?: number;
  shippedQuantity?: number;
  receivedQuantity?: number;
  canceledQuantity?: number;
  backorderedQuantity?: number;
  expectedUnitCostAmount: number;
  actualReceivedUnitCostAmount?: number | null;
  currency: string;
  backorderPolicy: "order_default" | "allow" | "cancel" | "ship_available";
  requestedShipDate?: string | null;
  cancelIfNotFilledDate?: string | null;
  doNotExceedUnitCostAmount?: number | null;
  allowSubstitutions?: boolean | null;
  lineNotes?: string | null;
  sourceDemand?: unknown[];
};
type PurchasingPurchaseOrder = {
  id: string;
  poNumber: string;
  vendorId: string;
  vendorName?: string | null;
  vendorAccountId?: string | null;
  shipToLocationId: string;
  billToLocationId?: string | null;
  poType: "replenishment" | "customer_special_order" | "preorder" | "event_book_fair" | "initial_inventory" | "direct_to_customer" | "manual";
  status: string;
  backorderPolicy: "allow" | "cancel" | "ship_available";
  requestedShipDate?: string | null;
  cancelIfNotFilledDate?: string | null;
  doNotExceedCostAmount?: number | null;
  currency: string;
  allowSubstitutions: boolean;
  fulfillmentPreference: "consolidate" | "ship_immediately";
  externalReference?: string | null;
  headerNotes?: string | null;
  sourceDemand?: unknown[];
  requestedTotalQuantity: number;
  receivedTotalQuantity: number;
  expectedTotalCostAmount: number;
  actualReceivedTotalCostAmount: number;
  version: number;
  createdAt?: string;
  updatedAt?: string;
  lines?: PurchasingPoLine[];
  receivingSessions?: PurchasingReceivingSession[];
};
type PurchasingReceivingSession = {
  id: string;
  purchaseOrderId: string;
  receivingNumber: string;
  shipmentReference?: string | null;
  status: string;
  locationId: string;
  receivedBy: string;
  receivedAt: string;
  notes?: string | null;
  lines?: PurchasingReceivingLine[];
};
type PurchasingReceivingLine = {
  id: string;
  receivingSessionId: string;
  purchaseOrderLineId?: string | null;
  variationId?: string | null;
  receivedQuantity: number;
  damagedQuantity: number;
  shortageQuantity: number;
  overageQuantity: number;
  unexpected: boolean;
  receivedUnitCostAmount: number;
  currency: string;
  customerRequestId?: string | null;
  notes?: string | null;
};
type PurchasingReplenishmentSuggestion = {
  id: string;
  variationId: string;
  editionId?: string | null;
  isbn13?: string | null;
  titleSnapshot: string;
  vendorId?: string | null;
  vendorAccountId?: string | null;
  vendorProductId?: string | null;
  suggestedQuantity: number;
  finalQuantity: number;
  status: "pending" | "excluded" | "deferred" | "approved" | "po_created";
  reasons: string[];
  onHandQuantity: number;
  reservedQuantity: number;
  openOrderQuantity: number;
  demandQuantity: number;
  salesVelocityPerWeek?: number | null;
  weeksOfSupply?: number | null;
  reorderThreshold: number;
  reorderTarget: number;
  expectedUnitCostAmount: number;
  currency: string;
  overrideReason?: string | null;
};
type PurchasingReplenishmentReview = {
  id: string;
  status: string;
  locationId: string;
  lookbackDays: number;
  targetWeeksSupply: number;
  suggestions: PurchasingReplenishmentSuggestion[];
};
type PurchasingSpecialOrder = {
  id: string;
  customerId: string;
  customerDisplayName?: string | null;
  title: string;
  author?: string | null;
  isbn?: string | null;
  quantity: number;
  fulfillmentPreference: "pickup" | "ship_to_customer" | "hold_for_customer";
  promisedDate?: string | null;
  estimatedDate?: string | null;
  depositStatus: "not_required" | "unpaid" | "partial" | "paid" | "refunded";
  purchasingStatus: "needed" | "ordered" | "acknowledged" | "backordered" | "shipped" | "received" | "ready_for_pickup" | "fulfilled" | "canceled";
  status: CustomerRequest["status"];
  variationId?: string | null;
  purchaseOrderLineId?: string | null;
  notes?: string | null;
};
type PurchasingExportTemplate = { id: string; vendorId?: string | null; name: string; format: "csv" | "spreadsheet_csv"; version: number; columnMapping: { header: string; field: string }[] };
type IntegrationProvider = {
  id: string;
  providerKey: string;
  displayName: string;
  providerType: string;
  status: "configured" | "testing" | "active" | "disabled";
  environment: "test" | "production";
  adapterVersion: string;
  mapVersion: string;
  capabilityModelVersion?: string;
  capabilities?: Record<string, unknown>;
  killSwitchEnabled: boolean;
  tenantKillSwitchEnabled?: boolean;
  rolloutPercent: number;
  rateLimitPerMinute: number;
  circuitState?: string;
  timeoutMs?: number;
  supportNotes?: string | null;
};
type IntegrationTradingPartner = {
  id: string;
  providerId: string;
  providerName?: string | null;
  vendorId: string;
  vendorName?: string | null;
  vendorAccountId?: string | null;
  environment: "test" | "production";
  status: "configured" | "testing" | "active" | "disabled";
  san?: string | null;
  pubnetId?: string | null;
  x12SenderId?: string | null;
  x12ReceiverId?: string | null;
  defaultTransport: string;
  documentFormat: string;
  warehouseMappings?: Record<string, unknown>;
  addressMappings?: Record<string, unknown>;
  shippingCodeMappings?: Record<string, unknown>;
  unitOfMeasureMappings?: Record<string, unknown>;
  paymentTerms?: string | null;
  certificationStatus: "not_started" | "testing" | "certified" | "blocked";
  certificationNotes?: string | null;
};
type IntegrationMessage = {
  id: string;
  providerId: string;
  providerKey: string;
  purchaseOrderId?: string | null;
  externalDocumentId?: string | null;
  messageType: string;
  messageVersion: string;
  correlationId: string;
  direction: "outbound" | "inbound";
  environment: "test" | "production";
  transport: string;
  processingStatus: string;
  retryCount: number;
  nextRetryAt?: string | null;
  transmittedAt?: string | null;
  receivedAt?: string | null;
  processedAt?: string | null;
  validationResults?: Array<{ severity: string; code: string; message: string }>;
  processingErrors?: Array<{ code: string; message: string }>;
  normalizedPayload?: Record<string, unknown>;
};
type IntegrationReviewItem = {
  id: string;
  messageId: string;
  purchaseOrderId?: string | null;
  purchaseOrderLineId?: string | null;
  reviewType: string;
  severity: string;
  status: string;
  summary: string;
  createdAt: string;
};
type IntegrationHealth = {
  queued: number;
  failed: number;
  pendingReview: number;
  transmitted: number;
  openReviewItems: number;
  lastActivityAt?: string | null;
  providers?: Array<{ providerId: string; providerKey: string; displayName: string; environment: string; status: string; killSwitchEnabled: boolean; circuitState: string; queued: number; failed: number; deadLetter: number; openReviewItems: number; lastActivityAt?: string | null; capabilities?: Record<string, unknown>; adapterVersion: string; mapVersion: string }>;
  alerts?: Array<{ severity: string; code: string; providerId?: string; message: string }>;
};
type PurchasingPoChangeRequest = {
  id: string;
  purchaseOrderId: string;
  requestNumber: string;
  changeType: string;
  status: string;
  reason: string;
  supplierTransmissionMethod: "electronic" | "email" | "phone" | "manual_contact";
  externalReference?: string | null;
  externalControlNumber?: string | null;
  supplierResponse?: string | null;
  requestedAt: string;
  responseAt?: string | null;
  poNumber?: string | null;
  vendorName?: string | null;
  lines?: Array<{ id: string; purchaseOrderLineId?: string | null; originalValue: Record<string, unknown>; requestedValue: Record<string, unknown>; acceptedValue?: Record<string, unknown>; status: string; supplierResponse?: string | null }>;
  history?: Array<{ id: string; eventType: string; actorUserId?: string | null; note?: string | null; createdAt: string }>;
};
type PurchasingBackorderRow = {
  purchaseOrderId: string;
  purchaseOrderLineId: string;
  poNumber: string;
  vendorId: string;
  vendorName: string;
  title: string;
  isbn13?: string | null;
  status: string;
  backorderedQuantity: number;
  remainingQuantity: number;
  shippedQuantity: number;
  receivedQuantity: number;
  expectedDate?: string | null;
  cancelIfNotFilledDate?: string | null;
  ageDays: number;
  customerRequestId?: string | null;
  customerQuantity: number;
  customerPriority: boolean;
};
type PurchasingBackorderPolicy = { locationId: string; retainDays: number; customerOrderPriority: boolean; autoCloseExpired: boolean; notifyOverdueAfterDays: number };
type PurchasingShipmentLine = {
  id: string;
  shipmentId: string;
  purchaseOrderId?: string | null;
  purchaseOrderLineId?: string | null;
  lineNumber: number;
  editionId?: string | null;
  variationId?: string | null;
  isbn13?: string | null;
  vendorSku?: string | null;
  title?: string | null;
  expectedQuantity: number;
  shippedQuantity: number;
  receivedQuantity: number;
  damagedQuantity: number;
  missingQuantity: number;
  overageQuantity: number;
  receivedUnitCostAmount?: number | null;
  currency: string;
  customerRequestId?: string | null;
  status: string;
  exceptions?: unknown[];
};
type PurchasingShipmentCartonLine = {
  id: string;
  cartonId: string;
  shipmentLineId: string;
  purchaseOrderId?: string | null;
  purchaseOrderLineId?: string | null;
  variationId?: string | null;
  isbn13?: string | null;
  title?: string | null;
  expectedQuantity: number;
  receivedQuantity: number;
  damagedQuantity: number;
  missingQuantity: number;
  overageQuantity: number;
  receivedUnitCostAmount?: number | null;
  currency: string;
  customerRequestId?: string | null;
  status: string;
};
type PurchasingShipmentCarton = {
  id: string;
  shipmentId: string;
  cartonNumber?: string | null;
  licensePlateBarcode: string;
  trackingNumber?: string | null;
  status: string;
  receivedSessionId?: string | null;
  receivedAt?: string | null;
  supplierShipmentNumber?: string | null;
  destinationLocationId?: string | null;
  lines?: PurchasingShipmentCartonLine[];
};
type PurchasingShipmentDiscrepancy = {
  id: string;
  shipmentId: string;
  supplierShipmentNumber?: string | null;
  purchaseOrderId?: string | null;
  purchaseOrderLineId?: string | null;
  discrepancyType: string;
  severity: string;
  status: string;
  summary: string;
  details?: Record<string, unknown>;
  supplierFollowupStatus?: string | null;
  resolutionReason?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
};
type PurchasingShipment = {
  id: string;
  supplierShipmentNumber: string;
  vendorId: string;
  vendorName?: string | null;
  vendorAccountId?: string | null;
  originWarehouse?: string | null;
  destinationLocationId: string;
  destinationName?: string | null;
  carrier?: string | null;
  shippingService?: string | null;
  trackingNumbers: unknown[];
  estimatedShipDate?: string | null;
  estimatedDeliveryDate?: string | null;
  actualShipDate?: string | null;
  actualDeliveryDate?: string | null;
  packingSlipNumber?: string | null;
  freightAmount?: number | null;
  currency: string;
  status: string;
  sourceMessageId?: string | null;
  replacesShipmentId?: string | null;
  poNumbers?: string[];
  lines?: PurchasingShipmentLine[];
  cartons?: PurchasingShipmentCarton[];
  discrepancies?: PurchasingShipmentDiscrepancy[];
  history?: Array<{ id: string; eventType: string; actorUserId?: string | null; note?: string | null; relatedMessageId?: string | null; createdAt: string }>;
  createdAt?: string;
  updatedAt?: string;
};
type CartonReceiveDraftLine = {
  shipmentLineId: string;
  receivedQuantity: string;
  damagedQuantity: string;
  shortageQuantity: string;
  overageQuantity: string;
  receivedUnitCostAmount: string;
  customerRequestId: string;
};
type PurchasingInvoiceLine = {
  id: string;
  lineNumber: number;
  purchaseOrderId?: string | null;
  purchaseOrderLineId?: string | null;
  shipmentId?: string | null;
  receivingSessionId?: string | null;
  isbn13?: string | null;
  vendorSku?: string | null;
  description: string;
  quantity: number;
  unitCostAmount: number;
  lineTotalAmount: number;
  currency: string;
  matchStatus: string;
  matchDetails?: Record<string, unknown>;
};
type PurchasingInvoiceDiscrepancy = {
  id: string;
  invoiceId: string;
  invoiceNumber?: string | null;
  invoiceLineId?: string | null;
  discrepancyType: string;
  severity: string;
  status: string;
  summary: string;
  details?: Record<string, unknown>;
  resolutionReason?: string | null;
  createdAt: string;
};
type PurchasingSupplierInvoice = {
  id: string;
  vendorId: string;
  vendorName?: string | null;
  vendorAccountId?: string | null;
  invoiceNumber: string;
  invoiceDate?: string | null;
  dueDate?: string | null;
  paymentTerms?: string | null;
  currency: string;
  payableStatus: string;
  exportStatus: string;
  invoiceTotalAmount: number;
  freightAmount: number;
  taxAmount: number;
  discountAmount: number;
  poNumbers?: string[];
  lines?: PurchasingInvoiceLine[];
  discrepancies?: PurchasingInvoiceDiscrepancy[];
  history?: Array<{ id: string; eventType: string; actorUserId?: string | null; note?: string | null; createdAt: string }>;
};
type PurchasingCreditMemo = { id: string; vendorId: string; vendorName?: string | null; creditNumber: string; creditType: string; status: string; amount: number; currency: string; originalInvoiceId?: string | null; notes?: string | null };
type PurchasingAccountingExportBatch = { id: string; adapterKey: string; status: string; exportedAt?: string | null; payloadText: string; payloadSha256: string; invoiceCount: number; creditCount: number; totalAmount: number; currency: string };
type PurchasingSupplierReturnLine = {
  id: string; supplierReturnId: string; lineNumber: number; variationId: string; isbn13?: string | null; vendorSku?: string | null; titleSnapshot: string; quantityRequested: number; quantityAuthorized: number; quantityStaged: number; quantityShipped: number; quantityCredited: number; reason: string; reasonDetail?: string | null; condition: string; returnability: string; returnWindowDeadline?: string | null; eligibilityStatus: string; eligibilityExplanation?: string[]; manualOverride: boolean; expectedUnitCreditAmount: number; currency: string;
};
type PurchasingSupplierReturn = {
  id: string; returnNumber: string; vendorId: string; vendorName?: string | null; vendorAccountId?: string | null; sourceLocationId: string; status: string; returnMethod: string; returnAuthorizationNumber?: string | null; authorizationExpiresAt?: string | null; shippingInstructions?: string | null; requiredPaperwork?: string[]; restockingFeeAmount: number; expectedCreditAmount: number; actualCreditAmount: number; creditRemainingAmount: number; currency: string; notes?: string | null; lines?: PurchasingSupplierReturnLine[]; cartons?: Array<{ id: string; cartonNumber: string; carrier?: string | null; trackingNumber?: string | null; weightOunces?: number | null; status: string }>; credits?: Array<{ id: string; status: string; expectedCreditAmount: number; actualCreditAmount: number; quantityDifference: number; unitCreditDifferenceAmount: number; feesOrDeductionsAmount: number; resolutionReason?: string | null }>; history?: Array<{ id: string; eventType: string; actorUserId?: string | null; note?: string | null; createdAt: string }>; updatedAt: string;
};
type PurchasingSupplierReturnSuggestion = {
  receivingLineId: string; purchaseOrderLineId: string; vendorId: string; vendorAccountId?: string | null; locationId: string; variationId: string; isbn13?: string | null; vendorSku?: string | null; titleSnapshot: string; returnability: string; ageDays: number; salesVelocityPerWeek: number; onHandQuantity: number; committedQuantity: number; expectedIncomingQuantity: number; eligibleQuantity: number; expectedCreditAmount: number; currency: string; suggestedQuantity: number; explanation: string[];
};
type StaffApi = <T>(path: string, init?: RequestInit) => Promise<T>;
type VendorDraft = {
  name: string; legalName: string; status: PurchasingVendor["status"]; orderingEmail: string; orderingPhone: string; orderingContactName: string; paymentTerms: string; defaultCurrency: string; defaultShippingMethod: string; defaultBackorderPolicy: PurchasingVendor["defaultBackorderPolicy"]; minimumOrderAmount: string; freeFreightThresholdAmount: string; notes: string; preferred: boolean;
};
type VendorAccountDraft = {
  accountNumber: string; accountName: string; status: PurchasingVendorAccount["status"]; orderingEmail: string; orderingPhone: string; orderingContactName: string; paymentTerms: string; currency: string; shippingMethod: string; backorderPolicy: PurchasingVendor["defaultBackorderPolicy"]; minimumOrderAmount: string; freeFreightThresholdAmount: string; notes: string;
};
type VendorAddressDraft = Omit<PurchasingVendorAddress, "id" | "vendorId">;
type PartyIdentifierDraft = Omit<PurchasingPartyIdentifier, "id" | "vendorId">;
type VendorProductDraft = {
  vendorAccountId: string; editionId: string; variationId: string; isbn13: string; isbn10: string; upcEanGtin: string; internalSku: string; vendorSku: string; costAmount: string; currency: string; discountBasisPoints: string; minimumOrderQuantity: string; casePackQuantity: string; returnability: PurchasingVendorProduct["returnability"]; expectedLeadTimeDays: string; preferred: boolean; effectiveAt: string; expiresAt: string; notes: string; active: boolean;
};
type PoDraft = {
  vendorId: string; vendorAccountId: string; shipToLocationId: string; billToLocationId: string; poType: PurchasingPurchaseOrder["poType"]; backorderPolicy: PurchasingVendor["defaultBackorderPolicy"]; requestedShipDate: string; cancelIfNotFilledDate: string; doNotExceedCostAmount: string; currency: string; allowSubstitutions: boolean; fulfillmentPreference: PurchasingPurchaseOrder["fulfillmentPreference"]; externalReference: string; headerNotes: string; sourceDemand: unknown[];
};
type PoChangeDraft = {
  changeType: string; purchaseOrderLineId: string; requestedQuantity: string; requestedShipDate: string; cancelIfNotFilledDate: string; backorderPolicy: string; shippingInstructions: string; replacementEditionId: string; replacementVariationId: string; replacementIsbn13: string; replacementTitle: string; replacementCostAmount: string; reason: string; supplierTransmissionMethod: "electronic" | "email" | "phone" | "manual_contact"; externalReference: string;
};

const defaultStoreProfile: PublicStoreProfile = {
  productName: productBrand.name,
  storeDisplayName: "Independent Bookstore",
  storeTagline: "Independent bookstore",
  storeDescription: "Browse available items for pickup.",
  logoUrl: null,
  publicWebsiteUrl: null,
  publicEmail: null,
  publicPhone: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  region: null,
  postalCode: null,
  country: null,
  socialLinks: {},
  businessHours: [],
  timezone: null,
  currencyCode: "USD",
  receiptFooterText: null,
  storefrontTitle: "Independent Bookstore",
  storefrontDescription: "Browse available items for pickup.",
  pickupPolicyText: null,
  shippingPolicyText: null,
  returnPolicyText: null,
  privacyPolicyText: null,
  termsPolicyText: null,
};

const cartIdStorageKey = "spine_cart_id";
const cartTokenStorageKey = "spine_cart_token";
const legacyCartIdStorageKey = "dreadful_cart_id";
const legacyCartTokenStorageKey = "dreadful_cart_token";
const themeStorageKey = "paperbackos_theme";
const storefrontSessionStorageKey = "paperbackos_storefront_session";

const defaultSettings: AppSettings = {
  defaultLowStockThreshold: 3,
  defaultCurrency: "USD",
  defaultImportPrices: {
    hardcover: { amount: 1300, currency: "USD" },
    softcover: { amount: 700, currency: "USD" },
    new: { amount: 2000, currency: "USD" },
  },
  customerRequestMinimumPrices: {
    hardcover: { amount: 1300, currency: "USD" },
    softcover: { amount: 700, currency: "USD" },
    new: { amount: 2000, currency: "USD" },
  },
  newItemLocationPolicy: "receiving_location",
  autoImportImages: false,
  shippingEnabled: true,
  storefrontDefaultOnlineVisibility: true,
  storefrontDefaultPickupEnabled: true,
  storefrontDefaultShippingEnabled: true,
  flatRateShippingAmount: { amount: 0, currency: "USD" },
  freeShippingThresholdAmount: undefined,
  storefrontCategoryVisibility: {},
  supplierSearchProviders: defaultSupplierSearchProviders.map((provider) => ({ ...provider, capabilities: [...provider.capabilities], credentialRequirements: [...provider.credentialRequirements] })),
};

const staffNavigation: { label: string; path: string; routes: AppRoute[]; roles: UserSession["role"][]; icon: typeof BookOpen; moduleKey?: ModuleKey }[] = [
  { label: "Register", path: "/pos/register", routes: ["pos"], roles: ["administrator", "manager", "staff"], icon: PackagePlus },
  { label: "Inventory", path: "/app/inventory", routes: ["inventory", "item", "supplier-search"], roles: ["administrator", "manager", "staff"], icon: Boxes },
  { label: "Add Stock", path: "/app/stock", routes: ["receive"], roles: ["administrator", "manager", "staff"], icon: PackagePlus },
  { label: "Purchasing", path: "/app/purchasing", routes: ["purchasing"], roles: ["administrator", "manager", "staff"], icon: ClipboardList, moduleKey: "purchase_orders" },
  { label: "Trade-ins", path: "/app/trade-ins", routes: ["trade-ins"], roles: ["administrator", "manager", "staff"], icon: BookOpen, moduleKey: "trade_ins" },
  { label: "Requests", path: "/app/customers", routes: ["customers"], roles: ["administrator", "manager", "staff"], icon: User },
  { label: "Labels", path: "/app/labels", routes: ["labels"], roles: ["administrator", "manager", "staff"], icon: Tags },
  { label: "Reports", path: "/app/reports", routes: ["reports"], roles: ["administrator", "manager"], icon: Grid2X2, moduleKey: "advanced_reports" },
  { label: "Sales & Returns", path: "/app/returns", routes: ["returns", "orders"], roles: ["administrator", "manager", "staff"], icon: RotateCcw },
  { label: "Imports", path: "/app/imports", routes: ["imports"], roles: ["administrator", "manager"], icon: Upload, moduleKey: "imports" },
  { label: "Settings", path: "/app/settings", routes: ["settings", "loyalty", "storefront-admin", "onboarding"], roles: ["administrator", "manager"], icon: Settings },
  { label: "Help", path: "/app/help", routes: ["help"], roles: ["administrator", "manager", "staff"], icon: CircleHelp },
];

const helpTopics: HelpTopic[] = [
  {
    id: "register-new-sale",
    title: "Start and complete a register sale",
    category: "Register",
    routes: ["pos"],
    actionPath: "/pos/register",
    actionLabel: "Open Register",
    summary: "Use the register to scan items, add manual lines, apply discounts, and collect payment.",
    prerequisites: ["A register session must be open.", "Square settings are required for card payments."],
    steps: [
      "Open Register. A pending sale is created automatically when the register is ready.",
      "Scan a barcode, search inventory, or add a manual line when an item is not in inventory.",
      "Review quantities, discounts, customer credit, and the cart total before payment.",
      "Choose the payment method. Square app handoff and Square Terminal depend on the store's Square setup.",
      "After payment succeeds, the sale completes and the next pending sale begins automatically.",
    ],
    tips: ["Keep the sale open until Square reports success or cancellation.", "Use manual lines for non-inventory items, fees, or one-off merchandise."],
    troubleshooting: ["If Square returns without payment, the pending payment should cancel and return you to the register.", "If a scanned item is not found, use the manual item prompt instead of starting a new sale."],
    related: ["register-scanner", "discounts", "gift-cards"],
  },
  {
    id: "register-scanner",
    title: "Use camera or phone scanning at the register",
    category: "Register",
    routes: ["pos", "scanner"],
    actionPath: "/pos/register",
    actionLabel: "Open Register",
    summary: "Register scanning can use the device camera or a paired phone scanner.",
    steps: [
      "On supported mobile/tablet devices, use the camera scanner for quick barcode entry.",
      "On a computer or iPad setup, use Pair Phone to open a secure scanner link on a phone.",
      "Keep the register sale open while phone scans are sent into the cart.",
      "If a scan is not found, enter the item description and price to add a manual sale line.",
    ],
    troubleshooting: ["If the camera button appears on a desktop browser, check device detection and screen-size behavior.", "If camera access fails, allow camera permission in the browser and reload the page."],
  },
  {
    id: "add-stock-batch",
    title: "Add stock with a receiving batch",
    category: "Add Stock",
    routes: ["receive", "scanner"],
    actionPath: "/app/stock",
    actionLabel: "Open Add Stock",
    summary: "Receiving batches collect scanned items, default choices, quantities, and label prep before inventory is updated.",
    prerequisites: ["Choose the receiving location before completing a batch.", "Set batch defaults before scanning when most items share the same condition or price."],
    steps: [
      "Open Add Stock and choose Settings before the first scan.",
      "Set the receiving location, default condition, and default format prices for the batch.",
      "Scan one item or use quantity controls when you have multiple copies of the same item.",
      "Use photo barcode scanning on mobile when several back-cover barcodes are visible.",
      "Review and complete the batch to apply inventory movements exactly once.",
    ],
    tips: ["For multiples of the same title, scan once and adjust the quantity before completing.", "Use paired phone scanning on desktop setups and photo barcode scanning on mobile devices."],
    troubleshooting: ["A partial completion usually means at least one scanned line could not be turned into an inventory movement.", "If a CD, DVD, VHS, or other UPC is not found, use the manual details on that line before completion."],
    related: ["inventory-defaults", "labels"],
  },
  {
    id: "inventory-defaults",
    title: "Set inventory and storefront defaults",
    category: "Settings",
    routes: ["settings", "receive", "storefront-admin"],
    roles: ["administrator", "manager"],
    actionPath: "/app/settings",
    actionLabel: "Open Settings",
    summary: "Defaults reduce repetitive choices when adding new inventory.",
    steps: [
      "Open Settings and review Inventory Defaults.",
      "Choose the default receiving condition and pricing by format.",
      "Set default storefront visibility, pickup, and shipping choices for new items.",
      "Save settings before starting the next receiving batch.",
    ],
    tips: ["Batch settings persist for receiving work, while inventory defaults apply to newly created items."],
  },
  {
    id: "inventory-edit-counts",
    title: "Update inventory counts from an item record",
    category: "Inventory",
    routes: ["inventory", "item"],
    actionPath: "/app/inventory",
    actionLabel: "Open Inventory",
    summary: "Item records show catalog metadata and current stock, with controls for count adjustments.",
    steps: [
      "Open Inventory and select the item.",
      "Review format, condition, location, price, and storefront visibility.",
      "Use the stock adjustment controls to increase or decrease count with a reason.",
      "Save changes and refresh the inventory list if another device may have changed stock.",
    ],
    troubleshooting: ["If the visible count does not change after a save, refresh the item because another device or batch may have updated stock."],
  },
  {
    id: "trade-ins",
    title: "Process trade-ins",
    category: "Trade-ins",
    routes: ["trade-ins"],
    actionPath: "/app/trade-ins",
    actionLabel: "Open Trade-ins",
    summary: "Trade-ins move through quote, inspection, approval, inventory acquisition, and credit issuance.",
    prerequisites: ["Trade-ins must be enabled in settings.", "Credit mode and dollar values must be complete before changing policy modes."],
    steps: [
      "Select or create the customer before quoting items.",
      "Scan or enter items and review the credit policy result.",
      "Inspect items, approve accepted lines, and map them to inventory variations.",
      "Acquire approved inventory before issuing store credit.",
      "Only managers or administrators can issue or reverse store credit.",
    ],
    troubleshooting: ["If Save is blocked in trade-in settings, enter the required dollar value for the current credit mode before switching modes.", "If Issue credit is disabled, check that the trade-in is approved, inventory is acquired, and the user has manager permission."],
  },
  {
    id: "discounts",
    title: "Create and apply discounts",
    category: "Register",
    routes: ["pos", "settings"],
    actionPath: "/pos/register",
    actionLabel: "Open Register",
    summary: "Discounts can be applied manually at the register or configured as named rules.",
    steps: [
      "Open Register and add the sale items.",
      "Use the discount controls to apply an amount or percentage where allowed.",
      "For reusable rules, configure named discounts in the register discount settings.",
      "Review the discount line before collecting payment.",
    ],
    tips: ["Use named discount rules for recurring events and manual discounts for one-off customer service adjustments."],
  },
  {
    id: "gift-cards",
    title: "Sell and redeem gift cards",
    category: "Register",
    routes: ["pos", "storefront-admin"],
    actionPath: "/pos/register",
    actionLabel: "Open Register",
    summary: "Gift cards can be sold through eligible checkout paths and redeemed as supported tender.",
    steps: [
      "Add the gift card purchase or scanned gift card to the sale.",
      "Complete payment to activate purchased gift cards.",
      "For redemption, use the available gift card tender flow when the sale is payable.",
      "Check Reports for issued and redeemed values when reconciling liability.",
    ],
    troubleshooting: ["Square gift card redemption depends on Square tender support for the chosen payment flow.", "Gift card purchases activate only after the purchase payment is completed."],
  },
  {
    id: "storefront-setup",
    title: "Set up the online storefront",
    category: "Storefront",
    routes: ["storefront-admin", "settings"],
    roles: ["administrator", "manager"],
    actionPath: "/app/settings/storefront",
    actionLabel: "Open Storefront Settings",
    summary: "Storefront settings control public store text, collections, item visibility, pickup, and shipping.",
    steps: [
      "Open Settings, then Storefront.",
      "Set store display name, storefront title, description, logo, and contact details.",
      "Configure pickup and shipping policies.",
      "Build public collections or staff picks when you want curated browsing.",
      "Use item visibility controls to decide what appears online.",
    ],
    troubleshooting: ["If the storefront still shows generic text, confirm the store profile and storefront title were saved for the active deployment.", "If checkout only shows pickup, confirm shipping is enabled and the item allows shipping."],
  },
  {
    id: "sales-returns",
    title: "Look up sales and process returns",
    category: "Sales & Returns",
    routes: ["returns", "orders"],
    actionPath: "/app/returns",
    actionLabel: "Open Sales & Returns",
    summary: "Sales and returns tools help review completed sales and process supported store-credit return workflows.",
    steps: [
      "Open Sales & Returns and search by sale, order, customer, or date.",
      "Review original sale lines and prior return activity.",
      "Choose eligible return lines and disposition.",
      "Confirm the return only after the physical item handling is correct.",
    ],
    troubleshooting: ["If a return is not eligible, review prior returns, payment state, and whether the workflow supports the original tender type."],
  },
  {
    id: "reports",
    title: "Run reports",
    category: "Reports",
    routes: ["reports"],
    roles: ["administrator", "manager"],
    actionPath: "/app/reports",
    actionLabel: "Open Reports",
    summary: "Reports summarize sales, inventory, customers, refunds, loyalty, and online storefront activity.",
    steps: [
      "Open Reports and choose a date preset or custom range.",
      "Filter by location, register, employee, channel, product type, condition, or payment method as needed.",
      "Use source links to investigate recent transactions.",
      "Export CSV when you need spreadsheet review.",
    ],
    tips: ["Use exported CSVs when reconciling with external accounting or spreadsheet work."],
  },
  {
    id: "first-store-setup",
    title: "Set up a store for launch",
    category: "Setup",
    routes: ["help", "settings", "onboarding"],
    roles: ["administrator", "manager"],
    actionPath: "/app/settings/onboarding",
    actionLabel: "Open Onboarding",
    summary: "Work through the core settings needed before a store can run live operations.",
    prerequisites: ["Administrator or manager access is required."],
    steps: [
      "Open Settings and confirm store identity, location, currency, and business details.",
      "Configure inventory defaults, conditions, formats, shelves, and receiving location.",
      "Configure Register and Square settings before taking card payments.",
      "Configure storefront profile, pickup, shipping, and online visibility defaults.",
      "Run a test receiving batch, test sale, and storefront checkout before launch.",
    ],
    tips: ["Use the onboarding checklist to track launch blockers before staff training."],
  },
  {
    id: "square-setup",
    title: "Set up Square payments",
    category: "Setup",
    routes: ["help", "pos", "settings", "reconciliation"],
    roles: ["administrator", "manager"],
    actionPath: "/app/settings",
    actionLabel: "Open Settings",
    summary: "Square configuration controls POS app handoff, Terminal payments, web checkout, webhooks, and reconciliation.",
    prerequisites: ["Square application ID, access token, location, and webhook signature key must be configured in Cloudflare."],
    steps: [
      "Confirm Square environment matches the store's live or sandbox mode.",
      "Set the POS application ID for mobile Square app handoff.",
      "Set the webhook notification URL exactly as shown by the app deployment.",
      "Add the same callback URL in the Square Developer dashboard.",
      "Run a small test payment and confirm it reconciles in Operations.",
    ],
    troubleshooting: ["If Square says callback URL does not match, compare the URL character-for-character in Square Developer settings.", "If the web app reports missing web callback URL, add the callback URL to the Square app configuration."],
  },
  {
    id: "imports",
    title: "Import inventory from a file",
    category: "Inventory",
    routes: ["imports"],
    roles: ["administrator", "manager"],
    actionPath: "/app/imports",
    actionLabel: "Open Imports",
    summary: "Imports preview CSV or XLSX inventory before applying catalog and stock changes.",
    steps: [
      "Open Imports and choose a CSV or XLSX file.",
      "Review detected columns and adjust field mapping.",
      "Preview the import and resolve blocked rows or conflicts.",
      "Apply the import only after the preview matches the intended inventory changes.",
      "Use reversal tools if an applied import needs to be safely backed out.",
    ],
    troubleshooting: ["Legacy XLS files are not accepted; save the file as CSV or XLSX first."],
  },
  {
    id: "labels",
    title: "Print labels",
    category: "Inventory",
    routes: ["labels", "receive"],
    actionPath: "/app/labels",
    actionLabel: "Open Labels",
    summary: "Labels can be prepared from receiving batches or created from selected inventory.",
    steps: [
      "Open Labels after completing a receiving batch or search for inventory to label.",
      "Choose the label template and sheet profile.",
      "Preview the print sheet before opening the browser print dialog.",
      "Use printer settings that match the selected Avery profile.",
    ],
    troubleshooting: ["If alignment is off, confirm browser scaling is set to actual size or 100%."],
  },
  {
    id: "manager-support",
    title: "What happens after reporting a problem",
    category: "Help",
    routes: ["help"],
    summary: "Problem reports flow to PaperbackOS Manager for tracking.",
    steps: [
      "The report is saved with store, page, role, browser, version, and optional screenshot context.",
      "PaperbackOS can triage the issue in Manager and update its status.",
      "Urgent reports should still be paired with a direct message or call if store operations are blocked.",
    ],
  },
  {
    id: "media-upc-lookup",
    title: "Scan books, movies, music, and other barcodes",
    category: "Add Stock",
    routes: ["receive", "inventory"],
    actionPath: "/app/stock",
    actionLabel: "Open Add Stock",
    summary: "Add Stock tries ISBN lookup first, then product barcode lookup, and uses the best match to choose product type and item details.",
    prerequisites: ["Shared UPC lookup credentials must be configured for non-book barcode matches.", "Manual review is still needed when a lookup source returns incomplete or uncertain data."],
    steps: [
      "Start a receiving batch and set the default location, condition, and format pricing.",
      "Scan a book ISBN, DVD, Blu-ray, VHS, CD, vinyl, or general retail UPC.",
      "Review the detected product type, title, format, price, and quantity before completing.",
      "When lookup confidence is low, fill in the missing title, format, or price on the line.",
      "Complete the batch to create catalog records and stock movements together.",
    ],
    tips: ["UPC results can vary by source, so treat unfamiliar non-book matches as suggestions.", "Use format defaults to price common paperback, hardcover, media, and miscellaneous items quickly."],
    troubleshooting: ["If only some scanned lines complete, reopen the batch and check unresolved lines for missing required details.", "If a known UPC never resolves, it may need a manual catalog entry or a lookup provider key."],
    related: ["add-stock-batch", "inventory-defaults", "labels"],
  },
  {
    id: "photo-barcode-scan",
    title: "Use photo barcode scanning",
    category: "Add Stock",
    routes: ["receive", "scanner"],
    actionPath: "/app/stock",
    actionLabel: "Open Add Stock",
    summary: "Photo scanning can read several visible back-cover barcodes from one mobile photo and add them to the active receiving batch.",
    prerequisites: ["Use a mobile device with camera permission enabled.", "Place books so barcodes are flat, visible, and not covered by price stickers."],
    steps: [
      "Open Add Stock on a mobile device.",
      "Start or resume the receiving batch.",
      "Use the photo scan option and take a clear picture of the visible barcodes.",
      "Review detected codes before adding them to the batch.",
      "Fix any missed or incorrect codes manually before completing.",
    ],
    tips: ["Bright, even lighting matters more than getting every book perfectly aligned.", "For glossy covers, tilt the books slightly to avoid glare across the barcode."],
    troubleshooting: ["If a barcode is missed, retake the photo closer or scan that item normally.", "Photo scan is hidden on larger computer screens because paired phone scanning is the better workflow there."],
    related: ["add-stock-batch", "pair-phone-scanner"],
  },
  {
    id: "pair-phone-scanner",
    title: "Pair a phone as a scanner",
    category: "Scanning",
    routes: ["receive", "pos", "scanner"],
    actionPath: "/app/stock",
    actionLabel: "Open Add Stock",
    summary: "A computer or iPad can keep the main workflow open while a phone sends scans into the active batch or register sale.",
    prerequisites: ["The main device must keep the receiving batch or register sale open.", "The phone needs camera permission and the pairing link or QR code."],
    steps: [
      "Open Add Stock or Register on the main device.",
      "Choose Pair Phone and scan the QR code with the phone.",
      "Keep scanning on the phone; each accepted code appears on the main device.",
      "Use the main device to review quantities, prices, and unresolved scans.",
      "End or replace the paired session if the phone should no longer send scans.",
    ],
    tips: ["Pairing is designed for back-room receiving tables and counter setups where the main screen should stay put."],
    troubleshooting: ["If scans stop arriving, check that both devices are online and the main workflow is still open.", "Pair Phone is hidden on mobile screens because that device can scan directly."],
    related: ["register-scanner", "photo-barcode-scan"],
  },
  {
    id: "register-open-close",
    title: "Open, run, and close a register",
    category: "Register",
    routes: ["pos", "reports"],
    actionPath: "/pos/register",
    actionLabel: "Open Register",
    summary: "Register sessions track the drawer, sales, cash movement, and closing amounts for a work period.",
    prerequisites: ["At least one active register and location must exist.", "Opening cash should be counted before the first sale."],
    steps: [
      "Open Register and choose the active register and location.",
      "Enter opening cash if cash tracking is used.",
      "Run sales throughout the session; a new pending sale starts automatically after each completed sale.",
      "Record paid-in or paid-out cash movements as they happen.",
      "Close the register by counting cash and reviewing over/short results.",
    ],
    tips: ["Keep one register session per physical drawer when possible.", "Use Reports after close to compare app totals against Square and cash counts."],
    troubleshooting: ["If a sale cannot complete, confirm the register session is still open.", "If Square returns without payment, the pending payment should cancel and leave the sale ready for another tender."],
    related: ["register-new-sale", "square-setup", "reports"],
  },
  {
    id: "discount-rules",
    title: "Use named and automatic discounts",
    category: "Register",
    routes: ["pos", "settings"],
    roles: ["administrator", "manager", "staff"],
    actionPath: "/pos/register",
    actionLabel: "Open Register",
    summary: "Discount rules can be manual, named, scheduled, item-based, sale-total-based, or quantity-based depending on store policy.",
    prerequisites: ["Manager approval may be required for protected discounts.", "Date and time rules depend on the store's configured timezone."],
    steps: [
      "Create named discounts for recurring store policies or events.",
      "Choose percentage or dollar discount behavior.",
      "Set rules for eligible items, sale subtotal, item count, or active dates.",
      "At the register, review automatically applied discounts before payment.",
      "Use manual discounts only when the sale needs a one-time adjustment.",
    ],
    tips: ["Name discounts clearly so receipts and reports make sense later.", "Use approval thresholds for high-value discounts."],
    troubleshooting: ["If an automatic discount does not appear, check its active dates, minimum sale amount, item eligibility, and manager approval setting."],
    related: ["discounts", "register-new-sale", "reports"],
  },
  {
    id: "online-checkout",
    title: "Check online pickup and shipping orders",
    category: "Storefront",
    routes: ["storefront-admin", "orders"],
    roles: ["administrator", "manager", "staff"],
    actionPath: "/app/orders",
    actionLabel: "Open Orders",
    summary: "Online checkout depends on item availability, fulfillment settings, Square checkout, and order review from staff.",
    prerequisites: ["Square web checkout must be configured for customer-site card payments.", "Pickup or shipping must be enabled both globally and on the item."],
    steps: [
      "Confirm storefront profile, pickup, shipping, and item visibility defaults in Settings.",
      "Make sure each listed item allows the intended fulfillment option.",
      "Customer checkout creates or updates a storefront order.",
      "Staff review incoming orders, reserve inventory, and update fulfillment status.",
      "Use tracking details when shipped orders leave the store.",
    ],
    tips: ["Run a low-dollar test checkout after changing Square or fulfillment settings.", "Use default pickup and shipping settings before a large receiving session."],
    troubleshooting: ["If checkout only offers pickup, confirm shipping is enabled at the store and item level.", "If the Square button does not work, verify Square web checkout credentials and callback URLs."],
    related: ["storefront-setup", "square-setup", "inventory-defaults"],
  },
  {
    id: "report-problem",
    title: "Report a problem",
    category: "Help",
    routes: ["help", "inventory", "receive", "item", "settings", "pos", "trade-ins", "customers", "orders", "returns", "reconciliation", "supplier-search", "imports", "labels", "reports", "loyalty", "storefront-admin", "onboarding", "purchasing"],
    summary: "Send a problem report to PaperbackOS Manager with page context and an optional screenshot.",
    steps: [
      "Use Report problem from the sidebar footer.",
      "Choose the severity and add a short title.",
      "Describe what you expected, what happened, and what you were doing right before it happened.",
      "Attach a screenshot if it helps explain the issue.",
      "Send the report. It will appear in the Manager Support inbox.",
    ],
  },
];

let csrfToken = "";

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  return requestJson<T>(path, init, csrfToken);
}

async function apiText<T>(path: string, init: RequestInit = {}): Promise<T> {
  return requestText<T>(path, init, csrfToken);
}

export function App() {
  const [route, setRoute] = useState<AppRoute>(() => routeFromPath(window.location.pathname));
  const [view, setView] = useState<View>(() => viewFromRoute(routeFromPath(window.location.pathname)));
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [supplierSearchItem, setSupplierSearchItem] = useState<InventoryItem | null>(null);
  const [customerRequestPrefill, setCustomerRequestPrefill] = useState<CustomerRequestPrefill | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">(() => localStorage.getItem(themeStorageKey) === "light" ? "light" : "dark");
  const [session, setSession] = useState<UserSession | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem("staffNavCollapsed") === "true");
  const [storeProfile, setStoreProfile] = useState<PublicStoreProfile>(defaultStoreProfile);
  const [organizationModules, setOrganizationModules] = useState<OrganizationModuleView[]>([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [modulesError, setModulesError] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const [contextHelpOpen, setContextHelpOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(themeStorageKey, theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("staffNavCollapsed", String(navCollapsed));
  }, [navCollapsed]);

  useEffect(() => {
    api<{ profile: PublicStoreProfile }>("/api/storefront/v1/profile")
      .then((data) => setStoreProfile({ ...defaultStoreProfile, ...data.profile }))
      .catch(() => setStoreProfile(defaultStoreProfile));
  }, []);

  useEffect(() => {
    document.title = ["storefront", "cart", "product", "collection", "account"].includes(route)
      ? (storeProfile.storefrontTitle ?? storeProfile.storeDisplayName)
      : `${routeTitle(route)} | ${productBrand.name}`;
  }, [route, storeProfile]);

  useEffect(() => {
    api<{ user: UserSession }>("/api/admin/session")
      .then((data) => {
        csrfToken = data.user.csrfToken;
        setSession(data.user);
      })
      .catch(() => setSession(null))
      .finally(() => setSessionLoaded(true));
  }, []);

  useEffect(() => {
    if (!session) {
      setOrganizationModules([]);
      setModulesLoaded(false);
      return;
    }
    api<{ modules: OrganizationModuleView[] }>("/api/admin/modules")
      .then((data) => {
        setOrganizationModules(Array.isArray(data.modules) ? data.modules : []);
        setModulesError("");
      })
      .catch((error) => setModulesError(staffErrorMessage(error)))
      .finally(() => setModulesLoaded(true));
  }, [session]);

  useEffect(() => {
    const onPop = () => {
      const nextRoute = routeFromPath(window.location.pathname);
      setRoute(nextRoute);
      setView(viewFromRoute(nextRoute));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (route === "demo") return <DemoGateway storeProfile={storeProfile} navigate={(path) => navigatePath(path, setRoute, setView)} />;
  if (route === "scanner") return <PhoneScannerPage storeProfile={storeProfile} />;
  if (route === "storefront") return <Storefront storeProfile={storeProfile} navigate={(path) => navigatePath(path, setRoute, setView)} />;
  if (route === "cart") return <CartPage storeProfile={storeProfile} navigate={(path) => navigatePath(path, setRoute, setView)} />;
  if (route === "product") return <ProductPage storeProfile={storeProfile} navigate={(path) => navigatePath(path, setRoute, setView)} itemId={productIdFromPath(window.location.pathname)} />;
  if (route === "collection") return <CollectionPage storeProfile={storeProfile} navigate={(path) => navigatePath(path, setRoute, setView)} slug={collectionSlugFromPath(window.location.pathname)} />;
  if (route === "account") return <CustomerAccountPage storeProfile={storeProfile} navigate={(path) => navigatePath(path, setRoute, setView)} />;
  if (!sessionLoaded) return <div className="auth-page"><div className="skeleton" /></div>;
  if (!session) return <LoginScreen storeProfile={storeProfile} onLogin={(user) => { csrfToken = user.csrfToken; setSession(user); navigatePath("/app/inventory", setRoute, setView); }} />;

  const navigate = (path: string) => {
    navigatePath(path, setRoute, setView);
    setNavOpen(false);
  };
  const openItem = (item: InventoryItem) => {
    setSelectedItem(item);
    navigate("/app/items");
  };
  const openSupplierSearch = (item: InventoryItem) => {
    setSupplierSearchItem(item);
    navigate(`/app/items/${item.id}/supplier-search`);
  };
  const openCustomerRequest = (item: InventoryItem) => {
    setCustomerRequestPrefill(inventoryItemToRequestPrefill(item));
    navigate("/app/customers");
  };
  const moduleEnabled = Object.fromEntries(organizationModules.map((entry) => [entry.state.moduleKey, entry.state.enabled])) as Partial<Record<ModuleKey, boolean>>;
  const visibleNav = staffNavigation.filter((entry) => {
    if (!entry.roles.includes(session.role)) return false;
    const moduleKey = entry.moduleKey ?? moduleForNavigation(entry.label)?.key;
    return !moduleKey || isModuleEnabledInMap(moduleEnabled, moduleKey);
  });
  const routeModule = moduleForRoute(route as ModuleRoute);
  const disabledRouteModule = routeModule && !isModuleEnabledInMap(moduleEnabled, routeModule.key) ? routeModule : null;

  return (
    <div className={`shell ${navOpen ? "nav-open" : ""} ${navCollapsed ? "nav-collapsed" : ""}`}>
      <header className="mobile-topbar">
        <button className="icon" aria-label="Open navigation menu" aria-expanded={navOpen} onClick={() => setNavOpen((open) => !open)}>
          {navOpen ? <X /> : <Menu />}
        </button>
        <div className="brand">
          <ProductBrandMark />
          <div>
            <strong>{productBrand.name}</strong>
            <span>{storeProfile.storeDisplayName}</span>
          </div>
        </div>
      </header>
      {navOpen && <button className="nav-scrim" aria-label="Close navigation menu" onClick={() => setNavOpen(false)} />}
      <aside className="sidebar" aria-label="Primary navigation">
        <div className="brand">
          <ProductBrandMark />
          <img className="sidebar-brand-icon" src={productBrand.icon} alt={productBrand.name} />
          <div>
            <strong>{productBrand.name}</strong>
            <span>{storeProfile.storeDisplayName}</span>
          </div>
        </div>
        <button
          className="nav-collapse-toggle"
          aria-label={navCollapsed ? "Expand navigation labels" : "Collapse navigation to icons"}
          aria-expanded={!navCollapsed}
          title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
          onClick={() => setNavCollapsed((collapsed) => !collapsed)}
        >
          {navCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
        </button>
        <nav>
          {visibleNav.map((entry) => {
            const active = entry.routes.includes(route);
            const Icon = entry.icon;
            return (
              <button key={entry.label} className={active ? "active" : ""} aria-current={active ? "page" : undefined} title={entry.label} onClick={() => navigate(entry.path)}>
                <Icon aria-hidden /> <span className="nav-label">{entry.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="context-card" aria-label="Current store context">
            <strong>{storeProfile.storeDisplayName}</strong>
            <span>{storeProfile.currencyCode ?? defaultSettings.defaultCurrency}</span>
          </div>
          <button title="Page help" onClick={() => setContextHelpOpen(true)}><CircleHelp aria-hidden /> <span className="nav-label">Page help</span></button>
          <button title="Sign out" onClick={async () => { await api("/api/auth/logout", { method: "POST" }); csrfToken = ""; setSession(null); }}><Shield aria-hidden /> <span className="nav-label">Sign out</span></button>
          <button title="Report a problem" onClick={() => setSupportOpen(true)}><AlertTriangle aria-hidden /> <span className="nav-label">Report problem</span></button>
          <div className="session"><Shield aria-hidden /> {`${session.displayName} - ${session.role}`}</div>
        </div>
      </aside>
      <main>
        {modulesError && <div className="error" role="alert"><AlertTriangle /> {modulesError}</div>}
        {!modulesLoaded && <p className="empty-inline">Loading organization modules...</p>}
        {disabledRouteModule ? (
          <ModuleDisabledScreen moduleKey={disabledRouteModule.key} canConfigure={session.role === "administrator"} onNavigate={navigate} />
        ) : <>
        {route === "inventory" && <InventoryDashboard onOpen={openItem} onSupplierSearch={openSupplierSearch} onCustomerRequest={openCustomerRequest} />}
        {view === "receive" && <ReceivingOperationsPage api={api} storeProfile={storeProfile} currentUser={session} />}
        {route === "item" && <ItemEditor item={selectedItem} onBack={() => navigate("/app/inventory")} />}
        {route === "supplier-search" && <SupplierSearchScreen itemId={itemIdFromSupplierSearchPath(window.location.pathname)} initialItem={supplierSearchItem ?? selectedItem} onBack={() => navigate("/app/inventory")} onOpenPurchasing={() => navigate("/app/purchasing")} />}
        {route === "pos" && <Phase5PosRegister api={api} storeProfile={storeProfile} currentUser={session} />}
        {route === "trade-ins" && <TradeInWorkspace api={api} storeProfile={storeProfile} currentUser={session} />}
        {route === "purchasing" && <PurchasingWorkspace api={api} storeProfile={storeProfile} currentUser={session} navigate={navigate} />}
        {route === "customers" && <CustomersPage prefill={customerRequestPrefill} onPrefillConsumed={() => setCustomerRequestPrefill(null)} />}
        {route === "orders" && <OrderManagement />}
        {route === "reports" && <ReportsPage />}
        {route === "loyalty" && <LoyaltyPage />}
        {route === "storefront-admin" && <StorefrontAdminPage />}
        {route === "onboarding" && <OnboardingPage />}
        {route === "returns" && <StoreCreditReturnsWorkspace api={api} storeProfile={storeProfile} currentUser={session} />}
        {route === "reconciliation" && <ReconciliationWorkspace api={api} storeProfile={storeProfile} currentUser={session} />}
        {route === "imports" && <ImportsPage />}
        {route === "labels" && <LabelsPage api={api} apiText={apiText} storeProfile={storeProfile} currentUser={session} />}
        {route === "settings" && <InventorySettingsPage api={api} shellStoreProfile={storeProfile} currentUser={session} theme={theme} onThemeChange={setTheme} onNavigate={navigate} organizationModules={organizationModules} onModulesChanged={setOrganizationModules} />}
        {route === "help" && <HelpPage currentUser={session} navigate={navigate} />}
        </>}
      </main>
      {contextHelpOpen && <ContextHelpDialog route={route} currentUser={session} navigate={navigate} onClose={() => setContextHelpOpen(false)} />}
      {supportOpen && <SupportProblemDialog api={api} storeProfile={storeProfile} currentUser={session} route={route} onClose={() => setSupportOpen(false)} />}
    </div>
  );
}

function HelpPage({ currentUser, navigate }: { currentUser: UserSession; navigate: (path: string) => void }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const availableTopics = useMemo(() => visibleHelpTopics(currentUser.role), [currentUser.role]);
  const categories = useMemo(() => ["all", ...Array.from(new Set(availableTopics.map((topic) => topic.category))).sort()], [availableTopics]);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleTopics = availableTopics.filter((topic) => {
    const categoryMatch = category === "all" || topic.category === category;
    if (!categoryMatch) return false;
    if (!normalizedQuery) return true;
    const haystack = [topic.title, topic.category, topic.summary, ...(topic.prerequisites ?? []), ...topic.steps, ...(topic.tips ?? []), ...(topic.troubleshooting ?? []), ...(topic.related ?? [])].join(" ").toLowerCase();
    return haystack.includes(normalizedQuery);
  });
  return (
    <div className="phase-grid help-page">
      <div className="page-header">
        <div><p className="eyebrow">Help</p><h1>Help Center</h1><p className="empty-inline">Search setup guides, workflows, and page-specific help.</p></div>
      </div>
      <div className="toolbar help-toolbar">
          <label className="searchbox"><Search aria-hidden /><input value={query} placeholder="Search help topics" onChange={(event) => setQuery(event.target.value)} /></label>
          <label>Category<select value={category} onChange={(event) => setCategory(event.target.value)}>{categories.map((entry) => <option key={entry} value={entry}>{entry === "all" ? "All categories" : entry}</option>)}</select></label>
      </div>
      <section className="panel">
        <div className="section-heading"><h2>Topics</h2><span className="empty-inline">{visibleTopics.length} found</span></div>
        <div className="help-topic-list">
          {visibleTopics.map((topic) => <HelpTopicCard key={topic.id} topic={topic} expanded navigate={navigate} />)}
          {visibleTopics.length === 0 && <p className="empty-inline">No help topics match that search.</p>}
        </div>
      </section>
    </div>
  );
}

function ContextHelpDialog({ route, currentUser, navigate, onClose }: { route: AppRoute; currentUser: UserSession; navigate: (path: string) => void; onClose: () => void }) {
  const topics = topicsForRoute(route, currentUser.role);
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal help-dialog" role="dialog" aria-modal="true" aria-labelledby="context-help-title">
        <div className="section-heading">
          <div><p className="eyebrow">Page help</p><h2 id="context-help-title">{routeTitle(route)}</h2></div>
          <button className="icon" aria-label="Close page help" onClick={onClose}><X /></button>
        </div>
        <div className="help-topic-list">
          {topics.map((topic) => <HelpTopicCard key={topic.id} topic={topic} expanded navigate={(path) => { onClose(); navigate(path); }} />)}
          {topics.length === 0 && <p className="empty-inline">No contextual help is available for this page yet.</p>}
        </div>
        <div className="modal-actions">
          <button onClick={() => { onClose(); navigate("/app/help"); }}>Open Help Center</button>
          <button className="primary" onClick={onClose}>Done</button>
        </div>
      </section>
    </div>
  );
}

function HelpTopicCard({ topic, expanded = false, navigate }: { topic: HelpTopic; expanded?: boolean; navigate?: (path: string) => void }) {
  return (
    <article className="help-topic-card">
      <div>
        <span className="badge">{topic.category}</span>
        <h3>{topic.title}</h3>
        <p>{topic.summary}</p>
      </div>
      {expanded && <>
        {topic.prerequisites?.length ? <HelpList title="Before You Start" items={topic.prerequisites} /> : null}
        <HelpList title="Steps" items={topic.steps} ordered />
        {topic.tips?.length ? <HelpList title="Tips" items={topic.tips} /> : null}
        {topic.troubleshooting?.length ? <HelpList title="Troubleshooting" items={topic.troubleshooting} /> : null}
      </>}
      {topic.actionPath && navigate && <button className="help-topic-action" onClick={() => navigate(topic.actionPath || "/app/help")}>{topic.actionLabel || "Open screen"}</button>}
    </article>
  );
}

function HelpList({ title, items, ordered = false }: { title: string; items: string[]; ordered?: boolean }) {
  const ListTag = ordered ? "ol" : "ul";
  return (
    <div className="help-list">
      <strong>{title}</strong>
      <ListTag>{items.map((item) => <li key={item}>{item}</li>)}</ListTag>
    </div>
  );
}

function visibleHelpTopics(role: UserSession["role"]): HelpTopic[] {
  return helpTopics.filter((topic) => !topic.roles || topic.roles.includes(role));
}

function topicsForRoute(route: AppRoute, role: UserSession["role"]): HelpTopic[] {
  const topics = visibleHelpTopics(role);
  const exact = topics.filter((topic) => topic.routes.includes(route));
  return exact.length ? exact : topics.filter((topic) => topic.routes.includes("help"));
}

function SupportProblemDialog({ api, storeProfile, currentUser, route, onClose }: { api: <T>(path: string, init?: RequestInit) => Promise<T>; storeProfile: PublicStoreProfile; currentUser: UserSession; route: AppRoute; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<"low" | "normal" | "high" | "urgent">("normal");
  const [attachment, setAttachment] = useState<{ name: string; type: "image/png" | "image/jpeg" | "image/webp"; size: number; dataUrl: string } | undefined>();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const chooseFile = async (file?: File) => {
    setError("");
    setAttachment(undefined);
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setError("Choose a PNG, JPEG, or WebP image.");
      return;
    }
    if (file.size > 750000) {
      setError("Choose an image under 750 KB.");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setAttachment({ name: file.name, type: file.type as "image/png" | "image/jpeg" | "image/webp", size: file.size, dataUrl });
  };

  const submitIssue = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError("");
    setMessage("");
    try {
      const response = await api<{ issueId?: string }>("/api/admin/support/issues", {
        method: "POST",
        body: JSON.stringify({
          title,
          description,
          severity,
          attachment,
          idempotencyKey: crypto.randomUUID(),
          context: {
            route,
            path: window.location.pathname,
            search: window.location.search,
            clientName: storeProfile.storeDisplayName,
            productName: storeProfile.productName ?? productBrand.name,
            userRole: currentUser.role,
            viewport: { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio },
          },
        }),
      });
      setMessage(response.issueId ? `Reported as ${response.issueId.slice(0, 8)}.` : "Problem reported.");
      setTimeout(onClose, 650);
    } catch (err) {
      setError(staffErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal support-dialog" role="dialog" aria-modal="true" aria-labelledby="support-dialog-title">
        <div className="section-heading">
          <div><p className="eyebrow">Support</p><h2 id="support-dialog-title">Report a problem</h2></div>
          <button className="icon" aria-label="Close report problem dialog" onClick={onClose}><X /></button>
        </div>
        <form className="form-grid dialog-form" onSubmit={submitIssue}>
          <label>Severity<select value={severity} onChange={(event) => setSeverity(event.target.value as "low" | "normal" | "high" | "urgent")}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option></select></label>
          <label>Screenshot<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void chooseFile(event.target.files?.[0])} /></label>
          <label className="wide">Title<input value={title} maxLength={180} required onChange={(event) => setTitle(event.target.value)} /></label>
          <label className="wide">What happened?<textarea value={description} maxLength={8000} required onChange={(event) => setDescription(event.target.value)} /></label>
          <p className="empty-inline wide">{attachment ? `${attachment.name} attached` : `Page context will include ${window.location.pathname}`}</p>
          {message && <div className="success wide">{message}</div>}
          {error && <div className="error-banner wide" role="alert">{error}</div>}
          <div className="modal-actions wide">
            <button type="button" onClick={onClose}>Cancel</button>
            <button className="primary" type="submit" disabled={pending}>{pending ? "Sending..." : "Send report"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function PhoneScannerPage({ storeProfile }: { storeProfile: PublicStoreProfile }) {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const kind = window.location.pathname.startsWith("/scan/pos") ? "pos" : "receiving";
  const sessionId = params.get("session") ?? "";
  const token = params.get("token") ?? "";
  const [manualInput, setManualInput] = useState("");
  const [status, setStatus] = useState("Checking scanner link...");
  const [ready, setReady] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!sessionId || !token) {
      setStatus("This scanner link is missing pairing details.");
      return;
    }
    requestJson<{ session: { expiresAt: string } }>(`/api/scanner/${kind}/${encodeURIComponent(sessionId)}?${new URLSearchParams({ token })}`)
      .then((data) => {
        setReady(true);
        setStatus(`Connected. Link expires ${new Date(data.session.expiresAt).toLocaleTimeString()}.`);
      })
      .catch((error) => setStatus(staffErrorMessage(error)));
  }, [kind, sessionId, token]);

  const submitScan = async (rawValue: string) => {
    const trimmed = rawValue.trim();
    if (!ready || !trimmed || pending) return;
    setPending(true);
    const normalized = normalizeIsbn(trimmed);
    const input = isValidIsbn(normalized) ? normalized : trimmed;
    setStatus(`Sending ${input}...`);
    try {
      await requestJson(`/api/scanner/${kind}/${encodeURIComponent(sessionId)}/scans?${new URLSearchParams({ token })}`, {
        method: "POST",
        body: JSON.stringify({ input, idempotencyKey: crypto.randomUUID() }),
      });
      setManualInput("");
      setStatus(`Sent ${input}. Keep scanning.`);
    } catch (error) {
      setStatus(staffErrorMessage(error));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="phone-scanner-page">
      <section className="phone-scanner-shell">
        <div className="brand compact-brand">
          <ProductBrandMark />
          <div>
            <strong>{storeProfile.storeDisplayName}</strong>
            <span>{kind === "pos" ? "Register scanner" : "Add stock scanner"}</span>
          </div>
        </div>
        <RemoteCameraScanner disabled={!ready || pending} onDetected={submitScan} />
        <form className="phone-scanner-manual" onSubmit={(event) => { event.preventDefault(); void submitScan(manualInput); }}>
          <label>Manual barcode
            <input inputMode="numeric" value={manualInput} disabled={!ready || pending} onChange={(event) => setManualInput(event.target.value)} placeholder="Type or paste barcode" />
          </label>
          <button className="primary" disabled={!ready || pending || !manualInput.trim()}>Send</button>
        </form>
        <p className="phone-scanner-status" role="status">{status}</p>
      </section>
    </main>
  );
}

function RemoteCameraScanner({ disabled, onDetected }: { disabled?: boolean; onDetected: (value: string) => Promise<void> | void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const onDetectedRef = useRef(onDetected);
  const processingRef = useRef(false);
  const lastScanRef = useRef<{ value: string; scannedAt: number } | null>(null);
  const [message, setMessage] = useState("Starting camera...");

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (disabled) return undefined;
    let active = true;
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      controlsRef.current?.stop();
      controlsRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
    const completeScan = async (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return false;
      const normalizedIsbn = normalizeIsbn(trimmed);
      const value = isValidIsbn(normalizedIsbn) ? normalizedIsbn : trimmed;
      const lastScan = lastScanRef.current;
      const now = Date.now();
      if (processingRef.current || (lastScan?.value === value && now - lastScan.scannedAt < 1400)) return false;
      processingRef.current = true;
      lastScanRef.current = { value, scannedAt: now };
      setMessage(`Scanned ${value}`);
      try {
        await onDetectedRef.current(value);
        if (active) setMessage("Scan sent. Aim at the next barcode.");
      } catch (error) {
        if (active) setMessage(error instanceof Error ? error.message : "Could not send scan.");
      } finally {
        processingRef.current = false;
      }
      return true;
    };
    const startZxingFallback = async () => {
      const video = videoRef.current;
      if (!video) return;
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        setMessage("Point the camera at a barcode.");
        controlsRef.current = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          video,
          (result) => {
            if (!active || !result) return;
            void completeScan(result.getText());
          },
        );
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera barcode scanning.");
      }
    };
    const start = async () => {
      const Detector = (globalThis as typeof globalThis & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
      if (!navigator.mediaDevices?.getUserMedia) {
        setMessage("Camera access is not available in this browser.");
        return;
      }
      if (!Detector) {
        await startZxingFallback();
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        const detector = new Detector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"] });
        setMessage("Point the camera at a barcode.");
        const detect = async () => {
          if (!active || !videoRef.current) return;
          try {
            const [barcode] = await detector.detect(videoRef.current);
            if (barcode?.rawValue) await completeScan(barcode.rawValue);
          } catch {
            setMessage("Looking for a readable barcode...");
          }
          frameRef.current = requestAnimationFrame(() => { void detect(); });
        };
        void detect();
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera.");
      }
    };
    void start();
    return () => {
      active = false;
      stop();
    };
  }, [disabled]);

  return (
    <div className="phone-scanner-camera">
      <video ref={videoRef} className="scanner-video" muted playsInline />
      <div className="scanner-target" aria-hidden />
      <span>{disabled ? "Waiting for pairing..." : message}</span>
    </div>
  );
}

function routeFromPath(path: string): AppRoute {
  if (demoGatewayEnabled && path === "/") return "demo";
  if (path.startsWith("/scan/")) return "scanner";
  if (path.startsWith("/cart") || path.startsWith("/checkout")) return "cart";
  if (path.startsWith("/account")) return "account";
  if (path.startsWith("/shop/book/")) return "product";
  if (path.startsWith("/shop/collections/")) return "collection";
  if (path.startsWith("/pos")) return "pos";
  if (path.startsWith("/app/settings/loyalty")) return "loyalty";
  if (path.startsWith("/app/settings/storefront")) return "storefront-admin";
  if (path.startsWith("/app/settings/onboarding")) return "onboarding";
  if (path.startsWith("/app/help")) return "help";
  if (path.startsWith("/app/purchasing")) return "purchasing";
  if (path.startsWith("/app/stock")) return "receive";
  if (path.includes("/supplier-search")) return "supplier-search";
  if (path.startsWith("/app/items")) return "item";
  if (path.startsWith("/app/settings")) return "settings";
  if (path.startsWith("/app/labels")) return "labels";
  if (path.startsWith("/app/reports")) return "reports";
  if (path.startsWith("/app/loyalty")) return "loyalty";
  if (path.startsWith("/app/storefront")) return "storefront-admin";
  if (path.startsWith("/app/onboarding")) return "onboarding";
  if (path.startsWith("/app/trade-ins")) return "trade-ins";
  if (path.startsWith("/app/customers")) return "customers";
  if (path.startsWith("/app/orders")) return "orders";
  if (path.startsWith("/app/returns")) return "returns";
  if (path.startsWith("/app/reconciliation")) return "reconciliation";
  if (path.startsWith("/app/imports")) return "imports";
  if (path.startsWith("/app")) return "inventory";
  return "storefront";
}

function ModuleDisabledScreen({ moduleKey, canConfigure, onNavigate }: { moduleKey: ModuleKey; canConfigure: boolean; onNavigate: (path: string) => void }) {
  const definition = getModuleDefinition(moduleKey);
  return (
    <section className="page module-disabled-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Module disabled</p>
          <h1>{definition.displayName}</h1>
        </div>
      </header>
      <div className="feature-panel" role="status">
        <AlertTriangle aria-hidden />
        <div>
          <strong>{definition.displayName} is disabled for this organization.</strong>
          <p>{definition.disabledReadPolicy}</p>
          <p>Existing data is preserved and can be accessed again if an administrator re-enables the module.</p>
        </div>
      </div>
      <div className="actions">
        <button onClick={() => onNavigate("/app/inventory")}><Boxes /> Inventory</button>
        {canConfigure && <button className="primary" onClick={() => onNavigate("/app/settings")}><Settings /> Module settings</button>}
      </div>
    </section>
  );
}

function viewFromRoute(route: AppRoute): View {
  if (route === "scanner") return "inventory";
  if (route === "receive") return "receive";
  if (route === "supplier-search") return "supplier-search";
  if (route === "item") return "item";
  if (route === "settings") return "settings";
  if (route === "labels") return "labels";
  if (route === "imports") return "imports";
  if (route === "returns") return "returns";
  if (route === "reconciliation") return "reconciliation";
  if (route === "purchasing") return "purchasing";
  if (route === "help") return "help";
  return "inventory";
}

function navigatePath(path: string, setRoute: (route: AppRoute) => void, setView: (view: View) => void) {
  window.history.pushState({}, "", path);
  const route = routeFromPath(path);
  setRoute(route);
  setView(viewFromRoute(route));
}

function itemIdFromSupplierSearchPath(path: string): string | undefined {
  const match = path.match(/^\/app\/items\/([^/]+)\/supplier-search$/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function productIdFromPath(path: string): string {
  const match = path.match(/^\/shop\/book\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : "";
}

function collectionSlugFromPath(path: string): string {
  const match = path.match(/^\/shop\/collections\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : "";
}

function routeTitle(route: AppRoute): string {
  if (route === "demo") return "Demo";
  if (route === "scanner") return "Phone scanner";
  if (route === "pos") return "Register";
  if (route === "receive") return "Add Stock";
  if (route === "returns" || route === "orders") return "Sales & Returns";
  if (route === "reconciliation") return "Operations";
  if (route === "imports") return "Imports";
  if (route === "reports") return "Reports";
  if (route === "purchasing") return "Purchasing";
  if (route === "loyalty") return "Loyalty";
  if (route === "storefront-admin") return "Storefront";
  if (route === "onboarding") return "Onboarding";
  if (route === "help") return "Help";
  if (route === "settings") return "Settings";
  if (route === "trade-ins") return "Trade-ins";
  if (route === "customers") return "Requests";
  return "Inventory";
}

function ProductBrandMark() {
  return <img className="product-wordmark" src={productBrand.wordmarkInverse} alt={productBrand.name} />;
}

function DemoGateway({ storeProfile, navigate }: { storeProfile: PublicStoreProfile; navigate: (path: string) => void }) {
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", evaluationNotes: "", website: "" });
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("");
    setError("");
    try {
      await api("/api/demo/access-request", { method: "POST", body: JSON.stringify(form) });
      setStatus("Thanks. We received your request and will email the demo login details.");
      setForm({ name: "", organization: "", email: "", phone: "", evaluationNotes: "", website: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    }
  };
  const goToStorefront = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (/^https?:\/\//.test(demoStorefrontUrl)) return;
    event.preventDefault();
    navigate(demoStorefrontUrl);
  };
  const goToAdmin = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (/^https?:\/\//.test(demoAdminUrl)) return;
    event.preventDefault();
    navigate(demoAdminUrl);
  };
  return (
    <main className="demo-gateway">
      <section className="demo-hero">
        <nav>
          <ProductBrandMark />
          <span>Demo resets nightly</span>
        </nav>
        <div>
          <p className="eyebrow">{storeProfile.storeDisplayName}</p>
          <h1>PaperbackOS Demo</h1>
          <p>Explore the storefront instantly, or request staff demo credentials to tour the operating system behind the counter.</p>
        </div>
      </section>
      <section className="demo-choice-band" aria-label="Demo choices">
        <article className="demo-choice">
          <Mail aria-hidden />
          <div>
            <h2>Get Access</h2>
            <p>Send your contact info and we will email the shared demo login.</p>
          </div>
          <a className="button-link primary" href="#demo-access-form">Request Login</a>
        </article>
        <article className="demo-choice">
          <BookOpen aria-hidden />
          <div>
            <h2>View Storefront Demo</h2>
            <p>Browse the public bookstore experience without signing in.</p>
          </div>
          <a className="button-link" href={demoStorefrontUrl} onClick={goToStorefront}>Open Storefront</a>
        </article>
        <article className="demo-choice">
          <MonitorCog aria-hidden />
          <div>
            <h2>Admin Demo</h2>
            <p>Use the emailed demo credentials to enter the staff workspace.</p>
          </div>
          <a className="button-link" href={demoAdminUrl} onClick={goToAdmin}>Open Login</a>
        </article>
      </section>
      <section className="demo-access-section" id="demo-access-form">
        <form className="demo-access-form" onSubmit={submit}>
          <div>
            <p className="eyebrow">Demo Access</p>
            <h2>Receive the demo login</h2>
          </div>
          {status && <div className="success">{status}</div>}
          {error && <div className="error">{error}</div>}
          <label>Name<input required autoComplete="name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
          <label>Bookstore or organization<input required autoComplete="organization" value={form.organization} onChange={(event) => setForm({ ...form, organization: event.target.value })} /></label>
          <label>Email<input required type="email" autoComplete="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
          <label>Phone optional<input autoComplete="tel" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
          <label className="wide">What are you hoping to evaluate? optional<textarea rows={4} value={form.evaluationNotes} onChange={(event) => setForm({ ...form, evaluationNotes: event.target.value })} /></label>
          <label className="demo-honey" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(event) => setForm({ ...form, website: event.target.value })} /></label>
          <div className="wide actions"><button className="primary" type="submit">Send Request</button></div>
        </form>
      </section>
    </main>
  );
}

function readCartStorage(): { cartId: string; cartToken: string } {
  const cartId = localStorage.getItem(cartIdStorageKey) ?? localStorage.getItem(legacyCartIdStorageKey) ?? "";
  const cartToken = localStorage.getItem(cartTokenStorageKey) ?? localStorage.getItem(legacyCartTokenStorageKey) ?? "";
  return { cartId, cartToken };
}

function writeCartStorage(cartId: string, cartToken: string) {
  localStorage.setItem(cartIdStorageKey, cartId);
  localStorage.setItem(cartTokenStorageKey, cartToken);
  localStorage.removeItem(legacyCartIdStorageKey);
  localStorage.removeItem(legacyCartTokenStorageKey);
  notifyStorefrontCartChanged();
}

function clearCartStorage() {
  localStorage.removeItem(cartIdStorageKey);
  localStorage.removeItem(cartTokenStorageKey);
  localStorage.removeItem(legacyCartIdStorageKey);
  localStorage.removeItem(legacyCartTokenStorageKey);
  notifyStorefrontCartChanged();
}

function cartItemCount(cart: { lines?: PublicCartLine[]; giftCardLines?: PublicGiftCardLine[] } | undefined | null): number {
  const inventoryCount = (cart?.lines ?? []).reduce((sum, line) => sum + Math.max(0, Number(line.quantity) || 0), 0);
  const giftCardCount = (cart?.giftCardLines ?? []).reduce((sum, line) => sum + Math.max(0, Number(line.quantity) || 0), 0);
  return inventoryCount + giftCardCount;
}

function notifyStorefrontCartChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("paperbackos:storefront-cart-changed"));
}

function useStorefrontCartCount(): number {
  const [count, setCount] = useState(0);
  const refreshCount = useCallback(() => {
    const { cartId, cartToken } = readCartStorage();
    if (!cartId || !cartToken) {
      setCount(0);
      return;
    }
    void api<{ cart: { lines: PublicCartLine[]; giftCardLines?: PublicGiftCardLine[] } }>(`/api/storefront/v1/cart?${new URLSearchParams({ cartId, token: cartToken })}`)
      .then((data) => setCount(cartItemCount(data.cart)))
      .catch(() => {
        clearCartStorage();
        setCount(0);
      });
  }, []);

  useEffect(() => {
    refreshCount();
    const onCartChanged = () => refreshCount();
    const onStorage = (event: StorageEvent) => {
      if (!event.key || event.key === cartIdStorageKey || event.key === cartTokenStorageKey || event.key === legacyCartIdStorageKey || event.key === legacyCartTokenStorageKey) {
        refreshCount();
      }
    };
    window.addEventListener("paperbackos:storefront-cart-changed", onCartChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("paperbackos:storefront-cart-changed", onCartChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [refreshCount]);

  return count;
}

function StorefrontCartLink({ navigate, count }: { navigate: (path: string) => void; count: number }) {
  return (
    <a className="button-link" href="/cart" onClick={(event) => { event.preventDefault(); navigate("/cart"); }}>
      Cart ({count})
    </a>
  );
}

function readStorefrontSession(): string {
  return localStorage.getItem(storefrontSessionStorageKey) ?? "";
}

function writeStorefrontSession(token: string) {
  localStorage.setItem(storefrontSessionStorageKey, token);
}

function clearStorefrontSession() {
  localStorage.removeItem(storefrontSessionStorageKey);
}

function storefrontSessionHeaders(token = readStorefrontSession()): HeadersInit {
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function loadOrCreateStorefrontCart(): Promise<{ id: string; token: string }> {
  const existing = readCartStorage();
  if (existing.cartId && existing.cartToken) return { id: existing.cartId, token: existing.cartToken };
  const data = await api<{ cart: { id: string; token: string; lines: [] } }>("/api/storefront/v1/cart", { method: "POST", headers: storefrontSessionHeaders(), body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }) });
  writeCartStorage(data.cart.id, data.cart.token);
  return { id: data.cart.id, token: data.cart.token };
}

async function putStorefrontCartLines(currentCart: { id: string; token: string }, lines: { variationId: string; copyId?: string; quantity: number }[]): Promise<{ lines: PublicCartLine[] }> {
  const data = await api<{ cart: { lines: PublicCartLine[] } }>("/api/storefront/v1/cart/items", {
    method: "PUT",
    body: JSON.stringify({ cartId: currentCart.id, token: currentCart.token, lines }),
  });
  notifyStorefrontCartChanged();
  return data.cart;
}

function storefrontAppearanceClass(profile: PublicStoreProfile, extra = "") {
  return [
    "storefront",
    `storefront-theme-${profile.themePreset ?? "bookshop"}`,
    `storefront-density-${profile.gridDensity ?? "balanced"}`,
    `storefront-cards-${profile.cardStyle ?? "detailed"}`,
    extra,
  ].filter(Boolean).join(" ");
}

function storefrontAppearanceStyle(profile: PublicStoreProfile): React.CSSProperties {
  return {
    ...(profile.colorPrimary ? { "--store-primary": profile.colorPrimary } : {}),
    ...(profile.colorAccent ? { "--store-accent": profile.colorAccent } : {}),
    ...(profile.colorBackground ? { "--store-bg": profile.colorBackground } : {}),
    ...(profile.colorSurface ? { "--store-surface": profile.colorSurface } : {}),
    ...(profile.colorText ? { "--store-text": profile.colorText } : {}),
    ...(profile.headingFont ? { "--store-heading-font": storefrontHeadingFont(profile.headingFont) } : {}),
    ...(profile.bodyFont ? { "--store-body-font": storefrontBodyFont(profile.bodyFont) } : {}),
  } as React.CSSProperties;
}

function storefrontHeadingFont(font: NonNullable<PublicStoreProfile["headingFont"]>): string {
  return {
    modern_sans: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
    bookish_serif: 'Georgia, "Times New Roman", serif',
    classic_serif: 'Garamond, "Palatino Linotype", "Book Antiqua", Palatino, serif',
    friendly_rounded: '"Trebuchet MS", Avenir, "Segoe UI", sans-serif',
  }[font];
}

function storefrontBodyFont(font: NonNullable<PublicStoreProfile["bodyFont"]>): string {
  return {
    clean_sans: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
    humanist_sans: '"Trebuchet MS", Calibri, "Segoe UI", sans-serif',
    literary_serif: 'Georgia, "Times New Roman", serif',
  }[font];
}

function themeColorFallback(theme: PublicStoreProfile["themePreset"] | undefined, key: "colorPrimary" | "colorAccent" | "colorBackground" | "colorSurface" | "colorText") {
  const palettes = {
    bookshop: { colorPrimary: "#173f35", colorAccent: "#d66b1f", colorBackground: "#07121d", colorSurface: "#102132", colorText: "#fff3df" },
    modern: { colorPrimary: "#1f4f46", colorAccent: "#dc4c3e", colorBackground: "#f4f7f5", colorSurface: "#ffffff", colorText: "#17221f" },
    classic: { colorPrimary: "#6b1f2a", colorAccent: "#c69b4b", colorBackground: "#f5f0e6", colorSurface: "#fffdf8", colorText: "#241b18" },
    bright: { colorPrimary: "#006d77", colorAccent: "#ef476f", colorBackground: "#f7fbfa", colorSurface: "#ffffff", colorText: "#152629" },
  };
  return palettes[theme ?? "bookshop"][key];
}

function LoginScreen({ storeProfile, onLogin }: { storeProfile: PublicStoreProfile; onLogin: (user: UserSession) => void }) {
  const [username, setUsername] = useState("bookshopadmin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const login = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      const result = await api<{ user: UserSession }>("/api/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
      onLogin(result.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    }
  };
  return (
    <main className="auth-page">
      <form className="login-card" onSubmit={login}>
        <div className="brand">
          <ProductBrandMark />
          <div>
            <strong>{productBrand.name}</strong>
            <span>{storeProfile.storeDisplayName}</span>
          </div>
        </div>
        <h1>Staff Login</h1>
        <p>{productBrand.tagline}</p>
        {error && <div className="error">{error}</div>}
        <label>Username<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} /></label>
        <label>Password<input autoComplete="current-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <button className="primary" type="submit">Log in</button>
      </form>
    </main>
  );
}

function Storefront({ storeProfile, navigate }: { storeProfile: PublicStoreProfile; navigate: (path: string) => void }) {
  const [items, setItems] = useState<StorefrontItem[]>([]);
  const [categories, setCategories] = useState<{ id: string; ids?: string[]; name: string }[]>([]);
  const [collections, setCollections] = useState<StorefrontCollectionView[]>([]);
  const [staffPicks, setStaffPicks] = useState<StorefrontStaffPickView[]>([]);
  const [popularItems, setPopularItems] = useState<StorefrontItem[]>([]);
  const [signedItems, setSignedItems] = useState<StorefrontItem[]>([]);
  const [genreShelves, setGenreShelves] = useState<{ category: { id: string; name: string }; items: StorefrontItem[] }[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("relevance");
  const [authorFilter, setAuthorFilter] = useState("");
  const [publisherFilter, setPublisherFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [cartId, setCartId] = useState(readCartStorage().cartId);
  const [cartToken, setCartToken] = useState(readCartStorage().cartToken);
  const [cart, setCart] = useState<{ lines: PublicCartLine[] } | null>(null);
  const [status, setStatus] = useState("");
  const storefrontTitle = storeProfile.storefrontTitle ?? storeProfile.storeDisplayName;
  const storefrontDescription = storeProfile.storefrontDescription ?? storeProfile.storeDescription ?? "";
  const goToCart = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate("/cart");
  };
  const goToStaff = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate("/app/inventory");
  };
  const goToAccount = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate("/account");
  };
  const load = useCallback(async () => {
    const params = new URLSearchParams({ q: query, limit: "100" });
    const category = categories.find((entry) => entry.id === categoryId);
    if (categoryId) params.set("categoryId", categoryId);
    if (sort) params.set("sort", sort);
    if (authorFilter.trim()) params.set("author", authorFilter.trim());
    if (publisherFilter.trim()) params.set("publisher", publisherFilter.trim());
    if (conditionFilter.trim()) params.set("condition", conditionFilter.trim());
    if (minPrice.trim()) params.set("minPrice", String(Math.round(Number(minPrice) * 100)));
    if (maxPrice.trim()) params.set("maxPrice", String(Math.round(Number(maxPrice) * 100)));
    for (const extraId of category?.ids?.filter((id) => id !== categoryId) ?? []) params.append("categoryId", extraId);
    const data = await api<{ items: StorefrontItem[] }>(`/api/storefront/v1/items?${params}`);
    setItems(data.items ?? []);
  }, [authorFilter, categories, categoryId, conditionFilter, maxPrice, minPrice, publisherFilter, query, sort]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api<{ categories: { id: string; ids?: string[]; name: string }[] }>("/api/storefront/v1/categories")
      .then((data) => setCategories(data.categories ?? []))
      .catch(() => setCategories([]));
    api<{ collections: StorefrontCollectionView[] }>("/api/storefront/v1/collections")
      .then((data) => setCollections(data.collections ?? []))
      .catch(() => setCollections([]));
    api<{ staffPicks: StorefrontStaffPickView[] }>("/api/storefront/v1/staff-picks")
      .then((data) => setStaffPicks(data.staffPicks ?? []))
      .catch(() => setStaffPicks([]));
    api<{ items: StorefrontItem[] }>("/api/storefront/v1/popular?limit=8")
      .then((data) => setPopularItems(data.items ?? []))
      .catch(() => setPopularItems([]));
    api<{ items: StorefrontItem[] }>("/api/storefront/v1/items?signedOnly=true&limit=8")
      .then((data) => setSignedItems(data.items ?? []))
      .catch(() => setSignedItems([]));
    api<{ shelves: { category: { id: string; name: string }; items: StorefrontItem[] }[] }>("/api/storefront/v1/genre-shelves")
      .then((data) => setGenreShelves(data.shelves ?? []))
      .catch(() => setGenreShelves([]));
  }, []);
  useEffect(() => {
    if (!cartId || !cartToken) return;
    api<{ cart: { lines: PublicCartLine[] } }>(`/api/storefront/v1/cart?${new URLSearchParams({ cartId, token: cartToken })}`)
      .then((data) => setCart(data.cart))
      .catch(() => {
        clearCartStorage();
        setCartId("");
        setCartToken("");
        setCart(null);
      });
  }, [cartId, cartToken]);
  const ensureCart = async () => {
    const current = await loadOrCreateStorefrontCart();
    setCartId(current.id);
    setCartToken(current.token);
    return current;
  };
  const addToCart = async (item: StorefrontItem) => {
    const currentCart = await ensureCart();
    const variationId = item.variations[0]?.id;
    if (!variationId) return;
    const existing = cart?.lines ?? [];
    const next = existing.some((line) => line.variationId === variationId)
      ? existing.map((line) => line.variationId === variationId ? { variationId, quantity: line.quantity + 1 } : { variationId: line.variationId, quantity: line.quantity })
      : [...existing.map((line) => ({ variationId: line.variationId, quantity: line.quantity })), { variationId, quantity: 1 }];
    const data = await putStorefrontCartLines(currentCart, next);
    setCart(data);
    setStatus(`${item.title} added to cart`);
  };
  const goToProduct = (event: React.MouseEvent, item: StorefrontItem) => {
    event.preventDefault();
    navigate(`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`);
  };
  const homepageSections = storeProfile.homepageSections?.length ? storeProfile.homepageSections : ["collections", "staff_picks", "popular", "genre_shelves", "signed", "catalog"];
  const showHomeSection = (section: string) => homepageSections.includes(section);
  const sectionOrder = (section: string) => {
    const index = homepageSections.indexOf(section);
    return index >= 0 ? index : 99;
  };
  const sectionLayout = (section: string) => storeProfile.sectionLayouts?.[section] ?? (section === "catalog" ? storeProfile.catalogLayout ?? "grid" : "grid");
  return (
    <main className={storefrontAppearanceClass(storeProfile, demoGatewayEnabled ? "demo-storefront" : "")} style={storefrontAppearanceStyle(storeProfile)}>
      <header className={`storefront-hero hero-${storeProfile.heroStyle ?? "immersive"}`}>
        <nav><strong>{storeProfile.storeDisplayName}</strong><div className="store-nav-actions"><a className="button-link" href="/account" onClick={goToAccount}>Account</a><a className="button-link" href="/cart" onClick={goToCart}>Cart ({cartItemCount(cart)})</a><a className="button-link" href="/app/inventory" onClick={goToStaff}>Staff login</a></div></nav>
        <div>
          {storeProfile.storeTagline && <p className="eyebrow">{storeProfile.storeTagline}</p>}
          <h1>{storefrontTitle}</h1>
          {storefrontDescription && <p>{storefrontDescription}</p>}
        </div>
      </header>
      <section className="shop-band">
        {storeProfile.announcementEnabled && storeProfile.announcementText && <div className="announcement-banner" role="status">{storeProfile.announcementText}</div>}
        {showHomeSection("collections") && collections.length > 0 && (
          <div className={`collection-strip section-layout-${sectionLayout("collections")}`} aria-label="Featured collections" style={{ order: sectionOrder("collections") }}>
            {collections.slice(0, 6).map((collection) => (
              <a key={collection.id} href={`/shop/collections/${collection.slug}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/collections/${collection.slug}`); }}>
                <strong>{collection.name}</strong>
                {collection.description && <span>{descriptionSnippet(collection.description)}</span>}
              </a>
            ))}
          </div>
        )}
        {showHomeSection("staff_picks") && staffPicks.length > 0 && (
          <section className="storefront-section" style={{ order: sectionOrder("staff_picks") }}>
            <div className="section-heading"><div><p className="eyebrow">Staff picks</p><h2>Recommended by the store</h2></div></div>
            <div className={`store-grid compact section-layout-${sectionLayout("staff_picks")}`}>
              {staffPicks.slice(0, 4).map((pick) => {
                const item = items.find((entry) => entry.id === pick.variationId);
                return (
                  <article className="store-card staff-pick-card" key={pick.id}>
                    {demoGatewayEnabled && item && <a href={`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`); }}><Image imageUrl={item.imageUrl} /></a>}
                    <div>
                      <h3>{item?.title ?? "Staff pick"}</h3>
                      {item?.author && <p className="book-meta">{item.author}</p>}
                      <p>{pick.recommendationText}</p>
                      <small>{pick.staffDisplayName}</small>
                      <a className="button-link" href={`/shop/book/${pick.variationId}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/book/${pick.variationId}`); }}>View book</a>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}
        {showHomeSection("popular") && popularItems.length > 0 && <StorefrontShelf title="Popular at this store" eyebrow="Popular books" items={popularItems} navigate={navigate} order={sectionOrder("popular")} layout={sectionLayout("popular")} />}
        {showHomeSection("signed") && signedItems.length > 0 && <StorefrontShelf title="Signed copies" eyebrow="Special copies" items={signedItems} navigate={navigate} order={sectionOrder("signed")} layout={sectionLayout("signed")} />}
        {showHomeSection("genre_shelves") && genreShelves.length > 0 && (
          <section className="storefront-section" style={{ order: sectionOrder("genre_shelves") }}>
            <div className="section-heading"><div><p className="eyebrow">Genre shelves</p><h2>Browse by shelf</h2></div></div>
            {genreShelves.slice(0, 4).map((shelf) => <StorefrontShelf key={shelf.category.id} title={shelf.category.name} items={shelf.items} navigate={navigate} nested layout={sectionLayout("genre_shelves")} />)}
          </section>
        )}
        {showHomeSection("store_info") && (
          <section className={`storefront-section store-info-section section-layout-${sectionLayout("store_info")}`} style={{ order: sectionOrder("store_info") }}>
            <div><p className="eyebrow">Visit the store</p><h2>{storeProfile.storeDisplayName}</h2><p>{storeProfile.storeDescription ?? storefrontDescription}</p></div>
            <div>{storeProfile.publicPhone && <a href={`tel:${storeProfile.publicPhone}`}>{storeProfile.publicPhone}</a>}{storeProfile.publicEmail && <a href={`mailto:${storeProfile.publicEmail}`}>{storeProfile.publicEmail}</a>}</div>
          </section>
        )}
        {showHomeSection("loyalty") && (
          <section className={`storefront-section loyalty-callout section-layout-${sectionLayout("loyalty")}`} style={{ order: sectionOrder("loyalty") }}>
            <div><p className="eyebrow">Reader rewards</p><h2>Keep your bookstore account close</h2><p>Track orders, save a wishlist, and manage loyalty benefits.</p></div>
            <button className="primary" onClick={() => navigate("/account")}>Open account</button>
          </section>
        )}
        {showHomeSection("catalog") && <div className="storefront-catalog-section" style={{ order: sectionOrder("catalog") }}><div className="toolbar">
          <label className="searchbox"><Search /><span className="sr-only">Search shop</span><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void load(); }} placeholder="Search books, authors, ISBNs" /></label>
          <label>
            <span className="sr-only">Shop category</span>
            <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
              <option value="">All categories</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          </label>
          <label>
            <span className="sr-only">Sort books</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="relevance">Relevance</option>
              <option value="title">Title</option>
              <option value="author">Author</option>
              <option value="price_asc">Price low to high</option>
              <option value="price_desc">Price high to low</option>
            </select>
          </label>
          <button onClick={load}><Search /> Search</button>
          <a className="button-link" href="/cart" onClick={goToCart}>View cart ({cartItemCount(cart)})</a>
        </div>
        <div className="toolbar compact-toolbar storefront-filter-bar" aria-label="Storefront filters">
          <label>Author<input value={authorFilter} onChange={(event) => setAuthorFilter(event.target.value)} placeholder="Any author" /></label>
          <label>Publisher<input value={publisherFilter} onChange={(event) => setPublisherFilter(event.target.value)} placeholder="Any publisher" /></label>
          <label>Condition<input value={conditionFilter} onChange={(event) => setConditionFilter(event.target.value)} placeholder="new, good" /></label>
          <label>Min price<input type="number" min="0" step="0.01" value={minPrice} onChange={(event) => setMinPrice(event.target.value)} /></label>
          <label>Max price<input type="number" min="0" step="0.01" value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)} /></label>
        </div>
        <div className="category-strip" aria-label="Shop categories">
          <button className={categoryId === "" ? "active" : ""} onClick={() => setCategoryId("")}>All</button>
          {categories.map((category) => <button className={categoryId === category.id ? "active" : ""} key={category.id} onClick={() => setCategoryId(category.id)}>{category.name}</button>)}
        </div>
        {status && <div className="success">{status}</div>}
        <div className={`store-grid catalog-grid section-layout-${sectionLayout("catalog")}`}>
          {items.map((item) => (
            <article className="store-card" key={item.id}>
              <a href={`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`} onClick={(event) => goToProduct(event, item)}><Image imageUrl={item.imageUrl} /></a>
              <div>
                <h2><a href={`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`} onClick={(event) => goToProduct(event, item)}>{item.title}</a></h2>
                <p>{item.author ?? "Unknown author"}</p>
                <p>{descriptionSnippet(item.description ?? "")}</p>
                <strong>{formatMoney(item.priceMoney)}</strong>
                <button onClick={() => void addToCart(item)} disabled={!isStorefrontItemAvailable(item)}>Add to cart</button>
              </div>
            </article>
          ))}
          {items.length === 0 && <EmptyState />}
        </div></div>}
      </section>
      <StoreFooter profile={storeProfile} />
    </main>
  );
}

function StorefrontShelf({ title, eyebrow, items, navigate, order, nested = false, layout = "grid" }: { title: string; eyebrow?: string; items: StorefrontItem[]; navigate: (path: string) => void; order?: number; nested?: boolean; layout?: string }) {
  return (
    <section className={nested ? "storefront-subsection" : "storefront-section"} style={order === undefined ? undefined : { order }}>
      <div className="section-heading"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h2>{title}</h2></div></div>
      <div className={`store-grid compact section-layout-${layout}`}>
        {items.slice(0, 6).map((item) => (
          <article className="store-card" key={item.id}>
            <a href={`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`); }}><Image imageUrl={item.imageUrl} /></a>
            <div>
              <h3><a href={`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/book/${encodeURIComponent(item.publicId ?? item.id)}`); }}>{item.title}</a></h3>
              <p>{item.author ?? "Unknown author"}</p>
              {item.signedCopy && <p className="empty-inline">Signed copy</p>}
              <strong>{formatMoney(item.priceMoney)}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProductPage({ storeProfile, navigate, itemId }: { storeProfile: PublicStoreProfile; navigate: (path: string) => void; itemId: string }) {
  const [item, setItem] = useState<StorefrontItem | null>(null);
  const [related, setRelated] = useState<StorefrontItem[]>([]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const cartCount = useStorefrontCartCount();
  const token = readStorefrontSession();
  useEffect(() => {
    if (!itemId) return;
    api<{ item: StorefrontItem }>(`/api/storefront/v1/items/${encodeURIComponent(itemId)}`)
      .then((data) => {
        setItem(data.item);
        const params = new URLSearchParams({ limit: "8" });
        if (data.item.author) params.set("q", data.item.author);
        else if (data.item.categoryId) params.set("categoryId", data.item.categoryId);
        return api<{ items: StorefrontItem[] }>(`/api/storefront/v1/items?${params}`);
      })
      .then((data) => setRelated((data.items ?? []).filter((entry) => entry.id !== itemId).slice(0, 4)))
      .catch((caught: Error) => setError(caught.message));
  }, [itemId]);
  const addToCart = async (choice?: { variationId: string; copyId?: string }) => {
    if (!item) return;
    const current = await loadOrCreateStorefrontCart();
    const existing = await api<{ cart: { lines: PublicCartLine[] } }>(`/api/storefront/v1/cart?${new URLSearchParams({ cartId: current.id, token: current.token })}`).catch(() => ({ cart: { lines: [] } }));
    const variationId = choice?.variationId ?? item.variations?.[0]?.id ?? item.id;
    const next = [...existing.cart.lines.map((line) => ({ variationId: line.variationId, copyId: line.copyId, quantity: line.quantity })), { variationId, copyId: choice?.copyId, quantity: 1 }];
    await putStorefrontCartLines(current, next);
    setStatus(`${item.title} added to cart`);
  };
  const addWishlist = async () => {
    if (!item || !token) {
      navigate("/account");
      return;
    }
    await api("/api/storefront/v1/account/wishlist", {
      method: "POST",
      headers: storefrontSessionHeaders(token),
      body: JSON.stringify({ variationId: item.id, idempotencyKey: crypto.randomUUID() }),
    });
    setStatus("Added to wishlist");
  };
  return (
    <main className={storefrontAppearanceClass(storeProfile)} style={storefrontAppearanceStyle(storeProfile)}>
      <header className="storefront-simple-header">
        <a href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>{storeProfile.storeDisplayName}</a>
        <div className="store-nav-actions"><a className="button-link" href="/account" onClick={(event) => { event.preventDefault(); navigate("/account"); }}>Account</a><StorefrontCartLink navigate={navigate} count={cartCount} /></div>
      </header>
      <section className="product-page">
        {error && <div className="error">{error}</div>}
        {!item && !error && <div className="skeleton" />}
        {item && (
          <>
            <div className="product-media"><Image imageUrl={item.imageUrl} /></div>
            <div className="product-detail">
              <p className="eyebrow">{[item.category, item.trackingMode === "copy" ? "Used copy choices" : undefined].filter(Boolean).join(" / ")}</p>
              <h1>{item.title}</h1>
              {item.subtitle && <p>{item.subtitle}</p>}
              <p>{item.author ?? "Unknown author"}</p>
              {item.publisher && <p>{item.publisher}</p>}
              <strong>{formatMoney(item.priceMoney)}</strong>
              <p>{item.availabilityLabel ?? (isStorefrontItemAvailable(item) ? "Available" : "Unavailable")}</p>
              {item.isbn && <p className="empty-inline">ISBN {item.isbn}</p>}
              {item.description && <p>{item.description}</p>}
              {status && <div className="success">{status}</div>}
              <div className="actions">
                <button className="primary" disabled={!isStorefrontItemAvailable(item)} onClick={() => void addToCart()}>Add to cart</button>
                <button onClick={() => void addWishlist()}>Save to wishlist</button>
              </div>
              {item.choices && item.choices.length > 0 && (
                <div className="choice-list">
                  <h2>Available copies</h2>
                  {item.choices.map((choice) => (
                    <button key={`${choice.variationId}-${choice.copyId ?? "qty"}`} disabled={choice.availableQuantity <= 0} onClick={() => void addToCart(choice)}>
                      <span>{choice.label}</span>
                      <strong>{formatMoney(choice.priceMoney)}</strong>
                    </button>
                  ))}
                </div>
              )}
              {item.editionGroup && item.editionGroup.alternatives.length > 1 && (
                <div className="choice-list" aria-label="Other formats for this edition">
                  <h2>Other formats for this edition</h2>
                  {item.editionGroup.alternatives.map((alternative) => (
                    <button key={alternative.id} disabled={!alternative.available} onClick={() => navigate(`/shop/book/${alternative.id}`)} aria-current={alternative.id === item.id ? "true" : undefined}>
                      <span>{alternative.format ?? alternative.title}{alternative.id === item.id ? " (current)" : ""}</span>
                      <strong>{formatMoney(alternative.priceMoney)}</strong>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>
      {related.length > 0 && (
        <section className="shop-band">
          <div className="section-heading"><div><p className="eyebrow">Related</p><h2>More to browse</h2></div></div>
          <div className="store-grid compact">
            {related.map((entry) => (
              <article className="store-card" key={entry.id}>
                <Image imageUrl={entry.imageUrl} />
                <div><h3><a href={`/shop/book/${entry.id}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/book/${entry.id}`); }}>{entry.title}</a></h3><p>{entry.author}</p></div>
              </article>
            ))}
          </div>
        </section>
      )}
      <StoreFooter profile={storeProfile} />
    </main>
  );
}

function isStorefrontItemAvailable(item: StorefrontItem) {
  return item.available || Number(item.availableQuantity ?? 0) > 0;
}

function cartLinePriceMoney(line: PublicCartLine): Money {
  return line.priceMoney ?? {
    amount: Number(line.unitPriceAmount ?? (line.lineTotalAmount !== undefined ? Math.round(line.lineTotalAmount / Math.max(1, line.quantity)) : 0)),
    currency: line.currency ?? "USD",
  };
}

function CollectionPage({ storeProfile, navigate, slug }: { storeProfile: PublicStoreProfile; navigate: (path: string) => void; slug: string }) {
  const [collection, setCollection] = useState<StorefrontCollectionView | null>(null);
  const [error, setError] = useState("");
  const cartCount = useStorefrontCartCount();
  useEffect(() => {
    if (!slug) return;
    api<{ collection: StorefrontCollectionView }>(`/api/storefront/v1/collections/${encodeURIComponent(slug)}`)
      .then((data) => setCollection(data.collection))
      .catch((caught: Error) => setError(caught.message));
  }, [slug]);
  return (
    <main className={storefrontAppearanceClass(storeProfile)} style={storefrontAppearanceStyle(storeProfile)}>
      <header className="storefront-simple-header">
        <a href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>{storeProfile.storeDisplayName}</a>
        <div className="store-nav-actions"><a className="button-link" href="/account" onClick={(event) => { event.preventDefault(); navigate("/account"); }}>Account</a><StorefrontCartLink navigate={navigate} count={cartCount} /></div>
      </header>
      <section className="shop-band">
        {error && <div className="error">{error}</div>}
        {!collection && !error && <div className="skeleton" />}
        {collection && (
          <>
            <div className="section-heading"><div><p className="eyebrow">Collection</p><h1>{collection.name}</h1>{collection.description && <p>{collection.description}</p>}</div></div>
            <div className="store-grid">
              {(collection.items ?? []).map((item) => (
                <article className="store-card" key={item.id}>
                  <Image imageUrl={item.imageUrl} />
                  <div>
                    <h2><a href={`/shop/book/${item.id}`} onClick={(event) => { event.preventDefault(); navigate(`/shop/book/${item.id}`); }}>{item.title}</a></h2>
                    <p>{item.author ?? "Unknown author"}</p>
                    <strong>{formatMoney(item.priceMoney)}</strong>
                    <button onClick={() => navigate(`/shop/book/${item.id}`)}>View details</button>
                  </div>
                </article>
              ))}
              {(collection.items ?? []).length === 0 && <EmptyState />}
            </div>
          </>
        )}
      </section>
      <StoreFooter profile={storeProfile} />
    </main>
  );
}

function CustomerAccountPage({ storeProfile, navigate }: { storeProfile: PublicStoreProfile; navigate: (path: string) => void }) {
  const [token, setToken] = useState(readStorefrontSession());
  const [account, setAccount] = useState<CustomerAccountView | null>(null);
  const [loyalty, setLoyalty] = useState<LoyaltyBalanceView | null>(null);
  const [loyaltyProgram, setLoyaltyProgram] = useState<{ enabled: boolean; name: string; description?: string; termsText?: string } | null>(null);
  const [ledger, setLedger] = useState<{ id: string; entryType: string; pointsDelta: number; balanceAfter: number; reason?: string; createdAt: string }[]>([]);
  const [orders, setOrders] = useState<{ id: string; status: string; grandTotalAmount: number; currency: string; lines: PublicCartLine[] }[]>([]);
  const [wishlist, setWishlist] = useState<{ id: string; items: StorefrontItem[] } | null>(null);
  const [identifier, setIdentifier] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const cartCount = useStorefrontCartCount();
  const headers = useMemo(() => storefrontSessionHeaders(token), [token]);
  const loadAccount = useCallback(async (sessionToken = token) => {
    if (!sessionToken) return;
    const authHeaders = storefrontSessionHeaders(sessionToken);
    const [accountData, loyaltyData, ordersData, wishlistData] = await Promise.all([
      api<{ account: CustomerAccountView }>("/api/storefront/v1/account", { headers: authHeaders }),
      api<{ balance: LoyaltyBalanceView; ledger: typeof ledger; program?: typeof loyaltyProgram }>("/api/storefront/v1/account/loyalty", { headers: authHeaders }).catch(() => ({ balance: null as unknown as LoyaltyBalanceView, ledger: [], program: null })),
      api<{ orders: typeof orders }>("/api/storefront/v1/account/orders", { headers: authHeaders }).catch(() => ({ orders: [] })),
      api<{ wishlist: { id: string; items: StorefrontItem[] } }>("/api/storefront/v1/account/wishlist", { headers: authHeaders }).catch(() => ({ wishlist: null as unknown as { id: string; items: StorefrontItem[] } })),
    ]);
    setAccount(accountData.account);
    setLoyalty(loyaltyData.balance);
    setLoyaltyProgram(loyaltyData.program ?? null);
    setLedger(loyaltyData.ledger ?? []);
    setOrders(ordersData.orders ?? []);
    setWishlist(wishlistData.wishlist);
  }, [token]);
  useEffect(() => {
    if (!token) return;
    void loadAccount().catch((caught: Error) => {
      clearStorefrontSession();
      setToken("");
      setError(caught.message);
    });
  }, [loadAccount, token]);
  const requestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    const body = identifier.includes("@") ? { emailAddress: identifier, displayName } : { phoneNumber: identifier, displayName };
    const data = await api<{ ok: boolean; expiresAt: string; devCode?: string }>("/api/storefront/v1/account/request-code", { method: "POST", body: JSON.stringify(body) });
    setDevCode(data.devCode ?? "");
    setStatus("Enter the 6-digit sign-in code. This response is the same whether the account already existed or was just created.");
  };
  const verifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    const body = identifier.includes("@") ? { emailAddress: identifier, code } : { phoneNumber: identifier, code };
    const data = await api<{ account: CustomerAccountView; sessionToken: string }>("/api/storefront/v1/account/verify-code", { method: "POST", body: JSON.stringify(body) });
    writeStorefrontSession(data.sessionToken);
    setToken(data.sessionToken);
    setAccount(data.account);
    setStatus("Signed in");
    await loadAccount(data.sessionToken);
  };
  const savePreferences = async () => {
    if (!account) return;
    const data = await api<{ account: CustomerAccountView }>("/api/storefront/v1/account", {
      method: "PATCH",
      headers,
      body: JSON.stringify(account.preferences),
    });
    setAccount(data.account);
    setStatus("Preferences saved");
  };
  const enrollLoyalty = async () => {
    const data = await api<{ balance: LoyaltyBalanceView }>("/api/storefront/v1/account/loyalty/enrollment", {
      method: "POST",
      headers,
      body: JSON.stringify({ termsAccepted: true, idempotencyKey: crypto.randomUUID() }),
    });
    setLoyalty(data.balance);
    setStatus("Loyalty enrollment complete");
  };
  const removeWishlist = async (variationId: string) => {
    const data = await api<{ wishlist: { id: string; items: StorefrontItem[] } }>("/api/storefront/v1/account/wishlist", {
      method: "DELETE",
      headers,
      body: JSON.stringify({ variationId, idempotencyKey: crypto.randomUUID() }),
    });
    setWishlist(data.wishlist);
  };
  const logoutAccount = async () => {
    await api("/api/storefront/v1/account/logout", { method: "POST", headers });
    clearStorefrontSession();
    setToken("");
    setAccount(null);
    setLoyalty(null);
    setWishlist(null);
    setOrders([]);
  };
  const requestDeletion = async () => {
    const data = await api<{ account: CustomerAccountView }>("/api/storefront/v1/account", { method: "DELETE", headers });
    setAccount(data.account);
    clearStorefrontSession();
    setToken("");
    setStatus("Account deletion request recorded and active sessions signed out.");
  };
  return (
    <main className={storefrontAppearanceClass(storeProfile)} style={storefrontAppearanceStyle(storeProfile)}>
      <header className="storefront-simple-header">
        <a href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>{storeProfile.storeDisplayName}</a>
        <div className="store-nav-actions"><StorefrontCartLink navigate={navigate} count={cartCount} /><a className="button-link" href="/app/inventory" onClick={(event) => { event.preventDefault(); navigate("/app/inventory"); }}>Staff login</a></div>
      </header>
      <section className="account-page">
        <div className="section-heading"><div><p className="eyebrow">Customer account</p><h1>Your bookstore account</h1></div></div>
        {status && <div className="success">{status}</div>}
        {error && <div className="error">{error}</div>}
        {!account ? (
          <div className="account-login-grid">
            <form className="panel" onSubmit={requestCode}>
              <h2>Send sign-in code</h2>
              <label>Email or phone<input value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="email" /></label>
              <label>Display name<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
              <button className="primary" type="submit" disabled={!identifier.trim()}>Continue</button>
              {devCode && <p className="empty-inline">Local development code: {devCode}</p>}
            </form>
            <form className="panel" onSubmit={verifyCode}>
              <h2>Verify code</h2>
              <label>6-digit code<input value={code} onChange={(event) => setCode(event.target.value)} inputMode="numeric" autoComplete="one-time-code" /></label>
              <button className="primary" type="submit" disabled={!identifier.trim() || code.length < 6}>Sign in</button>
            </form>
          </div>
        ) : (
          <div className="account-grid">
            <section className="panel">
              <h2>{account.customer.displayName}</h2>
              <p>{account.customer.emailAddress ?? account.customer.phoneNumber ?? "Customer account"}</p>
              <div className="actions"><button onClick={() => void logoutAccount()}>Log out</button><button className="danger" onClick={() => void requestDeletion()}>Request deletion</button></div>
            </section>
            <section className="panel">
              <h2>Loyalty</h2>
              {loyalty ? (
                <div>
                  {loyaltyProgram && <p>{loyaltyProgram.description ?? loyaltyProgram.name}</p>}
                  {loyaltyProgram?.termsText && <details><summary>Program terms</summary><p>{loyaltyProgram.termsText}</p></details>}
                  <button onClick={() => void enrollLoyalty()}>Join or confirm enrollment</button>
                  <div className="metric-grid"><Metric label="Balance" value={`${loyalty.balance} pts`} /><Metric label="Level" value={loyalty.currentLevel ?? "Reader"} /><Metric label="Next" value={loyalty.nextLevel ? `${loyalty.nextLevel.pointsNeeded} pts` : "Complete"} /></div>
                  <DataTable empty="No points are scheduled to expire." headers={["Points", "Expires"]} rows={(loyalty.expiringPoints ?? []).slice(0, 6).map((row) => [row.points, new Date(row.expiresAt).toLocaleDateString()])} />
                  <DataTable empty="No loyalty activity yet." headers={["Type", "Points", "Balance", "Reason", "Date"]} rows={ledger.slice(0, 8).map((row) => [row.entryType, row.pointsDelta, row.balanceAfter, row.reason ?? "-", new Date(row.createdAt).toLocaleDateString()])} />
                </div>
              ) : <p className="empty-inline">Loyalty is not active for this account yet.</p>}
            </section>
            <section className="panel">
              <h2>Orders and reservations</h2>
              <DataTable empty="No online orders yet." headers={["Order", "Status", "Total", "Items"]} rows={orders.map((order) => [order.id, order.status, formatMoney({ amount: order.grandTotalAmount, currency: order.currency }), order.lines.length])} />
            </section>
            <section className="panel">
              <h2>Wishlist</h2>
              {(wishlist?.items ?? []).length === 0 ? <p className="empty-inline">Save books from product pages to see them here.</p> : (
                <div className="wishlist-list">
                  {wishlist?.items.map((item) => (
                    <article key={item.id} className="wishlist-item">
                      <div><strong>{item.title}</strong><span>{formatMoney(item.priceMoney)} / {item.availabilityLabel}</span></div>
                      <button onClick={() => navigate(`/shop/book/${item.id}`)}>View</button>
                      <button onClick={() => void removeWishlist(item.id)}>Remove</button>
                    </article>
                  ))}
                </div>
              )}
            </section>
            <section className="panel">
              <h2>Preferences</h2>
              <label className="checkbox-row"><input type="checkbox" checked={account.preferences.emailMarketingOptIn} onChange={(event) => setAccount({ ...account, preferences: { ...account.preferences, emailMarketingOptIn: event.target.checked } })} /> Email updates</label>
              <label className="checkbox-row"><input type="checkbox" checked={account.preferences.smsMarketingOptIn} onChange={(event) => setAccount({ ...account, preferences: { ...account.preferences, smsMarketingOptIn: event.target.checked } })} /> SMS updates</label>
              <label className="checkbox-row"><input type="checkbox" checked={account.preferences.availabilityNotificationsOptIn} onChange={(event) => setAccount({ ...account, preferences: { ...account.preferences, availabilityNotificationsOptIn: event.target.checked } })} /> Availability notifications</label>
              <button className="primary" onClick={() => void savePreferences()}>Save preferences</button>
            </section>
          </div>
        )}
      </section>
      <StoreFooter profile={storeProfile} />
    </main>
  );
}

function CartPage({ storeProfile, navigate }: { storeProfile: PublicStoreProfile; navigate: (path: string) => void }) {
  const [cartId, setCartId] = useState(readCartStorage().cartId);
  const [cartToken, setCartToken] = useState(readCartStorage().cartToken);
  const [cart, setCart] = useState<StorefrontPublicCart | null>(null);
  const [status, setStatus] = useState("");
  const [giftCardForm, setGiftCardForm] = useState({ amount: "25", recipientName: "", recipientEmail: "", senderName: "", message: "" });
  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    email: "",
    phone: "",
    method: "pickup" as StorefrontFulfillmentMethod,
    addressLine1: "",
    addressLine2: "",
    locality: "",
    administrativeDistrictLevel1: "",
    postalCode: "",
    country: "US",
  });
  const continueShopping = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate("/");
  };
  const loadCart = useCallback(async () => {
    if (!cartId || !cartToken) {
      setCart(null);
      return;
    }
    try {
      const data = await api<{ cart: StorefrontPublicCart }>(`/api/storefront/v1/cart?${new URLSearchParams({ cartId, token: cartToken })}`);
      setCart(data.cart);
    } catch (error) {
      clearCartStorage();
      setCartId("");
      setCartToken("");
      setCart(null);
      setStatus(error instanceof Error ? error.message : "Cart could not be loaded");
    }
  }, [cartId, cartToken]);
  useEffect(() => { void loadCart(); }, [loadCart]);
  const updateLines = async (lines: { variationId: string; quantity: number }[]) => {
    if (!cartId) return;
    const data = await api<{ cart: StorefrontPublicCart }>("/api/storefront/v1/cart/items", { method: "PUT", body: JSON.stringify({ cartId, token: cartToken, lines }) });
    setCart(data.cart);
    notifyStorefrontCartChanged();
    setStatus("Cart updated");
  };
  const setQuantity = (variationId: string, quantity: number) => {
    const nextQuantity = Math.max(1, Math.min(20, Number.isFinite(quantity) ? quantity : 1));
    const next = (cart?.lines ?? []).map((line) => ({ variationId: line.variationId, quantity: line.variationId === variationId ? nextQuantity : line.quantity }));
    void updateLines(next);
  };
  const removeLine = (variationId: string) => {
    const next = (cart?.lines ?? []).filter((line) => line.variationId !== variationId).map((line) => ({ variationId: line.variationId, quantity: line.quantity }));
    void updateLines(next);
  };
  const updateGiftCards = async (lines: PublicGiftCardLine[]) => {
    if (!cartId || !cartToken) return;
    const data = await api<{ cart: StorefrontPublicCart }>("/api/storefront/v1/cart/gift-cards", {
      method: "PUT",
      body: JSON.stringify({ cartId, token: cartToken, lines: lines.map((line) => ({ id: line.id, amount: line.amount, quantity: line.quantity, recipientName: line.recipientName, recipientEmail: line.recipientEmail, senderName: line.senderName, message: line.message })) }),
    });
    setCart(data.cart);
    notifyStorefrontCartChanged();
    setStatus("Cart updated");
  };
  const addGiftCard = async () => {
    const current = cartId && cartToken ? { id: cartId, token: cartToken } : await loadOrCreateStorefrontCart();
    if (!cartId || !cartToken) {
      setCartId(current.id);
      setCartToken(current.token);
    }
    const amount = Math.round(Number(giftCardForm.amount) * 100);
    if (!Number.isFinite(amount) || amount < 100) {
      setStatus("Enter a gift card amount");
      return;
    }
    const existingGiftCards = cart?.giftCardLines ?? [];
    const data = await api<{ cart: StorefrontPublicCart }>("/api/storefront/v1/cart/gift-cards", {
      method: "PUT",
      body: JSON.stringify({
        cartId: current.id,
        token: current.token,
        lines: [
          ...existingGiftCards.map((line) => ({ id: line.id, amount: line.amount, quantity: line.quantity, recipientName: line.recipientName, recipientEmail: line.recipientEmail, senderName: line.senderName, message: line.message })),
          { amount, quantity: 1, recipientName: giftCardForm.recipientName || undefined, recipientEmail: giftCardForm.recipientEmail || undefined, senderName: giftCardForm.senderName || undefined, message: giftCardForm.message || undefined },
        ],
      }),
    });
    setCart(data.cart);
    notifyStorefrontCartChanged();
    setGiftCardForm({ amount: "25", recipientName: "", recipientEmail: "", senderName: "", message: "" });
    setStatus("Gift card added to cart");
  };
  const removeGiftCard = (id: string) => {
    void updateGiftCards((cart?.giftCardLines ?? []).filter((line) => line.id !== id));
  };
  const checkoutOrder = async () => {
    if (!cartId || !cartToken) return;
    if (!checkoutForm.email || !checkoutForm.name) {
      setStatus("Enter checkout contact details");
      return;
    }
    if (checkoutForm.method === "shipping" && (!checkoutForm.addressLine1 || !checkoutForm.locality || !checkoutForm.administrativeDistrictLevel1 || !checkoutForm.postalCode || !checkoutForm.country)) {
      setStatus("Enter the shipping address before checkout");
      return;
    }
    const fulfillment = checkoutForm.method === "shipping"
      ? {
        method: "shipping" as const,
        shippingMethodId: "flat_rate" as const,
        shippingAddress: {
          name: checkoutForm.name,
          line1: checkoutForm.addressLine1,
          line2: checkoutForm.addressLine2 || undefined,
          city: checkoutForm.locality,
          region: checkoutForm.administrativeDistrictLevel1,
          postalCode: checkoutForm.postalCode,
          country: checkoutForm.country.toUpperCase(),
        },
      }
      : { method: "pickup" as const };
    try {
      setStatus("");
      const data = await api<{ checkoutUrl?: string }>("/api/storefront/v1/checkout", {
        method: "POST",
        body: JSON.stringify({
          cartId,
          token: cartToken,
          idempotencyKey: crypto.randomUUID(),
          customer: { email: checkoutForm.email, name: checkoutForm.name, phone: checkoutForm.phone || undefined },
          fulfillment,
        }),
      });
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      setStatus("Square checkout did not return a payment link.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Square checkout could not be started");
    }
  };
  const subtotal = cart?.subtotalAmount ?? 0;
  const taxTotal = cart?.taxTotalAmount ?? 0;
  const shippingTotal = cart?.shippingAmount ?? 0;
  const grandTotal = cart?.grandTotalAmount ?? subtotal + taxTotal + shippingTotal;
  const currency = cart?.currency ?? (cart?.lines[0] ? cartLinePriceMoney(cart.lines[0]).currency : "USD");
  const cartHasLines = Boolean((cart?.lines.length ?? 0) + (cart?.giftCardLines?.length ?? 0));
  return (
    <main className={storefrontAppearanceClass(storeProfile)} style={storefrontAppearanceStyle(storeProfile)}>
      <header className="storefront-simple-header">
        <strong>{storeProfile.storeDisplayName}</strong>
        <a className="button-link" href="/" onClick={continueShopping}>Continue shopping</a>
      </header>
      <section className="cart-page">
        <div className="cart-main">
          <p className="eyebrow">Online order</p>
          <h1>Cart</h1>
          {status && <div className="success">{status}</div>}
          {!cartHasLines || !cart ? <EmptyState /> : (
            <div className="cart-lines">
              {cart.lines.map((line) => (
                <article className="cart-line" key={line.variationId}>
                  <div>
                    <h2>{line.title}</h2>
                    <p>{formatMoney(cartLinePriceMoney(line))} each</p>
                  </div>
                  <label>Quantity<input type="number" min={1} max={20} value={line.quantity} onChange={(event) => setQuantity(line.variationId, Number(event.target.value))} /></label>
                  <strong>{formatMoney({ amount: cartLinePriceMoney(line).amount * line.quantity, currency: cartLinePriceMoney(line).currency })}</strong>
                  <button onClick={() => removeLine(line.variationId)}>Remove</button>
                </article>
              ))}
              {(cart.giftCardLines ?? []).map((line) => (
                <article className="cart-line" key={line.id}>
                  <div>
                    <h2>{line.title}</h2>
                    <p>{line.recipientEmail ? `Digital delivery to ${line.recipientEmail}` : "Digital Square gift card"}</p>
                    {line.squareGiftCardGan && <p>Card {line.squareGiftCardGan}</p>}
                    {line.failureMessage && <p>{line.failureMessage}</p>}
                  </div>
                  <span className="quantity-readonly">Qty {line.quantity}</span>
                  <strong>{formatMoney({ amount: line.lineTotalAmount, currency: line.currency })}</strong>
                  <button onClick={() => removeGiftCard(line.id)}>Remove</button>
                </article>
              ))}
            </div>
          )}
          <section className="gift-card-panel">
            <h2>Gift Card</h2>
            <div className="form-grid">
              <label>Amount<input inputMode="decimal" value={giftCardForm.amount} onChange={(event) => setGiftCardForm({ ...giftCardForm, amount: event.target.value })} /></label>
              <label>Recipient email<input type="email" value={giftCardForm.recipientEmail} onChange={(event) => setGiftCardForm({ ...giftCardForm, recipientEmail: event.target.value })} /></label>
              <label>Recipient name<input value={giftCardForm.recipientName} onChange={(event) => setGiftCardForm({ ...giftCardForm, recipientName: event.target.value })} /></label>
              <label>From<input value={giftCardForm.senderName} onChange={(event) => setGiftCardForm({ ...giftCardForm, senderName: event.target.value })} /></label>
              <label className="wide">Message<textarea rows={3} value={giftCardForm.message} onChange={(event) => setGiftCardForm({ ...giftCardForm, message: event.target.value })} /></label>
              <div className="wide actions"><button onClick={() => void addGiftCard()}>Add gift card</button></div>
            </div>
          </section>
        </div>
        <aside className="cart-summary checkout-panel">
          <h2>Checkout</h2>
          <dl><dt>Subtotal</dt><dd>{formatMoney({ amount: subtotal, currency })}</dd><dt>Tax</dt><dd>{formatMoney({ amount: taxTotal, currency })}</dd>{shippingTotal > 0 && <><dt>Shipping</dt><dd>{formatMoney({ amount: shippingTotal, currency })}</dd></>}<dt>Total</dt><dd>{formatMoney({ amount: grandTotal, currency })}</dd></dl>
          <div className="form-grid">
            <label>Name<input value={checkoutForm.name} onChange={(event) => setCheckoutForm({ ...checkoutForm, name: event.target.value })} /></label>
            <label>Email<input type="email" value={checkoutForm.email} onChange={(event) => setCheckoutForm({ ...checkoutForm, email: event.target.value })} /></label>
            <label>Phone<input value={checkoutForm.phone} onChange={(event) => setCheckoutForm({ ...checkoutForm, phone: event.target.value })} /></label>
            <label>Fulfillment<select value={checkoutForm.method} onChange={(event) => setCheckoutForm({ ...checkoutForm, method: event.target.value as StorefrontFulfillmentMethod })}><option value="pickup">Pickup</option>{storeProfile.shippingEnabled && <option value="shipping">Shipping</option>}</select></label>
            {checkoutForm.method === "shipping" && (
              <>
                <label className="wide">Address<input value={checkoutForm.addressLine1} onChange={(event) => setCheckoutForm({ ...checkoutForm, addressLine1: event.target.value })} /></label>
                <label className="wide">Address 2<input value={checkoutForm.addressLine2} onChange={(event) => setCheckoutForm({ ...checkoutForm, addressLine2: event.target.value })} /></label>
                <label>City<input value={checkoutForm.locality} onChange={(event) => setCheckoutForm({ ...checkoutForm, locality: event.target.value })} /></label>
                <label>State<input maxLength={32} value={checkoutForm.administrativeDistrictLevel1} onChange={(event) => setCheckoutForm({ ...checkoutForm, administrativeDistrictLevel1: event.target.value.toUpperCase() })} /></label>
                <label>Postal code<input value={checkoutForm.postalCode} onChange={(event) => setCheckoutForm({ ...checkoutForm, postalCode: event.target.value })} /></label>
                <label>Country<input maxLength={2} value={checkoutForm.country} onChange={(event) => setCheckoutForm({ ...checkoutForm, country: event.target.value.toUpperCase() })} /></label>
                <p className="empty-inline wide">{storeProfile.flatRateShippingAmount && storeProfile.flatRateShippingAmount > 0 ? `Flat-rate shipping: ${formatMoney({ amount: storeProfile.flatRateShippingAmount, currency })}` : "Shipping is available at no charge."}</p>
              </>
            )}
            <div className="wide actions"><button className="primary" disabled={!cartHasLines} onClick={() => void checkoutOrder()}>Checkout with Square</button></div>
          </div>
        </aside>
      </section>
      <StoreFooter profile={storeProfile} />
    </main>
  );
}

function StoreFooter({ profile }: { profile: PublicStoreProfile }) {
  const contact = [
    profile.publicEmail,
    profile.publicPhone,
    [profile.addressLine1, profile.addressLine2, profile.city, profile.region, profile.postalCode, profile.country].filter(Boolean).join(", "),
  ].filter((entry): entry is string => Boolean(entry));
  const socials = Object.entries(profile.socialLinks ?? {}).filter(([, value]) => Boolean(value));
  const policies = [
    ["Pickup", profile.pickupPolicyText],
    ["Shipping", profile.shippingPolicyText],
    ["Returns", profile.returnPolicyText],
    ["Privacy", profile.privacyPolicyText],
    ["Terms", profile.termsPolicyText],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
  return (
    <footer className="store-footer">
      <div className="store-footer-grid">
        <div>
          <strong>{profile.storeDisplayName}</strong>
          {contact.map((entry) => <span key={entry}>{entry}</span>)}
          {profile.businessHours.length > 0 && <div className="store-hours">{profile.businessHours.slice(0, 7).map((day, index) => <span key={day.dayOfWeek ?? index}>{dayLabel(day.dayOfWeek)}: {day.closed ? "Closed" : (day.intervals ?? []).map((interval) => `${interval.opens}-${interval.closes}`).join(", ")}</span>)}</div>}
        </div>
        {socials.length > 0 && <div className="store-socials">{socials.map(([platform, url]) => <a key={platform} href={url} rel="noreferrer" target="_blank">{socialLabel(platform)}</a>)}</div>}
        {policies.length > 0 && (
          <div className="store-policies" aria-label="Store policies">
            {policies.map(([label, text]) => (
              <details key={label}>
                <summary>{label}</summary>
                <p>{text}</p>
              </details>
            ))}
          </div>
        )}
        <div>Powered by <a href={productBrand.website}>{productBrand.name}</a></div>
      </div>
    </footer>
  );
}

function dayLabel(day?: string): string {
  if (!day) return "Hours";
  return day.charAt(0).toUpperCase() + day.slice(1);
}

function socialLabel(platform?: string): string {
  if (!platform) return "Social";
  if (platform === "xTwitter") return "X/Twitter";
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

function PosRegister() {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [cart, setCart] = useState<{ item: InventoryItem; quantity: number }[]>([]);
  const [registers, setRegisters] = useState<{ id: string; name: string; square_location_id: string; terminal_device_id?: string }[]>([]);
  const [registerId, setRegisterId] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    api<{ registers: { id: string; name: string; square_location_id: string; terminal_device_id?: string }[] }>("/api/pos/registers").then((data) => {
      setRegisters(data.registers);
      setRegisterId(data.registers[0]?.id ?? "");
    }).catch((error: Error) => setStatus(error.message));
  }, []);
  const search = async () => {
    const data = await api<{ items: InventoryItem[] }>(`/api/pos/catalog?${new URLSearchParams({ q: query, limit: "20" })}`);
    setItems(data.items);
  };
  const add = (item: InventoryItem) => {
    setCart((current) => current.some((line) => line.item.id === item.id) ? current.map((line) => line.item.id === item.id ? { ...line, quantity: line.quantity + 1 } : line) : [...current, { item, quantity: 1 }]);
    setStatus(`${item.title} added`);
  };
  const updateQuantity = (itemId: string, quantity: number) => {
    const nextQuantity = Math.max(1, Math.min(99, Number.isFinite(quantity) ? quantity : 1));
    setCart((current) => current.map((line) => line.item.id === itemId ? { ...line, quantity: nextQuantity } : line));
  };
  const removeFromCart = (itemId: string) => {
    setCart((current) => current.filter((line) => line.item.id !== itemId));
  };
  const checkout = async () => {
    const lines = cart.flatMap((line) => line.item.variations[0]?.id ? [{ variationId: line.item.variations[0].id, quantity: line.quantity }] : []);
    const data = await api<{ terminalCheckoutId?: string; terminalStatus?: string }>("/api/pos/checkout/terminal", {
      method: "POST",
      body: JSON.stringify({ registerId, idempotencyKey: crypto.randomUUID(), lines }),
    });
    setStatus(`Terminal checkout ${data.terminalStatus ?? "started"}: ${data.terminalCheckoutId ?? ""}`);
  };
  return (
    <section className="page pos-page">
      <header className="page-header"><div><p className="eyebrow">In-store</p><h1>POS Register</h1></div><button className="primary" onClick={checkout} disabled={!cart.length}><Upload /> Send to terminal</button></header>
      {status && <div className="success">{status}</div>}
      <div className="pos-layout">
        <div>
          <div className="receive-panel">
            <label className="scan-input">Scan or search<input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void search(); }} placeholder="ISBN, SKU, title, author" /></label>
            <label>Register<select value={registerId} onChange={(event) => setRegisterId(event.target.value)}>{registers.map((register) => <option key={register.id} value={register.id}>{register.name}</option>)}</select></label>
            <button onClick={search}><Search /> Search</button>
          </div>
          <div className="card-grid">{items.map((item) => <article className="item-card" key={item.id} onClick={() => add(item)}><Image imageUrl={item.imageUrl} /><div><h3>{item.title}</h3><p>{item.author}</p><dl><dt>Qty</dt><dd>{item.availableQuantity ?? "-"}</dd><dt>Price</dt><dd>{formatMoney(item.priceMoney)}</dd></dl></div></article>)}</div>
        </div>
        <aside className="pos-cart">
          <h2>Cart</h2>
          {cart.map((line) => (
            <div className="pos-cart-line" key={line.item.id}>
              <Image imageUrl={line.item.imageUrl} />
              <div>
                <strong>{line.item.title}</strong>
                <span>{line.quantity} x {formatMoney(line.item.priceMoney)}</span>
                <div className="pos-cart-controls">
                  <button className="icon" aria-label={`Decrease ${line.item.title}`} onClick={() => updateQuantity(line.item.id, line.quantity - 1)}>-</button>
                  <label><span className="sr-only">Quantity for {line.item.title}</span><input value={line.quantity} type="number" min={1} max={99} onChange={(event) => updateQuantity(line.item.id, Number(event.target.value))} /></label>
                  <button className="icon" aria-label={`Increase ${line.item.title}`} onClick={() => updateQuantity(line.item.id, line.quantity + 1)}>+</button>
                  <button aria-label={`Remove ${line.item.title}`} onClick={() => removeFromCart(line.item.id)}>Remove</button>
                </div>
              </div>
            </div>
          ))}
          <strong>Total {formatMoney({ amount: cart.reduce((sum, line) => sum + (line.item.priceMoney?.amount ?? 0) * line.quantity, 0), currency: "USD" })}</strong>
        </aside>
      </div>
    </section>
  );
}

function CustomerPicker({ selectedCustomer, onSelect }: { selectedCustomer: AppCustomer | null; onSelect: (customer: AppCustomer) => void }) {
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState<AppCustomer[]>([]);
  const [creating, setCreating] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ givenName: "", familyName: "", emailAddress: "", phoneNumber: "" });
  const [message, setMessage] = useState("");
  const search = async () => {
    if (!query.trim()) return;
    const data = await api<{ customers: AppCustomer[] }>(`/api/pos/customers/search?${new URLSearchParams({ q: query })}`);
    setCustomers(data.customers);
    setMessage(data.customers.length ? `${data.customers.length} customers found` : "No customers found");
  };
  const create = async () => {
    const data = await api<{ customer?: AppCustomer }>("/api/pos/customers", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({
        givenName: newCustomer.givenName || undefined,
        familyName: newCustomer.familyName || undefined,
        emailAddress: newCustomer.emailAddress || undefined,
        phoneNumber: newCustomer.phoneNumber || undefined,
      }),
    });
    if (data.customer) {
      onSelect(data.customer);
      setCreating(false);
      setQuery(data.customer.displayName);
      setCustomers([]);
      setMessage(`Created customer ${data.customer.displayName}`);
    }
  };
  return (
    <section className="customer-picker wide">
      <div className="receive-panel">
        <label className="scan-input">Customer search<input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void search(); }} placeholder="Name, email, or phone" /></label>
        <button onClick={search}><Search /> Search customers</button>
        <button onClick={() => setCreating((value) => !value)}>{creating ? "Cancel new customer" : "Add customer"}</button>
      </div>
      {selectedCustomer && <div className="success">Selected customer: {selectedCustomer.displayName}</div>}
      {message && <p className="empty-inline">{message}</p>}
      {customers.length > 0 && (
        <div className="customer-results">
          {customers.map((customer) => (
            <button key={customer.id} onClick={() => onSelect(customer)}>
              <strong>{customer.displayName}</strong>
              <span>{[customer.emailAddress, customer.phoneNumber].filter(Boolean).join(" / ") || customer.id}</span>
            </button>
          ))}
        </div>
      )}
      {creating && (
        <div className="metadata-editor">
          <label>First name<input value={newCustomer.givenName} onChange={(event) => setNewCustomer({ ...newCustomer, givenName: event.target.value })} /></label>
          <label>Last name<input value={newCustomer.familyName} onChange={(event) => setNewCustomer({ ...newCustomer, familyName: event.target.value })} /></label>
          <label>Email<input type="email" value={newCustomer.emailAddress} onChange={(event) => setNewCustomer({ ...newCustomer, emailAddress: event.target.value })} /></label>
          <label>Phone<input value={newCustomer.phoneNumber} onChange={(event) => setNewCustomer({ ...newCustomer, phoneNumber: event.target.value })} /></label>
          <div className="wide actions"><button className="primary" onClick={create}>Create customer</button></div>
        </div>
      )}
    </section>
  );
}

function CustomerEditDialog({ customer, onClose, onSaved }: { customer: AppCustomer; onClose: () => void; onSaved: (customer: AppCustomer) => void }) {
  const [form, setForm] = useState({
    givenName: customer.givenName ?? "",
    familyName: customer.familyName ?? "",
    emailAddress: customer.emailAddress ?? "",
    phoneNumber: customer.phoneNumber ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const saveCustomer = async () => {
    setSaving(true);
    setError("");
    try {
      const data = await api<{ customer: AppCustomer }>(`/api/pos/customers/${encodeURIComponent(customer.id)}`, {
        method: "PATCH",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          givenName: form.givenName.trim() || undefined,
          familyName: form.familyName.trim() || undefined,
          emailAddress: form.emailAddress.trim() || undefined,
          phoneNumber: form.phoneNumber.trim() || undefined,
        }),
      });
      onSaved(data.customer);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Customer update failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal customer-dialog" role="dialog" aria-modal="true" aria-labelledby="customer-edit-title">
        <div className="section-heading">
          <div><p className="eyebrow">Customer</p><h2 id="customer-edit-title">Edit customer</h2></div>
          <button aria-label="Close customer editor" onClick={onClose}><X /></button>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
        <div className="metadata-editor">
          <label>First name<input autoFocus value={form.givenName} onChange={(event) => setForm({ ...form, givenName: event.target.value })} /></label>
          <label>Last name<input value={form.familyName} onChange={(event) => setForm({ ...form, familyName: event.target.value })} /></label>
          <label>Email<input type="email" value={form.emailAddress} onChange={(event) => setForm({ ...form, emailAddress: event.target.value })} /></label>
          <label>Phone<input value={form.phoneNumber} onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })} /></label>
          <div className="wide actions">
            <button onClick={onClose}>Cancel</button>
            <button className="primary" onClick={saveCustomer} disabled={saving || !Object.values(form).some((value) => value.trim())}>{saving ? "Saving..." : "Save customer"}</button>
          </div>
        </div>
      </section>
    </div>
  );
}

type TradeInCondition = "new" | "like_new" | "very_good" | "good" | "acceptable" | "poor";
type TradeInStatus = "draft" | "quoted" | "awaiting_inspection" | "awaiting_approval" | "approved" | "completed" | "cancelled" | "rejected" | "exception";
type TradeInDraftLine = {
  id: string;
  title: string;
  authors: string;
  isbn: string;
  condition: TradeInCondition;
  intendedPrice: string;
  notes: string;
};
type TradeInDetailItem = {
  id: string;
  lineNumber: number;
  isbn?: string | null;
  title: string;
  authors: string[];
  condition: string;
  quantity: number;
  intendedPriceMoney: Money;
  creditMoney: Money;
  decision: "accept" | "reject";
  ruleSnapshot?: Record<string, unknown>;
  notes?: string | null;
};
type TradeInDetail = {
  draft: {
    id: string;
    status: TradeInStatus;
    version: number;
    customerId?: string;
    policyVersion: number;
    totalCreditAmount: number;
    currency: string;
    inventoryAcquiredAt?: string | null;
    creditIssuedAt?: string | null;
    expiresAt?: string | null;
  };
  items: TradeInDetailItem[];
  acquisition: {
    itemCount: number;
    totalQuantity: number;
    movements: { draftItemId: string; variationId: string; movementId: string; copyId?: string | null; quantity: number; locationId: string }[];
  } | null;
  issuance: {
    draftId: string;
    status: TradeInStatus;
    version: number;
    customerId: string;
    accountId: string;
    ledgerEntryId: string;
    amount: number;
    currency: string;
    balanceAmount: number;
  } | null;
};
type TradeInCreditAccount = { id: string; customerId: string; balanceAmount: number; heldAmount: number; availableBalance: number; currency: string; status: string };
type TradeInLocation = { id: string; name: string; code: string; active: boolean };
type TradeInFeatures = { phase9FeaturesEnabled: boolean; storeCreditRedemptionEnabled?: boolean };
type TradeInWizardStep = "customer" | "books" | "review" | "inventory" | "credit";
type InventoryRequestMatch = {
  requestId: string;
  customerId?: string;
  customerDisplayName?: string;
  title: string;
  author?: string;
  isbn?: string;
  status: string;
  matchedBy: "isbn" | "title_author" | "title";
  inventoryTitle: string;
  variationId?: string;
};
type RequestMatchUpdate = { requestId: string; status: string };

const tradeInPendingKey = "spine_trade_in_pending";
const tradeInConditions: { value: TradeInCondition; label: string }[] = [
  { value: "new", label: "New" },
  { value: "like_new", label: "Like new" },
  { value: "very_good", label: "Very good" },
  { value: "good", label: "Good" },
  { value: "acceptable", label: "Acceptable" },
  { value: "poor", label: "Poor / reject" },
];

function TradeInWorkspace({ api, storeProfile, currentUser }: { api: <T>(path: string, init?: RequestInit) => Promise<T>; storeProfile: PublicStoreProfile; currentUser: UserSession }) {
  const scanRef = useRef<HTMLInputElement | null>(null);
  const [features, setFeatures] = useState<TradeInFeatures>({ phase9FeaturesEnabled: false });
  const [locations, setLocations] = useState<TradeInLocation[]>([]);
  const [formats, setFormats] = useState<CatalogFormat[]>([]);
  const [inventoryDefaults, setInventoryDefaults] = useState<Partial<InventoryDefaults>>({});
  const [locationId, setLocationId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<AppCustomer | null>(null);
  const [creditAccount, setCreditAccount] = useState<TradeInCreditAccount | null>(null);
  const [lines, setLines] = useState<TradeInDraftLine[]>([]);
  const [draftLine, setDraftLine] = useState<TradeInDraftLine>(() => newTradeInLine());
  const [draftDetail, setDraftDetail] = useState<TradeInDetail | null>(null);
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventoryMatches, setInventoryMatches] = useState<InventoryItem[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [createdVariationLabels, setCreatedVariationLabels] = useState<Record<string, string>>({});
  const [shelfCodes, setShelfCodes] = useState<Record<string, string>>({});
  const [metadataMatches, setMetadataMatches] = useState<BookMetadata[]>([]);
  const [requestMatches, setRequestMatches] = useState<InventoryRequestMatch[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [mutationKeys, setMutationKeys] = useState<Record<string, string>>(() => readTradeInPendingKeys());

  const activeLocation = locations.find((location) => location.id === locationId);
  const acceptedItems = draftDetail?.items.filter((item) => item.decision === "accept") ?? [];
  const rejectedItems = draftDetail?.items.filter((item) => item.decision === "reject") ?? [];
  const selectedVariationIds = new Set(Object.values(mappings).filter(Boolean));
  const canManageMoney = currentUser.role === "administrator" || currentUser.role === "manager";
  const allAcceptedMapped = acceptedItems.length > 0 && acceptedItems.every((item) => Boolean(mappings[item.id]));
  const inventoryAcquired = Boolean(draftDetail?.draft.inventoryAcquiredAt || draftDetail?.acquisition);
  const creditIssued = Boolean(draftDetail?.draft.creditIssuedAt || draftDetail?.issuance);
  const creditMode = tradeInDetailCreditMode(draftDetail);
  const showInventoryMapping = Boolean(draftDetail && draftDetail.draft.status === "approved" && !inventoryAcquired && acceptedItems.length > 0);
  const needsApproval = Boolean(draftDetail && ["awaiting_inspection", "awaiting_approval"].includes(draftDetail.draft.status) && !canManageMoney);
  const wizardStep: TradeInWizardStep = !selectedCustomer ? "customer" : !draftDetail ? "books" : creditIssued ? "credit" : showInventoryMapping ? "inventory" : "review";
  const currentStep = ({ customer: 1, books: 2, review: 3, inventory: 4, credit: 5 } as const)[wizardStep];
  const defaultFormatCode = formats.find((format) => format.id === inventoryDefaults.defaultFormatId)?.code
    ?? formats.find((format) => format.code === "trade_paperback")?.code
    ?? formats.find((format) => format.active)?.code
    ?? "other";
  const defaultTrackingMode = inventoryDefaults.defaultTrackingMode === "copy" ? "copy" : "quantity";

  const saveKeys = (next: Record<string, string>) => {
    setMutationKeys(next);
    localStorage.setItem(tradeInPendingKey, JSON.stringify(next));
  };
  const keyFor = (name: string) => {
    const existing = mutationKeys[name];
    if (existing) return existing;
    const next = { ...mutationKeys, [name]: crypto.randomUUID() };
    saveKeys(next);
    return next[name];
  };
  const clearKeys = () => saveKeys({});

  const loadContext = useCallback(async () => {
    setError(null);
    const [settingsData, featureData] = await Promise.all([
      api<{ locations: TradeInLocation[]; formats?: CatalogFormat[]; defaults: Partial<InventoryDefaults> & { defaultLocationId?: string } }>("/api/admin/inventory-settings"),
      api<{ features?: TradeInFeatures }>("/api/pos/features").catch((): { features: TradeInFeatures } => ({ features: { phase9FeaturesEnabled: false } })),
    ]);
    const activeLocations = settingsData.locations.filter((location) => location.active);
    setLocations(activeLocations);
    setFormats((settingsData.formats ?? []).filter((format) => format.active));
    setInventoryDefaults(settingsData.defaults ?? {});
    const defaultLocationId = settingsData.defaults.defaultLocationId;
    setLocationId((current) => current || activeLocations.find((location) => location.id === defaultLocationId)?.id || activeLocations[0]?.id || "");
    setFeatures({ phase9FeaturesEnabled: Boolean(featureData.features?.phase9FeaturesEnabled), storeCreditRedemptionEnabled: Boolean(featureData.features?.storeCreditRedemptionEnabled) });
  }, [api]);

  useEffect(() => {
    void loadContext().catch((caught) => setError(caught));
  }, [loadContext]);

  useEffect(() => {
    if (!selectedCustomer) {
      setCreditAccount(null);
      return;
    }
    api<{ account: TradeInCreditAccount | null }>(`/api/pos/customers/${encodeURIComponent(selectedCustomer.id)}/credit`)
      .then((data) => setCreditAccount(data.account))
      .catch((caught) => {
        setCreditAccount(null);
        setError(caught);
      });
  }, [api, selectedCustomer]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === "s") {
        event.preventDefault();
        scanRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const runMutation = async (label: string, action: () => Promise<void>) => {
    setPending(label);
    setError(null);
    setMessage("");
    try {
      await action();
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(null);
    }
  };

  const addDraftLine = () => {
    if (!draftLine.title.trim()) {
      setError(new Error("Title is required before a trade-in item can be quoted."));
      return;
    }
    setLines((current) => [...current, { ...draftLine, id: crypto.randomUUID(), authors: draftLine.authors.trim(), isbn: normalizeIsbn(draftLine.isbn) }]);
    setDraftLine(newTradeInLine());
    setMetadataMatches([]);
    setMessage("Book added to this trade-in.");
    scanRef.current?.focus();
  };

  const searchMetadata = async () => {
    const isbn = normalizeIsbn(draftLine.isbn);
    if (!isbn && !draftLine.title.trim()) return;
    await runMutation("metadata-search", async () => {
      const body = isbn ? { isbn } : { title: draftLine.title.trim(), author: draftLine.authors.trim() || undefined };
      const data = await api<{ results: BookMetadata[] }>("/api/admin/metadata/search", { method: "POST", body: JSON.stringify(body) });
      setMetadataMatches(data.results);
      setMessage(data.results.length ? `${data.results.length} book match${data.results.length === 1 ? "" : "es"} found.` : "No match found. Enter the book details manually.");
    });
  };

  const applyMetadata = (match: BookMetadata) => {
    setDraftLine((current) => ({
      ...current,
      title: match.title,
      authors: match.authors.join(", "),
      isbn: match.isbn13 ?? match.isbn10 ?? current.isbn,
    }));
    setMetadataMatches([]);
    setMessage("Book details filled in.");
  };

  const createQuote = () => runMutation("quote", async () => {
    if (!selectedCustomer) throw new Error("Select a customer before creating a Phase 9 trade-in draft.");
    if (lines.length === 0) throw new Error("Add at least one trade-in line before quoting.");
    const quote = await api<{ quote: { draftId?: string; persisted?: boolean }; replayed?: boolean }>("/api/pos/trade-ins/quote", {
      method: "POST",
      body: JSON.stringify({
        customerId: selectedCustomer.id,
        idempotencyKey: keyFor("quote"),
        items: lines.map((line) => ({
          isbn: line.isbn || undefined,
          title: line.title,
          authors: splitAuthors(line.authors),
          condition: line.condition,
          intendedPriceMoney: inputToMoney(line.intendedPrice, storeProfile.currencyCode ?? "USD"),
          notes: line.notes || undefined,
        })),
      }),
    });
    if (!quote.quote.draftId || quote.quote.persisted === false) throw new Error("The trade-in quote was not persisted. Phase 9 completion is unavailable for this draft.");
    await refreshDraft(quote.quote.draftId);
    setMessage(quote.replayed ? "Recovered this trade-in." : "Trade-in priced.");
  });

  const refreshDraft = async (draftId = draftDetail?.draft.id) => {
    if (!draftId) return;
    const data = await api<{ tradeIn: TradeInDetail }>(`/api/pos/trade-ins/drafts/${encodeURIComponent(draftId)}`);
    setDraftDetail(data.tradeIn);
    setMappings((current) => {
      const next = { ...current };
      for (const movement of data.tradeIn.acquisition?.movements ?? []) next[movement.draftItemId] = movement.variationId;
      return next;
    });
  };

  const transition = (toStatus: "awaiting_inspection" | "awaiting_approval" | "approved") => () => runMutation(`transition-${toStatus}`, async () => {
    if (!draftDetail) return;
    const response = await api<{ draft: TradeInDetail["draft"]; replayed?: boolean }>(`/api/pos/trade-ins/drafts/${encodeURIComponent(draftDetail.draft.id)}/transition`, {
      method: "PATCH",
      body: JSON.stringify({
        expectedStatus: draftDetail.draft.status,
        expectedVersion: draftDetail.draft.version,
        toStatus,
        reasonCode: toStatus,
        idempotencyKey: keyFor(`transition:${draftDetail.draft.id}:${toStatus}:${draftDetail.draft.version}`),
      }),
    });
    await refreshDraft(response.draft.id);
    setMessage(response.replayed ? "Recovered this trade-in." : `Now ${formatTradeInStatus(response.draft.status)}.`);
  });

  const searchInventory = async (query = inventorySearch) => {
    const q = query.trim();
    if (!q) return;
    await runMutation("inventory-search", async () => {
      const params = new URLSearchParams({ q, limit: "20", active: "active", itemType: "book" });
      if (locationId) params.set("locationId", locationId);
      const data = await api<{ items: InventoryItem[] }>(`/api/admin/inventory?${params}`);
      setInventoryMatches(data.items);
      setMessage(data.items.length ? `${data.items.length} inventory match${data.items.length === 1 ? "" : "es"} found.` : "No existing inventory match found.");
    });
  };

  const createInventoryItemForTradeIn = (item: TradeInDetailItem) => runMutation(`create-inventory-${item.id}`, async () => {
    const isbn = normalizeIsbn(item.isbn ?? "");
    if (isbn) {
      const params = new URLSearchParams({ q: isbn, limit: "20", active: "active", itemType: "book" });
      if (locationId) params.set("locationId", locationId);
      const existing = await api<{ items: InventoryItem[] }>(`/api/admin/inventory?${params}`);
      const variation = existing.items.flatMap((match) => match.variations).find((candidate) => candidate.gtin === isbn || candidate.sku === isbn);
      if (variation) {
        setInventoryMatches((current) => mergeInventoryMatches(current, existing.items));
        setMappings((current) => ({ ...current, [item.id]: variation.id }));
        setMessage(`${item.title} matched existing inventory.`);
        return;
      }
    }
    const editionResponse = await api<{ edition: CatalogEdition }>("/api/admin/catalog/editions", {
      method: "POST",
      body: JSON.stringify({
        work: {
          title: item.title,
          contributors: item.authors.map((author) => ({ displayName: author, role: "author" })),
        },
        identifiers: isbn ? [{
          identifierType: isbn.length === 13 ? "isbn13" : "isbn10",
          value: isbn,
          source: "trade_in",
          primary: true,
        }] : [],
      }),
    });
    const variationResponse = await api<{ variation: ProductVariation }>("/api/admin/catalog/variations", {
      method: "POST",
      body: JSON.stringify({
        editionId: editionResponse.edition.id,
        formatCode: defaultFormatCode,
        internalSku: isbn || undefined,
        defaultCostAmount: 0,
        defaultSellingPriceAmount: item.intendedPriceMoney.amount,
        currency: item.intendedPriceMoney.currency,
        barcodeBehavior: "store_barcode",
        trackingMode: defaultTrackingMode,
        specialAttributes: {},
      }),
    });
    setMappings((current) => ({ ...current, [item.id]: variationResponse.variation.id }));
    setCreatedVariationLabels((current) => ({ ...current, [item.id]: `${item.title} / ${variationResponse.variation.formatName ?? defaultFormatCode}` }));
    setMessage(`${item.title} was added to inventory and selected for this trade-in.`);
  });

  const acquireInventory = () => runMutation("inventory-acquisition", async () => {
    if (!draftDetail || !locationId) return;
    if (!allAcceptedMapped) throw new Error("Map every accepted trade-in line to an existing inventory variation before acquisition.");
    const response = await api<{ acquisition: { version: number; requestMatches?: InventoryRequestMatch[] }; replayed?: boolean }>(`/api/pos/trade-ins/drafts/${encodeURIComponent(draftDetail.draft.id)}/inventory-acquisition`, {
      method: "POST",
      body: JSON.stringify({
        expectedStatus: "approved",
        expectedVersion: draftDetail.draft.version,
        locationId,
        idempotencyKey: keyFor(`acquire:${draftDetail.draft.id}:${draftDetail.draft.version}:${locationId}:${acceptedItems.map((item) => `${item.id}:${mappings[item.id]}`).join("|")}`),
        items: acceptedItems.map((item) => ({ draftItemId: item.id, variationId: mappings[item.id], shelfCode: shelfCodes[item.id] || undefined })),
      }),
    });
    await refreshDraft(draftDetail.draft.id);
    setRequestMatches(response.acquisition.requestMatches ?? []);
    setMessage(response.replayed ? "Recovered the existing inventory step." : "Accepted books added to inventory.");
  });

  const issueCredit = () => runMutation("credit-issuance", async () => {
    if (!draftDetail || !selectedCustomer) return;
    const response = await api<{ issuance: TradeInDetail["issuance"]; replayed?: boolean }>(`/api/pos/trade-ins/drafts/${encodeURIComponent(draftDetail.draft.id)}/credit-issuance`, {
      method: "POST",
      body: JSON.stringify({
        expectedStatus: "approved",
        expectedVersion: draftDetail.draft.version,
        idempotencyKey: keyFor(`issue:${draftDetail.draft.id}:${draftDetail.draft.version}`),
      }),
    });
    await refreshDraft(draftDetail.draft.id);
    const account = await api<{ account: TradeInCreditAccount | null }>(`/api/pos/customers/${encodeURIComponent(selectedCustomer.id)}/credit`);
    setCreditAccount(account.account);
    setMessage(response.replayed ? "Recovered the existing credit issuance." : `Issued ${tradeInCreditDisplay(response.issuance?.amount ?? 0, response.issuance?.currency ?? storeProfile.currencyCode ?? "USD", creditMode)}.`);
  });

  const startNext = () => {
    setLines([]);
    setDraftLine(newTradeInLine());
    setDraftDetail(null);
    setMappings({});
    setCreatedVariationLabels({});
    setShelfCodes({});
    setInventoryMatches([]);
    setMetadataMatches([]);
    setMessage("Ready for the next trade-in.");
    clearKeys();
    scanRef.current?.focus();
  };

  const primaryReviewAction = (() => {
    if (!draftDetail) return null;
    if (draftDetail.draft.status === "quoted") return { label: "Start review", onClick: transition("awaiting_inspection"), disabled: Boolean(pending) };
    if (draftDetail.draft.status === "awaiting_inspection") return { label: canManageMoney ? "Approve trade-in" : "Send for approval", onClick: canManageMoney ? transition("approved") : transition("awaiting_approval"), disabled: Boolean(pending) };
    if (draftDetail.draft.status === "awaiting_approval") return { label: "Approve trade-in", onClick: transition("approved"), disabled: !canManageMoney || Boolean(pending) };
    if (draftDetail.draft.status === "approved" && !inventoryAcquired) return { label: "Add books to inventory", onClick: acquireInventory, disabled: !canManageMoney || !allAcceptedMapped || Boolean(pending) };
    if (draftDetail.draft.status === "approved" && inventoryAcquired && !creditIssued) return { label: "Issue store credit", onClick: issueCredit, disabled: !canManageMoney || Boolean(pending) };
    return null;
  })();

  if (!features.phase9FeaturesEnabled) {
    return (
      <section className="page">
        <header className="page-header">
          <div><p className="eyebrow">Used books</p><h1>Trade-ins</h1></div>
        </header>
        <div className="empty-state" role="status">
          <AlertTriangle aria-hidden />
          <h2>Phase 9 trade-ins are disabled</h2>
          <p>Trade-in intake and store-credit issuance are unavailable in this environment. No legacy completion path is exposed.</p>
          <small>{storeProfile.productName ?? productBrand.name} / {storeProfile.storeDisplayName}</small>
        </div>
      </section>
    );
  }

  return (
    <section className="page trade-in-workspace">
      <header className="page-header">
        <div>
          <p className="eyebrow">Used books</p>
          <h1>Trade-ins</h1>
        </div>
        <button onClick={() => void refreshDraft()} disabled={!draftDetail || Boolean(pending)} aria-label="Refresh trade-in"><RefreshCw /> Refresh</button>
      </header>

      {Boolean(error) && <div className="error" role="alert">{tradeInErrorMessage(error)}</div>}
      {message && <div className="success" role="status">{message}</div>}

      <ol className="trade-stepper" aria-label="Trade-in progress">
        {["Customer", "Books", "Offer", "Inventory", "Credit"].map((step, index) => (
          <li key={step} className={currentStep >= index + 1 ? "active" : ""}><span>{index + 1}</span>{step}</li>
        ))}
      </ol>

      <div className="trade-wizard">
        {wizardStep === "customer" && (
          <section className="panel trade-card">
            <h2>Choose customer</h2>
            <CustomerPicker selectedCustomer={selectedCustomer} onSelect={(customer) => { setSelectedCustomer(customer); clearKeys(); }} />
          </section>
        )}

        {wizardStep === "books" && (
          <section className="panel trade-card">
            <div className="section-heading">
              <h2>Add books</h2>
              {selectedCustomer && (
                <div className="selected-customer compact">
                  <User aria-hidden />
                  <div><strong>{selectedCustomer.displayName}</strong><span>{creditAccount ? `${formatMoney({ amount: creditAccount.availableBalance, currency: creditAccount.currency })} available` : "No active store-credit account yet"}</span></div>
                </div>
              )}
            </div>
            <div className="form-grid trade-intake-grid">
              <label className="wide scan-input">Scan or search
                <input ref={scanRef} value={draftLine.isbn || draftLine.title} onChange={(event) => {
                  const value = event.target.value;
                  setDraftLine((current) => /^\d[\d -]*$/.test(value) ? { ...current, isbn: value } : { ...current, title: value });
                }} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void searchMetadata(); } }} placeholder="ISBN, barcode, title, or author" />
              </label>
              <label>Title<input value={draftLine.title} onChange={(event) => setDraftLine({ ...draftLine, title: event.target.value })} /></label>
              <label>Authors<input value={draftLine.authors} onChange={(event) => setDraftLine({ ...draftLine, authors: event.target.value })} /></label>
              <label>ISBN<input value={draftLine.isbn} onChange={(event) => setDraftLine({ ...draftLine, isbn: event.target.value })} /></label>
              <label>Condition<select value={draftLine.condition} onChange={(event) => setDraftLine({ ...draftLine, condition: event.target.value as TradeInCondition })}>{tradeInConditions.map((condition) => <option key={condition.value} value={condition.value}>{condition.label}</option>)}</select></label>
              <label>Used price<input inputMode="decimal" value={draftLine.intendedPrice} onChange={(event) => setDraftLine({ ...draftLine, intendedPrice: event.target.value })} /></label>
              <label className="wide">Note<input value={draftLine.notes} onChange={(event) => setDraftLine({ ...draftLine, notes: event.target.value })} maxLength={500} /></label>
              <div className="wide actions">
                <button onClick={searchMetadata} disabled={Boolean(pending)}><Search /> Find book</button>
                <button className="primary" onClick={addDraftLine} disabled={Boolean(pending)}>Add book</button>
              </div>
            </div>
            {metadataMatches.length > 0 && (
              <div className="result-list compact-results">
                {metadataMatches.map((match) => (
                  <button key={`${match.title}-${match.isbn13 ?? match.isbn10 ?? match.authors.join(",")}`} onClick={() => applyMetadata(match)}>
                    <strong>{match.title}</strong>
                    <span>{[match.authors.join(", "), match.isbn13 ?? match.isbn10, match.publisher, match.publicationDate].filter(Boolean).join(" / ")}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="trade-line-list">
              {lines.length === 0 ? <p className="empty-inline">No books added yet.</p> : lines.map((line) => (
                <article key={line.id} className="trade-line-card">
                  <div><strong>{line.title}</strong><span>{[line.authors, line.isbn].filter(Boolean).join(" / ")}</span></div>
                  <span>{formatCondition(line.condition)}</span>
                  <strong>{formatMoney(inputToMoney(line.intendedPrice, storeProfile.currencyCode ?? "USD"))}</strong>
                  <button onClick={() => setLines((current) => current.filter((candidate) => candidate.id !== line.id))}>Remove</button>
                </article>
              ))}
            </div>
            <div className="actions trade-wizard-actions">
              <button onClick={() => { setSelectedCustomer(null); setCreditAccount(null); clearKeys(); }}>Back</button>
              <button className="primary" onClick={createQuote} disabled={lines.length === 0 || Boolean(pending)}>Price trade-in</button>
            </div>
          </section>
        )}

        {wizardStep === "review" && draftDetail && (
          <section className="panel trade-card">
            <div className="section-heading">
              <h2>Review offer</h2>
              <strong>{formatTradeInStatus(draftDetail.draft.status)}</strong>
            </div>
            <div className="summary-grid">
              <div><span>Customer</span><strong>{selectedCustomer?.displayName}</strong></div>
              <div><span>Accepted</span><strong>{acceptedItems.length}</strong></div>
              <div><span>Rejected</span><strong>{rejectedItems.length}</strong></div>
              <div><span>{tradeInCreditLabel(creditMode)}</span><strong>{tradeInCreditDisplay(draftDetail.draft.totalCreditAmount, draftDetail.draft.currency, creditMode)}</strong></div>
            </div>
            <div className="trade-line-list">
              {draftDetail.items.map((item) => (
                <article key={item.id} className="trade-line-card">
                  <div><strong>{item.title}</strong><span>{[item.authors.join(", "), item.isbn].filter(Boolean).join(" / ")}</span></div>
                  <span className={item.decision === "accept" ? "status-ok" : "status-warn"}>{item.decision === "accept" ? "Accepted" : "Not accepted"}</span>
                  <strong>{tradeInCreditDisplay(item.creditMoney.amount, item.creditMoney.currency, tradeInItemCreditMode(item))}</strong>
                </article>
              ))}
            </div>
            <div className="actions trade-wizard-actions">
              {primaryReviewAction && <button className="primary" onClick={primaryReviewAction.onClick} disabled={primaryReviewAction.disabled}>{primaryReviewAction.label}</button>}
              {needsApproval && <p className="empty-inline">A manager or administrator must approve and issue credit.</p>}
            </div>
          </section>
        )}

        {wizardStep === "inventory" && draftDetail && (
          <section className="panel trade-card">
            <div className="section-heading">
              <h2>Add accepted books to inventory</h2>
              <button onClick={() => void searchInventory()} disabled={Boolean(pending)}>Search inventory</button>
            </div>
            <label className="wide">Find existing item
              <input value={inventorySearch} onChange={(event) => setInventorySearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void searchInventory(); } }} placeholder="Title, ISBN, SKU, or barcode" />
            </label>
            <div className="trade-line-list">
              {acceptedItems.map((item) => {
                const mappedVariationIsVisible = inventoryMatches.some((match) => match.variations.some((variation) => variation.id === mappings[item.id]));
                return (
                  <article key={item.id} className="trade-line-card trade-mapping-card">
                    <div><strong>{item.title}</strong><span>{item.isbn || "No ISBN"}</span></div>
                    <select aria-label={`Inventory mapping for ${item.title}`} value={mappings[item.id] ?? ""} onChange={(event) => setMappings({ ...mappings, [item.id]: event.target.value })}>
                      <option value="">Select existing variation</option>
                      {mappings[item.id] && !mappedVariationIsVisible && <option value={mappings[item.id]}>{createdVariationLabels[item.id] ?? "New inventory item"}</option>}
                      {inventoryMatches.flatMap((match) => match.variations.map((variation) => (
                        <option key={`${item.id}-${variation.id}`} value={variation.id} disabled={selectedVariationIds.has(variation.id) && mappings[item.id] !== variation.id}>
                          {match.title} / {variation.name} / {variation.sku ?? variation.gtin ?? variation.id}
                        </option>
                      )))}
                    </select>
                    <input aria-label={`Shelf for ${item.title}`} placeholder="Shelf" value={shelfCodes[item.id] ?? ""} onChange={(event) => setShelfCodes({ ...shelfCodes, [item.id]: event.target.value })} />
                    <button onClick={() => createInventoryItemForTradeIn(item)} disabled={Boolean(pending || mappings[item.id])}>Create new item</button>
                  </article>
                );
              })}
            </div>
            <div className="actions trade-wizard-actions">
              <button className="primary" onClick={acquireInventory} disabled={!canManageMoney || !allAcceptedMapped || Boolean(pending)}>Add books to inventory</button>
            </div>
          </section>
        )}

        {wizardStep === "credit" && draftDetail && (
          <section className="panel trade-card">
            {draftDetail.issuance ? (
              <div className="completion-panel">
                <Check aria-hidden />
                <div>
                  <h2>Trade-in completed</h2>
                  <strong>{tradeInCreditDisplay(draftDetail.issuance.amount, draftDetail.issuance.currency, creditMode)} issued</strong>
                  <p>Ledger reference {draftDetail.issuance.ledgerEntryId}. Current available balance {creditAccount ? formatMoney({ amount: creditAccount.availableBalance, currency: creditAccount.currency }) : formatMoney({ amount: draftDetail.issuance.balanceAmount, currency: draftDetail.issuance.currency })}.</p>
                  <button onClick={startNext}>Start next trade-in</button>
                </div>
              </div>
            ) : (
              <>
                <h2>Issue store credit</h2>
                <div className="summary-grid">
                  <div><span>Customer</span><strong>{selectedCustomer?.displayName}</strong></div>
                  <div><span>Books added</span><strong>{acceptedItems.length}</strong></div>
                  <div><span>{tradeInCreditLabel(creditMode)}</span><strong>{tradeInCreditDisplay(draftDetail.draft.totalCreditAmount, draftDetail.draft.currency, creditMode)}</strong></div>
                  <div><span>Status</span><strong>{formatTradeInStatus(draftDetail.draft.status)}</strong></div>
                </div>
                <div className="actions trade-wizard-actions">
                  <button className="primary" onClick={issueCredit} disabled={!canManageMoney || Boolean(pending)}>Issue store credit</button>
                  {!canManageMoney && <p className="empty-inline">A manager or administrator must issue store credit.</p>}
                </div>
              </>
            )}
          </section>
        )}
      </div>

      {requestMatches.length > 0 && (
        <RequestMatchDialog
          matches={requestMatches}
          onClose={() => setRequestMatches([])}
          onMarkReceived={async (match) => {
            const data = await api<{ request: RequestMatchUpdate }>(`/api/pos/customer-requests/${encodeURIComponent(match.requestId)}`, {
              method: "PATCH",
              body: JSON.stringify({ status: "received" }),
            });
            setRequestMatches((current) => current.map((entry) => entry.requestId === match.requestId ? { ...entry, status: data.request.status } : entry));
          }}
        />
      )}

      <footer className="context-footer" aria-label="Page context">
        <span><strong>Product:</strong> {storeProfile.productName ?? productBrand.name}</span>
        <span><strong>Store:</strong> {storeProfile.storeDisplayName}</span>
        <span><strong>Location:</strong> {activeLocation ? `${activeLocation.name} (${activeLocation.code})` : "No receiving location"}</span>
        <span><strong>Role:</strong> {currentUser.displayName} - {currentUser.role}{canManageMoney ? " / approval enabled" : " / intake only"}</span>
        <span><strong>Feature:</strong> Phase 9 enabled; credit issuance remains server-authoritative</span>
      </footer>
    </section>
  );
}

function RequestMatchDialog({ matches, onClose, onMarkReceived }: { matches: InventoryRequestMatch[]; onClose: () => void; onMarkReceived: (match: InventoryRequestMatch) => Promise<void> }) {
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const markReceived = async (match: InventoryRequestMatch) => {
    setPendingRequestId(match.requestId);
    setError("");
    try {
      await onMarkReceived(match);
    } catch {
      setError("Could not mark the request received. Try again from the Requests page.");
    } finally {
      setPendingRequestId(null);
    }
  };
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal request-match-dialog" role="dialog" aria-modal="true" aria-labelledby="request-match-title">
        <header className="section-header">
          <div>
            <p className="eyebrow">Requested book</p>
            <h2 id="request-match-title">{matches.length === 1 ? "This book was requested" : "These books were requested"}</h2>
          </div>
          <button className="icon" aria-label="Close request notification" onClick={onClose}><X /></button>
        </header>
        <div className="settings-list">
          {matches.map((match) => (
            <article key={match.requestId}>
              <strong>{match.title}</strong>
              <span>{match.customerDisplayName ?? "Customer request"}{match.author ? ` / ${match.author}` : ""}</span>
              <span>Matched by {formatRequestMatchType(match.matchedBy)}{match.isbn ? ` / ${match.isbn}` : ""} / {match.status}</span>
              {match.status === "received"
                ? <span className="status-ok">Marked received</span>
                : <button onClick={() => void markReceived(match)} disabled={pendingRequestId === match.requestId}><Check /> {pendingRequestId === match.requestId ? "Marking..." : "Mark request received"}</button>}
            </article>
          ))}
        </div>
        {error && <p className="error-text">{error}</p>}
        <div className="modal-actions">
          <button className="primary" onClick={onClose}><Check /> OK</button>
        </div>
      </section>
    </div>
  );
}

function formatRequestMatchType(value: InventoryRequestMatch["matchedBy"]): string {
  if (value === "isbn") return "ISBN";
  if (value === "title_author") return "title and author";
  return "title";
}

function CustomersPage({ prefill, onPrefillConsumed }: { prefill?: CustomerRequestPrefill | null; onPrefillConsumed?: () => void }) {
  const [customer, setCustomer] = useState<AppCustomer | null>(null);
  const [requests, setRequests] = useState<CustomerRequest[]>([]);
  const [requestForm, setRequestForm] = useState<{ title: string; author: string; isbn: string; notes: string; requestFormat: ImportFormat; metadata?: BookMetadata }>({ title: "", author: "", isbn: "", notes: "", requestFormat: "softcover" });
  const [metadataMatches, setMetadataMatches] = useState<BookMetadata[]>([]);
  const [inventoryMatches, setInventoryMatches] = useState<InventoryItem[]>([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [supplierSearchRequest, setSupplierSearchRequest] = useState<CustomerRequest | null>(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [customerLoyalty, setCustomerLoyalty] = useState<LoyaltyBalanceView | null>(null);
  const [customerLoyaltyLoading, setCustomerLoyaltyLoading] = useState(false);
  const [customerLoyaltyError, setCustomerLoyaltyError] = useState("");
  const [message, setMessage] = useState("");
  const loadRequests = useCallback(async () => {
    const data = await api<{ requests: CustomerRequest[] }>("/api/pos/customer-requests");
    setRequests(data.requests);
  }, []);
  useEffect(() => { void loadRequests(); }, [loadRequests]);
  useEffect(() => { api<{ settings: Partial<AppSettings> }>("/api/admin/settings").then((data) => setSettings(mergeSettings(data.settings))).catch(() => undefined); }, []);
  useEffect(() => {
    if (!prefill) return;
    setRequestForm({
      title: prefill.title,
      author: prefill.author ?? "",
      isbn: normalizeIsbn(prefill.isbn ?? ""),
      notes: prefill.notes ?? "",
      requestFormat: prefill.requestFormat,
      metadata: prefill.metadata,
    });
    setMetadataMatches([]);
    setInventoryMatches([]);
    setMessage("Book details loaded. Select a customer, then add the request.");
    onPrefillConsumed?.();
  }, [onPrefillConsumed, prefill]);
  const selectCustomer = (nextCustomer: AppCustomer) => {
    setCustomer(nextCustomer);
    setCustomerDialogOpen(false);
    setMessage(`Showing requests for ${nextCustomer.displayName}`);
  };
  const saveSelectedCustomer = (nextCustomer: AppCustomer) => {
    setCustomer(nextCustomer);
    setCustomerEditOpen(false);
    setRequests((current) => current.map((entry) => entry.customerId === nextCustomer.id || entry.squareCustomerId === nextCustomer.id ? { ...entry, customerDisplayName: nextCustomer.displayName } : entry));
    setMessage(`Updated ${nextCustomer.displayName}`);
  };
  const loadCustomerLoyalty = useCallback(async (customerId: string) => {
    setCustomerLoyaltyLoading(true);
    setCustomerLoyaltyError("");
    try {
      const data = await api<{ balance: LoyaltyBalanceView }>(`/api/admin/loyalty/customers/${encodeURIComponent(customerId)}`);
      setCustomerLoyalty(data.balance);
    } catch (error) {
      setCustomerLoyalty(null);
      setCustomerLoyaltyError(error instanceof Error ? error.message : "Unable to load loyalty details");
    } finally {
      setCustomerLoyaltyLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!customer) {
      setCustomerLoyalty(null);
      setCustomerLoyaltyError("");
      return;
    }
    void loadCustomerLoyalty(customer.id);
  }, [customer, loadCustomerLoyalty]);
  const enrollSelectedCustomer = async () => {
    if (!customer) return;
    setCustomerLoyaltyLoading(true);
    setCustomerLoyaltyError("");
    try {
      const data = await api<{ balance: LoyaltyBalanceView }>("/api/admin/loyalty/enrollments", {
        method: "POST",
        body: JSON.stringify({ customerId: customer.id, termsAccepted: false, source: "customer_management", idempotencyKey: crypto.randomUUID() }),
      });
      setCustomerLoyalty(data.balance);
      setMessage(`${customer.displayName} enrolled in loyalty`);
    } catch (error) {
      setCustomerLoyaltyError(error instanceof Error ? error.message : "Unable to enroll customer");
    } finally {
      setCustomerLoyaltyLoading(false);
    }
  };
  const retrieveRequestMetadata = async () => {
    if (!requestForm.isbn && !requestForm.title.trim()) {
      setMessage("Enter an ISBN or title before retrieving metadata");
      return;
    }
    setMetadataLoading(true);
    setMessage("");
    try {
      const query = requestForm.isbn
        ? { isbn: requestForm.isbn }
        : { title: requestForm.title, author: requestForm.author || undefined };
      const data = await api<{ results: BookMetadata[] }>("/api/admin/metadata/search", { method: "POST", body: JSON.stringify(query) });
      setMetadataMatches(data.results);
      setMessage(data.results.length ? `${data.results.length} metadata matches found` : "No metadata matches found");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Metadata lookup failed");
    } finally {
      setMetadataLoading(false);
    }
  };
  const applyRequestMetadata = (metadata: BookMetadata) => {
    setRequestForm({
      ...requestForm,
      title: metadata.title,
      author: metadata.authors.join(", "),
      isbn: metadata.isbn13 ?? metadata.isbn10 ?? requestForm.isbn,
      requestFormat: inferImportFormat(metadata),
      metadata,
    });
    setMessage(`Applied metadata from ${metadata.source}`);
  };
  const searchRequestInventory = async () => {
    const query = requestInventoryQuery(requestForm);
    if (!query) {
      setMessage("Enter an ISBN, title, or title and author before checking inventory");
      return;
    }
    setMetadataLoading(true);
    setMessage("");
    try {
      const data = await api<{ items: InventoryItem[]; sync?: { refreshedAt?: string } }>(`/api/admin/inventory?${new URLSearchParams({ q: query, limit: "12" })}`);
      setInventoryMatches(data.items);
      setMessage(data.items.length ? `${data.items.length} current inventory match(es) found` : "No current inventory matches found");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Inventory lookup failed");
    } finally {
      setMetadataLoading(false);
    }
  };
  const applyRequestInventoryItem = (item: InventoryItem) => {
    const prefill = inventoryItemToRequestPrefill(item);
    setRequestForm({
      ...requestForm,
      title: prefill.title,
      author: prefill.author ?? "",
      isbn: normalizeIsbn(prefill.isbn ?? requestForm.isbn),
      requestFormat: prefill.requestFormat,
      metadata: prefill.metadata,
    });
    setMessage(`Using current inventory details for ${item.title}`);
  };
  const createRequest = async () => {
    if (!customer) {
      setMessage("Select a customer before adding a request");
      return;
    }
    const data = await api<{ request: CustomerRequest }>("/api/pos/customer-requests", {
      method: "POST",
      body: JSON.stringify({
        customerId: customer.id,
        title: requestForm.title,
        author: requestForm.author || undefined,
        isbn: requestForm.isbn || undefined,
        notes: requestForm.notes || undefined,
        requestFormat: requestForm.requestFormat,
        metadata: requestForm.metadata,
      }),
    });
    setRequests((current) => [data.request, ...current.filter((entry) => entry.id !== data.request.id)]);
    setRequestForm({ title: "", author: "", isbn: "", notes: "", requestFormat: "softcover" });
    setMetadataMatches([]);
    setInventoryMatches([]);
    setRequestDialogOpen(false);
    setMessage("Customer request added");
  };
  const updateRequest = async (request: CustomerRequest, patch: Partial<CustomerRequest>) => {
    const data = await api<{ request: CustomerRequest }>(`/api/pos/customer-requests/${encodeURIComponent(request.id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    setRequests((current) => current.map((entry) => entry.id === request.id ? data.request : entry));
    return data.request;
  };
  const visibleRequests = customer
    ? requests.filter((request) => request.customerId === customer.id || request.squareCustomerId === customer.id)
    : requests;
  if (supplierSearchRequest) {
    return (
      <SupplierSearchScreen
        title={supplierSearchRequest.title}
        author={supplierSearchRequest.author ?? ""}
        isbn={supplierSearchRequest.isbn ?? ""}
        onBack={() => setSupplierSearchRequest(null)}
        onSelectOffer={(result, pricing) => {
          void updateRequest(supplierSearchRequest, {
            status: "ordered",
            supplierName: result.providerName,
            supplierUrl: result.productUrl,
            foundPriceMoney: pricing.foundPriceMoney,
            shippingMoney: pricing.shippingMoney,
            taxMoney: pricing.taxMoney,
            landedCostMoney: pricing.landedCostMoney,
            customerPriceMoney: pricing.customerPriceMoney,
          }).then(() => {
            setMessage(`Saved quote for ${supplierSearchRequest.title}: customer price ${formatMoney(pricing.customerPriceMoney)}`);
            setSupplierSearchRequest(null);
          });
        }}
        requestFormat={supplierSearchRequest.requestFormat ?? "softcover"}
        minimumPrices={settings.customerRequestMinimumPrices}
      />
    );
  }
  return (
    <section className="page">
      <header className="page-header"><div><p className="eyebrow">Customer requests</p><h1>Requests</h1></div></header>
      {message && <div className="success">{message}</div>}
      <section className="register-customer-bar request-customer-bar" aria-label="Request customer filter">
        {customer ? (
          <div className="customer-chip">
            <User />
            <strong>{customer.displayName}</strong>
            <span>{[customer.emailAddress, customer.phoneNumber].filter(Boolean).join(" / ") || "Selected customer"}</span>
            <button aria-label="Clear selected customer" onClick={() => { setCustomer(null); setMessage("Showing all customer requests"); }}>
              <X />
            </button>
          </div>
        ) : (
          <span>No customer selected - showing all requests</span>
        )}
        <div className="request-customer-actions">
          <button onClick={() => setCustomerDialogOpen(true)}><User /> {customer ? "Change customer" : "Select customer"}</button>
          {customer && <button onClick={() => setCustomerEditOpen(true)}>Edit customer</button>}
          <button className="primary" disabled={!customer} onClick={() => setRequestDialogOpen(true)}>Add request</button>
        </div>
      </section>
      {customer && (
        <section className="panel" aria-label="Selected customer loyalty">
          <div className="section-heading">
            <div>
              <h2>Loyalty for {customer.displayName}</h2>
              <p className="empty-inline">
                {customerLoyaltyLoading
                  ? "Loading loyalty details..."
                  : customerLoyalty?.enrolled
                    ? `${customerLoyalty.balance} points available / ${customerLoyalty.availableRewards.length} reward(s)`
                    : "Customer is not enrolled in loyalty yet"}
              </p>
            </div>
            <div className="actions">
              <button onClick={() => void loadCustomerLoyalty(customer.id)} disabled={customerLoyaltyLoading}><RefreshCw /> Refresh</button>
              {!customerLoyalty?.enrolled && <button className="primary" onClick={() => void enrollSelectedCustomer()} disabled={customerLoyaltyLoading}>Enroll in loyalty</button>}
            </div>
          </div>
          {customerLoyaltyError && <p className="error-text">{customerLoyaltyError}</p>}
          {customerLoyalty?.enrolled && (
            <dl className="metadata-fields">
              <dt>Balance</dt><dd>{customerLoyalty.balance} points</dd>
              <dt>Lifetime earned</dt><dd>{customerLoyalty.lifetimeEarned} points</dd>
              <dt>Level</dt><dd>{customerLoyalty.currentLevel ?? "No level yet"}</dd>
              <dt>Next reward</dt><dd>{customerLoyalty.availableRewards.find((reward) => reward.pointsCost <= customerLoyalty.balance)?.name ?? "No reward currently available"}</dd>
            </dl>
          )}
        </section>
      )}
      <section className="panel">
        <div className="section-heading">
          <div>
            <h2>{customer ? `Requests for ${customer.displayName}` : "All customer requests"}</h2>
            <p className="empty-inline">{visibleRequests.length} request(s){customer ? " for the selected customer" : " across all customers"}{!customer ? ". Select a customer to add a new request." : ""}</p>
          </div>
          <button onClick={() => void loadRequests()}><RefreshCw /> Refresh requests</button>
        </div>
        <div className="receiving-list">
        {visibleRequests.map((request) => (
          <article className="receive-row" key={request.id}>
            {request.metadata?.coverImageUrls[0] ? <Image imageUrl={request.metadata.coverImageUrls[0]} /> : <div className="cover placeholder"><BookOpen aria-hidden /></div>}
            <div>
              <div className="receive-row-header">
                <div>
                  <h2>{request.title}</h2>
                  <p>{[request.author, request.isbn].filter(Boolean).join(" / ") || "No book details"}</p>
                  <p className="empty-inline">Customer: {request.customerDisplayName ?? request.customerId}</p>
                </div>
                <span className="badge">{request.status}</span>
              </div>
              {request.notes && <p className="square-match-note">{request.notes}</p>}
              {request.metadata?.description && (
                <div className="description-snippet">
                  <strong>Description snippet</strong>
                  <p>{descriptionSnippet(request.metadata.description)}</p>
                </div>
              )}
              {(request.landedCostMoney || request.customerPriceMoney) && (
                <dl className="metadata-fields pricing-fields">
                  <dt>Format</dt><dd>{request.requestFormat === "hardcover" ? "Hardcover" : request.requestFormat === "new" ? "New" : "Paperback"}</dd>
                  <dt>Landed cost</dt><dd>{formatMoney(request.landedCostMoney)}</dd>
                  <dt>Customer price</dt><dd>{formatMoney(request.customerPriceMoney)}</dd>
                </dl>
              )}
              <div className="receive-row-actions">
                <button onClick={() => setSupplierSearchRequest(request)}><ExternalLink /> Find copies</button>
                <label>Format
                  <select value={request.requestFormat ?? "softcover"} onChange={(event) => void updateRequest(request, { requestFormat: event.target.value as ImportFormat })}>
                    <option value="softcover">Paperback</option>
                    <option value="hardcover">Hardcover</option>
                    <option value="new">New</option>
                  </select>
                </label>
                <label>Customer price
                  <input
                    className="price-input"
                    inputMode="decimal"
                    defaultValue={moneyToInput(request.customerPriceMoney ?? settings.customerRequestMinimumPrices[request.requestFormat ?? "softcover"])}
                    onBlur={(event) => void updateRequest(request, { customerPriceMoney: inputToMoney(event.target.value, request.customerPriceMoney?.currency ?? settings.defaultCurrency) })}
                  />
                </label>
                <label>Status
                  <select value={request.status} onChange={(event) => void updateRequest(request, { status: event.target.value as CustomerRequest["status"] })}>
                    <option value="requested">Requested</option>
                    <option value="sourcing">Sourcing</option>
                    <option value="ordered">Ordered for customer</option>
                    <option value="received">Received</option>
                    <option value="purchased">Customer bought it</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
                <label>Supplier<input value={request.supplierName ?? ""} onChange={(event) => void updateRequest(request, { supplierName: event.target.value || undefined })} /></label>
                <label>Supplier URL<input value={request.supplierUrl ?? ""} onChange={(event) => void updateRequest(request, { supplierUrl: event.target.value || undefined })} /></label>
              </div>
            </div>
          </article>
        ))}
        {visibleRequests.length === 0 && <EmptyState />}
        </div>
      </section>
      {customerDialogOpen && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal customer-dialog" role="dialog" aria-modal="true" aria-labelledby="request-customer-dialog-title">
            <div className="section-heading">
              <div><p className="eyebrow">Customer</p><h2 id="request-customer-dialog-title">Find customer</h2></div>
              <button aria-label="Close customer lookup" onClick={() => setCustomerDialogOpen(false)}><X /></button>
            </div>
            <CustomerPicker selectedCustomer={customer} onSelect={selectCustomer} />
          </section>
        </div>
      )}
      {customerEditOpen && customer && <CustomerEditDialog customer={customer} onClose={() => setCustomerEditOpen(false)} onSaved={saveSelectedCustomer} />}
      {requestDialogOpen && customer && (
        <div className="modal-backdrop" role="presentation">
          <section className="modal request-dialog" role="dialog" aria-modal="true" aria-labelledby="request-dialog-title">
            <div className="section-heading">
              <div><p className="eyebrow">Request</p><h2 id="request-dialog-title">Add request</h2><p className="empty-inline">For {customer.displayName}</p></div>
              <button aria-label="Close request form" onClick={() => setRequestDialogOpen(false)}><X /></button>
            </div>
            <div className="form-grid dialog-form">
              <label>Requested title<input value={requestForm.title} onChange={(event) => setRequestForm({ ...requestForm, title: event.target.value })} /></label>
              <label>Author<input value={requestForm.author} onChange={(event) => setRequestForm({ ...requestForm, author: event.target.value })} /></label>
              <label>ISBN<input value={requestForm.isbn} onChange={(event) => setRequestForm({ ...requestForm, isbn: normalizeIsbn(event.target.value) })} /></label>
              <label>Format<select value={requestForm.requestFormat} onChange={(event) => setRequestForm({ ...requestForm, requestFormat: event.target.value as ImportFormat })}><option value="softcover">Paperback</option><option value="hardcover">Hardcover</option><option value="new">New</option></select></label>
              <label className="wide">Notes<textarea rows={4} value={requestForm.notes} onChange={(event) => setRequestForm({ ...requestForm, notes: event.target.value })} /></label>
              {requestForm.metadata && (
                <div className="wide description-snippet">
                  <strong>{requestForm.metadata.title}</strong>
                  {requestForm.metadata.description && <p>{descriptionSnippet(requestForm.metadata.description)}</p>}
                </div>
              )}
              <div className="wide actions"><button onClick={() => void searchRequestInventory()} disabled={metadataLoading}><Search /> Check inventory</button><button onClick={() => void retrieveRequestMetadata()} disabled={metadataLoading}><Search /> {metadataLoading ? "Searching..." : "Retrieve metadata"}</button><button className="primary" onClick={createRequest} disabled={!requestForm.title.trim()}>Add request</button></div>
              {inventoryMatches.length > 0 && (
                <section className="wide metadata-results">
                  <h2>Current inventory matches</h2>
                  {inventoryMatches.map((match) => (
                    <article key={match.id}>
                      <Image imageUrl={match.imageUrl} />
                      <div>
                        <strong>{match.title}</strong>
                        <span>{[match.author, match.isbn ?? match.sku, match.availableQuantity === 1 ? "1 available" : `${match.availableQuantity ?? "Unknown"} available`].filter(Boolean).join(" / ")}</span>
                        <p>Use this when the requested book already exists locally but is out of stock or needs another copy sourced.</p>
                        <button onClick={() => applyRequestInventoryItem(match)}>Use inventory details</button>
                      </div>
                    </article>
                  ))}
                </section>
              )}
              {metadataMatches.length > 0 && (
                <section className="wide metadata-results">
                  <h2>Metadata matches</h2>
                  {metadataMatches.map((match) => (
                    <article key={`${match.source}-${match.sourceId}`}>
                      <Image imageUrl={match.coverImageUrls[0]} />
                      <div>
                        <strong>{match.title}</strong>
                        <span>{match.authors.join(", ") || "Unknown author"} / {match.confidence}</span>
                        {match.description && <p>{descriptionSnippet(match.description)}</p>}
                        <button onClick={() => applyRequestMetadata(match)}>Apply metadata</button>
                      </div>
                    </article>
                  ))}
                </section>
              )}
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

const purchasingPoTypes: { value: PurchasingPurchaseOrder["poType"]; label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "replenishment", label: "Replenishment" },
  { value: "customer_special_order", label: "Customer special order" },
  { value: "preorder", label: "Preorder" },
  { value: "event_book_fair", label: "Event / book fair" },
  { value: "initial_inventory", label: "Initial inventory" },
  { value: "direct_to_customer", label: "Direct to customer" },
];
const purchasingStatuses = ["draft", "pending_approval", "approved", "submitted", "acknowledged", "partially_filled", "shipped", "partially_received", "received", "completed", "canceled", "closed"];
const purchasingBackorderPolicies: { value: PurchasingVendor["defaultBackorderPolicy"]; label: string }[] = [
  { value: "allow", label: "Allow backorders" },
  { value: "ship_available", label: "Ship available" },
  { value: "cancel", label: "Cancel unavailable" },
];

function PurchasingWorkspace({ api, storeProfile, currentUser, navigate }: { api: StaffApi; storeProfile: PublicStoreProfile; currentUser: UserSession; navigate: (path: string) => void }) {
  const canManage = currentUser.role === "administrator" || currentUser.role === "manager";
  const canConfigureIntegrations = currentUser.role === "administrator";
  const [tab, setTab] = useState<"vendors" | "orders" | "receiving" | "shipments" | "invoices" | "supplier-returns" | "replenishment" | "special-orders" | "operations" | "changes" | "backorders" | "integrations">("orders");
  const [vendors, setVendors] = useState<PurchasingVendor[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchasingPurchaseOrder[]>([]);
  const [locations, setLocations] = useState<{ id: string; name: string; code?: string; active: boolean }[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedPoId, setSelectedPoId] = useState("");
  const [selectedVendor, setSelectedVendor] = useState<PurchasingVendor | null>(null);
  const [selectedPo, setSelectedPo] = useState<PurchasingPurchaseOrder | null>(null);
  const [poHistory, setPoHistory] = useState<{ id: string; eventType: string; actorUserId?: string | null; note?: string | null; createdAt: string; after?: Record<string, unknown> }[]>([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorStatus, setVendorStatus] = useState("");
  const [poQuery, setPoQuery] = useState("");
  const [poStatus, setPoStatus] = useState("");
  const [vendorDraft, setVendorDraft] = useState(() => newVendorDraft(storeProfile.currencyCode ?? "USD"));
  const [accountDraft, setAccountDraft] = useState(() => newVendorAccountDraft(storeProfile.currencyCode ?? "USD"));
  const [addressDraft, setAddressDraft] = useState(() => newVendorAddressDraft());
  const [identifierDraft, setIdentifierDraft] = useState(() => newPartyIdentifierDraft());
  const [vendorProductDraft, setVendorProductDraft] = useState(() => newVendorProductDraft(storeProfile.currencyCode ?? "USD"));
  const [poDraft, setPoDraft] = useState(() => newPoDraft(storeProfile.currencyCode ?? "USD"));
  const [poLines, setPoLines] = useState<PurchasingPoLine[]>([newPoLine(storeProfile.currencyCode ?? "USD")]);
  const [inventoryQuery, setInventoryQuery] = useState("");
  const [inventoryMatches, setInventoryMatches] = useState<InventoryItem[]>([]);
  const [bulkLines, setBulkLines] = useState("");
  const [receivingLines, setReceivingLines] = useState<Record<string, PurchasingReceivingLine>>({});
  const [receivingMeta, setReceivingMeta] = useState({ shipmentReference: "", notes: "" });
  const [receivingPreview, setReceivingPreview] = useState<{ valid: boolean; lines: { errors: string[]; warnings: string[]; inventoryQuantityDelta: number }[] } | null>(null);
  const [postedSession, setPostedSession] = useState<PurchasingReceivingSession | null>(null);
  const [correctionSession, setCorrectionSession] = useState<PurchasingReceivingSession | null>(null);
  const [correction, setCorrection] = useState({ receivingLineId: "", quantityDelta: "-1", unitCost: "0.00", reason: "" });
  const [replenishmentParams, setReplenishmentParams] = useState({ lookbackDays: "56", targetWeeksSupply: "6", locationId: "loc-main" });
  const [replenishmentReview, setReplenishmentReview] = useState<PurchasingReplenishmentReview | null>(null);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<string[]>([]);
  const [specialOrders, setSpecialOrders] = useState<PurchasingSpecialOrder[]>([]);
  const [specialOrderQuery, setSpecialOrderQuery] = useState("");
  const [specialOrderDraft, setSpecialOrderDraft] = useState({ customerId: "", title: "", author: "", isbn: "", variationId: "", quantity: "1", fulfillmentPreference: "pickup", promisedDate: "", estimatedDate: "", depositStatus: "not_required", notes: "" });
  const [exportTemplates, setExportTemplates] = useState<PurchasingExportTemplate[]>([]);
  const [templateDraft, setTemplateDraft] = useState({ vendorId: "", name: "Default PO CSV", format: "csv" as "csv" | "spreadsheet_csv", columnMappingText: "PO Number=poNumber\nVendor SKU=vendorSku\nISBN-13=isbn13\nTitle=title\nQuantity=quantity\nUnit Cost=unitCost" });
  const [ackImportText, setAckImportText] = useState("vendorSku,acknowledgedQuantity,backorderedQuantity,canceledQuantity\n");
  const [integrationProviders, setIntegrationProviders] = useState<IntegrationProvider[]>([]);
  const [tradingPartners, setTradingPartners] = useState<IntegrationTradingPartner[]>([]);
  const [integrationMessages, setIntegrationMessages] = useState<IntegrationMessage[]>([]);
  const [integrationReviews, setIntegrationReviews] = useState<IntegrationReviewItem[]>([]);
  const [integrationHealth, setIntegrationHealth] = useState<IntegrationHealth | null>(null);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [providerDraft, setProviderDraft] = useState({ displayName: "Pubnet", environment: "test", status: "testing", killSwitchEnabled: false, rolloutPercent: "0", rateLimitPerMinute: "30", adapterVersion: "pubnet-x12-3060-v1", mapVersion: "pubnet-850-3060-2020" });
  const [partnerDraft, setPartnerDraft] = useState({ vendorId: "", vendorAccountId: "", san: "", pubnetId: "", x12SenderId: "", x12ReceiverId: "", defaultTransport: "local_test", certificationStatus: "not_started", certificationNotes: "" });
  const [credentialDraft, setCredentialDraft] = useState({ credentialName: "Pubnet mailbox", credentialType: "pubnet_mailbox", secretBindingName: "PUBNET_MAILBOX_PASSWORD" });
  const [electronicPreview, setElectronicPreview] = useState<{ validation: Array<{ severity: string; code: string; message: string }>; x12Preview?: string; warning?: string } | null>(null);
  const [changeRequests, setChangeRequests] = useState<PurchasingPoChangeRequest[]>([]);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState<PurchasingPoChangeRequest | null>(null);
  const [changeDraft, setChangeDraft] = useState<PoChangeDraft>(() => newPoChangeDraft());
  const [changeResponseDraft, setChangeResponseDraft] = useState({ status: "accepted", supplierResponse: "", externalReference: "", externalControlNumber: "" });
  const [backorders, setBackorders] = useState<PurchasingBackorderRow[]>([]);
  const [selectedBackorderIds, setSelectedBackorderIds] = useState<string[]>([]);
  const [backorderPolicy, setBackorderPolicy] = useState<PurchasingBackorderPolicy>(() => defaultBackorderPolicy());
  const [shipments, setShipments] = useState<PurchasingShipment[]>([]);
  const [selectedShipmentId, setSelectedShipmentId] = useState("");
  const [selectedShipment, setSelectedShipment] = useState<PurchasingShipment | null>(null);
  const [shipmentDiscrepancies, setShipmentDiscrepancies] = useState<PurchasingShipmentDiscrepancy[]>([]);
  const [asnFixtureText, setAsnFixtureText] = useState(() => defaultAsnFixture());
  const [cartonBarcode, setCartonBarcode] = useState("");
  const [cartonPreview, setCartonPreview] = useState<PurchasingShipmentCarton | null>(null);
  const [cartonReceiveLines, setCartonReceiveLines] = useState<Record<string, CartonReceiveDraftLine>>({});
  const [shipmentResolution, setShipmentResolution] = useState({ status: "resolved", supplierFollowupStatus: "logged", resolutionReason: "" });
  const [invoices, setInvoices] = useState<PurchasingSupplierInvoice[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<PurchasingSupplierInvoice | null>(null);
  const [invoiceDiscrepancies, setInvoiceDiscrepancies] = useState<PurchasingInvoiceDiscrepancy[]>([]);
  const [creditMemos, setCreditMemos] = useState<PurchasingCreditMemo[]>([]);
  const [accountingExports, setAccountingExports] = useState<PurchasingAccountingExportBatch[]>([]);
  const [invoiceFixtureText, setInvoiceFixtureText] = useState(() => defaultInvoiceFixture());
  const [creditMemoDraft, setCreditMemoDraft] = useState({ vendorId: "", vendorAccountId: "", creditNumber: "", creditType: "damaged_copy", amount: "0.00", originalInvoiceId: "", notes: "" });
  const [invoiceResolution, setInvoiceResolution] = useState({ status: "resolved", resolutionReason: "" });
  const [supplierReturns, setSupplierReturns] = useState<PurchasingSupplierReturn[]>([]);
  const [selectedSupplierReturnId, setSelectedSupplierReturnId] = useState("");
  const [selectedSupplierReturn, setSelectedSupplierReturn] = useState<PurchasingSupplierReturn | null>(null);
  const [supplierReturnSuggestions, setSupplierReturnSuggestions] = useState<PurchasingSupplierReturnSuggestion[]>([]);
  const [supplierReturnDraft, setSupplierReturnDraft] = useState({ vendorId: "", sourceLocationId: "loc-main", variationId: "", isbn13: "", titleSnapshot: "", receivingLineId: "", quantity: "1", reason: "overstock", condition: "sellable", expectedUnitCreditAmount: "0.00", manualOverride: false, manualOverrideReason: "" });
  const [supplierReturnCreditDraft, setSupplierReturnCreditDraft] = useState({ creditMemoId: "", actualCreditAmount: "0.00", feesOrDeductionsAmount: "0.00", status: "credited", resolutionReason: "" });
  const poScanRef = useRef<HTMLInputElement | null>(null);
  const receiveScanRef = useRef<HTMLInputElement | null>(null);
  const cartonScanRef = useRef<HTMLInputElement | null>(null);
  const receiveLocationId = poDraft.shipToLocationId || locations[0]?.id || "loc-main";

  const loadVendors = useCallback(async () => {
    const params = new URLSearchParams({ limit: "100" });
    if (vendorQuery.trim()) params.set("q", vendorQuery.trim());
    if (vendorStatus) params.set("status", vendorStatus);
    const data = await api<{ vendors: PurchasingVendor[] }>(`/api/admin/purchasing/vendors?${params}`);
    setVendors(data.vendors);
  }, [api, vendorQuery, vendorStatus]);
  const loadPurchaseOrders = useCallback(async () => {
    const params = new URLSearchParams({ limit: "100" });
    if (poQuery.trim()) params.set("q", poQuery.trim());
    if (poStatus) params.set("status", poStatus);
    const data = await api<{ purchaseOrders: PurchasingPurchaseOrder[] }>(`/api/admin/purchasing/purchase-orders?${params}`);
    setPurchaseOrders(data.purchaseOrders);
  }, [api, poQuery, poStatus]);
  const loadLocations = useCallback(async () => {
    const data = await api<{ locations: { id: string; name: string; code?: string; active: boolean }[]; defaults?: { defaultLocationId?: string } }>("/api/admin/inventory-settings");
    const activeLocations = data.locations.filter((location) => location.active);
    setLocations(activeLocations);
    setPoDraft((current) => ({ ...current, shipToLocationId: current.shipToLocationId || data.defaults?.defaultLocationId || activeLocations[0]?.id || "loc-main", billToLocationId: current.billToLocationId || data.defaults?.defaultLocationId || activeLocations[0]?.id || "loc-main" }));
    setReplenishmentParams((current) => ({ ...current, locationId: current.locationId || data.defaults?.defaultLocationId || activeLocations[0]?.id || "loc-main" }));
  }, [api]);
  const loadSpecialOrders = useCallback(async () => {
    const params = new URLSearchParams({ limit: "100" });
    if (specialOrderQuery.trim()) params.set("q", specialOrderQuery.trim());
    const data = await api<{ specialOrders: PurchasingSpecialOrder[] }>(`/api/admin/purchasing/special-orders?${params}`);
    setSpecialOrders(data.specialOrders);
  }, [api, specialOrderQuery]);
  const loadExportTemplates = useCallback(async () => {
    const data = await api<{ templates: PurchasingExportTemplate[] }>("/api/admin/purchasing/export-templates?limit=100");
    setExportTemplates(data.templates);
  }, [api]);
  const loadIntegrations = useCallback(async () => {
    const [providers, partners, messagesData, reviews, health] = await Promise.all([
      api<{ providers: IntegrationProvider[] }>("/api/admin/integrations/providers?limit=100"),
      api<{ tradingPartners: IntegrationTradingPartner[] }>("/api/admin/integrations/trading-partners?limit=100"),
      api<{ messages: IntegrationMessage[] }>("/api/admin/integrations/messages?limit=100"),
      api<{ reviewItems: IntegrationReviewItem[] }>("/api/admin/integrations/review-items?limit=100"),
      api<{ health: IntegrationHealth }>("/api/admin/integrations/health"),
    ]);
    const providerRows = providers.providers ?? [];
    setIntegrationProviders(providerRows);
    setTradingPartners(partners.tradingPartners ?? []);
    setIntegrationMessages(messagesData.messages ?? []);
    setIntegrationReviews(reviews.reviewItems ?? []);
    setIntegrationHealth(health.health ?? null);
    setSelectedProviderId((current) => current || providerRows[0]?.id || "");
  }, [api]);
  const loadChangeRequests = useCallback(async () => {
    const data = await api<{ changeRequests: PurchasingPoChangeRequest[] }>("/api/admin/purchasing/change-requests?limit=100");
    setChangeRequests(data.changeRequests ?? []);
  }, [api]);
  const loadBackorders = useCallback(async () => {
    const [queue, policy] = await Promise.all([
      api<{ backorders: PurchasingBackorderRow[] }>("/api/admin/purchasing/backorders?limit=100"),
      api<{ policy: PurchasingBackorderPolicy }>("/api/admin/purchasing/backorders/policy"),
    ]);
    setBackorders(queue.backorders ?? []);
    setBackorderPolicy(policy.policy ?? defaultBackorderPolicy());
  }, [api]);
  const loadShipments = useCallback(async () => {
    const data = await api<{ shipments: PurchasingShipment[] }>("/api/admin/purchasing/shipments?limit=100");
    setShipments(data.shipments ?? []);
  }, [api]);
  const loadShipmentDiscrepancies = useCallback(async () => {
    const data = await api<{ discrepancies: PurchasingShipmentDiscrepancy[] }>("/api/admin/purchasing/shipment-discrepancies?limit=100");
    setShipmentDiscrepancies(data.discrepancies ?? []);
  }, [api]);
  const loadInvoices = useCallback(async () => {
    const [invoiceRows, discrepancyRows, credits, exportsData] = await Promise.all([
      api<{ invoices: PurchasingSupplierInvoice[] }>("/api/admin/purchasing/invoices?limit=100"),
      api<{ discrepancies: PurchasingInvoiceDiscrepancy[] }>("/api/admin/purchasing/invoice-discrepancies?limit=100"),
      api<{ creditMemos: PurchasingCreditMemo[] }>("/api/admin/purchasing/credit-memos?limit=100"),
      api<{ batches: PurchasingAccountingExportBatch[] }>("/api/admin/purchasing/accounting-exports?limit=50"),
    ]);
    setInvoices(invoiceRows.invoices ?? []);
    setInvoiceDiscrepancies(discrepancyRows.discrepancies ?? []);
    setCreditMemos(credits.creditMemos ?? []);
    setAccountingExports(exportsData.batches ?? []);
  }, [api]);
  const loadSupplierReturns = useCallback(async () => {
    const data = await api<{ supplierReturns: PurchasingSupplierReturn[] }>("/api/admin/purchasing/supplier-returns?limit=100");
    setSupplierReturns(data.supplierReturns ?? []);
  }, [api]);
  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      await Promise.all([loadVendors(), loadPurchaseOrders(), loadLocations(), loadSpecialOrders(), loadExportTemplates(), loadIntegrations(), loadChangeRequests(), loadBackorders(), loadShipments(), loadShipmentDiscrepancies(), loadInvoices(), loadSupplierReturns()]);
    } catch (err) {
      setError(staffErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [loadBackorders, loadChangeRequests, loadExportTemplates, loadIntegrations, loadInvoices, loadLocations, loadPurchaseOrders, loadShipmentDiscrepancies, loadShipments, loadSpecialOrders, loadSupplierReturns, loadVendors]);

  useEffect(() => { void refreshAll(); }, [refreshAll]);
  useEffect(() => {
    if (!selectedVendorId) {
      setSelectedVendor(null);
      return;
    }
    api<{ vendor: PurchasingVendor }>(`/api/admin/purchasing/vendors/${encodeURIComponent(selectedVendorId)}`)
      .then((data) => setSelectedVendor(data.vendor))
      .catch((err) => setError(staffErrorMessage(err)));
  }, [api, selectedVendorId, message]);
  useEffect(() => {
    if (!selectedPoId) {
      setSelectedPo(null);
      setPoHistory([]);
      return;
    }
    Promise.all([
      api<{ purchaseOrder: PurchasingPurchaseOrder }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPoId)}`),
      api<{ history: typeof poHistory }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPoId)}/history`).catch(() => ({ history: [] })),
    ]).then(([poData, historyData]) => {
      setSelectedPo(poData.purchaseOrder);
      setPoHistory(historyData.history);
      setChangeDraft(newPoChangeDraft());
      if (poData.purchaseOrder.status === "draft") {
        setPoDraft(poToDraft(poData.purchaseOrder));
        setPoLines(poData.purchaseOrder.lines?.length ? poData.purchaseOrder.lines : [newPoLine(poData.purchaseOrder.currency)]);
      }
    }).catch((err) => setError(staffErrorMessage(err)));
  }, [api, selectedPoId, message]);
  useEffect(() => {
    if (!selectedShipmentId) {
      setSelectedShipment(null);
      return;
    }
    api<{ shipment: PurchasingShipment }>(`/api/admin/purchasing/shipments/${encodeURIComponent(selectedShipmentId)}`)
      .then((data) => setSelectedShipment(data.shipment))
      .catch((err) => setError(staffErrorMessage(err)));
  }, [api, selectedShipmentId, message]);
  useEffect(() => {
    if (!selectedInvoiceId) {
      setSelectedInvoice(null);
      return;
    }
    api<{ invoice: PurchasingSupplierInvoice }>(`/api/admin/purchasing/invoices/${encodeURIComponent(selectedInvoiceId)}`)
      .then((data) => setSelectedInvoice(data.invoice))
      .catch((err) => setError(staffErrorMessage(err)));
  }, [api, selectedInvoiceId, message]);
  useEffect(() => {
    if (!selectedSupplierReturnId) {
      setSelectedSupplierReturn(null);
      return;
    }
    api<{ supplierReturn: PurchasingSupplierReturn }>(`/api/admin/purchasing/supplier-returns/${encodeURIComponent(selectedSupplierReturnId)}`)
      .then((data) => setSelectedSupplierReturn(data.supplierReturn))
      .catch((err) => setError(staffErrorMessage(err)));
  }, [api, selectedSupplierReturnId, message]);

  const vendorOpenPurchaseOrders = useMemo(() => purchaseOrders.filter((po) => po.vendorId === selectedVendor?.id && !["completed", "canceled", "closed"].includes(po.status)), [purchaseOrders, selectedVendor]);
  const duplicateVendorWarning = vendorDraft.name.trim() && vendors.some((vendor) => vendor.name.toLowerCase() === vendorDraft.name.trim().toLowerCase() && vendor.id !== selectedVendor?.id);
  const selectedVendorAccounts = selectedVendor?.accounts ?? vendors.find((vendor) => vendor.id === poDraft.vendorId)?.accounts ?? [];
  const selectedVendorForDraft = selectedVendor?.id === poDraft.vendorId ? selectedVendor : vendors.find((vendor) => vendor.id === poDraft.vendorId);
  const poEditable = !selectedPo || selectedPo.status === "draft";
  const activeReceivingPo = selectedPo && !["draft", "pending_approval", "approved", "canceled", "closed"].includes(selectedPo.status) ? selectedPo : null;
  const poTotal = poLines.reduce((sum, line) => sum + line.requestedQuantity * line.expectedUnitCostAmount, 0);
  const thresholdWarning = selectedVendorForDraft?.minimumOrderAmount && poTotal < selectedVendorForDraft.minimumOrderAmount
    ? `Below minimum order ${formatMoney({ amount: selectedVendorForDraft.minimumOrderAmount, currency: selectedVendorForDraft.defaultCurrency })}`
    : selectedVendorForDraft?.freeFreightThresholdAmount && poTotal < selectedVendorForDraft.freeFreightThresholdAmount
      ? `Below free freight ${formatMoney({ amount: selectedVendorForDraft.freeFreightThresholdAmount, currency: selectedVendorForDraft.defaultCurrency })}`
      : "";

  const run = async (action: () => Promise<void>) => {
    setError("");
    setMessage("");
    try {
      await action();
    } catch (err) {
      setError(staffErrorMessage(err));
    }
  };
  const saveVendor = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const body = { ...vendorDraft, minimumOrderAmount: moneyInputToOptionalCents(vendorDraft.minimumOrderAmount), freeFreightThresholdAmount: moneyInputToOptionalCents(vendorDraft.freeFreightThresholdAmount) };
    const data = selectedVendor
      ? await api<{ vendor: PurchasingVendor }>(`/api/admin/purchasing/vendors/${encodeURIComponent(selectedVendor.id)}`, { method: "PATCH", body: JSON.stringify(body) })
      : await api<{ vendor: PurchasingVendor }>("/api/admin/purchasing/vendors", { method: "POST", body: JSON.stringify(body) });
    setSelectedVendorId(data.vendor.id);
    setVendorDraft(vendorToDraft(data.vendor));
    setMessage(selectedVendor ? "Vendor updated" : "Vendor created");
    await loadVendors();
  });
  const createVendorChild = (kind: "account" | "address" | "identifier" | "product") => run(async () => {
    if (!selectedVendor) throw new Error("Select a vendor first.");
    if (!canManage) throw new Error("A manager or administrator is required.");
    if (kind === "account") await api(`/api/admin/purchasing/vendors/${encodeURIComponent(selectedVendor.id)}/accounts`, { method: "POST", body: JSON.stringify({ ...accountDraft, minimumOrderAmount: moneyInputToOptionalCents(accountDraft.minimumOrderAmount), freeFreightThresholdAmount: moneyInputToOptionalCents(accountDraft.freeFreightThresholdAmount) }) });
    if (kind === "address") await api(`/api/admin/purchasing/vendors/${encodeURIComponent(selectedVendor.id)}/addresses`, { method: "POST", body: JSON.stringify(addressDraft) });
    if (kind === "identifier") await api(`/api/admin/purchasing/vendors/${encodeURIComponent(selectedVendor.id)}/identifiers`, { method: "POST", body: JSON.stringify(identifierDraft) });
    if (kind === "product") await api("/api/admin/purchasing/vendor-products", { method: "POST", body: JSON.stringify({ ...vendorProductDraft, vendorId: selectedVendor.id, costAmount: moneyInputToOptionalCents(vendorProductDraft.costAmount) ?? 0, discountBasisPoints: optionalNumber(vendorProductDraft.discountBasisPoints), minimumOrderQuantity: Number(vendorProductDraft.minimumOrderQuantity || 1), casePackQuantity: Number(vendorProductDraft.casePackQuantity || 1), expectedLeadTimeDays: optionalNumber(vendorProductDraft.expectedLeadTimeDays) }) });
    setAccountDraft(newVendorAccountDraft(selectedVendor.defaultCurrency));
    setAddressDraft(newVendorAddressDraft());
    setIdentifierDraft(newPartyIdentifierDraft());
    setVendorProductDraft(newVendorProductDraft(selectedVendor.defaultCurrency));
    setMessage("Vendor detail saved");
  });
  const searchInventory = () => run(async () => {
    if (!inventoryQuery.trim()) return;
    const data = await api<{ items: InventoryItem[] }>(`/api/admin/inventory?${new URLSearchParams({ q: inventoryQuery.trim(), limit: "20", active: "all" })}`);
    setInventoryMatches(data.items);
  });
  const addInventoryItemToPo = (item: InventoryItem) => {
    const variation = item.variations[0];
    setPoLines((current) => [...current.filter((line) => line.titleSnapshot || line.isbn13 || line.variationId), {
      ...newPoLine(poDraft.currency),
      id: crypto.randomUUID(),
      variationId: variation?.id ?? item.id,
      editionId: item.id,
      isbn13: normalizeIsbn(item.isbn ?? item.sku ?? variation?.gtin ?? variation?.sku ?? "") || undefined,
      internalSku: variation?.sku ?? item.sku,
      titleSnapshot: item.title,
      expectedUnitCostAmount: Math.round((item.priceMoney?.amount ?? 0) * 0.55),
    }]);
    setInventoryQuery("");
    setInventoryMatches([]);
    poScanRef.current?.focus();
  };
  const scanPoLine = () => {
    const value = inventoryQuery.trim();
    if (!value) return;
    const normalized = normalizeIsbn(value);
    const match = inventoryMatches.find((item) => itemHasIsbn(item, normalized) || item.title.toLowerCase().includes(value.toLowerCase()));
    if (match) addInventoryItemToPo(match);
    else {
      setPoLines((current) => [...current.filter((line) => line.titleSnapshot || line.isbn13 || line.vendorSku), { ...newPoLine(poDraft.currency), isbn13: normalized || value, titleSnapshot: value }]);
      setInventoryQuery("");
      poScanRef.current?.focus();
    }
  };
  const applyBulkLines = () => {
    const parsed = bulkLines.split(/\r?\n/).map((row) => row.split(",").map((part) => part.trim())).filter((parts) => parts[0]);
    if (!parsed.length) return;
    setPoLines((current) => [...current.filter((line) => line.titleSnapshot || line.isbn13 || line.vendorSku), ...parsed.map(([identifier, qty, cost, title]) => ({ ...newPoLine(poDraft.currency), id: crypto.randomUUID(), isbn13: normalizeIsbn(identifier) || undefined, vendorSku: normalizeIsbn(identifier) ? undefined : identifier, titleSnapshot: title || identifier, requestedQuantity: Number(qty || 1), expectedUnitCostAmount: moneyInputToOptionalCents(cost || "0") ?? 0 }))]);
    setBulkLines("");
  };
  const savePo = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const body = poDraftPayload(poDraft, poLines);
    const data = selectedPo && selectedPo.status === "draft"
      ? await api<{ purchaseOrder: PurchasingPurchaseOrder }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}`, { method: "PATCH", body: JSON.stringify({ ...body, expectedVersion: selectedPo.version }) })
      : await api<{ purchaseOrder: PurchasingPurchaseOrder }>("/api/admin/purchasing/purchase-orders", { method: "POST", body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }) });
    setSelectedPoId(data.purchaseOrder.id);
    setMessage(`${data.purchaseOrder.poNumber} saved`);
    await loadPurchaseOrders();
  });
  const transitionPo = (action: "approve" | "submit" | "cancel" | "close") => run(async () => {
    if (!selectedPo) return;
    const note = action === "cancel" || action === "close" ? window.prompt(`Reason for ${action}`) : "";
    if ((action === "cancel" || action === "close") && !note) return;
    const data = await api<{ purchaseOrder: PurchasingPurchaseOrder }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/${action}`, { method: "POST", body: JSON.stringify({ expectedVersion: selectedPo.version, note: note || undefined, idempotencyKey: crypto.randomUUID() }) });
    setSelectedPo(data.purchaseOrder);
    setMessage(`${data.purchaseOrder.poNumber} ${action === "submit" ? "submitted" : action === "approve" ? "approved" : action === "cancel" ? "canceled" : "closed"}`);
    await loadPurchaseOrders();
  });
  const loadReceivingSession = (sessionId: string) => run(async () => {
    const data = await api<{ receivingSession: PurchasingReceivingSession }>(`/api/admin/purchasing/receiving-sessions/${encodeURIComponent(sessionId)}`);
    setCorrectionSession(data.receivingSession);
    setCorrection({ receivingLineId: data.receivingSession.lines?.[0]?.id ?? "", quantityDelta: "-1", unitCost: moneyToInput({ amount: data.receivingSession.lines?.[0]?.receivedUnitCostAmount ?? 0, currency: selectedPo?.currency ?? "USD" }), reason: "" });
  });
  const ensureReceivingLines = () => {
    if (!activeReceivingPo) return [];
    return (activeReceivingPo.lines ?? []).map((line) => receivingLines[line.id] ?? { id: line.id, receivingSessionId: "", purchaseOrderLineId: line.id, variationId: line.variationId, receivedQuantity: 0, damagedQuantity: 0, shortageQuantity: 0, overageQuantity: 0, unexpected: false, receivedUnitCostAmount: line.actualReceivedUnitCostAmount ?? line.expectedUnitCostAmount, currency: line.currency, customerRequestId: firstSourceDemandId(line), notes: "" });
  };
  const updateReceivingLine = (lineId: string, patch: Partial<PurchasingReceivingLine>) => setReceivingLines((current) => ({ ...current, [lineId]: { ...(ensureReceivingLines().find((line) => line.id === lineId) as PurchasingReceivingLine), ...patch } }));
  const receiveScan = () => {
    if (!activeReceivingPo) return;
    const value = inventoryQuery.trim();
    if (!value) return;
    const normalized = normalizeIsbn(value);
    const match = (activeReceivingPo.lines ?? []).find((line) => [line.isbn13, line.isbn10, line.upcEanGtin, line.internalSku, line.vendorSku].some((id) => normalizeIsbn(id ?? "") === normalized || id === value));
    if (match) updateReceivingLine(match.id, { receivedQuantity: (receivingLines[match.id]?.receivedQuantity ?? 0) + 1 });
    else setError("Scanned item is not on this PO. Add it as an unexpected line from a matched inventory item before posting.");
    setInventoryQuery("");
    receiveScanRef.current?.focus();
  };
  const previewReceive = () => run(async () => {
    if (!activeReceivingPo) return;
    const data = await api<{ preview: typeof receivingPreview }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(activeReceivingPo.id)}/receiving/preview`, { method: "POST", body: JSON.stringify(receivingPayload(activeReceivingPo, ensureReceivingLines(), receiveLocationId, receivingMeta)) });
    setReceivingPreview(data.preview);
  });
  const postReceive = () => run(async () => {
    if (!activeReceivingPo) return;
    const body = { ...receivingPayload(activeReceivingPo, ensureReceivingLines(), receiveLocationId, receivingMeta), idempotencyKey: crypto.randomUUID() };
    const data = await api<{ receivingSession: PurchasingReceivingSession }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(activeReceivingPo.id)}/receiving`, { method: "POST", body: JSON.stringify(body) });
    setPostedSession(data.receivingSession);
    setCorrectionSession(data.receivingSession);
    setCorrection({ receivingLineId: data.receivingSession.lines?.[0]?.id ?? "", quantityDelta: "-1", unitCost: moneyToInput({ amount: data.receivingSession.lines?.[0]?.receivedUnitCostAmount ?? 0, currency: data.receivingSession.lines?.[0]?.currency ?? activeReceivingPo.currency }), reason: "" });
    setReceivingLines({});
    setMessage(`${data.receivingSession.receivingNumber} posted`);
    setSelectedPoId(activeReceivingPo.id);
    await loadPurchaseOrders();
  });
  const correctSession = () => run(async () => {
    if (!correctionSession || !correction.receivingLineId || !correction.reason.trim()) throw new Error("Select a receiving line and enter a correction reason.");
    await api(`/api/admin/purchasing/receiving-sessions/${encodeURIComponent(correctionSession.id)}/corrections`, { method: "POST", body: JSON.stringify({ receivingLineId: correction.receivingLineId, quantityDelta: Number(correction.quantityDelta), unitCostAmount: moneyInputToOptionalCents(correction.unitCost) ?? 0, reason: correction.reason.trim(), idempotencyKey: crypto.randomUUID() }) });
    setMessage("Receiving correction posted");
    await loadReceivingSession(correctionSession.id);
    if (selectedPoId) setSelectedPoId(selectedPoId);
  });
  const recordAsnFixture = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const parsed = JSON.parse(asnFixtureText) as Record<string, unknown>;
    const data = await api<{ shipment: PurchasingShipment }>("/api/admin/purchasing/shipments/inbound-asn", { method: "POST", body: JSON.stringify({ ...parsed, idempotencyKey: String(parsed.idempotencyKey ?? crypto.randomUUID()) }) });
    setSelectedShipmentId(data.shipment.id);
    setMessage(`Shipment ${data.shipment.supplierShipmentNumber} recorded from local/test ASN`);
    await Promise.all([loadShipments(), loadShipmentDiscrepancies(), loadIntegrations()]);
  });
  const loadCarton = (barcode = cartonBarcode) => run(async () => {
    const trimmed = barcode.trim();
    if (!trimmed) throw new Error("Scan or enter a carton barcode.");
    const data = await api<{ carton: PurchasingShipmentCarton }>(`/api/admin/purchasing/cartons/${encodeURIComponent(trimmed)}`);
    setCartonBarcode(trimmed);
    setCartonPreview(data.carton);
    setCartonReceiveLines(Object.fromEntries((data.carton.lines ?? []).map((line) => [line.shipmentLineId, {
      shipmentLineId: line.shipmentLineId,
      receivedQuantity: String(Math.max(0, line.expectedQuantity - line.receivedQuantity)),
      damagedQuantity: "0",
      shortageQuantity: "0",
      overageQuantity: "0",
      receivedUnitCostAmount: moneyToInput({ amount: line.receivedUnitCostAmount ?? 0, currency: line.currency }),
      customerRequestId: line.customerRequestId ?? "",
    } satisfies CartonReceiveDraftLine])));
    setTimeout(() => cartonScanRef.current?.focus(), 0);
  });
  const updateCartonReceiveLine = (shipmentLineId: string, patch: Partial<CartonReceiveDraftLine>) => {
    setCartonReceiveLines((current) => ({ ...current, [shipmentLineId]: { ...current[shipmentLineId], shipmentLineId, ...patch } }));
  };
  const postCartonReceiving = () => run(async () => {
    if (!cartonPreview) throw new Error("Load a carton before posting.");
    const lines = Object.values(cartonReceiveLines).map((line) => ({
      shipmentLineId: line.shipmentLineId,
      receivedQuantity: Number(line.receivedQuantity || 0),
      damagedQuantity: Number(line.damagedQuantity || 0),
      shortageQuantity: Number(line.shortageQuantity || 0),
      overageQuantity: Number(line.overageQuantity || 0),
      receivedUnitCostAmount: moneyInputToOptionalCents(line.receivedUnitCostAmount) ?? 0,
      customerRequestId: line.customerRequestId || undefined,
    }));
    const data = await api<{ receivingSession: PurchasingReceivingSession; shipment: PurchasingShipment }>(`/api/admin/purchasing/cartons/${encodeURIComponent(cartonPreview.licensePlateBarcode)}/receive`, { method: "POST", body: JSON.stringify({ locationId: receiveLocationId, notes: receivingMeta.notes, lines, idempotencyKey: crypto.randomUUID() }) });
    setPostedSession(data.receivingSession);
    setSelectedShipment(data.shipment);
    setSelectedShipmentId(data.shipment.id);
    setCartonPreview(null);
    setCartonReceiveLines({});
    setMessage(`Carton ${cartonPreview.licensePlateBarcode} posted as ${data.receivingSession.receivingNumber}`);
    await Promise.all([loadPurchaseOrders(), loadShipments(), loadShipmentDiscrepancies()]);
    setTimeout(() => cartonScanRef.current?.focus(), 0);
  });
  const resolveShipmentDiscrepancyUi = (discrepancyId: string) => run(async () => {
    if (!shipmentResolution.resolutionReason.trim()) throw new Error("Enter a resolution reason.");
    await api(`/api/admin/purchasing/shipment-discrepancies/${encodeURIComponent(discrepancyId)}/resolve`, { method: "POST", body: JSON.stringify({ ...shipmentResolution, idempotencyKey: crypto.randomUUID() }) });
    setMessage("Shipment discrepancy updated");
    setShipmentResolution({ status: "resolved", supplierFollowupStatus: "logged", resolutionReason: "" });
    await Promise.all([loadShipmentDiscrepancies(), selectedShipmentId ? api<{ shipment: PurchasingShipment }>(`/api/admin/purchasing/shipments/${encodeURIComponent(selectedShipmentId)}`).then((data) => setSelectedShipment(data.shipment)) : Promise.resolve()]);
  });
  const recordInvoiceFixture = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const parsed = JSON.parse(invoiceFixtureText) as Record<string, unknown>;
    const data = await api<{ invoice: PurchasingSupplierInvoice }>("/api/admin/purchasing/invoices", { method: "POST", body: JSON.stringify({ ...parsed, idempotencyKey: String(parsed.idempotencyKey ?? crypto.randomUUID()) }) });
    setSelectedInvoiceId(data.invoice.id);
    setMessage(`Invoice ${data.invoice.invoiceNumber} recorded from local/test payload`);
    await loadInvoices();
  });
  const matchSelectedInvoice = () => run(async () => {
    if (!selectedInvoice) throw new Error("Select an invoice first.");
    const data = await api<{ invoice: PurchasingSupplierInvoice }>(`/api/admin/purchasing/invoices/${encodeURIComponent(selectedInvoice.id)}/match`, { method: "POST", body: JSON.stringify({ costToleranceAmount: 1, roundingToleranceAmount: 1, quantityTolerance: 0, idempotencyKey: crypto.randomUUID() }) });
    setSelectedInvoice(data.invoice);
    setMessage(`Invoice ${data.invoice.invoiceNumber} matched as ${data.invoice.payableStatus.replaceAll("_", " ")}`);
    await loadInvoices();
  });
  const transitionInvoiceUi = (status: "approved" | "disputed" | "canceled" | "partially_paid" | "paid") => run(async () => {
    if (!selectedInvoice) throw new Error("Select an invoice first.");
    const reason = window.prompt(`Reason for ${status.replaceAll("_", " ")}?`);
    if (!reason) return;
    const data = await api<{ invoice: PurchasingSupplierInvoice }>(`/api/admin/purchasing/invoices/${encodeURIComponent(selectedInvoice.id)}/transition`, { method: "POST", body: JSON.stringify({ status, reason, idempotencyKey: crypto.randomUUID() }) });
    setSelectedInvoice(data.invoice);
    setMessage(`Invoice ${data.invoice.invoiceNumber} updated`);
    await loadInvoices();
  });
  const resolveInvoiceDiscrepancyUi = (discrepancyId: string) => run(async () => {
    if (!invoiceResolution.resolutionReason.trim()) throw new Error("Enter a resolution reason.");
    await api(`/api/admin/purchasing/invoice-discrepancies/${encodeURIComponent(discrepancyId)}/resolve`, { method: "POST", body: JSON.stringify({ ...invoiceResolution, idempotencyKey: crypto.randomUUID() }) });
    setInvoiceResolution({ status: "resolved", resolutionReason: "" });
    setMessage("Invoice discrepancy updated");
    await loadInvoices();
  });
  const createCreditMemoUi = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const invoice = selectedInvoice;
    const data = await api<{ creditMemo: PurchasingCreditMemo }>("/api/admin/purchasing/credit-memos", { method: "POST", body: JSON.stringify({ vendorId: creditMemoDraft.vendorId || invoice?.vendorId, vendorAccountId: creditMemoDraft.vendorAccountId || invoice?.vendorAccountId || undefined, creditNumber: creditMemoDraft.creditNumber, creditType: creditMemoDraft.creditType, amount: moneyInputToOptionalCents(creditMemoDraft.amount) ?? 0, currency: invoice?.currency ?? storeProfile.currencyCode ?? "USD", originalInvoiceId: creditMemoDraft.originalInvoiceId || invoice?.id || undefined, notes: creditMemoDraft.notes, idempotencyKey: crypto.randomUUID() }) });
    setMessage(`Credit memo ${data.creditMemo.creditNumber} recorded`);
    setCreditMemoDraft({ vendorId: "", vendorAccountId: "", creditNumber: "", creditType: "damaged_copy", amount: "0.00", originalInvoiceId: "", notes: "" });
    await loadInvoices();
  });
  const exportAccountingUi = () => run(async () => {
    const invoiceIds = invoices.filter((invoice) => invoice.payableStatus === "approved").map((invoice) => invoice.id);
    const creditMemoIds = creditMemos.filter((credit) => credit.status === "approved" || credit.status === "received").map((credit) => credit.id);
    if (!invoiceIds.length && !creditMemoIds.length) throw new Error("Approve at least one invoice or record a credit memo before export.");
    const data = await api<{ batch: PurchasingAccountingExportBatch }>("/api/admin/purchasing/accounting-exports", { method: "POST", body: JSON.stringify({ invoiceIds, creditMemoIds, adapterKey: "generic_csv", includePreviouslyExported: false, idempotencyKey: crypto.randomUUID() }) });
    setMessage(`Accounting CSV export created: ${data.batch.payloadSha256.slice(0, 12)}`);
    await loadInvoices();
  });
  const loadSupplierReturnSuggestionsUi = () => run(async () => {
    const data = await api<{ suggestions: PurchasingSupplierReturnSuggestion[] }>("/api/admin/purchasing/supplier-returns/suggestions", {
      method: "POST",
      body: JSON.stringify({ vendorId: supplierReturnDraft.vendorId || null, locationId: supplierReturnDraft.sourceLocationId || "loc-main", minAgeDays: 0, minOnHandQuantity: 1, includeConditional: true, limit: 50 }),
    });
    setSupplierReturnSuggestions(data.suggestions ?? []);
    setMessage(`Found ${(data.suggestions ?? []).length} supplier-return suggestion${(data.suggestions ?? []).length === 1 ? "" : "s"}`);
  });
  const checkSupplierReturnEligibilityUi = () => run(async () => {
    if (!supplierReturnDraft.vendorId || !supplierReturnDraft.variationId) throw new Error("Choose a vendor and item before checking eligibility.");
    const data = await api<{ eligibility: { eligible: boolean; eligibleQuantity: number; explanation: string } }>("/api/admin/purchasing/supplier-returns/eligibility", {
      method: "POST",
      body: JSON.stringify({ vendorId: supplierReturnDraft.vendorId, locationId: supplierReturnDraft.sourceLocationId || "loc-main", variationId: supplierReturnDraft.variationId, isbn13: supplierReturnDraft.isbn13 || null, quantity: Number(supplierReturnDraft.quantity || 1), reason: supplierReturnDraft.reason, includeManualOverride: supplierReturnDraft.manualOverride }),
    });
    setMessage(data.eligibility.eligible ? `Eligible quantity: ${data.eligibility.eligibleQuantity}` : data.eligibility.explanation);
  });
  const createSupplierReturnUi = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    if (!supplierReturnDraft.vendorId || !supplierReturnDraft.variationId || !supplierReturnDraft.titleSnapshot.trim()) throw new Error("Vendor, item, and title are required.");
    const data = await api<{ supplierReturn: PurchasingSupplierReturn }>("/api/admin/purchasing/supplier-returns", {
      method: "POST",
      body: JSON.stringify({
        vendorId: supplierReturnDraft.vendorId,
        sourceLocationId: supplierReturnDraft.sourceLocationId || "loc-main",
        returnMethod: "manual",
        lines: [{
          sourceReceivingLineId: supplierReturnDraft.receivingLineId || null,
          variationId: supplierReturnDraft.variationId,
          isbn13: supplierReturnDraft.isbn13 || null,
          titleSnapshot: supplierReturnDraft.titleSnapshot,
          quantity: Number(supplierReturnDraft.quantity || 1),
          reason: supplierReturnDraft.reason,
          condition: supplierReturnDraft.condition,
          manualOverride: supplierReturnDraft.manualOverride,
          manualOverrideReason: supplierReturnDraft.manualOverrideReason || null,
          expectedUnitCreditAmount: moneyInputToOptionalCents(supplierReturnDraft.expectedUnitCreditAmount) ?? 0,
        }],
        idempotencyKey: crypto.randomUUID(),
      }),
    });
    setSelectedSupplierReturnId(data.supplierReturn.id);
    setMessage(`Supplier return ${data.supplierReturn.returnNumber} drafted`);
    await loadSupplierReturns();
  });
  const chooseSupplierReturnSuggestion = (suggestion: PurchasingSupplierReturnSuggestion) => {
    setSupplierReturnDraft({
      vendorId: suggestion.vendorId,
      sourceLocationId: suggestion.locationId,
      variationId: suggestion.variationId,
      isbn13: suggestion.isbn13 ?? "",
      titleSnapshot: suggestion.titleSnapshot,
      receivingLineId: suggestion.receivingLineId,
      quantity: String(suggestion.suggestedQuantity),
      reason: "overstock",
      condition: "sellable",
      expectedUnitCreditAmount: (suggestion.expectedCreditAmount / Math.max(1, suggestion.suggestedQuantity) / 100).toFixed(2),
      manualOverride: false,
      manualOverrideReason: "",
    });
  };
  const authorizeSupplierReturnUi = () => run(async () => {
    if (!selectedSupplierReturn) throw new Error("Select a supplier return first.");
    const authorizationNumber = window.prompt("Return authorization number?");
    if (!authorizationNumber) return;
    const data = await api<{ supplierReturn: PurchasingSupplierReturn }>(`/api/admin/purchasing/supplier-returns/${encodeURIComponent(selectedSupplierReturn.id)}/authorization`, {
      method: "POST",
      body: JSON.stringify({ status: "authorized", authorizationNumber, quantityAuthorizations: (selectedSupplierReturn.lines ?? []).map((line) => ({ lineId: line.id, authorizedQuantity: line.quantityRequested })), note: "Supplier authorized return.", idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedSupplierReturn(data.supplierReturn);
    setMessage(`Supplier return ${data.supplierReturn.returnNumber} authorized`);
    await loadSupplierReturns();
  });
  const stageSupplierReturnUi = () => run(async () => {
    if (!selectedSupplierReturn) throw new Error("Select a supplier return first.");
    const data = await api<{ supplierReturn: PurchasingSupplierReturn }>(`/api/admin/purchasing/supplier-returns/${encodeURIComponent(selectedSupplierReturn.id)}/stage`, {
      method: "POST",
      body: JSON.stringify({ lineQuantities: (selectedSupplierReturn.lines ?? []).map((line) => ({ lineId: line.id, quantity: line.quantityAuthorized || line.quantityRequested })), note: "Inventory staged for supplier return.", idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedSupplierReturn(data.supplierReturn);
    setMessage(`Supplier return ${data.supplierReturn.returnNumber} staged`);
    await loadSupplierReturns();
  });
  const shipSupplierReturnUi = () => run(async () => {
    if (!selectedSupplierReturn) throw new Error("Select a supplier return first.");
    const trackingNumber = window.prompt("Tracking number?");
    if (!trackingNumber) return;
    const data = await api<{ supplierReturn: PurchasingSupplierReturn }>(`/api/admin/purchasing/supplier-returns/${encodeURIComponent(selectedSupplierReturn.id)}/ship`, {
      method: "POST",
      body: JSON.stringify({ cartons: [{ cartonNumber: "1", carrier: "Manual", trackingNumber }], carrier: "Manual", trackingNumber, shipmentDate: new Date().toISOString(), note: "Supplier return shipped.", idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedSupplierReturn(data.supplierReturn);
    setMessage(`Supplier return ${data.supplierReturn.returnNumber} shipped`);
    await loadSupplierReturns();
  });
  const cancelSupplierReturnUi = () => run(async () => {
    if (!selectedSupplierReturn) throw new Error("Select a supplier return first.");
    const reason = window.prompt("Cancellation reason?");
    if (!reason) return;
    const data = await api<{ supplierReturn: PurchasingSupplierReturn }>(`/api/admin/purchasing/supplier-returns/${encodeURIComponent(selectedSupplierReturn.id)}/cancel`, { method: "POST", body: JSON.stringify({ reason, releaseStagedInventory: true, idempotencyKey: crypto.randomUUID() }) });
    setSelectedSupplierReturn(data.supplierReturn);
    setMessage(`Supplier return ${data.supplierReturn.returnNumber} canceled`);
    await loadSupplierReturns();
  });
  const reconcileSupplierReturnCreditUi = () => run(async () => {
    if (!selectedSupplierReturn) throw new Error("Select a supplier return first.");
    if (!supplierReturnCreditDraft.resolutionReason.trim()) throw new Error("Enter a credit reconciliation reason.");
    const data = await api<{ supplierReturn: PurchasingSupplierReturn }>(`/api/admin/purchasing/supplier-returns/${encodeURIComponent(selectedSupplierReturn.id)}/credit-reconciliation`, {
      method: "POST",
      body: JSON.stringify({ creditMemoId: supplierReturnCreditDraft.creditMemoId || null, actualCreditAmount: moneyInputToOptionalCents(supplierReturnCreditDraft.actualCreditAmount) ?? 0, feesOrDeductionsAmount: moneyInputToOptionalCents(supplierReturnCreditDraft.feesOrDeductionsAmount) ?? 0, status: supplierReturnCreditDraft.status, resolutionReason: supplierReturnCreditDraft.resolutionReason, idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedSupplierReturn(data.supplierReturn);
    setSupplierReturnCreditDraft({ creditMemoId: "", actualCreditAmount: "0.00", feesOrDeductionsAmount: "0.00", status: "credited", resolutionReason: "" });
    setMessage(`Supplier return ${data.supplierReturn.returnNumber} credit updated`);
    await loadSupplierReturns();
  });
  const markRequestReceived = (requestId: string) => run(async () => {
    await api(`/api/admin/purchasing/special-orders/${encodeURIComponent(requestId)}`, { method: "PATCH", body: JSON.stringify({ purchasingStatus: "ready_for_pickup", status: "received", idempotencyKey: crypto.randomUUID() }) });
    setMessage(`Customer request ${requestId} marked ready for pickup`);
    await loadSpecialOrders();
  });
  const generateReplenishment = () => run(async () => {
    const body = {
      locationId: replenishmentParams.locationId || locations[0]?.id || "loc-main",
      lookbackDays: Number(replenishmentParams.lookbackDays || 56),
      targetWeeksSupply: Number(replenishmentParams.targetWeeksSupply || 6),
      idempotencyKey: crypto.randomUUID(),
    };
    const data = await api<{ review: PurchasingReplenishmentReview }>("/api/admin/purchasing/replenishment/reviews", { method: "POST", body: JSON.stringify(body) });
    setReplenishmentReview(data.review);
    setSelectedSuggestionIds(data.review.suggestions.filter((entry) => entry.vendorId && entry.finalQuantity > 0).map((entry) => entry.id));
    setMessage(`${data.review.suggestions.length} replenishment suggestion(s) generated`);
  });
  const updateSuggestion = (suggestion: PurchasingReplenishmentSuggestion, patch: Partial<Pick<PurchasingReplenishmentSuggestion, "finalQuantity" | "status" | "overrideReason">>) => run(async () => {
    if (!replenishmentReview) return;
    const data = await api<{ review: PurchasingReplenishmentReview }>(`/api/admin/purchasing/replenishment/reviews/${encodeURIComponent(replenishmentReview.id)}/suggestions/${encodeURIComponent(suggestion.id)}`, { method: "PATCH", body: JSON.stringify(patch) });
    setReplenishmentReview(data.review);
  });
  const createPosFromSuggestions = () => run(async () => {
    if (!replenishmentReview) return;
    const data = await api<{ purchaseOrders: PurchasingPurchaseOrder[]; review: PurchasingReplenishmentReview }>(`/api/admin/purchasing/replenishment/reviews/${encodeURIComponent(replenishmentReview.id)}/create-pos`, {
      method: "POST",
      body: JSON.stringify({ suggestionIds: selectedSuggestionIds, shipToLocationId: replenishmentParams.locationId || locations[0]?.id || "loc-main", submitForApproval: false, idempotencyKey: crypto.randomUUID() }),
    });
    setReplenishmentReview(data.review);
    setMessage(`${data.purchaseOrders.length} draft PO(s) created from replenishment`);
    await loadPurchaseOrders();
  });
  const saveSpecialOrder = () => run(async () => {
    const data = await api<{ specialOrder: PurchasingSpecialOrder }>("/api/admin/purchasing/special-orders", {
      method: "POST",
      body: JSON.stringify({
        ...specialOrderDraft,
        customerId: specialOrderDraft.customerId.trim(),
        isbn: specialOrderDraft.isbn ? normalizeIsbn(specialOrderDraft.isbn) : undefined,
        variationId: specialOrderDraft.variationId || undefined,
        quantity: Number(specialOrderDraft.quantity || 1),
        promisedDate: specialOrderDraft.promisedDate || undefined,
        estimatedDate: specialOrderDraft.estimatedDate || undefined,
        idempotencyKey: crypto.randomUUID(),
      }),
    });
    setSpecialOrderDraft({ customerId: "", title: "", author: "", isbn: "", variationId: "", quantity: "1", fulfillmentPreference: "pickup", promisedDate: "", estimatedDate: "", depositStatus: "not_required", notes: "" });
    setMessage(`Special order created for ${data.specialOrder.title}`);
    await loadSpecialOrders();
  });
  const updateSpecialOrderStatus = (order: PurchasingSpecialOrder, purchasingStatus: PurchasingSpecialOrder["purchasingStatus"]) => run(async () => {
    await api(`/api/admin/purchasing/special-orders/${encodeURIComponent(order.id)}`, { method: "PATCH", body: JSON.stringify({ purchasingStatus, status: purchasingStatus === "fulfilled" ? "purchased" : purchasingStatus === "canceled" ? "cancelled" : order.status, idempotencyKey: crypto.randomUUID() }) });
    setMessage(`${order.title} marked ${purchasingStatus.replaceAll("_", " ")}`);
    await loadSpecialOrders();
  });
  const saveExportTemplateFromDraft = () => run(async () => {
    const columnMapping = templateDraft.columnMappingText.split(/\r?\n/).map((row) => row.trim()).filter(Boolean).map((row) => {
      const [header, field] = row.split("=").map((part) => part.trim());
      return { header, field };
    });
    const data = await api<{ template: PurchasingExportTemplate }>("/api/admin/purchasing/export-templates", { method: "POST", body: JSON.stringify({ vendorId: templateDraft.vendorId || undefined, name: templateDraft.name, format: templateDraft.format, columnMapping }) });
    setMessage(`Export template ${data.template.name} v${data.template.version} saved`);
    await loadExportTemplates();
  });
  const exportSelectedPo = (format: "csv" | "spreadsheet_csv" | "print_html", deliveryMethod: "download" | "email_prepared") => run(async () => {
    if (!selectedPo) throw new Error("Select a purchase order first.");
    const data = await api<{ export: { payloadText: string; payloadSha256: string; exportFormat: string } }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/export`, {
      method: "POST",
      body: JSON.stringify({ exportFormat: format, deliveryMethod, templateId: exportTemplates[0]?.id, idempotencyKey: crypto.randomUUID(), note: deliveryMethod === "email_prepared" ? "Prepared for manual email/send" : undefined }),
    });
    if (deliveryMethod === "download") downloadText(data.export.payloadText, `${selectedPo.poNumber}.${format === "print_html" ? "html" : "csv"}`, format === "print_html" ? "text/html" : "text/csv");
    setMessage(deliveryMethod === "email_prepared" ? `Prepared sendable artifact ${data.export.payloadSha256.slice(0, 12)}` : `Downloaded ${selectedPo.poNumber}`);
  });
  const acknowledgeSelectedPo = () => run(async () => {
    if (!selectedPo) throw new Error("Select a submitted purchase order first.");
    const lines = (selectedPo.lines ?? []).map((line) => ({ purchaseOrderLineId: line.id, acknowledgedQuantity: Math.max(0, line.requestedQuantity - (line.canceledQuantity ?? 0)), backorderedQuantity: line.backorderedQuantity ?? 0, canceledQuantity: line.canceledQuantity ?? 0 }));
    const data = await api<{ purchaseOrder: PurchasingPurchaseOrder }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/acknowledgment`, { method: "POST", body: JSON.stringify({ expectedVersion: selectedPo.version, lines, idempotencyKey: crypto.randomUUID(), note: "Manual acknowledgment entered by staff" }) });
    setSelectedPo(data.purchaseOrder);
    setMessage(`${data.purchaseOrder.poNumber} manually acknowledged`);
    await loadPurchaseOrders();
  });
  const importAckCsv = () => run(async () => {
    if (!selectedPo) throw new Error("Select a purchase order first.");
    await api(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/acknowledgment/import`, { method: "POST", body: JSON.stringify({ sourceText: ackImportText, idempotencyKey: crypto.randomUUID() }) });
    setMessage("Vendor acknowledgment CSV imported");
    setSelectedPoId(selectedPo.id);
  });
  const saveIntegrationProviderFromDraft = () => run(async () => {
    if (!canConfigureIntegrations) throw new Error("Administrator permission is required.");
    const data = await api<{ provider: IntegrationProvider }>("/api/admin/integrations/providers", {
      method: "POST",
      body: JSON.stringify({
        providerKey: "pubnet",
        providerType: "book_industry_edi",
        displayName: providerDraft.displayName,
        environment: providerDraft.environment,
        status: providerDraft.status,
        adapterVersion: providerDraft.adapterVersion,
        mapVersion: providerDraft.mapVersion,
        killSwitchEnabled: providerDraft.killSwitchEnabled,
        rolloutPercent: Number(providerDraft.rolloutPercent || 0),
        rateLimitPerMinute: Number(providerDraft.rateLimitPerMinute || 30),
        capabilities: { outboundPurchaseOrder: true, orderResponse: true, technicalAck: true, shipment: true, invoice: true, localTestTransport: providerDraft.environment === "test" },
      }),
    });
    setSelectedProviderId(data.provider.id);
    setMessage(`${data.provider.displayName} ${data.provider.environment} provider saved`);
    await loadIntegrations();
  });
  const saveTradingPartnerFromDraft = () => run(async () => {
    if (!canConfigureIntegrations) throw new Error("Administrator permission is required.");
    if (!selectedProviderId) throw new Error("Create or select a provider first.");
    const data = await api<{ tradingPartner: IntegrationTradingPartner }>("/api/admin/integrations/trading-partners", {
      method: "POST",
      body: JSON.stringify({
        providerId: selectedProviderId,
        vendorId: partnerDraft.vendorId,
        vendorAccountId: partnerDraft.vendorAccountId || undefined,
        environment: integrationProviders.find((provider) => provider.id === selectedProviderId)?.environment ?? "test",
        status: "testing",
        san: partnerDraft.san || undefined,
        pubnetId: partnerDraft.pubnetId || undefined,
        x12SenderId: partnerDraft.x12SenderId || undefined,
        x12ReceiverId: partnerDraft.x12ReceiverId || undefined,
        defaultTransport: partnerDraft.defaultTransport,
        documentFormat: "x12_3060",
        certificationStatus: partnerDraft.certificationStatus,
        certificationNotes: partnerDraft.certificationNotes || undefined,
      }),
    });
    setMessage(`Trading partner saved for ${vendorName(vendors, data.tradingPartner.vendorId)}`);
    await loadIntegrations();
  });
  const bindIntegrationCredential = () => run(async () => {
    if (!canConfigureIntegrations) throw new Error("Administrator permission is required.");
    if (!selectedProviderId) throw new Error("Create or select a provider first.");
    const credential = await api<{ credential: { id: string; credentialName: string } }>(`/api/admin/integrations/providers/${encodeURIComponent(selectedProviderId)}/credentials`, {
      method: "POST",
      body: JSON.stringify({ ...credentialDraft, vendorId: partnerDraft.vendorId || undefined, vendorAccountId: partnerDraft.vendorAccountId || undefined }),
    });
    setMessage(`Credential binding ${credential.credential.credentialName} saved; secret value stays in Cloudflare`);
  });
  const previewElectronicPo = () => run(async () => {
    if (!selectedPo) throw new Error("Select a purchase order first.");
    if (!selectedProviderId) throw new Error("Select an integration provider first.");
    const provider = integrationProviders.find((entry) => entry.id === selectedProviderId);
    const partner = tradingPartners.find((entry) => entry.providerId === selectedProviderId && entry.vendorId === selectedPo.vendorId && (!selectedPo.vendorAccountId || entry.vendorAccountId === selectedPo.vendorAccountId));
    const data = await api<{ preview: typeof electronicPreview }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/electronic-preview`, {
      method: "POST",
      body: JSON.stringify({ providerId: selectedProviderId, tradingPartnerId: partner?.id, previewOnly: true, idempotencyKey: crypto.randomUUID() }),
    });
    setElectronicPreview(data.preview);
    setMessage(`${provider?.displayName ?? "Integration"} preview ready`);
  });
  const sendElectronicPo = () => run(async () => {
    if (!selectedPo) throw new Error("Select a purchase order first.");
    if (!selectedProviderId) throw new Error("Select an integration provider first.");
    const partner = tradingPartners.find((entry) => entry.providerId === selectedProviderId && entry.vendorId === selectedPo.vendorId && (!selectedPo.vendorAccountId || entry.vendorAccountId === selectedPo.vendorAccountId));
    const data = await api<{ message: IntegrationMessage }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/send-electronically`, {
      method: "POST",
      body: JSON.stringify({ providerId: selectedProviderId, tradingPartnerId: partner?.id, idempotencyKey: crypto.randomUUID() }),
    });
    setMessage(`Electronic order queued as ${data.message.correlationId.slice(0, 8)}`);
    await loadIntegrations();
  });
  const processQueue = () => run(async () => {
    await api("/api/admin/integrations/queue/process", { method: "POST", body: JSON.stringify({}) });
    setMessage("Integration queue processed");
    await loadIntegrations();
  });
  const simulateResponse = (messageId: string) => run(async () => {
    await api(`/api/admin/integrations/messages/${encodeURIComponent(messageId)}/simulate-response`, { method: "POST", body: JSON.stringify({ lineStatus: "accepted", idempotencyKey: crypto.randomUUID() }) });
    setMessage("Simulated test response stored and processed");
    await loadIntegrations();
    if (selectedPoId) setSelectedPoId(selectedPoId);
  });
  const resolveReview = (reviewId: string) => run(async () => {
    await api(`/api/admin/integrations/review-items/${encodeURIComponent(reviewId)}/resolve`, { method: "POST", body: JSON.stringify({ resolution: "resolved", reason: "Reviewed by staff", idempotencyKey: crypto.randomUUID() }) });
    setMessage("Review item resolved");
    await loadIntegrations();
  });
  const createPoChange = () => run(async () => {
    if (!selectedPo) throw new Error("Select a submitted purchase order first.");
    if (!canManage) throw new Error("A manager or administrator is required.");
    const body = poChangePayload(changeDraft);
    const data = await api<{ changeRequest: PurchasingPoChangeRequest }>(`/api/admin/purchasing/purchase-orders/${encodeURIComponent(selectedPo.id)}/change-requests`, {
      method: "POST",
      body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedChangeRequest(data.changeRequest);
    setChangeDraft(newPoChangeDraft());
    setMessage(`${data.changeRequest.requestNumber} created; supplier confirmation is still required`);
    await loadChangeRequests();
    await loadBackorders();
  });
  const transitionPoChange = (request: PurchasingPoChangeRequest, action: "approve" | "queue" | "send" | "cancel" | "supersede") => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const data = await api<{ changeRequest: PurchasingPoChangeRequest }>(`/api/admin/purchasing/change-requests/${encodeURIComponent(request.id)}/${action}`, {
      method: "POST",
      body: JSON.stringify({ reason: changeResponseDraft.supplierResponse || undefined, externalReference: changeResponseDraft.externalReference || undefined, externalControlNumber: changeResponseDraft.externalControlNumber || undefined, idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedChangeRequest(data.changeRequest);
    setMessage(`${data.changeRequest.requestNumber} marked ${data.changeRequest.status.replaceAll("_", " ")}`);
    await loadChangeRequests();
  });
  const recordPoChangeSupplierResponse = () => run(async () => {
    if (!selectedChangeRequest) throw new Error("Select a change request first.");
    if (!canManage) throw new Error("A manager or administrator is required.");
    const data = await api<{ changeRequest: PurchasingPoChangeRequest }>(`/api/admin/purchasing/change-requests/${encodeURIComponent(selectedChangeRequest.id)}/response`, {
      method: "POST",
      body: JSON.stringify({ ...changeResponseDraft, acceptedValue: poChangePayload(changeDraft), idempotencyKey: crypto.randomUUID() }),
    });
    setSelectedChangeRequest(data.changeRequest);
    setMessage(`${data.changeRequest.requestNumber} supplier response recorded`);
    await Promise.all([loadChangeRequests(), loadPurchaseOrders(), loadBackorders()]);
    if (selectedPoId) setSelectedPoId(selectedPoId);
  });
  const saveBackorderPolicyFromUi = () => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    const data = await api<{ policy: PurchasingBackorderPolicy }>("/api/admin/purchasing/backorders/policy", { method: "PUT", body: JSON.stringify(backorderPolicy) });
    setBackorderPolicy(data.policy);
    setMessage("Backorder policy saved");
  });
  const batchBackordersFromUi = (action: "close_backorder" | "cancel_backorder") => run(async () => {
    if (!canManage) throw new Error("A manager or administrator is required.");
    if (!selectedBackorderIds.length) throw new Error("Select at least one backorder line.");
    await api("/api/admin/purchasing/backorders/batch", { method: "POST", body: JSON.stringify({ action, purchaseOrderLineIds: selectedBackorderIds, reason: `${action === "close_backorder" ? "Close" : "Cancel"} selected backorders after staff review`, idempotencyKey: crypto.randomUUID() }) });
    setSelectedBackorderIds([]);
    setMessage(`${selectedBackorderIds.length} backorder line(s) ${action === "close_backorder" ? "closed" : "canceled"}`);
    await Promise.all([loadBackorders(), loadPurchaseOrders(), loadChangeRequests()]);
  });

  return (
    <section className="page purchasing-workspace">
      <header className="page-header">
        <div><p className="eyebrow">Operations</p><h1>Purchasing</h1></div>
        <div className="actions"><button onClick={() => void refreshAll()}><RefreshCw /> Refresh</button><button onClick={() => window.print()}>Print</button></div>
      </header>
      <div className="segmented purchasing-tabs" role="tablist" aria-label="Purchasing sections">
        <button className={tab === "vendors" ? "active" : ""} onClick={() => setTab("vendors")} role="tab" aria-selected={tab === "vendors"}>Vendors</button>
        <button className={tab === "orders" ? "active" : ""} onClick={() => setTab("orders")} role="tab" aria-selected={tab === "orders"}>Purchase orders</button>
        <button className={tab === "receiving" ? "active" : ""} onClick={() => setTab("receiving")} role="tab" aria-selected={tab === "receiving"}>Receiving</button>
        <button className={tab === "shipments" ? "active" : ""} onClick={() => setTab("shipments")} role="tab" aria-selected={tab === "shipments"}>Shipments</button>
        <button className={tab === "invoices" ? "active" : ""} onClick={() => setTab("invoices")} role="tab" aria-selected={tab === "invoices"}>Invoices</button>
        <button className={tab === "supplier-returns" ? "active" : ""} onClick={() => setTab("supplier-returns")} role="tab" aria-selected={tab === "supplier-returns"}>Supplier returns</button>
        <button className={tab === "replenishment" ? "active" : ""} onClick={() => setTab("replenishment")} role="tab" aria-selected={tab === "replenishment"}>Replenishment</button>
        <button className={tab === "special-orders" ? "active" : ""} onClick={() => setTab("special-orders")} role="tab" aria-selected={tab === "special-orders"}>Special orders</button>
        <button className={tab === "operations" ? "active" : ""} onClick={() => setTab("operations")} role="tab" aria-selected={tab === "operations"}>Operations</button>
        <button className={tab === "changes" ? "active" : ""} onClick={() => setTab("changes")} role="tab" aria-selected={tab === "changes"}>PO changes</button>
        <button className={tab === "backorders" ? "active" : ""} onClick={() => setTab("backorders")} role="tab" aria-selected={tab === "backorders"}>Backorders</button>
        <button className={tab === "integrations" ? "active" : ""} onClick={() => setTab("integrations")} role="tab" aria-selected={tab === "integrations"}>Integrations</button>
      </div>
      {message && <div className="success" aria-live="polite">{message}</div>}
      {error && <div className="error-banner" role="alert">{error}</div>}
      {loading && <Skeleton />}
      {!canManage && <div className="error">Staff can receive submitted purchase orders. Vendor changes, PO creation, approvals, cancellations, closing, and corrections require manager permission.</div>}

      {tab === "vendors" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Vendors</h2><button className="primary" disabled={!canManage} onClick={() => { setSelectedVendorId(""); setSelectedVendor(null); setVendorDraft(newVendorDraft(storeProfile.currencyCode ?? "USD")); }}>New vendor</button></div>
            <div className="toolbar compact-toolbar">
              <label className="searchbox">Search<Search /><input value={vendorQuery} onChange={(event) => setVendorQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void loadVendors(); }} placeholder="Name, legal name, contact" /></label>
              <label>Status<select value={vendorStatus} onChange={(event) => setVendorStatus(event.target.value)}><option value="">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
              <button onClick={() => void loadVendors()}><Search /> Search</button>
            </div>
            <DataTable empty="No vendors found." headers={["Vendor", "Status", "Terms", "Thresholds", "Open POs", ""]} rows={vendors.map((vendor) => [<strong key="v">{vendor.name}</strong>, <span className={`badge ${vendor.status}`} key="s">{vendor.status}</span>, vendor.paymentTerms ?? "-", vendorThresholdLabel(vendor), purchaseOrders.filter((po) => po.vendorId === vendor.id && !["completed", "canceled", "closed"].includes(po.status)).length, <button key="open" onClick={() => { setSelectedVendorId(vendor.id); setVendorDraft(vendorToDraft(vendor)); }}>Open</button>])} />
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{selectedVendor ? "Vendor details" : "Create vendor"}</h2>{duplicateVendorWarning && <span className="badge warning">Possible duplicate</span>}</div>
            <div className="form-grid">
              <label>Name<input value={vendorDraft.name} onChange={(event) => setVendorDraft({ ...vendorDraft, name: event.target.value })} required /></label>
              <label>Legal name<input value={vendorDraft.legalName} onChange={(event) => setVendorDraft({ ...vendorDraft, legalName: event.target.value })} /></label>
              <label>Status<select value={vendorDraft.status} onChange={(event) => setVendorDraft({ ...vendorDraft, status: event.target.value as "active" | "inactive" })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
              <label>Payment terms<input value={vendorDraft.paymentTerms} onChange={(event) => setVendorDraft({ ...vendorDraft, paymentTerms: event.target.value })} placeholder="Net 30" /></label>
              <label>Ordering contact<input value={vendorDraft.orderingContactName} onChange={(event) => setVendorDraft({ ...vendorDraft, orderingContactName: event.target.value })} /></label>
              <label>Ordering email<input type="email" value={vendorDraft.orderingEmail} onChange={(event) => setVendorDraft({ ...vendorDraft, orderingEmail: event.target.value })} /></label>
              <label>Ordering phone<input value={vendorDraft.orderingPhone} onChange={(event) => setVendorDraft({ ...vendorDraft, orderingPhone: event.target.value })} /></label>
              <label>Currency<input maxLength={3} value={vendorDraft.defaultCurrency} onChange={(event) => setVendorDraft({ ...vendorDraft, defaultCurrency: event.target.value.toUpperCase() })} /></label>
              <label>Shipping method<input value={vendorDraft.defaultShippingMethod} onChange={(event) => setVendorDraft({ ...vendorDraft, defaultShippingMethod: event.target.value })} /></label>
              <label>Backorders<select value={vendorDraft.defaultBackorderPolicy} onChange={(event) => setVendorDraft({ ...vendorDraft, defaultBackorderPolicy: event.target.value as PurchasingVendor["defaultBackorderPolicy"] })}>{purchasingBackorderPolicies.map((policy) => <option key={policy.value} value={policy.value}>{policy.label}</option>)}</select></label>
              <label>Minimum order<input inputMode="decimal" value={vendorDraft.minimumOrderAmount} onChange={(event) => setVendorDraft({ ...vendorDraft, minimumOrderAmount: event.target.value })} /></label>
              <label>Free freight<input inputMode="decimal" value={vendorDraft.freeFreightThresholdAmount} onChange={(event) => setVendorDraft({ ...vendorDraft, freeFreightThresholdAmount: event.target.value })} /></label>
              <label className="wide checkbox-row"><input type="checkbox" checked={vendorDraft.preferred} onChange={(event) => setVendorDraft({ ...vendorDraft, preferred: event.target.checked })} /> Preferred vendor</label>
              <label className="wide">Notes<textarea rows={3} value={vendorDraft.notes} onChange={(event) => setVendorDraft({ ...vendorDraft, notes: event.target.value })} /></label>
              <div className="wide actions"><button className="primary" disabled={!canManage} onClick={saveVendor}><Save /> Save vendor</button></div>
            </div>
            {selectedVendor && (
              <div className="purchasing-subsections">
                <section><h3>Accounts</h3><DataTable empty="No accounts." headers={["Account", "Status", "Terms", "Currency"]} rows={(selectedVendor.accounts ?? []).map((account) => [account.accountNumber, account.status, account.paymentTerms ?? selectedVendor.paymentTerms ?? "-", account.currency])} /><div className="form-grid compact-form"><label>Account number<input value={accountDraft.accountNumber} onChange={(event) => setAccountDraft({ ...accountDraft, accountNumber: event.target.value })} /></label><label>Account name<input value={accountDraft.accountName} onChange={(event) => setAccountDraft({ ...accountDraft, accountName: event.target.value })} /></label><label>Currency<input value={accountDraft.currency} onChange={(event) => setAccountDraft({ ...accountDraft, currency: event.target.value.toUpperCase() })} /></label><label>Payment terms<input value={accountDraft.paymentTerms} onChange={(event) => setAccountDraft({ ...accountDraft, paymentTerms: event.target.value })} /></label><button disabled={!canManage} onClick={() => void createVendorChild("account")}>Add account</button></div></section>
                <section><h3>Ship-to and bill-to mappings</h3><DataTable empty="No vendor locations." headers={["Type", "Label", "Address", "Default"]} rows={(selectedVendor.addresses ?? []).map((address) => [address.addressType.replaceAll("_", " "), address.label, `${address.line1}, ${address.city}`, address.defaultForType ? "Default" : "-"])} /><div className="form-grid compact-form"><label>Type<select value={addressDraft.addressType} onChange={(event) => setAddressDraft({ ...addressDraft, addressType: event.target.value as PurchasingVendorAddress["addressType"] })}><option value="ship_to">Ship-to</option><option value="bill_to">Bill-to</option><option value="remit_to">Remit-to</option><option value="ordering">Ordering</option></select></label><label>Label<input value={addressDraft.label} onChange={(event) => setAddressDraft({ ...addressDraft, label: event.target.value })} /></label><label>Line 1<input value={addressDraft.line1} onChange={(event) => setAddressDraft({ ...addressDraft, line1: event.target.value })} /></label><label>City<input value={addressDraft.city} onChange={(event) => setAddressDraft({ ...addressDraft, city: event.target.value })} /></label><label>Region<input value={addressDraft.region ?? ""} onChange={(event) => setAddressDraft({ ...addressDraft, region: event.target.value })} /></label><label>Postal<input value={addressDraft.postalCode ?? ""} onChange={(event) => setAddressDraft({ ...addressDraft, postalCode: event.target.value })} /></label><label className="checkbox-row"><input type="checkbox" checked={addressDraft.defaultForType} onChange={(event) => setAddressDraft({ ...addressDraft, defaultForType: event.target.checked })} /> Default</label><button disabled={!canManage} onClick={() => void createVendorChild("address")}>Add address</button></div></section>
                <section><h3>Trading-party identifiers</h3><p className="empty-inline">SAN, Pubnet ID, vendor account ID, X12 sender/receiver IDs, and GLNs identify trading parties or locations, not books or items.</p><DataTable empty="No identifiers." headers={["Type", "Value", "Qualifier", "Status"]} rows={(selectedVendor.identifiers ?? []).map((identifier) => [identifier.identifierType, identifier.identifierValue, identifier.qualifier ?? "-", identifier.active ? "Active" : "Inactive"])} /><div className="form-grid compact-form"><label>Type<select value={identifierDraft.identifierType} onChange={(event) => setIdentifierDraft({ ...identifierDraft, identifierType: event.target.value as PurchasingPartyIdentifier["identifierType"] })}><option value="san">SAN</option><option value="pubnet_id">Pubnet ID</option><option value="vendor_account_id">Vendor account ID</option><option value="x12_sender_id">X12 sender ID</option><option value="x12_receiver_id">X12 receiver ID</option><option value="gln">GLN</option><option value="other">Other</option></select></label><label>Value<input value={identifierDraft.identifierValue} onChange={(event) => setIdentifierDraft({ ...identifierDraft, identifierValue: event.target.value })} /></label><label>Qualifier<input value={identifierDraft.qualifier ?? ""} onChange={(event) => setIdentifierDraft({ ...identifierDraft, qualifier: event.target.value })} /></label><button disabled={!canManage} onClick={() => void createVendorChild("identifier")}>Add identifier</button></div></section>
                <section><h3>Product mappings</h3><DataTable empty="No product mappings." headers={["Title/SKU", "ISBN / GTIN", "Vendor SKU", "Cost", "Rules"]} rows={(selectedVendor.products ?? []).map((product) => [product.variationId ?? product.editionId ?? product.internalSku ?? "-", product.isbn13 ?? product.upcEanGtin ?? "-", product.vendorSku ?? "-", formatMoney({ amount: product.costAmount, currency: product.currency }), `${product.returnability}, MOQ ${product.minimumOrderQuantity}, pack ${product.casePackQuantity}${product.preferred ? ", preferred" : ""}`])} /><div className="form-grid compact-form"><label>Variation ID<input value={vendorProductDraft.variationId} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, variationId: event.target.value })} /></label><label>ISBN-13<input value={vendorProductDraft.isbn13} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, isbn13: normalizeIsbn(event.target.value) })} /></label><label>Internal SKU<input value={vendorProductDraft.internalSku} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, internalSku: event.target.value })} /></label><label>Vendor SKU<input value={vendorProductDraft.vendorSku} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, vendorSku: event.target.value })} /></label><label>Cost<input inputMode="decimal" value={vendorProductDraft.costAmount} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, costAmount: event.target.value })} /></label><label>Returnability<select value={vendorProductDraft.returnability} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, returnability: event.target.value as PurchasingVendorProduct["returnability"] })}><option value="unknown">Unknown</option><option value="returnable">Returnable</option><option value="non_returnable">Non-returnable</option><option value="conditional">Conditional</option></select></label><label>MOQ<input type="number" min={1} value={vendorProductDraft.minimumOrderQuantity} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, minimumOrderQuantity: event.target.value })} /></label><label>Case pack<input type="number" min={1} value={vendorProductDraft.casePackQuantity} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, casePackQuantity: event.target.value })} /></label><label className="checkbox-row"><input type="checkbox" checked={vendorProductDraft.preferred} onChange={(event) => setVendorProductDraft({ ...vendorProductDraft, preferred: event.target.checked })} /> Preferred</label><button disabled={!canManage} onClick={() => void createVendorChild("product")}>Add mapping</button></div></section>
                <section><h3>Open purchase orders</h3><DataTable empty="No open POs for this vendor." headers={["PO", "Status", "Qty", "Total", ""]} rows={vendorOpenPurchaseOrders.map((po) => [po.poNumber, po.status.replaceAll("_", " "), po.requestedTotalQuantity, formatMoney({ amount: po.expectedTotalCostAmount, currency: po.currency }), <button key="open" onClick={() => { setSelectedPoId(po.id); setTab("orders"); }}>Open</button>])} /></section>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "orders" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Purchase orders</h2><button className="primary" disabled={!canManage} onClick={() => { setSelectedPoId(""); setSelectedPo(null); setPoDraft(newPoDraft(storeProfile.currencyCode ?? "USD", locations[0]?.id)); setPoLines([newPoLine(storeProfile.currencyCode ?? "USD")]); }}>New PO</button></div>
            <div className="toolbar compact-toolbar">
              <label className="searchbox">Search<Search /><input value={poQuery} onChange={(event) => setPoQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void loadPurchaseOrders(); }} placeholder="PO, vendor, reference" /></label>
              <label>Status<select value={poStatus} onChange={(event) => setPoStatus(event.target.value)}><option value="">All</option>{purchasingStatuses.map((status) => <option key={status} value={status}>{status.replaceAll("_", " ")}</option>)}</select></label>
              <button onClick={() => void loadPurchaseOrders()}><Search /> Search</button>
            </div>
            <DataTable empty="No purchase orders found." headers={["PO", "Vendor", "Status", "Qty", "Total", "Updated", ""]} rows={purchaseOrders.map((po) => [po.poNumber, po.vendorName ?? vendorName(vendors, po.vendorId), <span className={`badge ${po.status}`} key="status">{po.status.replaceAll("_", " ")}</span>, `${po.receivedTotalQuantity}/${po.requestedTotalQuantity}`, formatMoney({ amount: po.expectedTotalCostAmount, currency: po.currency }), po.updatedAt ? new Date(po.updatedAt).toLocaleDateString() : "-", <button key="open" onClick={() => setSelectedPoId(po.id)}>Open</button>])} />
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{selectedPo ? `${selectedPo.poNumber} ${selectedPo.status.replaceAll("_", " ")}` : "Draft purchase order"}</h2><span className="badge">v{selectedPo?.version ?? 1}</span></div>
            {!poEditable && <div className="error">This PO has been submitted. Header and line edits are locked; use receiving, cancellation, closing, or audited corrections.</div>}
            <div className="form-grid">
              <label>Vendor<select disabled={!poEditable} value={poDraft.vendorId} onChange={(event) => { const vendor = vendors.find((entry) => entry.id === event.target.value); setPoDraft({ ...poDraft, vendorId: event.target.value, vendorAccountId: "", currency: vendor?.defaultCurrency ?? poDraft.currency, backorderPolicy: vendor?.defaultBackorderPolicy ?? poDraft.backorderPolicy }); setSelectedVendorId(event.target.value); }}><option value="">Select vendor</option>{vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</select></label>
              <label>Account<select disabled={!poEditable} value={poDraft.vendorAccountId} onChange={(event) => setPoDraft({ ...poDraft, vendorAccountId: event.target.value })}><option value="">Default account</option>{selectedVendorAccounts.map((account) => <option key={account.id} value={account.id}>{account.accountNumber} {account.accountName ?? ""}</option>)}</select></label>
              <label>PO type<select disabled={!poEditable} value={poDraft.poType} onChange={(event) => setPoDraft({ ...poDraft, poType: event.target.value as PurchasingPurchaseOrder["poType"] })}>{purchasingPoTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></label>
              <label>Ship to<select disabled={!poEditable} value={poDraft.shipToLocationId} onChange={(event) => setPoDraft({ ...poDraft, shipToLocationId: event.target.value })}>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}{location.code ? ` (${location.code})` : ""}</option>)}<option value="loc-main">Main</option></select></label>
              <label>Bill to<select disabled={!poEditable} value={poDraft.billToLocationId} onChange={(event) => setPoDraft({ ...poDraft, billToLocationId: event.target.value })}>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}{location.code ? ` (${location.code})` : ""}</option>)}<option value="loc-main">Main</option></select></label>
              <label>Backorders<select disabled={!poEditable} value={poDraft.backorderPolicy} onChange={(event) => setPoDraft({ ...poDraft, backorderPolicy: event.target.value as PurchasingVendor["defaultBackorderPolicy"] })}>{purchasingBackorderPolicies.map((policy) => <option key={policy.value} value={policy.value}>{policy.label}</option>)}</select></label>
              <label>Requested ship date<input disabled={!poEditable} type="date" value={poDraft.requestedShipDate} onChange={(event) => setPoDraft({ ...poDraft, requestedShipDate: event.target.value })} /></label>
              <label>Cancel if not filled<input disabled={!poEditable} type="date" value={poDraft.cancelIfNotFilledDate} onChange={(event) => setPoDraft({ ...poDraft, cancelIfNotFilledDate: event.target.value })} /></label>
              <label>Do-not-exceed cost<input disabled={!poEditable} inputMode="decimal" value={poDraft.doNotExceedCostAmount} onChange={(event) => setPoDraft({ ...poDraft, doNotExceedCostAmount: event.target.value })} /></label>
              <label>Fulfillment<select disabled={!poEditable} value={poDraft.fulfillmentPreference} onChange={(event) => setPoDraft({ ...poDraft, fulfillmentPreference: event.target.value as "consolidate" | "ship_immediately" })}><option value="consolidate">Consolidate</option><option value="ship_immediately">Ship immediately</option></select></label>
              <label>External reference<input disabled={!poEditable} value={poDraft.externalReference} onChange={(event) => setPoDraft({ ...poDraft, externalReference: event.target.value })} /></label>
              <label className="checkbox-row"><input disabled={!poEditable} type="checkbox" checked={poDraft.allowSubstitutions} onChange={(event) => setPoDraft({ ...poDraft, allowSubstitutions: event.target.checked })} /> Allow substitutions</label>
              <label className="wide">Header notes<textarea disabled={!poEditable} rows={2} value={poDraft.headerNotes} onChange={(event) => setPoDraft({ ...poDraft, headerNotes: event.target.value })} /></label>
            </div>
            <div className="metric-grid purchasing-metrics"><Metric label="Order total" value={formatMoney({ amount: poTotal, currency: poDraft.currency })} /><Metric label="Units" value={String(poLines.reduce((sum, line) => sum + Number(line.requestedQuantity || 0), 0))} /><Metric label="Received" value={String(selectedPo?.receivedTotalQuantity ?? 0)} /><Metric label="Vendor rule" value={thresholdWarning || "Thresholds met"} /></div>
            {thresholdWarning && <div className="error"><AlertTriangle /> {thresholdWarning}</div>}
            <section className="purchasing-line-editor">
              <div className="section-heading"><h3>Lines</h3><div className="actions"><button disabled={!poEditable} onClick={() => setPoLines((current) => [...current, newPoLine(poDraft.currency)])}>Add blank line</button><button disabled={!poEditable} onClick={applyBulkLines}>Apply bulk rows</button></div></div>
              <label className="wide scan-input">Scan ISBN or search inventory<input ref={poScanRef} disabled={!poEditable} value={inventoryQuery} onChange={(event) => setInventoryQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); scanPoLine(); } }} placeholder="Scan ISBN, enter SKU, or type a title" /></label>
              <div className="actions"><button disabled={!poEditable} onClick={() => void searchInventory()}><Search /> Search inventory</button><button disabled={!poEditable} onClick={scanPoLine}>Add scanned entry</button></div>
              {inventoryMatches.length > 0 && <div className="supplier-results">{inventoryMatches.map((item) => <article key={item.id} className="supplier-card purchasing-match"><Image imageUrl={item.imageUrl} /><div><strong>{item.title}</strong><p>{item.author ?? "Unknown author"}</p><dl className="metadata-fields"><dt>On hand</dt><dd>{item.availableQuantity ?? "-"}</dd><dt>Reserved</dt><dd>{Math.max(0, (item.availableQuantity ?? 0) - Number(item.stockStatus === "out_of_stock" ? 0 : item.availableQuantity ?? 0))}</dd><dt>Incoming</dt><dd>{incomingForItem(purchaseOrders, item)}</dd><dt>Recent sales</dt><dd>Use Reports</dd></dl><button disabled={!poEditable} onClick={() => addInventoryItemToPo(item)}>Add to PO</button></div></article>)}</div>}
              <label className="wide">Bulk rows<textarea disabled={!poEditable} rows={3} value={bulkLines} onChange={(event) => setBulkLines(event.target.value)} placeholder="ISBN or vendor SKU, quantity, cost, optional title" /></label>
              <div className="table-wrap purchasing-lines-table"><table><thead><tr><th>Item</th><th>Identifiers</th><th>Qty</th><th>Cost</th><th>Context</th><th>Line rules</th><th></th></tr></thead><tbody>{poLines.map((line, index) => <tr key={line.id}><td><input aria-label={`Title for line ${index + 1}`} disabled={!poEditable} value={line.titleSnapshot ?? ""} onChange={(event) => updatePoLine(setPoLines, line.id, { titleSnapshot: event.target.value })} /></td><td><input aria-label={`ISBN or SKU for line ${index + 1}`} disabled={!poEditable} value={line.isbn13 ?? line.vendorSku ?? line.internalSku ?? ""} onChange={(event) => updatePoLine(setPoLines, line.id, normalizeIsbn(event.target.value) ? { isbn13: normalizeIsbn(event.target.value) } : { vendorSku: event.target.value })} /></td><td><input className="qty" aria-label={`Quantity for line ${index + 1}`} disabled={!poEditable} type="number" min={1} value={line.requestedQuantity} onChange={(event) => updatePoLine(setPoLines, line.id, { requestedQuantity: Number(event.target.value) })} /></td><td><input aria-label={`Expected cost for line ${index + 1}`} disabled={!poEditable} inputMode="decimal" value={moneyToInput({ amount: line.expectedUnitCostAmount, currency: line.currency })} onChange={(event) => updatePoLine(setPoLines, line.id, { expectedUnitCostAmount: moneyInputToOptionalCents(event.target.value) ?? 0 })} /></td><td><small>On hand {line.variationId ? "available in inventory" : "unknown"} / incoming {incomingForIdentifier(purchaseOrders, line)}</small><small>{firstSourceDemandId(line) ? `Demand ${firstSourceDemandId(line)}` : "No linked demand"}</small></td><td><select aria-label={`Backorder policy for line ${index + 1}`} disabled={!poEditable} value={line.backorderPolicy} onChange={(event) => updatePoLine(setPoLines, line.id, { backorderPolicy: event.target.value as PurchasingVendor["defaultBackorderPolicy"] })}>{purchasingBackorderPolicies.map((policy) => <option key={policy.value} value={policy.value}>{policy.label}</option>)}</select><input aria-label={`Customer request or demand link for line ${index + 1}`} disabled={!poEditable} placeholder="Demand/request ID" value={firstSourceDemandId(line) ?? ""} onChange={(event) => updatePoLine(setPoLines, line.id, { sourceDemand: event.target.value ? [{ type: poDraft.poType, id: event.target.value }] : [] })} /></td><td><button disabled={!poEditable || poLines.length <= 1} onClick={() => setPoLines((current) => current.filter((entry) => entry.id !== line.id))}>Remove</button></td></tr>)}{poLines.length === 0 && <tr><td colSpan={7}>No lines.</td></tr>}</tbody></table></div>
              <div className="actions"><button className="primary" disabled={!canManage || !poEditable} onClick={savePo}><Save /> Save draft</button><button disabled={!canManage || !selectedPo || selectedPo.status !== "draft"} onClick={() => void transitionPo("approve")}><Check /> Approve</button><button disabled={!canManage || !selectedPo || selectedPo.status !== "approved"} onClick={() => void transitionPo("submit")}><Upload /> Submit</button><button className="danger" disabled={!canManage || !selectedPo || ["canceled", "closed", "completed"].includes(selectedPo.status)} onClick={() => void transitionPo("cancel")}>Cancel</button><button disabled={!canManage || !selectedPo || !["received", "completed"].includes(selectedPo.status)} onClick={() => void transitionPo("close")}>Close</button><button disabled={!selectedPo} onClick={() => window.print()}>Print / PDF</button></div>
            </section>
            {selectedPo && <section><h3>Change or cancellation request</h3>{poEditable ? <p className="empty-inline">Draft POs can be edited directly. Submitted or acknowledged orders require explicit change requests.</p> : <div className="form-grid compact-form"><label>Request type<select value={changeDraft.changeType} onChange={(event) => setChangeDraft({ ...newPoChangeDraft(), changeType: event.target.value, purchaseOrderLineId: changeDraft.purchaseOrderLineId, supplierTransmissionMethod: changeDraft.supplierTransmissionMethod })}><option value="cancel_po">Cancel entire PO</option><option value="cancel_line">Cancel line</option><option value="reduce_quantity">Reduce quantity</option><option value="increase_quantity">Increase quantity</option><option value="change_requested_ship_date">Change ship date</option><option value="change_cancel_if_not_filled_date">Change cancel date</option><option value="change_backorder_preference">Change backorder preference</option><option value="change_shipping_instructions">Change shipping instructions</option><option value="replace_edition">Substitution review</option><option value="close_backorder">Close abandoned backorder</option></select></label><label>Line<select value={changeDraft.purchaseOrderLineId} onChange={(event) => setChangeDraft({ ...changeDraft, purchaseOrderLineId: event.target.value })}><option value="">Whole order</option>{(selectedPo.lines ?? []).map((line) => <option key={line.id} value={line.id}>{line.titleSnapshot ?? line.isbn13 ?? line.vendorSku ?? line.id}</option>)}</select></label><label>Qty<input type="number" min={0} value={changeDraft.requestedQuantity} onChange={(event) => setChangeDraft({ ...changeDraft, requestedQuantity: event.target.value })} /></label><label>Ship date<input type="date" value={changeDraft.requestedShipDate} onChange={(event) => setChangeDraft({ ...changeDraft, requestedShipDate: event.target.value })} /></label><label>Cancel if not filled<input type="date" value={changeDraft.cancelIfNotFilledDate} onChange={(event) => setChangeDraft({ ...changeDraft, cancelIfNotFilledDate: event.target.value })} /></label><label>Backorder<select value={changeDraft.backorderPolicy} onChange={(event) => setChangeDraft({ ...changeDraft, backorderPolicy: event.target.value })}><option value="">No change</option>{purchasingBackorderPolicies.map((policy) => <option key={policy.value} value={policy.value}>{policy.label}</option>)}</select></label><label>Transmission<select value={changeDraft.supplierTransmissionMethod} onChange={(event) => setChangeDraft({ ...changeDraft, supplierTransmissionMethod: event.target.value as PoChangeDraft["supplierTransmissionMethod"] })}><option value="email">Email</option><option value="phone">Phone</option><option value="manual_contact">Manual contact</option><option value="electronic">Electronic if configured</option></select></label><label>External ref<input value={changeDraft.externalReference} onChange={(event) => setChangeDraft({ ...changeDraft, externalReference: event.target.value })} /></label><label className="wide">Shipping instructions<input value={changeDraft.shippingInstructions} onChange={(event) => setChangeDraft({ ...changeDraft, shippingInstructions: event.target.value })} /></label><label>Replacement edition<input value={changeDraft.replacementEditionId} onChange={(event) => setChangeDraft({ ...changeDraft, replacementEditionId: event.target.value })} /></label><label>Replacement ISBN<input value={changeDraft.replacementIsbn13} onChange={(event) => setChangeDraft({ ...changeDraft, replacementIsbn13: normalizeIsbn(event.target.value) })} /></label><label>Replacement title<input value={changeDraft.replacementTitle} onChange={(event) => setChangeDraft({ ...changeDraft, replacementTitle: event.target.value })} /></label><label>Replacement cost<input inputMode="decimal" value={changeDraft.replacementCostAmount} onChange={(event) => setChangeDraft({ ...changeDraft, replacementCostAmount: event.target.value })} /></label><label className="wide">Reason<textarea rows={2} value={changeDraft.reason} onChange={(event) => setChangeDraft({ ...changeDraft, reason: event.target.value })} /></label><button className="primary" disabled={!canManage || !changeDraft.reason.trim()} onClick={() => void createPoChange()}>Create change request</button></div>}</section>}
            {selectedPo && <section><h3>Related change requests</h3><DataTable empty="No change requests for this PO." headers={["Request", "Type", "Status", "Method", ""]} rows={changeRequests.filter((request) => request.purchaseOrderId === selectedPo.id).map((request) => [request.requestNumber, request.changeType.replaceAll("_", " "), <span className={`badge ${request.status}`} key="status">{request.status.replaceAll("_", " ")}</span>, request.supplierTransmissionMethod.replaceAll("_", " "), <button key="open" onClick={() => { setSelectedChangeRequest(request); setTab("changes"); }}>Review</button>])} /></section>}
            {selectedPo && <section><h3>Activity</h3><DataTable empty="No PO history." headers={["When", "Event", "Actor", "Note"]} rows={poHistory.map((entry) => [new Date(entry.createdAt).toLocaleString(), entry.eventType.replaceAll(".", " "), entry.actorUserId ?? "-", entry.note ?? String(entry.after?.note ?? "-")])} /></section>}
          </section>
        </div>
      )}

      {tab === "supplier-returns" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Publisher and distributor returns</h2><button onClick={() => void loadSupplierReturns()}><RefreshCw /> Refresh</button></div>
            <DataTable empty="No supplier returns." headers={["Return", "Vendor", "Status", "Expected", "Credit", ""]} rows={supplierReturns.map((ret) => [
              ret.returnNumber,
              ret.vendorName ?? vendorName(vendors, ret.vendorId),
              <span className={`badge ${ret.status}`} key="status">{ret.status.replaceAll("_", " ")}</span>,
              formatMoney({ amount: ret.expectedCreditAmount, currency: ret.currency }),
              formatMoney({ amount: ret.actualCreditAmount, currency: ret.currency }),
              <button key="open" onClick={() => setSelectedSupplierReturnId(ret.id)}>Open</button>,
            ])} />
            <section>
              <div className="section-heading"><h3>Return suggestions</h3><button onClick={() => void loadSupplierReturnSuggestionsUi()}><RefreshCw /> Suggest</button></div>
              <DataTable empty="No return suggestions. Adjust thresholds or choose items manually." headers={["Title", "Vendor", "Qty", "Age", "Velocity", "Expected credit", ""]} rows={supplierReturnSuggestions.map((suggestion) => [
                suggestion.titleSnapshot,
                vendorName(vendors, suggestion.vendorId),
                suggestion.suggestedQuantity,
                `${suggestion.ageDays} days`,
                suggestion.salesVelocityPerWeek.toFixed(2),
                formatMoney({ amount: suggestion.expectedCreditAmount, currency: suggestion.currency }),
                <button key="use" onClick={() => chooseSupplierReturnSuggestion(suggestion)}>Use</button>,
              ])} />
            </section>
            <section>
              <h3>Create return</h3>
              <div className="form-grid compact-form">
                <label>Vendor<select value={supplierReturnDraft.vendorId} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, vendorId: event.target.value })}><option value="">Choose vendor</option>{vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</select></label>
                <label>Source location<select value={supplierReturnDraft.sourceLocationId} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, sourceLocationId: event.target.value })}>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</select></label>
                <label>Variation ID<input value={supplierReturnDraft.variationId} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, variationId: event.target.value })} /></label>
                <label>ISBN<input value={supplierReturnDraft.isbn13} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, isbn13: event.target.value })} /></label>
                <label className="wide">Title<input value={supplierReturnDraft.titleSnapshot} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, titleSnapshot: event.target.value })} /></label>
                <label>Receiving line<input value={supplierReturnDraft.receivingLineId} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, receivingLineId: event.target.value })} /></label>
                <label>Quantity<input type="number" min="1" value={supplierReturnDraft.quantity} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, quantity: event.target.value })} /></label>
                <label>Reason<select value={supplierReturnDraft.reason} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, reason: event.target.value })}><option value="overstock">Overstock</option><option value="damaged_in_store">Damaged in store</option><option value="received_damaged">Received damaged</option><option value="defective">Defective</option><option value="incorrect_item">Incorrect item</option><option value="unauthorized_substitution">Unauthorized substitution</option><option value="recall">Recall</option><option value="event_overstock">Event overstock</option><option value="vendor_requested_return">Vendor requested return</option><option value="other">Other</option></select></label>
                <label>Condition<select value={supplierReturnDraft.condition} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, condition: event.target.value })}><option value="sellable">Sellable</option><option value="damaged">Damaged</option><option value="defective">Defective</option><option value="incorrect">Incorrect</option><option value="recall">Recall</option><option value="other">Other</option></select></label>
                <label>Expected unit credit<input value={supplierReturnDraft.expectedUnitCreditAmount} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, expectedUnitCreditAmount: event.target.value })} /></label>
                <label className="checkbox-row"><input type="checkbox" checked={supplierReturnDraft.manualOverride} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, manualOverride: event.target.checked })} /> Manual override</label>
                <label className="wide">Override reason<input value={supplierReturnDraft.manualOverrideReason} onChange={(event) => setSupplierReturnDraft({ ...supplierReturnDraft, manualOverrideReason: event.target.value })} /></label>
              </div>
              <div className="actions"><button onClick={() => void checkSupplierReturnEligibilityUi()}>Check eligibility</button><button className="primary" disabled={!canManage} onClick={() => void createSupplierReturnUi()}><PackagePlus /> Create return</button></div>
              <p className="empty-inline">Supplier returns are separate from customer sales returns. Electronic return documents are disabled until an official supplier specification is configured.</p>
            </section>
          </section>
          <section className="panel purchasing-detail-panel">
            {!selectedSupplierReturn ? <div className="empty"><RotateCcw /><h2>Select a supplier return</h2><p>Review authorization, staged inventory, shipment, credit, and history.</p></div> : (
              <>
                <div className="section-heading">
                  <div><h2>{selectedSupplierReturn.returnNumber}</h2><p>{selectedSupplierReturn.vendorName ?? vendorName(vendors, selectedSupplierReturn.vendorId)} · {selectedSupplierReturn.returnMethod}</p></div>
                  <span className={`badge ${selectedSupplierReturn.status}`}>{selectedSupplierReturn.status.replaceAll("_", " ")}</span>
                </div>
                <div className="purchasing-metrics">
                  <Metric label="Expected credit" value={formatMoney({ amount: selectedSupplierReturn.expectedCreditAmount, currency: selectedSupplierReturn.currency })} />
                  <Metric label="Actual credit" value={formatMoney({ amount: selectedSupplierReturn.actualCreditAmount, currency: selectedSupplierReturn.currency })} />
                  <Metric label="Remaining" value={formatMoney({ amount: selectedSupplierReturn.creditRemainingAmount, currency: selectedSupplierReturn.currency })} />
                </div>
                <div className="actions">
                  <button disabled={!canManage || !["draft", "authorization_requested"].includes(selectedSupplierReturn.status)} onClick={() => void authorizeSupplierReturnUi()}><Check /> Authorize</button>
                  <button disabled={!canManage || !["draft", "authorized"].includes(selectedSupplierReturn.status)} onClick={() => void stageSupplierReturnUi()}>Stage</button>
                  <button disabled={!canManage || !["staged", "packed"].includes(selectedSupplierReturn.status)} onClick={() => void shipSupplierReturnUi()}>Ship</button>
                  <button disabled={!canManage || ["shipped", "credited", "closed", "canceled"].includes(selectedSupplierReturn.status)} onClick={() => void cancelSupplierReturnUi()}><X /> Cancel</button>
                  <button onClick={() => window.print()}>Print packing list</button>
                </div>
                <section>
                  <h3>Lines and eligibility</h3>
                  <DataTable empty="No return lines." headers={["Title", "Reason", "Eligibility", "Requested", "Authorized", "Staged", "Shipped", "Expected credit"]} rows={(selectedSupplierReturn.lines ?? []).map((line) => [
                    <div key="title"><strong>{line.titleSnapshot}</strong><p>{line.isbn13 ?? line.vendorSku ?? line.variationId}</p></div>,
                    line.reason.replaceAll("_", " "),
                    <div key="elig"><span className={`badge ${line.eligibilityStatus}`}>{line.eligibilityStatus.replaceAll("_", " ")}</span><p>{(line.eligibilityExplanation ?? []).join(" ")}</p></div>,
                    line.quantityRequested,
                    line.quantityAuthorized,
                    line.quantityStaged,
                    line.quantityShipped,
                    formatMoney({ amount: line.quantityRequested * line.expectedUnitCreditAmount, currency: line.currency }),
                  ])} />
                </section>
                <section>
                  <h3>Cartons and tracking</h3>
                  <DataTable empty="No cartons recorded." headers={["Carton", "Carrier", "Tracking", "Weight", "Status"]} rows={(selectedSupplierReturn.cartons ?? []).map((carton) => [carton.cartonNumber, carton.carrier ?? "-", carton.trackingNumber ?? "-", carton.weightOunces ?? "-", carton.status])} />
                </section>
                <section>
                  <h3>Credit reconciliation</h3>
                  <div className="form-grid compact-form">
                    <label>Credit memo<select value={supplierReturnCreditDraft.creditMemoId} onChange={(event) => setSupplierReturnCreditDraft({ ...supplierReturnCreditDraft, creditMemoId: event.target.value })}><option value="">No credit memo</option>{creditMemos.map((credit) => <option key={credit.id} value={credit.id}>{credit.creditNumber} · {formatMoney({ amount: credit.amount, currency: credit.currency })}</option>)}</select></label>
                    <label>Actual credit<input value={supplierReturnCreditDraft.actualCreditAmount} onChange={(event) => setSupplierReturnCreditDraft({ ...supplierReturnCreditDraft, actualCreditAmount: event.target.value })} /></label>
                    <label>Fees/deductions<input value={supplierReturnCreditDraft.feesOrDeductionsAmount} onChange={(event) => setSupplierReturnCreditDraft({ ...supplierReturnCreditDraft, feesOrDeductionsAmount: event.target.value })} /></label>
                    <label>Status<select value={supplierReturnCreditDraft.status} onChange={(event) => setSupplierReturnCreditDraft({ ...supplierReturnCreditDraft, status: event.target.value })}><option value="credited">Credited</option><option value="partial_credit">Partial credit</option><option value="missing_credit">Missing credit</option><option value="disputed">Disputed</option><option value="resolved">Resolved</option></select></label>
                    <label className="wide">Reason<input value={supplierReturnCreditDraft.resolutionReason} onChange={(event) => setSupplierReturnCreditDraft({ ...supplierReturnCreditDraft, resolutionReason: event.target.value })} /></label>
                  </div>
                  <div className="actions"><button disabled={!canManage} onClick={() => void reconcileSupplierReturnCreditUi()}>Record reconciliation</button></div>
                  <DataTable empty="No credit reconciliation records." headers={["Status", "Expected", "Actual", "Difference", "Reason"]} rows={(selectedSupplierReturn.credits ?? []).map((credit) => [credit.status.replaceAll("_", " "), formatMoney({ amount: credit.expectedCreditAmount, currency: selectedSupplierReturn.currency }), formatMoney({ amount: credit.actualCreditAmount, currency: selectedSupplierReturn.currency }), credit.quantityDifference, credit.resolutionReason ?? "-"])} />
                </section>
                <section>
                  <h3>Activity history</h3>
                  <DataTable empty="No supplier-return history." headers={["When", "Event", "Note"]} rows={(selectedSupplierReturn.history ?? []).map((entry) => [new Date(entry.createdAt).toLocaleString(), entry.eventType.replaceAll("_", " "), entry.note ?? "-"])} />
                </section>
              </>
            )}
          </section>
        </div>
      )}

      {tab === "invoices" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Supplier invoices</h2><button onClick={() => void loadInvoices()}><RefreshCw /> Refresh</button></div>
            <DataTable empty="No supplier invoices." headers={["Invoice", "Vendor", "Status", "POs", "Total", ""]} rows={invoices.map((invoice) => [invoice.invoiceNumber, invoice.vendorName ?? vendorName(vendors, invoice.vendorId), <span className={`badge ${invoice.payableStatus}`} key="status">{invoice.payableStatus.replaceAll("_", " ")}</span>, invoice.poNumbers?.join(", ") || "-", formatMoney({ amount: invoice.invoiceTotalAmount, currency: invoice.currency }), <button key="open" onClick={() => setSelectedInvoiceId(invoice.id)}>Open</button>])} />
            <section>
              <h3>Approval queue</h3>
              <DataTable empty="No invoices need review." headers={["Invoice", "Issue", "Severity", ""]} rows={invoiceDiscrepancies.map((entry) => [entry.invoiceNumber ?? entry.invoiceId, entry.summary, entry.severity, <button key="resolve" disabled={!canManage || entry.status !== "open"} onClick={() => void resolveInvoiceDiscrepancyUi(entry.id)}>Resolve</button>])} />
              <div className="form-grid compact-form">
                <label>Status<select value={invoiceResolution.status} onChange={(event) => setInvoiceResolution({ ...invoiceResolution, status: event.target.value })}><option value="resolved">Resolved</option><option value="supplier_followup">Supplier follow-up</option><option value="dismissed">Dismissed</option></select></label>
                <label className="wide">Resolution reason<input value={invoiceResolution.resolutionReason} onChange={(event) => setInvoiceResolution({ ...invoiceResolution, resolutionReason: event.target.value })} placeholder="Reason and responsible user context" /></label>
              </div>
            </section>
            <section>
              <h3>Local/test invoice intake</h3>
              <p className="empty-inline">Use this for authorized test payloads only. Live X12 810 or partner-specific invoice parsing still requires official supplier documentation and credentials.</p>
              <textarea rows={10} value={invoiceFixtureText} onChange={(event) => setInvoiceFixtureText(event.target.value)} />
              <button disabled={!canManage} onClick={() => void recordInvoiceFixture()}>Record test invoice</button>
            </section>
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{selectedInvoice ? `Invoice ${selectedInvoice.invoiceNumber}` : "Invoice details"}</h2>{selectedInvoice && <span className={`badge ${selectedInvoice.payableStatus}`}>{selectedInvoice.payableStatus.replaceAll("_", " ")}</span>}</div>
            {!selectedInvoice ? <p className="empty-inline">Select an invoice to review three-way matching, discrepancies, approval, credits, and export readiness.</p> : (
              <div className="purchasing-subsections">
                <div className="metric-grid purchasing-metrics">
                  <Metric label="Invoice total" value={formatMoney({ amount: selectedInvoice.invoiceTotalAmount, currency: selectedInvoice.currency })} />
                  <Metric label="Freight" value={formatMoney({ amount: selectedInvoice.freightAmount, currency: selectedInvoice.currency })} />
                  <Metric label="Tax" value={formatMoney({ amount: selectedInvoice.taxAmount, currency: selectedInvoice.currency })} />
                  <Metric label="Export" value={selectedInvoice.exportStatus.replaceAll("_", " ")} />
                </div>
                <section>
                  <h3>Three-way match</h3>
                  <DataTable empty="No invoice lines." headers={["Line", "PO / receiving", "Item", "Qty", "Cost", "Match"]} rows={(selectedInvoice.lines ?? []).map((line) => [line.lineNumber, `${line.purchaseOrderId ?? "-"} / ${line.receivingSessionId ?? "-"}`, <span key="item"><strong>{line.description}</strong><small>{line.isbn13 ?? line.vendorSku ?? "-"}</small></span>, line.quantity, formatMoney({ amount: line.unitCostAmount, currency: line.currency }), <span key="match" className={`badge ${line.matchStatus}`}>{line.matchStatus.replaceAll("_", " ")}</span>])} />
                  <div className="actions">
                    <button onClick={() => void matchSelectedInvoice()}>Run match</button>
                    <button disabled={!canManage || !["matched", "review_required", "disputed"].includes(selectedInvoice.payableStatus)} onClick={() => void transitionInvoiceUi("approved")}><Check /> Approve</button>
                    <button disabled={!canManage} onClick={() => void transitionInvoiceUi("disputed")}>Dispute</button>
                    <button disabled={!canManage} onClick={() => void transitionInvoiceUi("paid")}>Mark paid</button>
                    <button disabled={!canManage} onClick={() => void transitionInvoiceUi("canceled")}>Cancel</button>
                    <button onClick={() => window.print()}>Print summary</button>
                  </div>
                </section>
                <section>
                  <h3>Discrepancies</h3>
                  <DataTable empty="No discrepancies." headers={["Type", "Severity", "Status", "Explanation"]} rows={(selectedInvoice.discrepancies ?? []).map((entry) => [entry.discrepancyType.replaceAll("_", " "), entry.severity, entry.status.replaceAll("_", " "), entry.summary])} />
                </section>
                <section>
                  <h3>Credit memo</h3>
                  <div className="form-grid compact-form">
                    <label>Credit number<input value={creditMemoDraft.creditNumber} onChange={(event) => setCreditMemoDraft({ ...creditMemoDraft, creditNumber: event.target.value })} /></label>
                    <label>Type<select value={creditMemoDraft.creditType} onChange={(event) => setCreditMemoDraft({ ...creditMemoDraft, creditType: event.target.value })}><option value="damaged_copy">Damaged copy</option><option value="short_shipment">Short shipment</option><option value="return_credit">Return credit</option><option value="freight_adjustment">Freight adjustment</option><option value="promotional_discount">Promotional discount</option><option value="credit_adjustment">Credit adjustment</option><option value="invoice_level">Invoice level</option><option value="line_level">Line level</option></select></label>
                    <label>Amount<input inputMode="decimal" value={creditMemoDraft.amount} onChange={(event) => setCreditMemoDraft({ ...creditMemoDraft, amount: event.target.value })} /></label>
                    <label className="wide">Notes<input value={creditMemoDraft.notes} onChange={(event) => setCreditMemoDraft({ ...creditMemoDraft, notes: event.target.value })} /></label>
                    <button disabled={!canManage || !creditMemoDraft.creditNumber} onClick={() => void createCreditMemoUi()}>Record credit</button>
                  </div>
                  <DataTable empty="No credit memos." headers={["Credit", "Type", "Status", "Amount"]} rows={creditMemos.filter((credit) => !credit.originalInvoiceId || credit.originalInvoiceId === selectedInvoice.id).map((credit) => [credit.creditNumber, credit.creditType.replaceAll("_", " "), credit.status, formatMoney({ amount: credit.amount, currency: credit.currency })])} />
                </section>
                <section>
                  <h3>Activity</h3>
                  <DataTable empty="No invoice history." headers={["When", "Event", "Actor", "Note"]} rows={(selectedInvoice.history ?? []).map((entry) => [new Date(entry.createdAt).toLocaleString(), entry.eventType.replaceAll(".", " "), entry.actorUserId ?? "-", entry.note ?? "-"])} />
                </section>
              </div>
            )}
            <section>
              <div className="section-heading"><h3>Accounting export</h3><button disabled={!canManage} onClick={() => void exportAccountingUi()}>Export approved CSV</button></div>
              <DataTable empty="No accounting exports." headers={["When", "Adapter", "Invoices", "Credits", "Total", "Hash"]} rows={accountingExports.map((batch) => [batch.exportedAt ? new Date(batch.exportedAt).toLocaleString() : "-", batch.adapterKey, batch.invoiceCount, batch.creditCount, formatMoney({ amount: batch.totalAmount, currency: batch.currency }), batch.payloadSha256.slice(0, 12)])} />
            </section>
          </section>
        </div>
      )}

      {tab === "replenishment" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Low-stock queue</h2><button className="primary" disabled={!canManage} onClick={() => void generateReplenishment()}>Generate suggestions</button></div>
            <div className="form-grid compact-form">
              <label>Location<select value={replenishmentParams.locationId} onChange={(event) => setReplenishmentParams({ ...replenishmentParams, locationId: event.target.value })}>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}{location.code ? ` (${location.code})` : ""}</option>)}<option value="loc-main">Main</option></select></label>
              <label>Sales lookback days<input type="number" min={1} max={730} value={replenishmentParams.lookbackDays} onChange={(event) => setReplenishmentParams({ ...replenishmentParams, lookbackDays: event.target.value })} /></label>
              <label>Weeks of supply<input type="number" min={1} max={104} value={replenishmentParams.targetWeeksSupply} onChange={(event) => setReplenishmentParams({ ...replenishmentParams, targetWeeksSupply: event.target.value })} /></label>
            </div>
            <p className="empty-inline">Suggestions account for on-hand, reserved demand, open purchase orders, customer special orders, reorder points, desired stock, sales velocity, MOQ, and case pack. Staff must review before POs are created.</p>
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{replenishmentReview ? `Review ${replenishmentReview.id.slice(0, 8)}` : "Replenishment review"}</h2><button disabled={!canManage || selectedSuggestionIds.length === 0} onClick={() => void createPosFromSuggestions()}>Create draft POs</button></div>
            <DataTable empty="Generate a review to see low-stock suggestions." headers={["Select", "Edition", "Qty", "Context", "Vendor", "Reason", "Action"]} rows={(replenishmentReview?.suggestions ?? []).map((suggestion) => [
              <input key="select" type="checkbox" aria-label={`Select ${suggestion.titleSnapshot}`} checked={selectedSuggestionIds.includes(suggestion.id)} disabled={!suggestion.vendorId || suggestion.status === "po_created"} onChange={(event) => setSelectedSuggestionIds((current) => event.target.checked ? [...current, suggestion.id] : current.filter((id) => id !== suggestion.id))} />,
              <div key="title"><strong>{suggestion.titleSnapshot}</strong><span>{suggestion.isbn13 ?? suggestion.variationId}</span></div>,
              <input key="qty" className="qty" type="number" min={0} value={suggestion.finalQuantity} onChange={(event) => void updateSuggestion(suggestion, { finalQuantity: Number(event.target.value) })} />,
              <small key="context">On hand {suggestion.onHandQuantity} / reserved {suggestion.reservedQuantity} / open PO {suggestion.openOrderQuantity} / demand {suggestion.demandQuantity} / velocity {suggestion.salesVelocityPerWeek?.toFixed(2) ?? "-"}</small>,
              suggestion.vendorId ? `${vendorName(vendors, suggestion.vendorId)} ${formatMoney({ amount: suggestion.expectedUnitCostAmount, currency: suggestion.currency })}` : <span className="badge warning" key="no-vendor">Needs vendor mapping</span>,
              <small key="reason">{suggestion.reasons.join(" | ")}</small>,
              <div className="actions" key="actions"><button disabled={!canManage} onClick={() => void updateSuggestion(suggestion, { status: "excluded", finalQuantity: 0, overrideReason: "Excluded by staff" })}>Exclude</button><button disabled={!canManage} onClick={() => void updateSuggestion(suggestion, { status: "deferred", overrideReason: "Deferred by staff" })}>Defer</button><button disabled={!canManage || suggestion.finalQuantity <= 0} onClick={() => void updateSuggestion(suggestion, { status: "approved" })}>Approve</button></div>,
            ])} />
          </section>
        </div>
      )}

      {tab === "special-orders" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Customer special orders</h2><button onClick={() => void loadSpecialOrders()}><RefreshCw /> Refresh</button></div>
            <div className="toolbar compact-toolbar"><label className="searchbox">Search<Search /><input value={specialOrderQuery} onChange={(event) => setSpecialOrderQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void loadSpecialOrders(); }} placeholder="Customer, title, ISBN" /></label><button onClick={() => void loadSpecialOrders()}>Search</button></div>
            <DataTable empty="No purchasing special orders." headers={["Customer", "Book", "Need", "Status", "Promise", ""]} rows={specialOrders.map((order) => [order.customerDisplayName ?? order.customerId, <div key="book"><strong>{order.title}</strong><span>{[order.author, order.isbn].filter(Boolean).join(" / ")}</span></div>, order.quantity, <span className={`badge ${order.purchasingStatus}`} key="status">{order.purchasingStatus.replaceAll("_", " ")}</span>, order.promisedDate ?? order.estimatedDate ?? "-", <div className="actions" key="actions"><button onClick={() => void updateSpecialOrderStatus(order, "ready_for_pickup")}>Ready</button><button onClick={() => void updateSpecialOrderStatus(order, "fulfilled")}>Fulfilled</button><button className="danger" onClick={() => void updateSpecialOrderStatus(order, "canceled")}>Cancel</button></div>])} />
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>Create special order</h2></div>
            <div className="form-grid">
              <label>Customer ID<input value={specialOrderDraft.customerId} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, customerId: event.target.value })} /></label>
              <label>Title<input value={specialOrderDraft.title} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, title: event.target.value })} /></label>
              <label>Author<input value={specialOrderDraft.author} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, author: event.target.value })} /></label>
              <label>ISBN<input value={specialOrderDraft.isbn} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, isbn: normalizeIsbn(event.target.value) })} /></label>
              <label>Variation ID<input value={specialOrderDraft.variationId} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, variationId: event.target.value })} /></label>
              <label>Quantity<input type="number" min={1} value={specialOrderDraft.quantity} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, quantity: event.target.value })} /></label>
              <label>Fulfillment<select value={specialOrderDraft.fulfillmentPreference} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, fulfillmentPreference: event.target.value })}><option value="pickup">Pickup</option><option value="ship_to_customer">Ship to customer</option><option value="hold_for_customer">Hold for customer</option></select></label>
              <label>Deposit<select value={specialOrderDraft.depositStatus} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, depositStatus: event.target.value })}><option value="not_required">Not required</option><option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option><option value="refunded">Refunded</option></select></label>
              <label>Promised<input type="date" value={specialOrderDraft.promisedDate} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, promisedDate: event.target.value })} /></label>
              <label>Estimated<input type="date" value={specialOrderDraft.estimatedDate} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, estimatedDate: event.target.value })} /></label>
              <label className="wide">Notes<textarea rows={3} value={specialOrderDraft.notes} onChange={(event) => setSpecialOrderDraft({ ...specialOrderDraft, notes: event.target.value })} /></label>
              <div className="wide actions"><button className="primary" disabled={!specialOrderDraft.customerId || !specialOrderDraft.title} onClick={() => void saveSpecialOrder()}>Create special order</button></div>
            </div>
          </section>
        </div>
      )}

      {tab === "operations" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>PO delivery</h2></div>
            <p className="empty-inline">Exports preserve the exact payload and audit who prepared or downloaded it. Email preparation does not mean vendor acceptance.</p>
            <DataTable empty="No export templates." headers={["Template", "Vendor", "Format", "Version"]} rows={exportTemplates.map((template) => [template.name, template.vendorId ? vendorName(vendors, template.vendorId) : "Any vendor", template.format, template.version])} />
            <div className="form-grid compact-form"><label>Vendor<select value={templateDraft.vendorId} onChange={(event) => setTemplateDraft({ ...templateDraft, vendorId: event.target.value })}><option value="">Any vendor</option>{vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</select></label><label>Name<input value={templateDraft.name} onChange={(event) => setTemplateDraft({ ...templateDraft, name: event.target.value })} /></label><label>Format<select value={templateDraft.format} onChange={(event) => setTemplateDraft({ ...templateDraft, format: event.target.value as "csv" | "spreadsheet_csv" })}><option value="csv">CSV</option><option value="spreadsheet_csv">Spreadsheet CSV</option></select></label><label className="wide">Column mapping<textarea rows={6} value={templateDraft.columnMappingText} onChange={(event) => setTemplateDraft({ ...templateDraft, columnMappingText: event.target.value })} /></label><button disabled={!canManage} onClick={() => void saveExportTemplateFromDraft()}>Save template version</button></div>
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{selectedPo ? `Operate ${selectedPo.poNumber}` : "Select a PO"}</h2><button disabled={!selectedPo} onClick={() => setTab("orders")}>Open PO list</button></div>
            {selectedPo ? <><div className="metric-grid purchasing-metrics"><Metric label="Status" value={selectedPo.status.replaceAll("_", " ")} /><Metric label="Total" value={formatMoney({ amount: selectedPo.expectedTotalCostAmount, currency: selectedPo.currency })} /><Metric label="Units" value={String(selectedPo.requestedTotalQuantity)} /><Metric label="Version" value={String(selectedPo.version)} /></div><div className="actions"><button disabled={!canManage} onClick={() => void exportSelectedPo("csv", "download")}>Export CSV</button><button disabled={!canManage} onClick={() => void exportSelectedPo("spreadsheet_csv", "download")}>Spreadsheet CSV</button><button disabled={!canManage} onClick={() => void exportSelectedPo("print_html", "download")}>Printable PO</button><button disabled={!canManage} onClick={() => void exportSelectedPo("csv", "email_prepared")}>Prepare email artifact</button><button disabled={!canManage || selectedPo.status !== "submitted"} onClick={() => void acknowledgeSelectedPo()}>Manual acknowledgment</button></div><label className="wide">Vendor response CSV<textarea rows={6} value={ackImportText} onChange={(event) => setAckImportText(event.target.value)} /></label><button disabled={!canManage || !selectedPo} onClick={() => void importAckCsv()}>Import acknowledgment CSV</button></> : <p className="empty-inline">Open a purchase order, then return here for exports, send records, manual acknowledgment, and vendor response import.</p>}
          </section>
        </div>
      )}

      {tab === "changes" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>PO change queue</h2><button onClick={() => void loadChangeRequests()}><RefreshCw /> Refresh</button></div>
            <p className="empty-inline">Requested, transmitted, and supplier-confirmed changes are separate audit states. Manual contact is not supplier confirmation until a response is recorded.</p>
            <DataTable empty="No PO changes." headers={["Request", "PO", "Vendor", "Type", "Status", ""]} rows={changeRequests.map((request) => [
              request.requestNumber,
              request.poNumber ?? purchaseOrders.find((po) => po.id === request.purchaseOrderId)?.poNumber ?? request.purchaseOrderId,
              request.vendorName ?? "-",
              request.changeType.replaceAll("_", " "),
              <span className={`badge ${request.status}`} key="status">{request.status.replaceAll("_", " ")}</span>,
              <button key="open" onClick={() => { setSelectedChangeRequest(request); setSelectedPoId(request.purchaseOrderId); }}>Open</button>,
            ])} />
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{selectedChangeRequest ? selectedChangeRequest.requestNumber : "Change request detail"}</h2>{selectedChangeRequest && <span className={`badge ${selectedChangeRequest.status}`}>{selectedChangeRequest.status.replaceAll("_", " ")}</span>}</div>
            {!selectedChangeRequest ? <p className="empty-inline">Select a change request to review supplier status, substitutions, and response history.</p> : (
              <>
                <div className="metric-grid purchasing-metrics"><Metric label="Type" value={selectedChangeRequest.changeType.replaceAll("_", " ")} /><Metric label="Method" value={selectedChangeRequest.supplierTransmissionMethod.replaceAll("_", " ")} /><Metric label="Requested" value={selectedChangeRequest.requestedAt ? new Date(selectedChangeRequest.requestedAt).toLocaleDateString() : "-"} /><Metric label="Response" value={selectedChangeRequest.responseAt ? new Date(selectedChangeRequest.responseAt).toLocaleDateString() : "Pending"} /></div>
                <DataTable empty="No changed values." headers={["Line", "Original", "Requested", "Accepted"]} rows={(selectedChangeRequest.lines ?? []).map((line) => [line.purchaseOrderLineId ?? "Whole order", compactJson(line.originalValue), compactJson(line.requestedValue), line.acceptedValue ? compactJson(line.acceptedValue) : "-"])} />
                {selectedChangeRequest.changeType === "replace_edition" && <section className="panel-subsection"><h3>Substitution review</h3><div className="comparison-grid"><div><strong>Original request</strong><dl className="metadata-fields"><dt>Edition</dt><dd>{String(selectedChangeRequest.lines?.[0]?.originalValue.editionId ?? "-")}</dd><dt>ISBN</dt><dd>{String(selectedChangeRequest.lines?.[0]?.originalValue.isbn13 ?? "-")}</dd><dt>Title</dt><dd>{String(selectedChangeRequest.lines?.[0]?.originalValue.titleSnapshot ?? "-")}</dd><dt>Cost</dt><dd>{formatMoney({ amount: Number(selectedChangeRequest.lines?.[0]?.originalValue.expectedUnitCostAmount ?? 0), currency: selectedPo?.currency ?? "USD" })}</dd></dl></div><div><strong>Supplier proposal</strong><dl className="metadata-fields"><dt>Edition</dt><dd>{String(selectedChangeRequest.lines?.[0]?.requestedValue.replacementEditionId ?? "-")}</dd><dt>ISBN</dt><dd>{String(selectedChangeRequest.lines?.[0]?.requestedValue.replacementIsbn13 ?? "-")}</dd><dt>Title</dt><dd>{String(selectedChangeRequest.lines?.[0]?.requestedValue.replacementTitle ?? "-")}</dd><dt>Cost</dt><dd>{formatMoney({ amount: Number(selectedChangeRequest.lines?.[0]?.requestedValue.replacementCostAmount ?? 0), currency: selectedPo?.currency ?? "USD" })}</dd></dl></div></div><p className="empty-inline">Approve only after staff confirms the proposed edition is acceptable for inventory and any linked customer commitment.</p></section>}
                <div className="actions"><button disabled={!canManage || !["draft", "pending_approval"].includes(selectedChangeRequest.status)} onClick={() => void transitionPoChange(selectedChangeRequest, "queue")}>Queue</button><button disabled={!canManage || selectedChangeRequest.status !== "queued"} onClick={() => void transitionPoChange(selectedChangeRequest, "send")}>Mark sent</button><button className="danger" disabled={!canManage || ["accepted", "partially_accepted", "rejected", "failed", "canceled", "superseded"].includes(selectedChangeRequest.status)} onClick={() => void transitionPoChange(selectedChangeRequest, "cancel")}>Cancel request</button></div>
                <div className="form-grid compact-form"><label>Supplier status<select value={changeResponseDraft.status} onChange={(event) => setChangeResponseDraft({ ...changeResponseDraft, status: event.target.value })}><option value="accepted">Accepted</option><option value="partially_accepted">Partially accepted</option><option value="rejected">Rejected</option><option value="failed">Failed</option><option value="supplier_received">Supplier received</option></select></label><label>Supplier reference<input value={changeResponseDraft.externalReference} onChange={(event) => setChangeResponseDraft({ ...changeResponseDraft, externalReference: event.target.value })} /></label><label>Control number<input value={changeResponseDraft.externalControlNumber} onChange={(event) => setChangeResponseDraft({ ...changeResponseDraft, externalControlNumber: event.target.value })} /></label><label className="wide">Response notes<textarea rows={2} value={changeResponseDraft.supplierResponse} onChange={(event) => setChangeResponseDraft({ ...changeResponseDraft, supplierResponse: event.target.value })} /></label><button className="primary" disabled={!canManage || !changeResponseDraft.supplierResponse.trim()} onClick={() => void recordPoChangeSupplierResponse()}>Record supplier response</button></div>
                <section><h3>Status timeline</h3><DataTable empty="No change history." headers={["When", "Event", "Actor", "Note"]} rows={(selectedChangeRequest.history ?? []).map((entry) => [new Date(entry.createdAt).toLocaleString(), entry.eventType.replaceAll(".", " "), entry.actorUserId ?? "-", entry.note ?? "-"])} /></section>
              </>
            )}
          </section>
        </div>
      )}

      {tab === "backorders" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Backorder aging</h2><button onClick={() => void loadBackorders()}><RefreshCw /> Refresh</button></div>
            <div className="form-grid compact-form"><label>Location<select value={backorderPolicy.locationId} onChange={(event) => setBackorderPolicy({ ...backorderPolicy, locationId: event.target.value })}>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}<option value="loc-main">Main</option></select></label><label>Retain days<input type="number" min={1} value={backorderPolicy.retainDays} onChange={(event) => setBackorderPolicy({ ...backorderPolicy, retainDays: Number(event.target.value) })} /></label><label>Notify after days<input type="number" min={1} value={backorderPolicy.notifyOverdueAfterDays} onChange={(event) => setBackorderPolicy({ ...backorderPolicy, notifyOverdueAfterDays: Number(event.target.value) })} /></label><label className="checkbox-row"><input type="checkbox" checked={backorderPolicy.customerOrderPriority} onChange={(event) => setBackorderPolicy({ ...backorderPolicy, customerOrderPriority: event.target.checked })} /> Customer orders first</label><label className="checkbox-row"><input type="checkbox" checked={backorderPolicy.autoCloseExpired} onChange={(event) => setBackorderPolicy({ ...backorderPolicy, autoCloseExpired: event.target.checked })} /> Auto-close expired</label><button disabled={!canManage} onClick={() => void saveBackorderPolicyFromUi()}>Save policy</button></div>
            <div className="actions"><button disabled={!canManage || selectedBackorderIds.length === 0} onClick={() => void batchBackordersFromUi("close_backorder")}>Close selected</button><button className="danger" disabled={!canManage || selectedBackorderIds.length === 0} onClick={() => void batchBackordersFromUi("cancel_backorder")}>Cancel selected</button></div>
          </section>
          <section className="panel purchasing-detail-panel">
            <h2>Supplier follow-up queue</h2>
            <DataTable empty="No aged backorders." headers={["Select", "PO", "Vendor", "Item", "Qty", "Dates", "Customer", ""]} rows={backorders.map((row) => [
              <input key="select" type="checkbox" aria-label={`Select ${row.title}`} checked={selectedBackorderIds.includes(row.purchaseOrderLineId)} disabled={row.shippedQuantity > 0 || row.receivedQuantity > 0} onChange={(event) => setSelectedBackorderIds((current) => event.target.checked ? [...current, row.purchaseOrderLineId] : current.filter((id) => id !== row.purchaseOrderLineId))} />,
              row.poNumber,
              row.vendorName ?? row.vendorId,
              <div key="item"><strong>{row.title}</strong><span>{row.isbn13 ?? row.purchaseOrderLineId}</span></div>,
              `${row.backorderedQuantity}/${row.remainingQuantity} open`,
              <small key="dates">Expected {row.expectedDate ?? "-"} / cancel {row.cancelIfNotFilledDate ?? "-"} / age {row.ageDays}d</small>,
              row.customerRequestId ? <span className="badge warning" key="customer">Customer order {row.customerQuantity}</span> : "-",
              <button key="po" onClick={() => { setSelectedPoId(row.purchaseOrderId); setTab("orders"); }}>Open PO</button>,
            ])} />
          </section>
        </div>
      )}

      {tab === "integrations" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Provider operations</h2><button onClick={() => void loadIntegrations()}><RefreshCw /> Refresh</button></div>
            <div className="metric-grid purchasing-metrics">
              <Metric label="Queued" value={String(integrationHealth?.queued ?? 0)} />
              <Metric label="Failed" value={String(integrationHealth?.failed ?? 0)} />
              <Metric label="Review" value={String(integrationHealth?.openReviewItems ?? 0)} />
              <Metric label="Transmitted" value={String(integrationHealth?.transmitted ?? 0)} />
            </div>
            <DataTable empty="No integration provider alerts." headers={["Severity", "Provider", "Alert"]} rows={(integrationHealth?.alerts ?? []).map((alert) => [
              <span className={`badge ${alert.severity}`} key="severity">{alert.severity}</span>,
              integrationProviders.find((provider) => provider.id === alert.providerId)?.displayName ?? "-",
              alert.message,
            ])} />
            <DataTable empty="No integration providers configured." headers={["Provider", "Env", "Status", "Capabilities", "Versions", "Ops"]} rows={integrationProviders.map((provider) => [
              <button key="provider" className="link-button" onClick={() => setSelectedProviderId(provider.id)}>{provider.displayName}</button>,
              <span className={`badge ${provider.environment}`} key="env">{provider.environment}</span>,
              provider.status,
              providerCapabilitySummary(provider.capabilities),
              `${provider.adapterVersion} / ${provider.mapVersion}`,
              `${provider.killSwitchEnabled || provider.tenantKillSwitchEnabled ? "Kill on" : "Kill off"} / ${provider.circuitState ?? "closed"}`,
            ])} />
            <DataTable empty="No provider message activity." headers={["Provider", "Queued", "Failed", "Dead letter", "Review", "Last activity"]} rows={(integrationHealth?.providers ?? []).map((provider) => [
              provider.displayName,
              String(provider.queued),
              String(provider.failed),
              String(provider.deadLetter),
              String(provider.openReviewItems),
              provider.lastActivityAt ? new Date(provider.lastActivityAt).toLocaleString() : "-",
            ])} />
            <div className="form-grid compact-form">
              <label>Name<input value={providerDraft.displayName} onChange={(event) => setProviderDraft({ ...providerDraft, displayName: event.target.value })} /></label>
              <label>Environment<select value={providerDraft.environment} onChange={(event) => setProviderDraft({ ...providerDraft, environment: event.target.value })}><option value="test">Test</option><option value="production">Production</option></select></label>
              <label>Status<select value={providerDraft.status} onChange={(event) => setProviderDraft({ ...providerDraft, status: event.target.value })}><option value="configured">Configured</option><option value="testing">Testing</option><option value="active">Active</option><option value="disabled">Disabled</option></select></label>
              <label>Rollout %<input type="number" min={0} max={100} value={providerDraft.rolloutPercent} onChange={(event) => setProviderDraft({ ...providerDraft, rolloutPercent: event.target.value })} /></label>
              <label>Rate/min<input type="number" min={1} value={providerDraft.rateLimitPerMinute} onChange={(event) => setProviderDraft({ ...providerDraft, rateLimitPerMinute: event.target.value })} /></label>
              <label className="checkbox-row"><input type="checkbox" checked={providerDraft.killSwitchEnabled} onChange={(event) => setProviderDraft({ ...providerDraft, killSwitchEnabled: event.target.checked })} /> Kill switch</label>
              <label>Adapter<input value={providerDraft.adapterVersion} onChange={(event) => setProviderDraft({ ...providerDraft, adapterVersion: event.target.value })} /></label>
              <label>Map<input value={providerDraft.mapVersion} onChange={(event) => setProviderDraft({ ...providerDraft, mapVersion: event.target.value })} /></label>
              <button disabled={!canConfigureIntegrations} onClick={() => void saveIntegrationProviderFromDraft()}>Save provider</button>
            </div>
            <p className="empty-inline">Credential values stay in Cloudflare secrets. This screen stores only binding names, environment, capabilities, and certification state.</p>
            <div className="form-grid compact-form">
              <label>Credential name<input value={credentialDraft.credentialName} onChange={(event) => setCredentialDraft({ ...credentialDraft, credentialName: event.target.value })} /></label>
              <label>Type<select value={credentialDraft.credentialType} onChange={(event) => setCredentialDraft({ ...credentialDraft, credentialType: event.target.value })}><option value="pubnet_mailbox">Pubnet mailbox</option><option value="sftp_password">SFTP password</option><option value="sftp_private_key">SFTP private key</option><option value="as2_certificate">AS2 certificate</option><option value="van_mailbox">VAN mailbox</option><option value="api_key">API key</option></select></label>
              <label>Secret binding<input value={credentialDraft.secretBindingName} onChange={(event) => setCredentialDraft({ ...credentialDraft, secretBindingName: event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") })} /></label>
              <button disabled={!canConfigureIntegrations || !selectedProviderId} onClick={() => void bindIntegrationCredential()}>Bind credential</button>
            </div>
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>Trading partners and messages</h2><button disabled={!canManage} onClick={() => void processQueue()}>Process queue</button></div>
            <div className="form-grid compact-form">
              <label>Vendor<select value={partnerDraft.vendorId} onChange={(event) => setPartnerDraft({ ...partnerDraft, vendorId: event.target.value, vendorAccountId: "" })}><option value="">Select vendor</option>{vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</select></label>
              <label>Vendor account<select value={partnerDraft.vendorAccountId} onChange={(event) => setPartnerDraft({ ...partnerDraft, vendorAccountId: event.target.value })}><option value="">Any account</option>{(vendors.find((vendor) => vendor.id === partnerDraft.vendorId)?.accounts ?? []).map((account) => <option key={account.id} value={account.id}>{account.accountNumber}</option>)}</select></label>
              <label>SAN<input value={partnerDraft.san} onChange={(event) => setPartnerDraft({ ...partnerDraft, san: event.target.value })} /></label>
              <label>Pubnet ID<input value={partnerDraft.pubnetId} onChange={(event) => setPartnerDraft({ ...partnerDraft, pubnetId: event.target.value })} /></label>
              <label>X12 sender<input value={partnerDraft.x12SenderId} onChange={(event) => setPartnerDraft({ ...partnerDraft, x12SenderId: event.target.value })} /></label>
              <label>X12 receiver<input value={partnerDraft.x12ReceiverId} onChange={(event) => setPartnerDraft({ ...partnerDraft, x12ReceiverId: event.target.value })} /></label>
              <label>Transport<select value={partnerDraft.defaultTransport} onChange={(event) => setPartnerDraft({ ...partnerDraft, defaultTransport: event.target.value })}><option value="local_test">Local test</option><option value="ftp_mailbox">FTP mailbox</option><option value="sftp">SFTP</option><option value="van">VAN</option><option value="as2">AS2</option><option value="api">API</option></select></label>
              <label>Certification<select value={partnerDraft.certificationStatus} onChange={(event) => setPartnerDraft({ ...partnerDraft, certificationStatus: event.target.value })}><option value="not_started">Not started</option><option value="testing">Testing</option><option value="certified">Certified</option><option value="blocked">Blocked</option></select></label>
              <label className="wide">Notes<textarea rows={2} value={partnerDraft.certificationNotes} onChange={(event) => setPartnerDraft({ ...partnerDraft, certificationNotes: event.target.value })} /></label>
              <button disabled={!canConfigureIntegrations || !selectedProviderId || !partnerDraft.vendorId} onClick={() => void saveTradingPartnerFromDraft()}>Save trading partner</button>
            </div>
            <DataTable empty="No trading partners configured." headers={["Vendor", "Env", "Transport", "Party ID", "Cert"]} rows={tradingPartners.map((partner) => [partner.vendorName ?? vendorName(vendors, partner.vendorId), partner.environment, partner.defaultTransport, partner.san ?? partner.pubnetId ?? partner.x12ReceiverId ?? "-", partner.certificationStatus.replaceAll("_", " ")])} />
            <section>
              <div className="section-heading"><h3>{selectedPo ? `Electronic ordering for ${selectedPo.poNumber}` : "Electronic ordering"}</h3><button disabled={!selectedPo} onClick={() => setTab("orders")}>Select PO</button></div>
              {selectedPo ? <><div className="actions"><button disabled={!canManage || !selectedProviderId} onClick={() => void previewElectronicPo()}>Preview electronic PO</button><button className="primary" disabled={!canManage || !selectedProviderId || selectedPo.status !== "submitted"} onClick={() => void sendElectronicPo()}>Send electronically</button></div>{electronicPreview && <div className="panel-subsection"><strong>{electronicPreview.warning}</strong><DataTable empty="No validation messages." headers={["Severity", "Code", "Message"]} rows={(electronicPreview.validation ?? []).map((entry) => [entry.severity, entry.code, entry.message])} /><pre className="code-preview">{(electronicPreview.x12Preview ?? "").slice(0, 1200)}</pre></div>}</> : <p className="empty-inline">Open a submitted purchase order before sending electronically.</p>}
            </section>
            <DataTable empty="No integration messages." headers={["Type", "Direction", "Status", "PO", "Transport", "Action"]} rows={integrationMessages.map((entry) => [
              `${entry.providerKey} ${entry.messageType}`,
              entry.direction,
              <span className={`badge ${entry.processingStatus}`} key="status">{entry.processingStatus.replaceAll("_", " ")}</span>,
              purchaseOrders.find((po) => po.id === entry.purchaseOrderId)?.poNumber ?? entry.externalDocumentId ?? "-",
              entry.transport,
              <div className="actions" key="actions"><button disabled={!canManage || !["failed", "terminal_error", "dead_letter"].includes(entry.processingStatus)} onClick={() => void api(`/api/admin/integrations/messages/${encodeURIComponent(entry.id)}/retry`, { method: "POST", body: JSON.stringify({ reason: "Manual retry from Purchasing", idempotencyKey: crypto.randomUUID() }) }).then(loadIntegrations).catch((err) => setError(staffErrorMessage(err)))}>Retry</button><button disabled={!canConfigureIntegrations || entry.environment !== "test" || entry.transport !== "local_test" || entry.direction !== "outbound"} onClick={() => void simulateResponse(entry.id)}>Simulate response</button></div>,
            ])} />
            <DataTable empty="No integration review items." headers={["Severity", "Type", "Summary", "Status", ""]} rows={integrationReviews.map((review) => [review.severity, review.reviewType.replaceAll("_", " "), review.summary, review.status, <button key="resolve" disabled={!canManage || review.status !== "open"} onClick={() => void resolveReview(review.id)}>Resolve</button>])} />
          </section>
        </div>
      )}

      {tab === "shipments" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <div className="section-heading"><h2>Incoming Shipments</h2><button onClick={() => void loadShipments()}>Refresh</button></div>
            <DataTable empty="No inbound shipments recorded." headers={["Shipment", "POs", "Vendor", "Status", "ETA", ""]} rows={shipments.map((shipment) => [
              shipment.supplierShipmentNumber,
              (shipment.poNumbers ?? []).join(", ") || "-",
              shipment.vendorName ?? vendorName(vendors, shipment.vendorId),
              <span className={`badge ${shipment.status}`} key="status">{shipment.status.replaceAll("_", " ")}</span>,
              shipment.estimatedDeliveryDate ? new Date(shipment.estimatedDeliveryDate).toLocaleDateString() : "-",
              <button key="open" onClick={() => setSelectedShipmentId(shipment.id)}>Open</button>,
            ])} />
            <section className="panel-subsection">
              <h3>Local/test ASN intake</h3>
              <p className="empty-inline">This records a normalized development or certified-sample ASN payload. It is not a live Pubnet mailbox or supplier transmission.</p>
              <label className="wide">Normalized ASN JSON<textarea rows={10} value={asnFixtureText} onChange={(event) => setAsnFixtureText(event.target.value)} /></label>
              <button disabled={!canManage} onClick={() => void recordAsnFixture()}>Record test ASN</button>
            </section>
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{selectedShipment ? `Shipment ${selectedShipment.supplierShipmentNumber}` : "Shipment details"}</h2>{selectedShipment && <span className={`badge ${selectedShipment.status}`}>{selectedShipment.status.replaceAll("_", " ")}</span>}</div>
            {!selectedShipment ? <p className="empty-inline">Open a shipment to review cartons, tracking, PO links, customer commitments, and discrepancies.</p> : (
              <>
                <div className="metric-grid purchasing-metrics">
                  <Metric label="Supplier status" value={selectedShipment.status.replaceAll("_", " ")} />
                  <Metric label="Expected delivery" value={selectedShipment.estimatedDeliveryDate ? new Date(selectedShipment.estimatedDeliveryDate).toLocaleDateString() : "Unknown"} />
                  <Metric label="Cartons" value={String(selectedShipment.cartons?.length ?? 0)} />
                  <Metric label="Freight" value={selectedShipment.freightAmount == null ? "-" : formatMoney({ amount: selectedShipment.freightAmount, currency: selectedShipment.currency })} />
                </div>
                <dl className="metadata-fields">
                  <dt>Carrier</dt><dd>{[selectedShipment.carrier, selectedShipment.shippingService].filter(Boolean).join(" / ") || "-"}</dd>
                  <dt>Tracking</dt><dd>{selectedShipment.trackingNumbers.map(String).join(", ") || "-"}</dd>
                  <dt>Origin</dt><dd>{selectedShipment.originWarehouse ?? "-"}</dd>
                  <dt>Destination</dt><dd>{selectedShipment.destinationName ?? locationName(locations, selectedShipment.destinationLocationId)}</dd>
                  <dt>Packing slip</dt><dd>{selectedShipment.packingSlipNumber ?? "-"}</dd>
                  <dt>Source message</dt><dd>{selectedShipment.sourceMessageId ?? "-"}</dd>
                </dl>
                <section>
                  <h3>Cartons</h3>
                  <DataTable empty="No carton detail supplied. Use ISBN scanning in Receiving." headers={["Carton", "Tracking", "Status", "Lines", "Received", ""]} rows={(selectedShipment.cartons ?? []).map((carton) => [
                    carton.licensePlateBarcode,
                    carton.trackingNumber ?? "-",
                    carton.status.replaceAll("_", " "),
                    carton.lines?.length ?? 0,
                    carton.receivedAt ? new Date(carton.receivedAt).toLocaleString() : "-",
                    <button key="receive" disabled={carton.status === "received"} onClick={() => { setTab("receiving"); setCartonBarcode(carton.licensePlateBarcode); void loadCarton(carton.licensePlateBarcode); }}>Receive carton</button>,
                  ])} />
                </section>
                <section>
                  <h3>Shipment lines</h3>
                  <DataTable empty="No shipment lines." headers={["Item", "PO", "Shipped", "Received", "Cost", "Customer", "Status"]} rows={(selectedShipment.lines ?? []).map((line) => [
                    <span key="item"><strong>{line.title ?? line.isbn13 ?? line.vendorSku ?? "Unmatched item"}</strong><small>{[line.isbn13, line.vendorSku].filter(Boolean).join(" / ")}</small></span>,
                    purchaseOrders.find((po) => po.id === line.purchaseOrderId)?.poNumber ?? line.purchaseOrderId ?? "-",
                    line.shippedQuantity,
                    `${line.receivedQuantity}${line.damagedQuantity ? ` / damaged ${line.damagedQuantity}` : ""}${line.missingQuantity ? ` / short ${line.missingQuantity}` : ""}`,
                    line.receivedUnitCostAmount == null ? "-" : formatMoney({ amount: line.receivedUnitCostAmount, currency: line.currency }),
                    line.customerRequestId ? <span className="badge warning" key="customer">Customer order</span> : "-",
                    line.status.replaceAll("_", " "),
                  ])} />
                </section>
                <section>
                  <h3>Discrepancies</h3>
                  <DataTable empty="No unresolved shipment discrepancies." headers={["Severity", "Type", "Summary", "Status", ""]} rows={(selectedShipment.discrepancies ?? []).map((entry) => [entry.severity, entry.discrepancyType.replaceAll("_", " "), entry.summary, entry.status.replaceAll("_", " "), <button key="focus" onClick={() => setShipmentResolution((current) => ({ ...current, resolutionReason: entry.summary }))}>Use as reason</button>])} />
                </section>
                <section>
                  <h3>Timeline</h3>
                  <DataTable empty="No shipment history." headers={["Event", "User", "When", "Note"]} rows={(selectedShipment.history ?? []).map((entry) => [entry.eventType.replaceAll("_", " "), entry.actorUserId ?? "-", entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "-", entry.note ?? "-"])} />
                </section>
              </>
            )}
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>Discrepancy Queue</h2><button onClick={() => void loadShipmentDiscrepancies()}>Refresh</button></div>
            <div className="form-grid compact-form">
              <label>Status<select value={shipmentResolution.status} onChange={(event) => setShipmentResolution({ ...shipmentResolution, status: event.target.value })}><option value="resolved">Resolved</option><option value="supplier_followup">Supplier follow-up</option><option value="dismissed">Dismissed</option></select></label>
              <label>Follow-up<select value={shipmentResolution.supplierFollowupStatus} onChange={(event) => setShipmentResolution({ ...shipmentResolution, supplierFollowupStatus: event.target.value })}><option value="logged">Logged</option><option value="contacted">Contacted</option><option value="waiting">Waiting</option><option value="closed">Closed</option></select></label>
              <label className="wide">Resolution reason<textarea rows={2} value={shipmentResolution.resolutionReason} onChange={(event) => setShipmentResolution({ ...shipmentResolution, resolutionReason: event.target.value })} /></label>
            </div>
            <DataTable empty="No open shipment discrepancies." headers={["Shipment", "Severity", "Issue", "PO", "Created", ""]} rows={shipmentDiscrepancies.map((entry) => [
              entry.supplierShipmentNumber ?? entry.shipmentId,
              entry.severity,
              entry.summary,
              entry.purchaseOrderId ? (purchaseOrders.find((po) => po.id === entry.purchaseOrderId)?.poNumber ?? entry.purchaseOrderId) : "-",
              entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "-",
              <div className="actions" key="actions"><button onClick={() => setSelectedShipmentId(entry.shipmentId)}>Open</button><button disabled={!canManage || entry.status !== "open"} onClick={() => void resolveShipmentDiscrepancyUi(entry.id)}>Update</button></div>,
            ])} />
          </section>
        </div>
      )}

      {tab === "receiving" && (
        <div className="purchasing-layout">
          <section className="panel purchasing-list-panel">
            <h2>Receivable POs</h2>
            <DataTable empty="No submitted purchase orders are ready to receive." headers={["PO", "Vendor", "Status", "Remaining", ""]} rows={purchaseOrders.filter((po) => !["draft", "pending_approval", "approved", "canceled", "closed"].includes(po.status)).map((po) => [po.poNumber, po.vendorName ?? vendorName(vendors, po.vendorId), po.status.replaceAll("_", " "), Math.max(0, po.requestedTotalQuantity - po.receivedTotalQuantity), <button key="receive" onClick={() => { setSelectedPoId(po.id); setTab("receiving"); setTimeout(() => receiveScanRef.current?.focus(), 0); }}>Receive</button>])} />
          </section>
          <section className="panel purchasing-detail-panel">
            <div className="section-heading"><h2>{activeReceivingPo ? `Receive ${activeReceivingPo.poNumber}` : "Receive purchase order"}</h2>{activeReceivingPo && <span className="badge">{activeReceivingPo.status.replaceAll("_", " ")}</span>}</div>
            <section className="panel-subsection">
              <h3>Carton receiving</h3>
              <div className="form-grid compact-form">
                <label className="wide scan-input">Scan carton or license plate<input ref={cartonScanRef} value={cartonBarcode} onChange={(event) => setCartonBarcode(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void loadCarton(); } }} placeholder="Carton barcode from ASN or packing label" /></label>
                <button onClick={() => void loadCarton()}>Load carton</button>
              </div>
              {!cartonPreview ? <p className="empty-inline">Carton receiving loads expected ASN contents when the supplier supplies carton detail. ISBN scanning remains available below.</p> : (
                <>
                  <div className="section-heading"><strong>{cartonPreview.licensePlateBarcode}</strong><span className={`badge ${cartonPreview.status}`}>{cartonPreview.status.replaceAll("_", " ")}</span></div>
                  <DataTable empty="No expected carton contents." headers={["Item", "Expected", "Receive", "Damaged", "Short", "Over", "Cost", "Customer"]} rows={(cartonPreview.lines ?? []).map((line) => {
                    const draft = cartonReceiveLines[line.shipmentLineId] ?? { shipmentLineId: line.shipmentLineId, receivedQuantity: "0", damagedQuantity: "0", shortageQuantity: "0", overageQuantity: "0", receivedUnitCostAmount: "0.00", customerRequestId: "" };
                    return [
                      <span key="item"><strong>{line.title ?? line.isbn13 ?? "Unmatched item"}</strong><small>{line.isbn13 ?? "-"}</small></span>,
                      line.expectedQuantity,
                      <input className="qty" aria-label={`Carton received quantity for ${line.title ?? line.id}`} key="received" type="number" min={0} value={draft.receivedQuantity} onChange={(event) => updateCartonReceiveLine(line.shipmentLineId, { receivedQuantity: event.target.value })} />,
                      <input className="qty" aria-label={`Carton damaged quantity for ${line.title ?? line.id}`} key="damaged" type="number" min={0} value={draft.damagedQuantity} onChange={(event) => updateCartonReceiveLine(line.shipmentLineId, { damagedQuantity: event.target.value })} />,
                      <input className="qty" aria-label={`Carton short quantity for ${line.title ?? line.id}`} key="short" type="number" min={0} value={draft.shortageQuantity} onChange={(event) => updateCartonReceiveLine(line.shipmentLineId, { shortageQuantity: event.target.value })} />,
                      <input className="qty" aria-label={`Carton overage quantity for ${line.title ?? line.id}`} key="over" type="number" min={0} value={draft.overageQuantity} onChange={(event) => updateCartonReceiveLine(line.shipmentLineId, { overageQuantity: event.target.value })} />,
                      <input aria-label={`Carton unit cost for ${line.title ?? line.id}`} key="cost" inputMode="decimal" value={draft.receivedUnitCostAmount} onChange={(event) => updateCartonReceiveLine(line.shipmentLineId, { receivedUnitCostAmount: event.target.value })} />,
                      line.customerRequestId ? <span className="badge warning" key="customer">Customer order</span> : "-",
                    ];
                  })} />
                  <div className="actions"><button className="primary" disabled={cartonPreview.status === "received"} onClick={() => void postCartonReceiving()}><Check /> Post carton receiving</button><button onClick={() => { setCartonPreview(null); setCartonReceiveLines({}); setTimeout(() => cartonScanRef.current?.focus(), 0); }}>Clear carton</button><button onClick={() => navigate("/app/labels")}>Open Labels</button></div>
                </>
              )}
            </section>
            {!activeReceivingPo ? <p className="empty-inline">Choose a submitted or partially received PO.</p> : (
              <>
                <div className="form-grid">
                  <label>Receiving location<select value={receiveLocationId} onChange={(event) => setPoDraft({ ...poDraft, shipToLocationId: event.target.value })}>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}{location.code ? ` (${location.code})` : ""}</option>)}<option value="loc-main">Main</option></select></label>
                  <label>Shipment reference<input value={receivingMeta.shipmentReference} onChange={(event) => setReceivingMeta({ ...receivingMeta, shipmentReference: event.target.value })} /></label>
                  <label className="wide scan-input">Scan received item<input ref={receiveScanRef} value={inventoryQuery} onChange={(event) => setInventoryQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); receiveScan(); } }} placeholder="Scan ISBN, UPC, vendor SKU, or internal SKU" /></label>
                  <label className="wide">Receiving notes<textarea rows={2} value={receivingMeta.notes} onChange={(event) => setReceivingMeta({ ...receivingMeta, notes: event.target.value })} /></label>
                </div>
                <div className="table-wrap purchasing-lines-table"><table><thead><tr><th>Item</th><th>Ordered</th><th>Previous</th><th>Remaining</th><th>Receive</th><th>Damaged</th><th>Short</th><th>Over</th><th>Cost</th><th>Demand</th></tr></thead><tbody>{ensureReceivingLines().map((line) => {
                  const poLine = activeReceivingPo.lines?.find((entry) => entry.id === line.purchaseOrderLineId);
                  const remaining = Math.max(0, (poLine?.requestedQuantity ?? 0) - (poLine?.receivedQuantity ?? 0) - (poLine?.canceledQuantity ?? 0));
                  return <tr key={line.id}><td><strong>{poLine?.titleSnapshot ?? poLine?.isbn13 ?? poLine?.vendorSku ?? "Line"}</strong><small>{[poLine?.isbn13, poLine?.vendorSku, poLine?.internalSku].filter(Boolean).join(" / ")}</small></td><td>{poLine?.requestedQuantity ?? 0}</td><td>{poLine?.receivedQuantity ?? 0}</td><td>{remaining}</td><td><input className="qty" aria-label={`Received quantity for ${poLine?.titleSnapshot ?? line.id}`} type="number" min={0} value={line.receivedQuantity} onChange={(event) => updateReceivingLine(line.id, { receivedQuantity: Number(event.target.value) })} /></td><td><input className="qty" aria-label={`Damaged quantity for ${poLine?.titleSnapshot ?? line.id}`} type="number" min={0} value={line.damagedQuantity} onChange={(event) => updateReceivingLine(line.id, { damagedQuantity: Number(event.target.value) })} /></td><td><input className="qty" aria-label={`Short quantity for ${poLine?.titleSnapshot ?? line.id}`} type="number" min={0} value={line.shortageQuantity} onChange={(event) => updateReceivingLine(line.id, { shortageQuantity: Number(event.target.value) })} /></td><td><input className="qty" aria-label={`Overage quantity for ${poLine?.titleSnapshot ?? line.id}`} type="number" min={0} value={line.overageQuantity} onChange={(event) => updateReceivingLine(line.id, { overageQuantity: Number(event.target.value) })} /></td><td><input aria-label={`Received unit cost for ${poLine?.titleSnapshot ?? line.id}`} inputMode="decimal" value={moneyToInput({ amount: line.receivedUnitCostAmount, currency: line.currency })} onChange={(event) => updateReceivingLine(line.id, { receivedUnitCostAmount: moneyInputToOptionalCents(event.target.value) ?? 0 })} /></td><td>{line.customerRequestId ? <button onClick={() => void markRequestReceived(line.customerRequestId ?? "")}>Mark request ready</button> : "-"}</td></tr>;
                })}</tbody></table></div>
                <div className="actions"><button onClick={() => void previewReceive()}>Preview inventory effects</button><button className="primary" onClick={() => void postReceive()}><Check /> Post receiving</button><button onClick={() => navigate("/app/labels")}>Open Labels</button></div>
                {receivingPreview && <section className={receivingPreview.valid ? "success" : "error"}><strong>{receivingPreview.valid ? "Preview valid" : "Preview has errors"}</strong><span>{receivingPreview.lines.reduce((sum, line) => sum + line.inventoryQuantityDelta, 0)} units will be added to inventory.</span>{receivingPreview.lines.flatMap((line) => [...line.errors, ...line.warnings]).map((entry, index) => <span key={index}>{entry}</span>)}</section>}
                {postedSession && <section className="success"><strong>{postedSession.receivingNumber} posted</strong><span>{postedSession.lines?.reduce((sum, line) => sum + line.receivedQuantity, 0) ?? 0} received units recorded.</span></section>}
                <section><h3>Receiving history and corrections</h3><DataTable empty="No receiving sessions." headers={["Session", "Status", "Shipment", "Received", ""]} rows={(activeReceivingPo.receivingSessions ?? []).map((session) => [session.receivingNumber, session.status, session.shipmentReference ?? "-", new Date(session.receivedAt).toLocaleString(), <button key="load" onClick={() => void loadReceivingSession(session.id)}>Review / correct</button>])} />{correctionSession && <div className="form-grid compact-form"><label>Receiving line<select value={correction.receivingLineId} onChange={(event) => { const line = correctionSession.lines?.find((entry) => entry.id === event.target.value); setCorrection({ ...correction, receivingLineId: event.target.value, unitCost: moneyToInput({ amount: line?.receivedUnitCostAmount ?? 0, currency: line?.currency ?? activeReceivingPo.currency }) }); }}>{(correctionSession.lines ?? []).map((line) => <option key={line.id} value={line.id}>{line.purchaseOrderLineId ?? line.variationId} / received {line.receivedQuantity}</option>)}</select></label><label>Quantity delta<input type="number" value={correction.quantityDelta} onChange={(event) => setCorrection({ ...correction, quantityDelta: event.target.value })} /></label><label>Unit cost<input inputMode="decimal" value={correction.unitCost} onChange={(event) => setCorrection({ ...correction, unitCost: event.target.value })} /></label><label className="wide">Correction reason<textarea rows={2} value={correction.reason} onChange={(event) => setCorrection({ ...correction, reason: event.target.value })} /></label><button disabled={!canManage} onClick={() => void correctSession()}>Post correction</button></div>}</section>
              </>
            )}
          </section>
        </div>
      )}
    </section>
  );
}

function newVendorDraft(currency: string): VendorDraft {
  return { name: "", legalName: "", status: "active", orderingEmail: "", orderingPhone: "", orderingContactName: "", paymentTerms: "", defaultCurrency: currency, defaultShippingMethod: "", defaultBackorderPolicy: "allow", minimumOrderAmount: "", freeFreightThresholdAmount: "", notes: "", preferred: false };
}
function vendorToDraft(vendor: PurchasingVendor): VendorDraft {
  return { name: vendor.name, legalName: vendor.legalName ?? "", status: vendor.status, orderingEmail: vendor.orderingEmail ?? "", orderingPhone: vendor.orderingPhone ?? "", orderingContactName: vendor.orderingContactName ?? "", paymentTerms: vendor.paymentTerms ?? "", defaultCurrency: vendor.defaultCurrency, defaultShippingMethod: vendor.defaultShippingMethod ?? "", defaultBackorderPolicy: vendor.defaultBackorderPolicy, minimumOrderAmount: moneyToInput({ amount: vendor.minimumOrderAmount ?? 0, currency: vendor.defaultCurrency }), freeFreightThresholdAmount: moneyToInput({ amount: vendor.freeFreightThresholdAmount ?? 0, currency: vendor.defaultCurrency }), notes: vendor.notes ?? "", preferred: vendor.preferred };
}
function newVendorAccountDraft(currency: string): VendorAccountDraft {
  return { accountNumber: "", accountName: "", status: "active", orderingEmail: "", orderingPhone: "", orderingContactName: "", paymentTerms: "", currency, shippingMethod: "", backorderPolicy: "allow", minimumOrderAmount: "", freeFreightThresholdAmount: "", notes: "" };
}
function newVendorAddressDraft(): VendorAddressDraft {
  return { addressType: "ship_to", label: "", contactName: "", companyName: "", line1: "", line2: "", city: "", region: "", postalCode: "", country: "US", phone: "", email: "", defaultForType: false, active: true };
}
function newPartyIdentifierDraft(): PartyIdentifierDraft {
  return { identifierType: "san", identifierValue: "", qualifier: "", notes: "", active: true };
}
function newVendorProductDraft(currency: string): VendorProductDraft {
  return { vendorAccountId: "", editionId: "", variationId: "", isbn13: "", isbn10: "", upcEanGtin: "", internalSku: "", vendorSku: "", costAmount: "0.00", currency, discountBasisPoints: "", minimumOrderQuantity: "1", casePackQuantity: "1", returnability: "unknown", expectedLeadTimeDays: "", preferred: false, effectiveAt: "", expiresAt: "", notes: "", active: true };
}
function newPoDraft(currency: string, locationId = "loc-main"): PoDraft {
  return { vendorId: "", vendorAccountId: "", shipToLocationId: locationId, billToLocationId: locationId, poType: "manual", backorderPolicy: "allow", requestedShipDate: "", cancelIfNotFilledDate: "", doNotExceedCostAmount: "", currency, allowSubstitutions: false, fulfillmentPreference: "consolidate", externalReference: "", headerNotes: "", sourceDemand: [] };
}
function poToDraft(po: PurchasingPurchaseOrder): PoDraft {
  return { vendorId: po.vendorId, vendorAccountId: po.vendorAccountId ?? "", shipToLocationId: po.shipToLocationId, billToLocationId: po.billToLocationId ?? po.shipToLocationId, poType: po.poType, backorderPolicy: po.backorderPolicy, requestedShipDate: po.requestedShipDate?.slice(0, 10) ?? "", cancelIfNotFilledDate: po.cancelIfNotFilledDate?.slice(0, 10) ?? "", doNotExceedCostAmount: moneyToInput({ amount: po.doNotExceedCostAmount ?? 0, currency: po.currency }), currency: po.currency, allowSubstitutions: po.allowSubstitutions, fulfillmentPreference: po.fulfillmentPreference, externalReference: po.externalReference ?? "", headerNotes: po.headerNotes ?? "", sourceDemand: po.sourceDemand ?? [] };
}
function newPoLine(currency: string): PurchasingPoLine {
  return { id: crypto.randomUUID(), requestedQuantity: 1, expectedUnitCostAmount: 0, currency, backorderPolicy: "allow", titleSnapshot: "", sourceDemand: [] };
}
function newPoChangeDraft(): PoChangeDraft {
  return { changeType: "cancel_line", purchaseOrderLineId: "", requestedQuantity: "", requestedShipDate: "", cancelIfNotFilledDate: "", backorderPolicy: "", shippingInstructions: "", replacementEditionId: "", replacementVariationId: "", replacementIsbn13: "", replacementTitle: "", replacementCostAmount: "", reason: "", supplierTransmissionMethod: "email", externalReference: "" };
}
function defaultBackorderPolicy(): PurchasingBackorderPolicy {
  return { locationId: "loc-main", retainDays: 90, customerOrderPriority: true, autoCloseExpired: false, notifyOverdueAfterDays: 14 };
}
function defaultAsnFixture(): string {
  return JSON.stringify({
    providerId: "local-test-pubnet",
    environment: "test",
    rawPayloadText: "LOCAL TEST ASN - replace with authorized official sample text when available",
    externalDocumentId: "ASN-TEST-001",
    interchangeControlNumber: "CTRL-TEST-001",
    supplierShipmentNumber: "SHIP-TEST-001",
    vendorId: "vendor1",
    vendorAccountId: "account1",
    originWarehouse: "Supplier warehouse",
    destinationLocationId: "loc-main",
    carrier: "Manual carrier",
    shippingService: "Ground",
    trackingNumbers: ["TRACK-TEST-001"],
    estimatedShipDate: new Date().toISOString().slice(0, 10),
    estimatedDeliveryDate: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
    packingSlipNumber: "PACK-TEST-001",
    freightAmount: 0,
    currency: "USD",
    lines: [{ poNumber: "PO-000001", isbn13: "9780141439846", title: "Example title", shippedQuantity: 1, unitCostAmount: 1000, cartonBarcode: "CARTON-TEST-001" }],
  }, null, 2);
}
function defaultInvoiceFixture(): string {
  return JSON.stringify({
    providerId: "local-test-pubnet",
    environment: "test",
    rawPayloadText: "LOCAL TEST INVOICE - replace with authorized official 810 or partner invoice sample text when available",
    externalDocumentId: "INV-TEST-001",
    interchangeControlNumber: "CTRL-INV-001",
    invoiceNumber: "INV-TEST-001",
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    paymentTerms: "Net 30",
    vendorId: "vendor1",
    vendorAccountId: "account1",
    supplierAccountReference: "ACCT-TEST",
    currency: "USD",
    freightAmount: 0,
    taxAmount: 0,
    lines: [{ poNumber: "PO-000001", isbn13: "9780141439846", vendorSku: "V-DRAC-PB", description: "Example title", quantity: 1, unitCostAmount: 1000 }],
  }, null, 2);
}
function locationName(locations: { id: string; name: string; code?: string; active: boolean }[], id?: string | null): string {
  if (!id) return "-";
  const location = locations.find((entry) => entry.id === id);
  return location ? `${location.name}${location.code ? ` (${location.code})` : ""}` : id;
}
function poChangePayload(draft: PoChangeDraft): Record<string, unknown> {
  return stripEmptyStrings({
    changeType: draft.changeType,
    purchaseOrderLineId: draft.purchaseOrderLineId || undefined,
    requestedQuantity: optionalNumber(draft.requestedQuantity),
    requestedShipDate: draft.requestedShipDate || undefined,
    cancelIfNotFilledDate: draft.cancelIfNotFilledDate || undefined,
    backorderPolicy: draft.backorderPolicy || undefined,
    shippingInstructions: draft.shippingInstructions || undefined,
    replacementEditionId: draft.replacementEditionId || undefined,
    replacementVariationId: draft.replacementVariationId || undefined,
    replacementIsbn13: draft.replacementIsbn13 || undefined,
    replacementTitle: draft.replacementTitle || undefined,
    replacementCostAmount: moneyInputToOptionalCents(draft.replacementCostAmount),
    reason: draft.reason || undefined,
    supplierTransmissionMethod: draft.supplierTransmissionMethod,
    externalReference: draft.externalReference || undefined,
  });
}
function compactJson(value: Record<string, unknown> | undefined): string {
  if (!value) return "-";
  return Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== "").map(([key, entry]) => `${key}: ${String(entry)}`).join(" / ") || "-";
}
function optionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}
function moneyInputToOptionalCents(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined;
}
function stripEmptyStrings<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, entry === "" ? undefined : entry])) as T;
}
function poDraftPayload(draft: ReturnType<typeof newPoDraft>, lines: PurchasingPoLine[]) {
  const cleanLines = lines.filter((line) => line.titleSnapshot || line.isbn13 || line.isbn10 || line.upcEanGtin || line.internalSku || line.vendorSku || line.variationId).map((line) => stripEmptyStrings({
    id: line.id,
    vendorProductId: line.vendorProductId ?? undefined,
    editionId: line.editionId ?? undefined,
    variationId: line.variationId ?? undefined,
    isbn13: line.isbn13 ?? undefined,
    isbn10: line.isbn10 ?? undefined,
    upcEanGtin: line.upcEanGtin ?? undefined,
    internalSku: line.internalSku ?? undefined,
    vendorSku: line.vendorSku ?? undefined,
    titleSnapshot: line.titleSnapshot ?? undefined,
    requestedQuantity: Number(line.requestedQuantity || 1),
    expectedUnitCostAmount: Number(line.expectedUnitCostAmount || 0),
    currency: line.currency || draft.currency,
    backorderPolicy: line.backorderPolicy || draft.backorderPolicy,
    requestedShipDate: line.requestedShipDate ?? undefined,
    cancelIfNotFilledDate: line.cancelIfNotFilledDate ?? undefined,
    doNotExceedUnitCostAmount: line.doNotExceedUnitCostAmount ?? undefined,
    allowSubstitutions: line.allowSubstitutions ?? draft.allowSubstitutions,
    lineNotes: line.lineNotes ?? undefined,
    sourceDemand: line.sourceDemand ?? [],
  }));
  return stripEmptyStrings({ ...draft, vendorAccountId: draft.vendorAccountId || undefined, billToLocationId: draft.billToLocationId || undefined, requestedShipDate: draft.requestedShipDate || undefined, cancelIfNotFilledDate: draft.cancelIfNotFilledDate || undefined, doNotExceedCostAmount: moneyInputToOptionalCents(draft.doNotExceedCostAmount), externalReference: draft.externalReference || undefined, headerNotes: draft.headerNotes || undefined, lines: cleanLines.length ? cleanLines : [newPoLine(draft.currency)] });
}
function receivingPayload(po: PurchasingPurchaseOrder, lines: PurchasingReceivingLine[], locationId: string, meta: { shipmentReference: string; notes: string }) {
  return {
    locationId,
    shipmentReference: meta.shipmentReference || undefined,
    notes: meta.notes || undefined,
    lines: lines.filter((line) => line.receivedQuantity + line.damagedQuantity + line.shortageQuantity + line.overageQuantity > 0).map((line) => stripEmptyStrings({
      purchaseOrderLineId: line.purchaseOrderLineId ?? undefined,
      variationId: line.variationId ?? undefined,
      receivedQuantity: Number(line.receivedQuantity || 0),
      damagedQuantity: Number(line.damagedQuantity || 0),
      shortageQuantity: Number(line.shortageQuantity || 0),
      overageQuantity: Number(line.overageQuantity || 0),
      unexpected: line.unexpected,
      receivedUnitCostAmount: Number(line.receivedUnitCostAmount || 0),
      currency: line.currency || po.currency,
      customerRequestId: line.customerRequestId ?? undefined,
      notes: line.notes ?? undefined,
    })),
  };
}
function updatePoLine(setPoLines: React.Dispatch<React.SetStateAction<PurchasingPoLine[]>>, id: string, patch: Partial<PurchasingPoLine>) {
  setPoLines((current) => current.map((line) => line.id === id ? { ...line, ...patch } : line));
}
function firstSourceDemandId(line: PurchasingPoLine): string | null {
  const first = Array.isArray(line.sourceDemand) ? line.sourceDemand[0] : null;
  return first && typeof first === "object" && "id" in first ? String((first as { id?: unknown }).id ?? "") || null : null;
}
function vendorName(vendors: PurchasingVendor[], vendorId: string): string {
  return vendors.find((vendor) => vendor.id === vendorId)?.name ?? vendorId;
}
function providerCapabilitySummary(capabilities?: Record<string, unknown>): string {
  const enabled = Object.entries(capabilities ?? {})
    .filter(([, value]) => value === true)
    .map(([key]) => key.replace(/[A-Z]/g, (match) => ` ${match.toLowerCase()}`))
    .slice(0, 4);
  return enabled.length ? enabled.join(", ") : "No active capabilities";
}
function vendorThresholdLabel(vendor: PurchasingVendor): string {
  const parts = [];
  if (vendor.minimumOrderAmount) parts.push(`Min ${formatMoney({ amount: vendor.minimumOrderAmount, currency: vendor.defaultCurrency })}`);
  if (vendor.freeFreightThresholdAmount) parts.push(`Freight ${formatMoney({ amount: vendor.freeFreightThresholdAmount, currency: vendor.defaultCurrency })}`);
  return parts.join(" / ") || "-";
}
function incomingForIdentifier(purchaseOrders: PurchasingPurchaseOrder[], line: PurchasingPoLine): number {
  return purchaseOrders.flatMap((po) => po.lines ?? []).filter((entry) => entry.variationId === line.variationId || entry.isbn13 === line.isbn13 || entry.vendorSku === line.vendorSku).reduce((sum, entry) => sum + Math.max(0, entry.requestedQuantity - (entry.receivedQuantity ?? 0) - (entry.canceledQuantity ?? 0)), 0);
}
function incomingForItem(purchaseOrders: PurchasingPurchaseOrder[], item: InventoryItem): number {
  const ids = new Set([item.id, item.isbn, item.sku, ...item.variations.flatMap((variation) => [variation.id, variation.sku, variation.gtin])].filter(Boolean));
  return purchaseOrders.flatMap((po) => po.lines ?? []).filter((line) => ids.has(line.variationId ?? "") || ids.has(line.isbn13 ?? "") || ids.has(line.internalSku ?? "")).reduce((sum, line) => sum + Math.max(0, line.requestedQuantity - (line.receivedQuantity ?? 0) - (line.canceledQuantity ?? 0)), 0);
}

function OrderManagement() {
  const [orders, setOrders] = useState<{ id: string; channel: string; status: string; square_order_id?: string; customer_id?: string; total_amount: number; currency: string; recovery_state_json?: string; created_at: string }[]>([]);
  const [tracking, setTracking] = useState<Record<string, string>>({});
  const [toast, setToast] = useState("");
  const load = useCallback(() => { api<{ orders: typeof orders }>("/api/pos/orders").then((data) => setOrders(data.orders)); }, []);
  useEffect(() => { load(); }, [load]);
  const updateOrder = async (orderId: string, status: string) => {
    await api(`/api/pos/orders/${encodeURIComponent(orderId)}`, {
      method: "PATCH",
      body: JSON.stringify({ status, trackingNumber: tracking[orderId] || undefined }),
    });
    setToast(`Order marked ${status.replaceAll("_", " ")}`);
    load();
  };
  return (
    <section className="page">
      <header className="page-header"><div><p className="eyebrow">Operations</p><h1>Orders</h1></div></header>
      {toast && <div className="success">{toast}</div>}
      <div className="table-wrap"><table><thead><tr><th>Order</th><th>Channel</th><th>Status</th><th>Square</th><th>Customer</th><th>Total</th><th>Fulfillment</th><th>Created</th></tr></thead><tbody>{orders.map((order) => <tr key={order.id}><td>{order.id}</td><td>{order.channel}</td><td>{order.status}</td><td>{order.square_order_id ?? "-"}</td><td>{order.customer_id ?? "-"}</td><td>{formatMoney({ amount: order.total_amount, currency: order.currency })}</td><td><div className="order-actions"><select value={order.status} onChange={(event) => void updateOrder(order.id, event.target.value)}><option value="checkout_started">Checkout started</option><option value="paid">Paid</option><option value="processing">Processing</option><option value="ready_for_pickup">Ready for pickup</option><option value="shipped">Shipped</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="refunded">Refunded</option></select><input placeholder="Tracking" value={tracking[order.id] ?? ""} onChange={(event) => setTracking({ ...tracking, [order.id]: event.target.value })} /></div></td><td>{new Date(order.created_at).toLocaleString()}</td></tr>)}</tbody></table></div>
    </section>
  );
}

function ReportsPage() {
  type Report = {
    summary: Record<string, number | boolean>;
    comparison?: { label: string; startDate: string; endDate: string; grossSales: number; netSales: number; transactionCount: number; unitsSold: number; grossSalesDelta: number; netSalesDelta: number; transactionCountDelta: number };
    series: { label: string; grossSales: number; netSales: number; transactions: number; units: number; costOfGoodsSold?: number; grossMargin?: number }[];
    breakdowns?: {
      byHour: { label: string; grossSales: number; transactions: number; units: number }[];
      byWeekday: { label: string; grossSales: number; transactions: number; units: number }[];
      byChannel: { label: string; grossSales: number; transactions: number; units: number }[];
      byLocation: { label: string; grossSales: number; transactions: number; units: number }[];
      byRegister: { label: string; grossSales: number; transactions: number; units: number }[];
      byEmployee: { label: string; grossSales: number; transactions: number; units: number }[];
      byPaymentMethod: { label: string; grossSales: number; transactions: number; units: number }[];
    };
    inventoryAgingBuckets?: { label: string; count: number; valueAmount: number }[];
    inventoryExceptions?: { missingCost: number; missingMetadata: number; missingLocation: number; negativeStock: number };
    inventoryMovementSummary?: { adjustments: number; shrinkage: number; damage: number; losses: number };
    inventoryVelocity?: {
      deadInventory: { variationId: string; title: string; onHand: number; daysSinceReceived?: number; daysSinceLastSale?: number }[];
      fastMoving: { variationId: string; title: string; unitsSold: number; sellThroughBasisPoints: number; daysSinceReceived?: number; daysSinceLastSale?: number }[];
    };
    onlineAnalytics?: { searches: number; searchesWithNoResults: number; productViews: number; collectionViews: number; wishlistDemand: number };
    refundAnalytics?: {
      bySource: { label: string; amount: number; count: number }[];
      recentRefunds: { refundId: string; returnId: string; originalSaleId: string; originalSaleSource: string; amount: number; status: string; resolvedAt?: string }[];
    };
    loyaltyAnalytics?: {
      pointsEarned: number;
      pointsRedeemed: number;
      pointsExpired: number;
      pointsAdjusted: number;
      outstandingPoints: number;
      outstandingStoreCreditAmount: number;
      redemptionCount: number;
      appliedRedemptions: number;
      reversedRedemptions: number;
      issuedRedemptions: number;
      posRedemptionAmount: number;
      onlineRedemptionAmount: number;
      expiringPoints30Days: number;
      campaignPoints: number;
      manualAdjustmentCount: number;
    };
    customerAnalytics?: {
      identifiedTransactions: number;
      anonymousTransactions: number;
      averageCustomerSpend: number;
      averagePurchaseFrequencyBasisPoints: number;
      customerLifetimeValue: number;
      inactiveCustomers: number;
      retentionCohorts: { cohort: string; customers: number; returningCustomers: number; periodSpend: number }[];
      preferredCategories: { category: string; customerCount: number; unitsSold: number; revenue: number; sampleSize: number }[];
    };
    receivingVendorAnalytics?: {
      vendorName: string;
      batches: number;
      receivedUnits: number;
      receivedCost: number;
      soldUnits: number;
      salesRevenue: number;
      grossMargin: number;
      unsoldUnits: number;
      sellThroughBasisPoints: number;
      incompleteBatches: number;
      discrepancyBatches: number;
    }[];
    performanceBreakdowns?: {
      byAuthor: { label: string; unitsSold: number; revenue: number; grossMargin: number; sampleSize: number }[];
      byPublisher: { label: string; unitsSold: number; revenue: number; grossMargin: number; sampleSize: number }[];
      byCategory: { label: string; unitsSold: number; revenue: number; grossMargin: number; sampleSize: number }[];
      byCondition: { label: string; unitsSold: number; revenue: number; grossMargin: number; sampleSize: number }[];
      byNewUsed: { label: string; unitsSold: number; revenue: number; grossMargin: number; sampleSize: number }[];
    };
    topProducts: { variationId: string; title: string; unitsSold: number; revenue: number; grossMargin: number; inventoryOnHand: number; sellThroughBasisPoints?: number; daysSinceLastSale?: number }[];
    customerInsights: { displayName: string; transactionCount: number; totalSpend: number; lastPurchaseAt?: string; firstPurchaseAt?: string; priorTransactions?: number; purchaseFrequencyBasisPoints?: number }[];
    drilldowns?: {
      recentTransactions: { id: string; channel: "in_store" | "online"; label: string; completedAt: string; grossSales: number; netSales: number; units: number; customerId?: string }[];
      productLinks: { variationId: string; title: string; unitsSold: number; revenue: number; href: string }[];
    };
  };
  type ReportSource = {
    id: string;
    channel: "in_store" | "online";
    status: string;
    completedAt?: string;
    locationId?: string;
    registerId?: string;
    employeeId?: string;
    customerId?: string;
    subtotalAmount?: number;
    discountTotalAmount?: number;
    taxTotalAmount?: number;
    shippingAmount?: number;
    grandTotalAmount?: number;
    lines: { id: string; title_snapshot?: string; quantity: number; line_total_amount: number }[];
    payments: { provider?: string; tender_type?: string; status: string; captured_amount?: number; requested_amount?: number }[];
  };
  type ReportFilterOptions = {
    locations: { id: string; name: string; code?: string }[];
    registers: { id: string; name: string }[];
    employees: { id: string; name: string }[];
    paymentMethods: { id: string; name: string }[];
  };
  const initialReportParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [preset, setPreset] = useState(initialReportParams.get("preset") || "last_30_days");
  const [startDate, setStartDate] = useState(initialReportParams.get("startDate")?.slice(0, 10) || "");
  const [endDate, setEndDate] = useState(initialReportParams.get("endDate")?.slice(0, 10) || "");
  const [channel, setChannel] = useState(initialReportParams.get("channel") || "all");
  const [productType, setProductType] = useState(initialReportParams.get("productType") || "all");
  const [newUsed, setNewUsed] = useState(initialReportParams.get("newUsed") || "all");
  const [comparison, setComparison] = useState(initialReportParams.get("comparison") || "previous_equivalent_period");
  const [locationId, setLocationId] = useState(initialReportParams.get("locationId") || "");
  const [registerId, setRegisterId] = useState(initialReportParams.get("registerId") || "");
  const [employeeId, setEmployeeId] = useState(initialReportParams.get("employeeId") || "");
  const [condition, setCondition] = useState(initialReportParams.get("condition") || "");
  const [author, setAuthor] = useState(initialReportParams.get("author") || "");
  const [publisher, setPublisher] = useState(initialReportParams.get("publisher") || "");
  const [categoryId, setCategoryId] = useState(initialReportParams.get("categoryId") || "");
  const [supplierName, setSupplierName] = useState(initialReportParams.get("supplierName") || "");
  const [paymentMethod, setPaymentMethod] = useState(initialReportParams.get("paymentMethod") || "");
  const [taxStatus, setTaxStatus] = useState(initialReportParams.get("taxStatus") || "all");
  const [report, setReport] = useState<Report | null>(null);
  const [filterOptions, setFilterOptions] = useState<ReportFilterOptions>({ locations: [], registers: [], employees: [], paymentMethods: [] });
  const [source, setSource] = useState<ReportSource | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const params = useMemo(() => {
    const next = new URLSearchParams({ preset, channel, comparison, taxStatus, productType, newUsed });
    if (preset === "custom" && startDate) next.set("startDate", `${startDate}T00:00:00.000Z`);
    if (preset === "custom" && endDate) next.set("endDate", `${endDate}T23:59:59.999Z`);
    for (const [key, value] of Object.entries({ locationId, registerId, employeeId, condition, author, publisher, categoryId, supplierName, paymentMethod })) {
      if (value.trim()) next.set(key, value.trim());
    }
    return next;
  }, [author, categoryId, channel, comparison, condition, employeeId, endDate, locationId, newUsed, paymentMethod, preset, productType, publisher, registerId, startDate, supplierName, taxStatus]);
  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, "", nextUrl);
    api<{ report: Report }>(`/api/admin/reports/overview?${params}`)
      .then((data) => setReport(data.report))
      .catch((err) => setError(staffErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(load, [load]);
  useEffect(() => {
    api<{ filters: ReportFilterOptions }>("/api/admin/reports/filters")
      .then((data) => setFilterOptions(data.filters))
      .catch(() => undefined);
  }, []);
  const exportCsv = async () => {
    const csv = await apiText<string>(`/api/admin/reports/export?${params}`, { headers: { "idempotency-key": crypto.randomUUID() } });
    downloadText(csv, `paperbackos-report-${preset}.csv`, "text/csv");
  };
  const openSource = async (row: { id: string; channel: "in_store" | "online" }) => {
    setError("");
    const data = await api<{ source: ReportSource }>(`/api/admin/reports/source/${encodeURIComponent(row.id)}?channel=${row.channel}`);
    setSource(data.source);
  };
  const summary = report?.summary ?? {};
  return (
    <section className="page">
      <header className="page-header">
        <div><p className="eyebrow">Analytics</p><h1>Reports</h1></div>
        <div className="actions"><button onClick={exportCsv} disabled={!report}><Upload /> Export CSV</button><button onClick={() => window.print()} disabled={!report}>Print</button><button onClick={load}><RefreshCw /> Refresh</button></div>
      </header>
      <div className="toolbar compact-toolbar report-filter-bar">
        <label>Range<select value={preset} onChange={(event) => setPreset(event.target.value)}><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="last_7_days">Last 7 days</option><option value="last_30_days">Last 30 days</option><option value="this_month">This month</option><option value="last_month">Last month</option><option value="this_quarter">This quarter</option><option value="this_year">This year</option><option value="previous_year">Previous year</option><option value="custom">Custom</option></select></label>
        {preset === "custom" && <><label>Start<input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></label><label>End<input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} /></label></>}
        <label>Channel<select value={channel} onChange={(event) => setChannel(event.target.value)}><option value="all">All</option><option value="in_store">In-store</option><option value="online">Online</option></select></label>
        <label>Product type<select value={productType} onChange={(event) => setProductType(event.target.value)}><option value="all">All products</option><option value="book">Books</option><option value="movie">Movies</option><option value="music">Music</option><option value="game">Games</option><option value="merchandise">Merchandise</option><option value="other">Other</option></select></label>
        <label>New/used<select value={newUsed} onChange={(event) => setNewUsed(event.target.value)}><option value="all">New and used</option><option value="new">New</option><option value="used">Used</option></select></label>
        <label>Compare<select value={comparison} onChange={(event) => setComparison(event.target.value)}><option value="none">None</option><option value="previous_equivalent_period">Previous equivalent period</option><option value="previous_month">Previous month</option><option value="previous_year">Previous year</option></select></label>
        <label>Tax<select value={taxStatus} onChange={(event) => setTaxStatus(event.target.value)}><option value="all">All tax statuses</option><option value="taxed">Taxed sales</option><option value="untaxed">Untaxed sales</option></select></label>
        <label>Location<select value={locationId} onChange={(event) => setLocationId(event.target.value)}><option value="">All locations</option>{filterOptions.locations.map((location) => <option key={location.id} value={location.id}>{location.name}{location.code ? ` (${location.code})` : ""}</option>)}{locationId && !filterOptions.locations.some((location) => location.id === locationId) && <option value={locationId}>{locationId}</option>}</select></label>
        <label>Register<select value={registerId} onChange={(event) => setRegisterId(event.target.value)}><option value="">All registers</option>{filterOptions.registers.map((register) => <option key={register.id} value={register.id}>{register.name}</option>)}{registerId && !filterOptions.registers.some((register) => register.id === registerId) && <option value={registerId}>{registerId}</option>}</select></label>
        <label>Employee<select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}><option value="">All employees</option>{filterOptions.employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}{employeeId && !filterOptions.employees.some((employee) => employee.id === employeeId) && <option value={employeeId}>{employeeId}</option>}</select></label>
        <label>Condition<input value={condition} onChange={(event) => setCondition(event.target.value)} placeholder="new, good, fair" /></label>
        <label>Author<input value={author} onChange={(event) => setAuthor(event.target.value)} placeholder="Author" /></label>
        <label>Publisher<input value={publisher} onChange={(event) => setPublisher(event.target.value)} placeholder="Publisher" /></label>
        <label>Category<input value={categoryId} onChange={(event) => setCategoryId(event.target.value)} placeholder="Category/format" /></label>
        <label>Supplier<input value={supplierName} onChange={(event) => setSupplierName(event.target.value)} placeholder="Vendor or source" /></label>
        <label>Payment<select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)}><option value="">All payment methods</option>{filterOptions.paymentMethods.map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}{paymentMethod && !filterOptions.paymentMethods.some((method) => method.id === paymentMethod) && <option value={paymentMethod}>{paymentMethod}</option>}</select></label>
      </div>
      {error && <div className="error">{error}</div>}
      {loading && <Skeleton />}
      {report && <div className="phase-grid">
        <div className="metric-grid">
          <Metric label="Gross sales" value={formatMoney({ amount: Number(summary.grossSales ?? 0), currency: "USD" })} />
          <Metric label="Net sales" value={formatMoney({ amount: Number(summary.netSales ?? 0), currency: "USD" })} />
          <Metric label="Gross profit" value={formatMoney({ amount: Number(summary.grossProfit ?? 0), currency: "USD" })} />
          <Metric label="Cost coverage" value={`${(Number(summary.costCoverageBasisPoints ?? 0) / 100).toFixed(1)}%`} />
          <Metric label="Transactions" value={String(summary.transactionCount ?? 0)} />
          <Metric label="Units sold" value={String(summary.unitsSold ?? 0)} />
          <Metric label="Inventory cost value" value={formatMoney({ amount: Number(summary.inventoryCostValue ?? summary.inventoryValue ?? 0), currency: "USD" })} />
          <Metric label="Inventory retail value" value={formatMoney({ amount: Number(summary.inventoryRetailValue ?? 0), currency: "USD" })} />
          <Metric label="Low stock" value={String(summary.lowStockCount ?? 0)} />
          <Metric label="Aging inventory" value={String(summary.agingInventoryCount ?? 0)} />
          <Metric label="Loyalty redemptions" value={formatMoney({ amount: Number(summary.loyaltyRedemptions ?? 0), currency: "USD" })} />
          <Metric label="Credit liability" value={formatMoney({ amount: Number(summary.storeCreditLiability ?? 0), currency: "USD" })} />
        </div>
        {summary.estimatedCostNotice && <div className="warning">Margin includes estimated cost. Known COGS: {formatMoney({ amount: Number(summary.knownCostAmount ?? 0), currency: "USD" })}; estimated COGS: {formatMoney({ amount: Number(summary.estimatedCostAmount ?? 0), currency: "USD" })}; revenue without cost data: {formatMoney({ amount: Number(summary.missingCostRevenue ?? 0), currency: "USD" })}.</div>}
        {report.comparison && (
          <section className="panel">
            <h2>Comparison</h2>
            <div className="metric-grid">
              <Metric label="Gross delta" value={formatMoney({ amount: report.comparison.grossSalesDelta, currency: "USD" })} />
              <Metric label="Net delta" value={formatMoney({ amount: report.comparison.netSalesDelta, currency: "USD" })} />
              <Metric label="Transaction delta" value={String(report.comparison.transactionCountDelta)} />
              <Metric label="Previous units" value={String(report.comparison.unitsSold)} />
            </div>
          </section>
        )}
        <section className="panel">
          <h2>Sales by day</h2>
          <div className="report-bars">{report.series.map((row) => <div key={row.label}><span>{row.label}</span><meter min={0} max={Math.max(...report.series.map((entry) => entry.netSales), 1)} value={row.netSales} /><strong>{formatMoney({ amount: row.netSales, currency: "USD" })}</strong></div>)}</div>
          <DataTable empty="No margin data in this period." headers={["Date", "Net sales", "COGS", "Gross margin", "Transactions"]} rows={report.series.map((row) => [row.label, formatMoney({ amount: row.netSales, currency: "USD" }), formatMoney({ amount: row.costOfGoodsSold ?? 0, currency: "USD" }), formatMoney({ amount: row.grossMargin ?? row.netSales, currency: "USD" }), row.transactions])} />
        </section>
        <section className="panel">
          <h2>Sales breakdowns</h2>
          <DataTable empty="No hourly sales in this period." headers={["Hour", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byHour ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
          <DataTable empty="No weekday sales in this period." headers={["Weekday", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byWeekday ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
          <DataTable empty="No channel sales in this period." headers={["Channel", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byChannel ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
          <DataTable empty="No location sales in this period." headers={["Location", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byLocation ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
          <DataTable empty="No register sales in this period." headers={["Register", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byRegister ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
          <DataTable empty="No employee sales in this period." headers={["Employee", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byEmployee ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
          <DataTable empty="No payment-method data in this period." headers={["Payment", "Gross", "Transactions", "Units"]} rows={(report.breakdowns?.byPaymentMethod ?? []).map((row) => [row.label, formatMoney({ amount: row.grossSales, currency: "USD" }), row.transactions, row.units])} />
        </section>
        <section className="panel">
          <h2>Refunds and returns</h2>
          <DataTable empty="No refunds in this period." headers={["Source", "Refunded", "Count"]} rows={(report.refundAnalytics?.bySource ?? []).map((row) => [row.label, formatMoney({ amount: row.amount, currency: "USD" }), row.count])} />
          <DataTable empty="No source-linked refund detail in this period." headers={["Refund", "Return", "Original sale", "Source", "Amount", "Resolved"]} rows={(report.refundAnalytics?.recentRefunds ?? []).map((row) => [row.refundId, row.returnId, row.originalSaleId, row.originalSaleSource, formatMoney({ amount: row.amount, currency: "USD" }), row.resolvedAt ? new Date(row.resolvedAt).toLocaleString() : "-"])} />
        </section>
        <section className="panel">
          <h2>Inventory health</h2>
          <div className="metric-grid">
            <Metric label="Missing cost" value={String(report.inventoryExceptions?.missingCost ?? 0)} />
            <Metric label="Missing metadata" value={String(report.inventoryExceptions?.missingMetadata ?? 0)} />
            <Metric label="Missing location" value={String(report.inventoryExceptions?.missingLocation ?? 0)} />
            <Metric label="Negative stock" value={String(report.inventoryExceptions?.negativeStock ?? 0)} />
            <Metric label="Adjustments" value={String(report.inventoryMovementSummary?.adjustments ?? 0)} />
            <Metric label="Shrinkage" value={String(report.inventoryMovementSummary?.shrinkage ?? 0)} />
            <Metric label="Damage" value={String(report.inventoryMovementSummary?.damage ?? 0)} />
            <Metric label="Losses" value={String(report.inventoryMovementSummary?.losses ?? 0)} />
          </div>
          <DataTable empty="No available copy inventory." headers={["Age", "Copies", "Value"]} rows={(report.inventoryAgingBuckets ?? []).map((row) => [row.label, row.count, formatMoney({ amount: row.valueAmount, currency: "USD" })])} />
          <h3>Dead or slow-moving inventory</h3>
          <DataTable empty="No slow-moving inventory found." headers={["Title", "On hand", "Days since received", "Days since sale"]} rows={(report.inventoryVelocity?.deadInventory ?? []).map((row) => [row.title, row.onHand, row.daysSinceReceived ?? "-", row.daysSinceLastSale ?? "Never"])} />
          <h3>Fast-moving inventory</h3>
          <DataTable empty="No fast-moving inventory found." headers={["Title", "Units sold", "Sell-through", "Days since sale"]} rows={(report.inventoryVelocity?.fastMoving ?? []).map((row) => [row.title, row.unitsSold, `${(row.sellThroughBasisPoints / 100).toFixed(1)}%`, row.daysSinceLastSale ?? "-"])} />
        </section>
        <section className="panel">
          <h2>Online store activity</h2>
          <div className="metric-grid">
            <Metric label="Searches" value={String(report.onlineAnalytics?.searches ?? 0)} />
            <Metric label="No-result searches" value={String(report.onlineAnalytics?.searchesWithNoResults ?? 0)} />
            <Metric label="Product views" value={String(report.onlineAnalytics?.productViews ?? 0)} />
            <Metric label="Collection views" value={String(report.onlineAnalytics?.collectionViews ?? 0)} />
            <Metric label="Wishlist demand" value={String(report.onlineAnalytics?.wishlistDemand ?? 0)} />
          </div>
        </section>
        <section className="panel">
          <h2>Loyalty activity</h2>
          <div className="metric-grid">
            <Metric label="Points earned" value={String(report.loyaltyAnalytics?.pointsEarned ?? 0)} />
            <Metric label="Points redeemed" value={String(report.loyaltyAnalytics?.pointsRedeemed ?? 0)} />
            <Metric label="Points expired" value={String(report.loyaltyAnalytics?.pointsExpired ?? 0)} />
            <Metric label="Points adjusted" value={String(report.loyaltyAnalytics?.pointsAdjusted ?? 0)} />
            <Metric label="Outstanding points" value={String(report.loyaltyAnalytics?.outstandingPoints ?? 0)} />
            <Metric label="Expiring next 30 days" value={String(report.loyaltyAnalytics?.expiringPoints30Days ?? 0)} />
            <Metric label="Campaign points" value={String(report.loyaltyAnalytics?.campaignPoints ?? 0)} />
            <Metric label="Store-credit liability" value={formatMoney({ amount: report.loyaltyAnalytics?.outstandingStoreCreditAmount ?? 0, currency: "USD" })} />
          </div>
          <DataTable empty="No loyalty redemptions in this period." headers={["Status", "Count", "Value"]} rows={[
            ["Applied", report.loyaltyAnalytics?.appliedRedemptions ?? 0, formatMoney({ amount: (report.loyaltyAnalytics?.posRedemptionAmount ?? 0) + (report.loyaltyAnalytics?.onlineRedemptionAmount ?? 0), currency: "USD" })],
            ["Issued", report.loyaltyAnalytics?.issuedRedemptions ?? 0, "-"],
            ["Reversed", report.loyaltyAnalytics?.reversedRedemptions ?? 0, "-"],
            ["POS value", report.loyaltyAnalytics?.redemptionCount ?? 0, formatMoney({ amount: report.loyaltyAnalytics?.posRedemptionAmount ?? 0, currency: "USD" })],
            ["Online value", report.loyaltyAnalytics?.redemptionCount ?? 0, formatMoney({ amount: report.loyaltyAnalytics?.onlineRedemptionAmount ?? 0, currency: "USD" })],
          ]} />
        </section>
        <section className="panel">
          <h2>Top products</h2>
          <DataTable empty="No product sales in this period." headers={["Title", "Units", "Revenue", "Margin", "On hand", "Sell-through", "Days since sale"]} rows={report.topProducts.map((row) => [row.title, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), row.inventoryOnHand, row.sellThroughBasisPoints === undefined ? "-" : `${(row.sellThroughBasisPoints / 100).toFixed(1)}%`, row.daysSinceLastSale ?? "-"])} />
          <h3>Author performance</h3>
          <DataTable empty="No author performance in this period." headers={["Author", "Units", "Revenue", "Margin", "Sample"]} rows={(report.performanceBreakdowns?.byAuthor ?? []).map((row) => [row.label, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), lowSampleLabel(row.sampleSize)])} />
          <h3>Publisher performance</h3>
          <DataTable empty="No publisher performance in this period." headers={["Publisher", "Units", "Revenue", "Margin", "Sample"]} rows={(report.performanceBreakdowns?.byPublisher ?? []).map((row) => [row.label, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), lowSampleLabel(row.sampleSize)])} />
          <h3>Category and condition performance</h3>
          <DataTable empty="No category performance in this period." headers={["Category", "Units", "Revenue", "Margin", "Sample"]} rows={(report.performanceBreakdowns?.byCategory ?? []).map((row) => [row.label, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), lowSampleLabel(row.sampleSize)])} />
          <DataTable empty="No condition performance in this period." headers={["Condition", "Units", "Revenue", "Margin", "Sample"]} rows={(report.performanceBreakdowns?.byCondition ?? []).map((row) => [row.label, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), lowSampleLabel(row.sampleSize)])} />
          <DataTable empty="No new/used performance in this period." headers={["Type", "Units", "Revenue", "Margin", "Sample"]} rows={(report.performanceBreakdowns?.byNewUsed ?? []).map((row) => [row.label, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), lowSampleLabel(row.sampleSize)])} />
        </section>
        <section className="panel">
          <h2>Customer insights</h2>
          <div className="metric-grid">
            <Metric label="Identified transactions" value={String(report.customerAnalytics?.identifiedTransactions ?? 0)} />
            <Metric label="Anonymous transactions" value={String(report.customerAnalytics?.anonymousTransactions ?? 0)} />
            <Metric label="Average spend" value={formatMoney({ amount: report.customerAnalytics?.averageCustomerSpend ?? 0, currency: "USD" })} />
            <Metric label="Lifetime value" value={formatMoney({ amount: report.customerAnalytics?.customerLifetimeValue ?? 0, currency: "USD" })} />
            <Metric label="Inactive customers" value={String(report.customerAnalytics?.inactiveCustomers ?? 0)} />
          </div>
          <DataTable empty="No identified customer purchases in this period." headers={["Customer", "Transactions", "Spend", "First purchase", "Last purchase", "Prior transactions"]} rows={report.customerInsights.map((row) => [row.displayName, row.transactionCount, formatMoney({ amount: row.totalSpend, currency: "USD" }), row.firstPurchaseAt ? new Date(row.firstPurchaseAt).toLocaleDateString() : "-", row.lastPurchaseAt ? new Date(row.lastPurchaseAt).toLocaleDateString() : "-", row.priorTransactions ?? 0])} />
          <h3>Retention cohorts</h3>
          <DataTable empty="No cohort data in this period." headers={["First purchase cohort", "Customers", "Returning", "Period spend"]} rows={(report.customerAnalytics?.retentionCohorts ?? []).map((row) => [row.cohort, row.customers, row.returningCustomers, formatMoney({ amount: row.periodSpend, currency: "USD" })])} />
          <h3>Preferred categories</h3>
          <DataTable empty="No category preferences with enough identified transactions." headers={["Category", "Customers", "Units", "Revenue", "Sample"]} rows={(report.customerAnalytics?.preferredCategories ?? []).map((row) => [row.category, row.customerCount, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), lowSampleLabel(row.sampleSize)])} />
        </section>
        <section className="panel">
          <h2>Receiving and vendors</h2>
          <DataTable empty="No receiving activity in this period." headers={["Vendor", "Batches", "Received", "Cost", "Sold", "Revenue", "Margin", "Unsold", "Sell-through", "Exceptions"]} rows={(report.receivingVendorAnalytics ?? []).map((row) => [row.vendorName, row.batches, row.receivedUnits, formatMoney({ amount: row.receivedCost, currency: "USD" }), row.soldUnits, formatMoney({ amount: row.salesRevenue, currency: "USD" }), formatMoney({ amount: row.grossMargin, currency: "USD" }), row.unsoldUnits, `${(row.sellThroughBasisPoints / 100).toFixed(1)}%`, `${row.incompleteBatches} incomplete / ${row.discrepancyBatches} discrepancy`])} />
        </section>
        <section className="panel">
          <h2>Source records</h2>
          <DataTable empty="No source transactions in this period." headers={["Transaction", "Channel", "Completed", "Net", "Units", "Detail"]} rows={(report.drilldowns?.recentTransactions ?? []).map((row) => [row.label, row.channel === "online" ? "Online" : "In-store", new Date(row.completedAt).toLocaleString(), formatMoney({ amount: row.netSales, currency: "USD" }), row.units, <button onClick={() => void openSource(row)}>Open</button>])} />
          <DataTable empty="No product records in this period." headers={["Product", "Units", "Revenue", "Open"]} rows={(report.drilldowns?.productLinks ?? []).map((row) => [row.title, row.unitsSold, formatMoney({ amount: row.revenue, currency: "USD" }), <a className="button-link" href={row.href}>Item record</a>])} />
          {source && (
            <div className="report-detail-panel">
              <div className="section-heading"><div><p className="eyebrow">{source.channel === "online" ? "Online order" : "POS sale"}</p><h3>{source.id}</h3></div><button onClick={() => setSource(null)}>Close</button></div>
              <div className="metric-grid">
                <Metric label="Status" value={source.status} />
                <Metric label="Total" value={formatMoney({ amount: source.grandTotalAmount ?? 0, currency: "USD" })} />
                <Metric label="Discounts" value={formatMoney({ amount: source.discountTotalAmount ?? 0, currency: "USD" })} />
                <Metric label="Tax" value={formatMoney({ amount: source.taxTotalAmount ?? 0, currency: "USD" })} />
              </div>
              <DataTable empty="No lines found." headers={["Line", "Qty", "Amount"]} rows={(source.lines ?? []).map((line) => [line.title_snapshot ?? line.id, line.quantity, formatMoney({ amount: Number(line.line_total_amount ?? 0), currency: "USD" })])} />
              <DataTable empty="No payment records found." headers={["Provider", "Tender", "Status", "Amount"]} rows={(source.payments ?? []).map((payment) => [payment.provider ?? "-", payment.tender_type ?? "-", payment.status, formatMoney({ amount: Number(payment.captured_amount ?? payment.requested_amount ?? 0), currency: "USD" })])} />
            </div>
          )}
        </section>
      </div>}
    </section>
  );
}

function LoyaltyPage() {
  type Program = { enabled: boolean; name: string; description?: string; termsText?: string; pointsPerCurrencyAmount: number; currencyAmount: number; minimumPurchaseAmount: number; taxEarnsPoints: boolean; shippingEarnsPoints: boolean; giftCardPurchasesEarnPoints: boolean; giftCardRedemptionsEarnPoints: boolean; discountedItemsEarnPoints: boolean; usedBooksPointsMultiplierBasisPoints: number; newBooksPointsMultiplierBasisPoints: number; merchandiseEarnsPoints: boolean; refundReversesPoints: boolean; autoEnrollCustomers: boolean; pointsExpireAfterDays?: number };
  type Reward = { id?: string; name: string; rewardType: "store_credit" | "fixed_discount"; pointsCost: number; rewardAmount: number; currency: string; minimumPurchaseAmount: number; active: boolean };
  type Level = { id?: string; name: string; thresholdPoints: number; thresholdSpendAmount: number; benefitsText?: string; active: boolean; displayOrder: number };
  type Campaign = { id?: string; name: string; description?: string; campaignType: "bonus_points" | "points_multiplier" | "birthday_bonus" | "anniversary_bonus" | "buy_n_books"; enabled: boolean; startsAt: string; endsAt?: string; priority: number; stackable: boolean; minimumPurchaseAmount: number; bonusPoints?: number; pointsMultiplierBasisPoints?: number; eligibility: Record<string, unknown>; expirationDate?: string; rewardExpiresAfterDays?: number };
  const [program, setProgram] = useState<Program | null>(null);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [rewardDraft, setRewardDraft] = useState<Reward>({ name: "$5 store credit reward", rewardType: "store_credit", pointsCost: 500, rewardAmount: 500, currency: "USD", minimumPurchaseAmount: 0, active: true });
  const [levels, setLevels] = useState<Level[]>([]);
  const [levelDraft, setLevelDraft] = useState<Level>({ name: "Reader", thresholdPoints: 0, thresholdSpendAmount: 0, benefitsText: "", active: true, displayOrder: 10 });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignDraft, setCampaignDraft] = useState<Campaign>({ name: "Weekend bonus", campaignType: "bonus_points", enabled: true, startsAt: new Date().toISOString().slice(0, 16), priority: 10, stackable: false, minimumPurchaseAmount: 0, bonusPoints: 25, pointsMultiplierBasisPoints: 20000, eligibility: {} });
  const [customerId, setCustomerId] = useState("");
  const [pointsDelta, setPointsDelta] = useState(25);
  const [reason, setReason] = useState("");
  const [ledger, setLedger] = useState<{ customerId: string; entryType: string; pointsDelta: number; balanceAfter: number; reason?: string; createdAt: string }[]>([]);
  const [balance, setBalance] = useState<{ balance: number; lifetimeEarned: number; currentLevel?: string; availableRewards: { id: string; name: string; pointsCost: number }[] } | null>(null);
  const [toast, setToast] = useState("");
  const load = useCallback(() => {
    api<{ program: Program }>("/api/admin/loyalty/program").then((data) => setProgram(data.program));
    api<{ ledger: typeof ledger }>("/api/admin/loyalty/ledger").then((data) => setLedger(data.ledger));
    api<{ rewards: Reward[] }>("/api/admin/loyalty/rewards").then((data) => setRewards(data.rewards));
    api<{ levels: Level[] }>("/api/admin/loyalty/levels").then((data) => setLevels(data.levels));
    api<{ campaigns: Campaign[] }>("/api/admin/loyalty/campaigns").then((data) => setCampaigns(data.campaigns));
  }, []);
  useEffect(load, [load]);
  const saveProgram = async () => {
    if (!program) return;
    const data = await api<{ program: Program }>("/api/admin/loyalty/program", { method: "PUT", body: JSON.stringify(program) });
    setProgram(data.program);
    setToast("Loyalty program saved");
  };
  const lookup = async () => {
    if (!customerId) return;
    const data = await api<{ balance: typeof balance; ledger: typeof ledger }>(`/api/admin/loyalty/customers/${encodeURIComponent(customerId)}`);
    setBalance(data.balance);
    setLedger(data.ledger);
  };
  const enroll = async () => {
    const data = await api<{ balance: typeof balance }>("/api/admin/loyalty/enrollments", { method: "POST", body: JSON.stringify({ customerId, termsAccepted: true, source: "staff", idempotencyKey: crypto.randomUUID() }) });
    setBalance(data.balance);
    setToast("Customer enrolled");
    load();
  };
  const adjust = async () => {
    const data = await api<{ balance: typeof balance }>("/api/admin/loyalty/adjustments", { method: "POST", body: JSON.stringify({ customerId, pointsDelta, reason, idempotencyKey: crypto.randomUUID() }) });
    setBalance(data.balance);
    setReason("");
    setToast("Adjustment posted");
    await lookup();
  };
  const saveReward = async () => {
    const data = await api<{ reward: Reward }>("/api/admin/loyalty/rewards", { method: "POST", body: JSON.stringify(rewardDraft) });
    setRewards((current) => [data.reward, ...current.filter((reward) => reward.id !== data.reward.id)]);
    setRewardDraft({ name: "$5 store credit reward", rewardType: "store_credit", pointsCost: 500, rewardAmount: 500, currency: "USD", minimumPurchaseAmount: 0, active: true });
    setToast("Reward saved");
  };
  const editReward = (reward: Reward) => setRewardDraft({ ...reward });
  const saveLevel = async () => {
    const data = await api<{ level: Level }>("/api/admin/loyalty/levels", { method: "POST", body: JSON.stringify(levelDraft) });
    setLevels((current) => [data.level, ...current.filter((level) => level.id !== data.level.id)].sort((a, b) => a.displayOrder - b.displayOrder));
    setLevelDraft({ name: "Reader", thresholdPoints: 0, thresholdSpendAmount: 0, benefitsText: "", active: true, displayOrder: 10 });
    setToast("Level saved");
  };
  const editLevel = (level: Level) => setLevelDraft({ ...level });
  const saveCampaign = async () => {
    const body = { ...campaignDraft, startsAt: toIsoInput(campaignDraft.startsAt), endsAt: campaignDraft.endsAt ? toIsoInput(campaignDraft.endsAt) : undefined, expirationDate: campaignDraft.expirationDate ? toIsoInput(campaignDraft.expirationDate) : undefined };
    const data = await api<{ campaign: Campaign }>("/api/admin/loyalty/campaigns", { method: "POST", body: JSON.stringify(body) });
    setCampaigns((current) => [data.campaign, ...current.filter((campaign) => campaign.id !== data.campaign.id)]);
    setCampaignDraft({ name: "Weekend bonus", campaignType: "bonus_points", enabled: true, startsAt: new Date().toISOString().slice(0, 16), priority: 10, stackable: false, minimumPurchaseAmount: 0, bonusPoints: 25, pointsMultiplierBasisPoints: 20000, eligibility: {} });
    setToast("Campaign saved");
  };
  const editCampaign = (campaign: Campaign) => setCampaignDraft({ ...campaign, startsAt: campaign.startsAt.slice(0, 16), endsAt: campaign.endsAt?.slice(0, 16), expirationDate: campaign.expirationDate?.slice(0, 16) });
  return (
    <section className="page">
      <header className="page-header"><div><p className="eyebrow">Customers</p><h1>Loyalty</h1></div><button onClick={saveProgram} disabled={!program}><Save /> Save program</button></header>
      {toast && <div className="success">{toast}</div>}
      {program && <div className="split">
        <section className="panel">
          <h2>Program configuration</h2>
          <label className="check-row"><input type="checkbox" checked={program.enabled} onChange={(event) => setProgram({ ...program, enabled: event.target.checked })} /> Enabled</label>
          <label>Name<input value={program.name} onChange={(event) => setProgram({ ...program, name: event.target.value })} /></label>
          <label>Description<textarea value={program.description ?? ""} onChange={(event) => setProgram({ ...program, description: event.target.value })} /></label>
          <div className="form-grid"><label>Points earned<input type="number" value={program.pointsPerCurrencyAmount} onChange={(event) => setProgram({ ...program, pointsPerCurrencyAmount: Number(event.target.value) })} /></label><label>Per dollars spent<input inputMode="decimal" value={moneyToInput({ amount: program.currencyAmount, currency: "USD" })} onChange={(event) => setProgram({ ...program, currencyAmount: inputToMoney(event.target.value, "USD").amount })} /></label><label>Minimum purchase<input inputMode="decimal" value={moneyToInput({ amount: program.minimumPurchaseAmount, currency: "USD" })} onChange={(event) => setProgram({ ...program, minimumPurchaseAmount: inputToMoney(event.target.value, "USD").amount })} /></label></div>
          <label className="check-row"><input type="checkbox" checked={program.taxEarnsPoints} onChange={(event) => setProgram({ ...program, taxEarnsPoints: event.target.checked })} /> Tax earns points</label>
          <label className="check-row"><input type="checkbox" checked={program.discountedItemsEarnPoints} onChange={(event) => setProgram({ ...program, discountedItemsEarnPoints: event.target.checked })} /> Discounted items earn points</label>
          <label className="check-row"><input type="checkbox" checked={program.merchandiseEarnsPoints} onChange={(event) => setProgram({ ...program, merchandiseEarnsPoints: event.target.checked })} /> Merchandise earns points</label>
          <label className="check-row"><input type="checkbox" checked={program.giftCardPurchasesEarnPoints} onChange={(event) => setProgram({ ...program, giftCardPurchasesEarnPoints: event.target.checked })} /> Gift-card purchases earn points</label>
          <div className="form-grid"><label>New book multiplier bps<input type="number" value={program.newBooksPointsMultiplierBasisPoints} onChange={(event) => setProgram({ ...program, newBooksPointsMultiplierBasisPoints: Number(event.target.value) })} /></label><label>Used book multiplier bps<input type="number" value={program.usedBooksPointsMultiplierBasisPoints} onChange={(event) => setProgram({ ...program, usedBooksPointsMultiplierBasisPoints: Number(event.target.value) })} /></label></div>
          <label className="check-row"><input type="checkbox" checked={program.autoEnrollCustomers} onChange={(event) => setProgram({ ...program, autoEnrollCustomers: event.target.checked })} /> Auto-enroll checkout customers</label>
          <label>Terms<textarea value={program.termsText ?? ""} onChange={(event) => setProgram({ ...program, termsText: event.target.value })} /></label>
        </section>
        <section className="panel">
          <h2>Customer balance</h2>
          <div className="actions"><input placeholder="Customer ID" value={customerId} onChange={(event) => setCustomerId(event.target.value)} /><button onClick={lookup}><Search /> Lookup</button><button onClick={enroll} disabled={!customerId}>Enroll</button></div>
          {balance && <div className="metric-grid"><Metric label="Balance" value={`${balance.balance} pts`} /><Metric label="Lifetime earned" value={`${balance.lifetimeEarned} pts`} /><Metric label="Level" value={balance.currentLevel ?? "Reader"} /></div>}
          <div className="form-grid"><label>Adjustment<input type="number" value={pointsDelta} onChange={(event) => setPointsDelta(Number(event.target.value))} /></label><label className="wide">Reason<input value={reason} onChange={(event) => setReason(event.target.value)} /></label><button className="primary" onClick={adjust} disabled={!customerId || !reason}>Post adjustment</button></div>
        </section>
      </div>}
      <div className="split">
        <section className="panel">
          <h2>Rewards</h2>
          <div className="form-grid">
            <label className="wide">Name<input value={rewardDraft.name} onChange={(event) => setRewardDraft({ ...rewardDraft, name: event.target.value })} /></label>
            <label>Type<select value={rewardDraft.rewardType} onChange={(event) => setRewardDraft({ ...rewardDraft, rewardType: event.target.value as Reward["rewardType"] })}><option value="store_credit">Store credit conversion</option><option value="fixed_discount">Checkout discount</option></select></label>
            <label>Points<input type="number" value={rewardDraft.pointsCost} onChange={(event) => setRewardDraft({ ...rewardDraft, pointsCost: Number(event.target.value) })} /></label>
            <label>Reward amount<input inputMode="decimal" value={moneyToInput({ amount: rewardDraft.rewardAmount, currency: rewardDraft.currency })} onChange={(event) => setRewardDraft({ ...rewardDraft, rewardAmount: inputToMoney(event.target.value, rewardDraft.currency).amount })} /></label>
            <label>Minimum purchase<input inputMode="decimal" value={moneyToInput({ amount: rewardDraft.minimumPurchaseAmount, currency: rewardDraft.currency })} onChange={(event) => setRewardDraft({ ...rewardDraft, minimumPurchaseAmount: inputToMoney(event.target.value, rewardDraft.currency).amount })} /></label>
            <label className="check-row"><input type="checkbox" checked={rewardDraft.active} onChange={(event) => setRewardDraft({ ...rewardDraft, active: event.target.checked })} /> Active</label>
            <button className="primary" onClick={saveReward}><Save /> Save reward</button>
          </div>
          <DataTable empty="No rewards configured." headers={["Name", "Type", "Points", "Value", "Status", ""]} rows={rewards.map((reward) => [reward.name, reward.rewardType, reward.pointsCost, formatMoney({ amount: reward.rewardAmount, currency: reward.currency }), reward.active ? "Active" : "Inactive", <button key={reward.id} onClick={() => editReward(reward)}>Edit</button>])} />
        </section>
        <section className="panel">
          <h2>Levels</h2>
          <div className="form-grid">
            <label>Name<input value={levelDraft.name} onChange={(event) => setLevelDraft({ ...levelDraft, name: event.target.value })} /></label>
            <label>Threshold points<input type="number" value={levelDraft.thresholdPoints} onChange={(event) => setLevelDraft({ ...levelDraft, thresholdPoints: Number(event.target.value) })} /></label>
            <label>Threshold spend<input inputMode="decimal" value={moneyToInput({ amount: levelDraft.thresholdSpendAmount, currency: "USD" })} onChange={(event) => setLevelDraft({ ...levelDraft, thresholdSpendAmount: inputToMoney(event.target.value, "USD").amount })} /></label>
            <label>Order<input type="number" value={levelDraft.displayOrder} onChange={(event) => setLevelDraft({ ...levelDraft, displayOrder: Number(event.target.value) })} /></label>
            <label className="check-row"><input type="checkbox" checked={levelDraft.active} onChange={(event) => setLevelDraft({ ...levelDraft, active: event.target.checked })} /> Active</label>
            <label className="wide">Benefits shown to staff and customers<textarea rows={3} value={levelDraft.benefitsText ?? ""} onChange={(event) => setLevelDraft({ ...levelDraft, benefitsText: event.target.value })} placeholder="Example: early event access, birthday bonus, or no configured benefits" /></label>
            <button className="primary" onClick={saveLevel}><Save /> Save level</button>
          </div>
          <DataTable empty="No loyalty levels configured." headers={["Name", "Points", "Spend", "Benefits", "Status", ""]} rows={levels.map((level) => [level.name, level.thresholdPoints, formatMoney({ amount: level.thresholdSpendAmount, currency: "USD" }), level.benefitsText || "No configured benefits", level.active ? "Active" : "Disabled", <button key={level.id} onClick={() => editLevel(level)}>Edit</button>])} />
        </section>
        <section className="panel">
          <h2>Campaigns</h2>
          <div className="form-grid">
            <label className="wide">Name<input value={campaignDraft.name} onChange={(event) => setCampaignDraft({ ...campaignDraft, name: event.target.value })} /></label>
            <label>Type<select value={campaignDraft.campaignType} onChange={(event) => setCampaignDraft({ ...campaignDraft, campaignType: event.target.value as Campaign["campaignType"], eligibility: event.target.value === "buy_n_books" ? { ...campaignDraft.eligibility, requiredQuantity: Number(campaignDraft.eligibility.requiredQuantity ?? 5) } : campaignDraft.eligibility })}><option value="bonus_points">Bonus points</option><option value="points_multiplier">Points multiplier</option><option value="birthday_bonus">Birthday bonus</option><option value="anniversary_bonus">Anniversary bonus</option><option value="buy_n_books">Buy books, earn reward</option></select></label>
            <label>Starts<input type="datetime-local" value={campaignDraft.startsAt} onChange={(event) => setCampaignDraft({ ...campaignDraft, startsAt: event.target.value })} /></label>
            <label>Ends<input type="datetime-local" value={campaignDraft.endsAt ?? ""} onChange={(event) => setCampaignDraft({ ...campaignDraft, endsAt: event.target.value || undefined })} /></label>
            <label>Bonus points<input type="number" value={campaignDraft.bonusPoints ?? 0} onChange={(event) => setCampaignDraft({ ...campaignDraft, bonusPoints: Number(event.target.value) })} /></label>
            <label>Multiplier bps<input type="number" value={campaignDraft.pointsMultiplierBasisPoints ?? 10000} onChange={(event) => setCampaignDraft({ ...campaignDraft, pointsMultiplierBasisPoints: Number(event.target.value) })} /></label>
            <label>Minimum purchase<input inputMode="decimal" value={moneyToInput({ amount: campaignDraft.minimumPurchaseAmount, currency: "USD" })} onChange={(event) => setCampaignDraft({ ...campaignDraft, minimumPurchaseAmount: inputToMoney(event.target.value, "USD").amount })} /></label>
            <label>Priority<input type="number" value={campaignDraft.priority} onChange={(event) => setCampaignDraft({ ...campaignDraft, priority: Number(event.target.value) })} /></label>
            <label>Reward expiration days<input type="number" value={campaignDraft.rewardExpiresAfterDays ?? ""} onChange={(event) => setCampaignDraft({ ...campaignDraft, rewardExpiresAfterDays: event.target.value ? Number(event.target.value) : undefined })} /></label>
            {campaignDraft.campaignType === "buy_n_books" && <label>Qualifying books<input type="number" min={1} value={Number(campaignDraft.eligibility.requiredQuantity ?? 5)} onChange={(event) => setCampaignDraft({ ...campaignDraft, eligibility: { ...campaignDraft.eligibility, requiredQuantity: Number(event.target.value) } })} /></label>}
            <label>New or used<select value={String(campaignDraft.eligibility.newUsed ?? "all")} onChange={(event) => setCampaignDraft({ ...campaignDraft, eligibility: { ...campaignDraft.eligibility, newUsed: event.target.value === "all" ? undefined : event.target.value } })}><option value="all">Any</option><option value="new">New books</option><option value="used">Used books</option></select></label>
            <label className="wide">Category or format filters<input value={Array.isArray(campaignDraft.eligibility.categoryIds) ? campaignDraft.eligibility.categoryIds.join(", ") : ""} onChange={(event) => setCampaignDraft({ ...campaignDraft, eligibility: { ...campaignDraft.eligibility, categoryIds: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) } })} placeholder="Mystery, hardcover, signed" /></label>
            <label className="wide">Specific product variation IDs<input value={Array.isArray(campaignDraft.eligibility.productIds) ? campaignDraft.eligibility.productIds.join(", ") : ""} onChange={(event) => setCampaignDraft({ ...campaignDraft, eligibility: { ...campaignDraft.eligibility, productIds: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) } })} /></label>
            <label className="check-row"><input type="checkbox" checked={campaignDraft.stackable} onChange={(event) => setCampaignDraft({ ...campaignDraft, stackable: event.target.checked })} /> Stack with other campaigns</label>
            <label className="check-row"><input type="checkbox" checked={campaignDraft.enabled} onChange={(event) => setCampaignDraft({ ...campaignDraft, enabled: event.target.checked })} /> Enabled</label>
            <button className="primary" onClick={saveCampaign}><Save /> Save campaign</button>
          </div>
          <DataTable empty="No campaigns configured." headers={["Name", "Type", "Window", "Rule", "Status", ""]} rows={campaigns.map((campaign) => [campaign.name, campaign.campaignType, `${new Date(campaign.startsAt).toLocaleDateString()} - ${campaign.endsAt ? new Date(campaign.endsAt).toLocaleDateString() : "open"}`, campaign.campaignType === "points_multiplier" ? `${(Number(campaign.pointsMultiplierBasisPoints ?? 10000) / 10000).toFixed(2)}x` : `${campaign.bonusPoints ?? 0} pts`, campaign.enabled ? "Enabled" : "Paused", <button key={campaign.id} onClick={() => editCampaign(campaign)}>Edit</button>])} />
        </section>
      </div>
      <section className="panel">
        <h2>Ledger</h2>
        <DataTable empty="No loyalty activity yet." headers={["Customer", "Type", "Points", "Balance", "Reason", "Created"]} rows={ledger.map((row) => [row.customerId, row.entryType, row.pointsDelta, row.balanceAfter, row.reason ?? "-", new Date(row.createdAt).toLocaleString()])} />
      </section>
    </section>
  );
}

function StorefrontAdminPage() {
  type Settings = { enabled: boolean; displayName: string; pickupLocationId: string; contactEmail?: string; contactPhone?: string; pickupInstructions?: string; announcementText?: string | null; announcementEnabled?: boolean; homepageSections?: string[]; themePreset?: "bookshop" | "modern" | "classic" | "bright"; colorPrimary?: string | null; colorAccent?: string | null; colorBackground?: string | null; colorSurface?: string | null; colorText?: string | null; heroStyle?: "immersive" | "compact" | "minimal"; catalogLayout?: "grid" | "list"; gridDensity?: "compact" | "balanced" | "spacious"; cardStyle?: "detailed" | "cover" | "compact"; headingFont?: PublicStoreProfile["headingFont"]; bodyFont?: PublicStoreProfile["bodyFont"]; sectionLayouts?: Record<string, "grid" | "shelf" | "list">; reservationDurationMinutes: number; reconciliationHoldMinutes: number; pickupPolicyText?: string; shippingPolicyText?: string; returnPolicyText?: string; privacyPolicyText?: string; termsPolicyText?: string; shippingEnabled?: boolean; flatRateShippingAmount?: number; freeShippingThresholdAmount?: number | null; handlingTimeDays?: number; usedBooksShippable?: boolean };
  type PublishingItem = { variation_id: string; square_item_id?: string; visible_online: number; pickup_enabled: number; shipping_enabled: number; featured: number; title?: string; contributors?: string; format_name?: string; default_selling_price_amount?: number; currency?: string; available_quantity?: number };
  type BatchToggle = "no_change" | "enabled" | "disabled";
  type PublishingBatchDraft = { scope: "selected" | "filtered"; visibleOnline: BatchToggle; pickupEnabled: BatchToggle; shippingEnabled: BatchToggle; featured: BatchToggle };
  const [settings, setSettings] = useState<Settings | null>(null);
  const [collections, setCollections] = useState<{ id: string; name: string; slug: string; publicStatus: string; featured: boolean }[]>([]);
  const [staffPicks, setStaffPicks] = useState<{ variationId: string; staffDisplayName: string; recommendationText: string; featured: boolean }[]>([]);
  const [publishingItems, setPublishingItems] = useState<PublishingItem[]>([]);
  const [publishingFilter, setPublishingFilter] = useState("");
  const [selectedPublishingIds, setSelectedPublishingIds] = useState<string[]>([]);
  const [publishingBatchDraft, setPublishingBatchDraft] = useState<PublishingBatchDraft>({ scope: "selected", visibleOnline: "no_change", pickupEnabled: "no_change", shippingEnabled: "no_change", featured: "no_change" });
  const [publishingBatchPending, setPublishingBatchPending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewViewport, setPreviewViewport] = useState<"desktop" | "mobile">("desktop");
  const [collectionDraft, setCollectionDraft] = useState({ name: "", slug: "", description: "", variationIds: "", startsAt: "", endsAt: "", ruleCategoryId: "", ruleAuthor: "", rulePublisher: "", ruleCondition: "", ruleSignedOnly: false });
  const [pickDraft, setPickDraft] = useState({ variationId: "", staffDisplayName: "", recommendationText: "" });
  const [toast, setToast] = useState("");
  const load = useCallback(() => {
    api<{ settings: Settings; collections: typeof collections; staffPicks: typeof staffPicks }>("/api/admin/storefront/settings").then((data) => { setSettings(data.settings); setCollections(data.collections); setStaffPicks(data.staffPicks); });
    api<{ visibility: PublishingItem[] }>("/api/admin/storefront/visibility").then((data) => setPublishingItems(data.visibility));
  }, []);
  useEffect(load, [load]);
  const saveSettings = async () => {
    if (!settings) return;
    await api("/api/admin/storefront/settings", { method: "PUT", body: JSON.stringify(settings) });
    setToast("Storefront settings saved");
    load();
  };
  const saveCollectionDraft = async () => {
    const rule = {
      categoryId: collectionDraft.ruleCategoryId || undefined,
      author: collectionDraft.ruleAuthor || undefined,
      publisher: collectionDraft.rulePublisher || undefined,
      condition: collectionDraft.ruleCondition || undefined,
      signedOnly: collectionDraft.ruleSignedOnly || undefined,
      limit: 12,
    };
    await api("/api/admin/storefront/collections", { method: "POST", body: JSON.stringify({ name: collectionDraft.name, slug: collectionDraft.slug, description: collectionDraft.description, startsAt: collectionDraft.startsAt ? toIsoInput(collectionDraft.startsAt) : undefined, endsAt: collectionDraft.endsAt ? toIsoInput(collectionDraft.endsAt) : undefined, rule, publicStatus: "public", featured: true, sortOrder: 0, variationIds: collectionDraft.variationIds.split(",").map((value) => value.trim()).filter(Boolean), idempotencyKey: crypto.randomUUID() }) });
    setCollectionDraft({ name: "", slug: "", description: "", variationIds: "", startsAt: "", endsAt: "", ruleCategoryId: "", ruleAuthor: "", rulePublisher: "", ruleCondition: "", ruleSignedOnly: false });
    setToast("Collection saved");
    load();
  };
  const savePick = async () => {
    await api("/api/admin/storefront/staff-picks", { method: "POST", body: JSON.stringify({ ...pickDraft, featured: true, publicStatus: "public", idempotencyKey: crypto.randomUUID() }) });
    setPickDraft({ variationId: "", staffDisplayName: "", recommendationText: "" });
    setToast("Staff pick saved");
    load();
  };
  const savePublishing = async (item: PublishingItem, patch: Partial<PublishingItem>) => {
    const next = { ...item, ...patch };
    await api("/api/admin/storefront/visibility", {
      method: "PUT",
      body: JSON.stringify({
        variationId: next.variation_id,
        squareItemId: next.square_item_id || next.variation_id,
        visibleOnline: Boolean(next.visible_online),
        pickupEnabled: Boolean(next.pickup_enabled),
        shippingEnabled: Boolean(next.shipping_enabled),
        featured: Boolean(next.featured),
      }),
    });
    setPublishingItems((current) => current.map((row) => row.variation_id === item.variation_id ? next : row));
    setToast("Publishing settings saved");
  };
  const togglePublishingSelection = (variationId: string, selected: boolean) => {
    setSelectedPublishingIds((current) => selected ? [...new Set([...current, variationId])] : current.filter((id) => id !== variationId));
  };
  const batchValue = (value: BatchToggle): boolean | undefined => value === "no_change" ? undefined : value === "enabled";
  const batchPatch = (): Partial<PublishingItem> => ({
    ...(publishingBatchDraft.visibleOnline === "no_change" ? {} : { visible_online: publishingBatchDraft.visibleOnline === "enabled" ? 1 : 0 }),
    ...(publishingBatchDraft.pickupEnabled === "no_change" ? {} : { pickup_enabled: publishingBatchDraft.pickupEnabled === "enabled" ? 1 : 0 }),
    ...(publishingBatchDraft.shippingEnabled === "no_change" ? {} : { shipping_enabled: publishingBatchDraft.shippingEnabled === "enabled" ? 1 : 0 }),
    ...(publishingBatchDraft.featured === "no_change" ? {} : { featured: publishingBatchDraft.featured === "enabled" ? 1 : 0 }),
  });
  const applyPublishingBatch = async () => {
    const ids = publishingBatchDraft.scope === "filtered" ? filteredPublishingItems.map((item) => item.variation_id) : selectedPublishingIds;
    const uniqueIds = [...new Set(ids)];
    const patch = batchPatch();
    if (!uniqueIds.length || Object.keys(patch).length === 0) return;
    setPublishingBatchPending(true);
    try {
      const result = await api<{ updated: number; requested: number }>("/api/admin/storefront/visibility/batch", {
        method: "PUT",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          variationIds: uniqueIds,
          visibleOnline: batchValue(publishingBatchDraft.visibleOnline),
          pickupEnabled: batchValue(publishingBatchDraft.pickupEnabled),
          shippingEnabled: batchValue(publishingBatchDraft.shippingEnabled),
          featured: batchValue(publishingBatchDraft.featured),
        }),
      });
      setPublishingItems((current) => current.map((row) => uniqueIds.includes(row.variation_id) ? { ...row, ...patch } : row));
      setToast(`Updated ${result.updated} of ${result.requested} publishing item(s)`);
    } finally {
      setPublishingBatchPending(false);
    }
  };
  const moveHomepageSection = (index: number, direction: -1 | 1) => {
    if (!settings) return;
    const sections = [...(settings.homepageSections ?? ["collections", "staff_picks", "catalog"])];
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const [section] = sections.splice(index, 1);
    sections.splice(target, 0, section);
    setSettings({ ...settings, homepageSections: sections });
  };
  const homeSectionLabels: Record<string, string> = { collections: "Featured collections", staff_picks: "Staff picks", popular: "Popular books", genre_shelves: "Genre shelves", signed: "Signed copies", catalog: "Catalog browsing", store_info: "Store information", loyalty: "Loyalty signup" };
  const homeSectionKeys = Object.keys(homeSectionLabels);
  const toggleHomepageSection = (section: string, visible: boolean) => {
    if (!settings) return;
    const current = settings.homepageSections ?? ["collections", "staff_picks", "popular", "genre_shelves", "signed", "catalog"];
    setSettings({ ...settings, homepageSections: visible ? [...current, section] : current.filter((entry) => entry !== section) });
  };
  const updateSectionLayout = (section: string, layout: "grid" | "shelf" | "list") => {
    if (!settings) return;
    setSettings({ ...settings, sectionLayouts: { ...(settings.sectionLayouts ?? {}), [section]: layout } });
  };
  const filteredPublishingItems = publishingItems.filter((item) => {
    const query = publishingFilter.trim().toLowerCase();
    if (!query) return true;
    return [item.title, item.contributors, item.format_name, item.variation_id].some((value) => String(value ?? "").toLowerCase().includes(query));
  });
  const visiblePublishingItems = filteredPublishingItems.slice(0, 100);
  const visibleSelectedCount = visiblePublishingItems.filter((item) => selectedPublishingIds.includes(item.variation_id)).length;
  const batchTargetCount = publishingBatchDraft.scope === "filtered" ? filteredPublishingItems.length : selectedPublishingIds.length;
  const batchHasChanges = Object.keys(batchPatch()).length > 0;
  const previewProfile: PublicStoreProfile | null = settings ? {
    productName: "PaperbackOS",
    storeDisplayName: settings.displayName || "Independent Bookstore",
    storeTagline: "Independent books, thoughtfully selected",
    storeDescription: "Browse new arrivals, staff favorites, and books waiting to be discovered.",
    logoUrl: null,
    publicWebsiteUrl: null,
    publicEmail: settings.contactEmail ?? null,
    publicPhone: settings.contactPhone ?? null,
    addressLine1: null,
    addressLine2: null,
    city: null,
    region: null,
    postalCode: null,
    country: null,
    socialLinks: {},
    businessHours: [],
    timezone: null,
    currencyCode: "USD",
    receiptFooterText: null,
    storefrontTitle: settings.displayName || "Independent Bookstore",
    storefrontDescription: "Browse available items for pickup or shipping.",
    announcementText: settings.announcementText,
    announcementEnabled: settings.announcementEnabled,
    homepageSections: settings.homepageSections,
    themePreset: settings.themePreset,
    colorPrimary: settings.colorPrimary,
    colorAccent: settings.colorAccent,
    colorBackground: settings.colorBackground,
    colorSurface: settings.colorSurface,
    colorText: settings.colorText,
    heroStyle: settings.heroStyle,
    catalogLayout: settings.catalogLayout,
    gridDensity: settings.gridDensity,
    cardStyle: settings.cardStyle,
    headingFont: settings.headingFont,
    bodyFont: settings.bodyFont,
    sectionLayouts: settings.sectionLayouts,
  } : null;
  const previewSections = settings?.homepageSections ?? ["collections", "staff_picks", "popular", "genre_shelves", "signed", "catalog"];
  const previewProducts = publishingItems.filter((item) => item.visible_online).slice(0, 4);
  const samplePreviewProducts = previewProducts.length ? previewProducts : [
    { variation_id: "preview-1", title: "A Bookshop Favorite", contributors: "Morgan Reed", format_name: "Paperback", default_selling_price_amount: 1299 },
    { variation_id: "preview-2", title: "The Last Chapter", contributors: "Jamie Wells", format_name: "Hardcover", default_selling_price_amount: 2400 },
    { variation_id: "preview-3", title: "Stories After Dark", contributors: "Alex Rowan", format_name: "Used paperback", default_selling_price_amount: 750 },
  ];
  const BatchPublishingSelect = ({ label, value, onChange }: { label: string; value: BatchToggle; onChange: (value: BatchToggle) => void }) => (
    <label>{label}
      <select value={value} onChange={(event) => onChange(event.target.value as BatchToggle)}>
        <option value="no_change">No change</option>
        <option value="enabled">Enable</option>
        <option value="disabled">Disable</option>
      </select>
    </label>
  );
  return (
    <section className="page">
      <header className="page-header"><div><p className="eyebrow">Public store</p><h1>Storefront</h1></div><button onClick={saveSettings} disabled={!settings}><Save /> Save settings</button></header>
      {toast && <div className="success">{toast}</div>}
      {settings && <section className="panel">
        <h2>Configuration</h2>
        <label className="check-row"><input type="checkbox" checked={settings.enabled} onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })} /> Storefront enabled</label>
        <div className="form-grid">
          <label className="check-row"><input type="checkbox" checked={Boolean(settings.shippingEnabled)} onChange={(event) => setSettings({ ...settings, shippingEnabled: event.target.checked })} /> Shipping checkout enabled</label>
          <label>Flat-rate shipping<input inputMode="decimal" value={moneyToInput({ amount: settings.flatRateShippingAmount ?? 0, currency: "USD" })} onChange={(event) => setSettings({ ...settings, flatRateShippingAmount: inputToMoney(event.target.value, "USD").amount })} /></label>
          <label>Free shipping at<input inputMode="decimal" value={settings.freeShippingThresholdAmount == null ? "" : moneyToInput({ amount: settings.freeShippingThresholdAmount, currency: "USD" })} onChange={(event) => setSettings({ ...settings, freeShippingThresholdAmount: event.target.value.trim() ? inputToMoney(event.target.value, "USD").amount : null })} /></label>
          <label>Handling days<input type="number" min={0} max={30} value={settings.handlingTimeDays ?? 2} onChange={(event) => setSettings({ ...settings, handlingTimeDays: Number(event.target.value) })} /></label>
          <label className="check-row"><input type="checkbox" checked={settings.usedBooksShippable !== false} onChange={(event) => setSettings({ ...settings, usedBooksShippable: event.target.checked })} /> Used books can ship</label>
        </div>
        <div className="form-grid">
          <label>Display name<input value={settings.displayName} onChange={(event) => setSettings({ ...settings, displayName: event.target.value })} /></label>
          <label>Pickup location<input value={settings.pickupLocationId} onChange={(event) => setSettings({ ...settings, pickupLocationId: event.target.value })} /></label>
          <label>Email<input value={settings.contactEmail ?? ""} onChange={(event) => setSettings({ ...settings, contactEmail: event.target.value })} /></label>
          <label>Phone<input value={settings.contactPhone ?? ""} onChange={(event) => setSettings({ ...settings, contactPhone: event.target.value })} /></label>
          <label className="check-row"><input type="checkbox" checked={Boolean(settings.announcementEnabled)} onChange={(event) => setSettings({ ...settings, announcementEnabled: event.target.checked })} /> Announcement enabled</label>
          <label className="wide">Announcement banner<input value={settings.announcementText ?? ""} onChange={(event) => setSettings({ ...settings, announcementText: event.target.value })} /></label>
          <label className="wide">Pickup instructions<textarea value={settings.pickupInstructions ?? ""} onChange={(event) => setSettings({ ...settings, pickupInstructions: event.target.value })} /></label>
          <label className="wide">Pickup policy<textarea value={settings.pickupPolicyText ?? ""} onChange={(event) => setSettings({ ...settings, pickupPolicyText: event.target.value })} /></label>
          <label className="wide">Shipping policy<textarea value={settings.shippingPolicyText ?? ""} onChange={(event) => setSettings({ ...settings, shippingPolicyText: event.target.value })} /></label>
          <label className="wide">Return policy<textarea value={settings.returnPolicyText ?? ""} onChange={(event) => setSettings({ ...settings, returnPolicyText: event.target.value })} /></label>
          <label className="wide">Privacy policy<textarea value={settings.privacyPolicyText ?? ""} onChange={(event) => setSettings({ ...settings, privacyPolicyText: event.target.value })} /></label>
          <label className="wide">Terms<textarea value={settings.termsPolicyText ?? ""} onChange={(event) => setSettings({ ...settings, termsPolicyText: event.target.value })} /></label>
        </div>
      </section>}
      {settings && <section className="panel storefront-designer">
        <div className="section-heading"><div><p className="eyebrow">Appearance</p><h2>Storefront design</h2></div><button type="button" onClick={() => setPreviewOpen(true)}><MonitorCog /> Preview</button></div>
        <div className="theme-choice-grid">
          {(["bookshop", "modern", "classic", "bright"] as const).map((theme) => <button key={theme} className={`theme-choice theme-choice-${theme} ${settings.themePreset === theme ? "active" : ""}`} onClick={() => setSettings({ ...settings, themePreset: theme })}><span /><strong>{theme === "bookshop" ? "Bookshop" : theme[0].toUpperCase() + theme.slice(1)}</strong></button>)}
        </div>
        <div className="form-grid storefront-appearance-controls">
          <label>Hero<select value={settings.heroStyle ?? "immersive"} onChange={(event) => setSettings({ ...settings, heroStyle: event.target.value as Settings["heroStyle"] })}><option value="immersive">Immersive image</option><option value="compact">Compact banner</option><option value="minimal">Minimal header</option></select></label>
          <label>Catalog flow<select value={settings.catalogLayout ?? "grid"} onChange={(event) => setSettings({ ...settings, catalogLayout: event.target.value as Settings["catalogLayout"] })}><option value="grid">Grid</option><option value="list">List</option></select></label>
          <label>Grid size<select value={settings.gridDensity ?? "balanced"} onChange={(event) => setSettings({ ...settings, gridDensity: event.target.value as Settings["gridDensity"] })}><option value="compact">Compact</option><option value="balanced">Balanced</option><option value="spacious">Spacious</option></select></label>
          <label>Product cards<select value={settings.cardStyle ?? "detailed"} onChange={(event) => setSettings({ ...settings, cardStyle: event.target.value as Settings["cardStyle"] })}><option value="detailed">Detailed</option><option value="cover">Cover focused</option><option value="compact">Compact</option></select></label>
        </div>
        <div className="form-grid storefront-font-controls">
          <label>Heading font<select value={settings.headingFont ?? ""} onChange={(event) => setSettings({ ...settings, headingFont: (event.target.value || null) as Settings["headingFont"] })}><option value="">Theme default</option><option value="modern_sans">Modern sans</option><option value="bookish_serif">Bookish serif</option><option value="classic_serif">Classic serif</option><option value="friendly_rounded">Friendly rounded</option></select></label>
          <label>Body font<select value={settings.bodyFont ?? ""} onChange={(event) => setSettings({ ...settings, bodyFont: (event.target.value || null) as Settings["bodyFont"] })}><option value="">Theme default</option><option value="clean_sans">Clean sans</option><option value="humanist_sans">Humanist sans</option><option value="literary_serif">Literary serif</option></select></label>
          <button type="button" onClick={() => setSettings({ ...settings, headingFont: null, bodyFont: null })}>Use theme fonts</button>
        </div>
        <div className="color-control-grid">
          {([['colorPrimary', 'Primary'], ['colorAccent', 'Accent'], ['colorBackground', 'Background'], ['colorSurface', 'Cards'], ['colorText', 'Text']] as const).map(([key, label]) => <label key={key}>{label}<span><input type="color" value={settings[key] ?? themeColorFallback(settings.themePreset, key)} onChange={(event) => setSettings({ ...settings, [key]: event.target.value })} /><input value={settings[key] ?? ""} placeholder="Theme default" pattern="#[0-9a-fA-F]{6}" onChange={(event) => setSettings({ ...settings, [key]: event.target.value || null })} /></span></label>)}
          <button onClick={() => setSettings({ ...settings, colorPrimary: null, colorAccent: null, colorBackground: null, colorSurface: null, colorText: null })}>Use theme colors</button>
        </div>
        <div className="section-heading"><div><p className="eyebrow">Homepage</p><h2>Sections and layout</h2></div></div>
        <div className="storefront-section-editor">
          {homeSectionKeys.map((section) => {
            const visible = (settings.homepageSections ?? []).includes(section);
            const index = (settings.homepageSections ?? []).indexOf(section);
            return <div key={section} className={visible ? "storefront-section-row active" : "storefront-section-row"}>
              <label className="check-row"><input type="checkbox" checked={visible} onChange={(event) => toggleHomepageSection(section, event.target.checked)} /> {homeSectionLabels[section]}</label>
              <select aria-label={`${homeSectionLabels[section]} layout`} value={settings.sectionLayouts?.[section] ?? (section === "catalog" ? settings.catalogLayout ?? "grid" : "grid")} onChange={(event) => updateSectionLayout(section, event.target.value as "grid" | "shelf" | "list")} disabled={!visible}><option value="grid">Grid</option><option value="shelf">Shelf</option><option value="list">List</option></select>
              <button className="icon" title="Move up" onClick={() => moveHomepageSection(index, -1)} disabled={!visible || index <= 0}>↑</button>
              <button className="icon" title="Move down" onClick={() => moveHomepageSection(index, 1)} disabled={!visible || index === (settings.homepageSections ?? []).length - 1}>↓</button>
            </div>;
          })}
        </div>
      </section>}
      {previewOpen && previewProfile && <div className="modal-backdrop storefront-preview-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setPreviewOpen(false); }}>
        <section className="modal storefront-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="storefront-preview-title">
          <header className="storefront-preview-toolbar">
            <div><p className="eyebrow">Unsaved appearance</p><h2 id="storefront-preview-title">Storefront preview</h2></div>
            <div className="segmented" aria-label="Preview size">
              <button type="button" className={previewViewport === "desktop" ? "active" : ""} onClick={() => setPreviewViewport("desktop")}>Desktop</button>
              <button type="button" className={previewViewport === "mobile" ? "active" : ""} onClick={() => setPreviewViewport("mobile")}>Mobile</button>
            </div>
            <button type="button" className="icon" title="Close preview" aria-label="Close preview" onClick={() => setPreviewOpen(false)}><X /></button>
          </header>
          <div className="storefront-preview-stage">
            <div className={`storefront-preview-viewport ${previewViewport}`}>
              <div className={storefrontAppearanceClass(previewProfile, "storefront-preview")} style={storefrontAppearanceStyle(previewProfile)}>
                <header className={`storefront-hero hero-${previewProfile.heroStyle ?? "immersive"}`}>
                  <nav><strong>{previewProfile.storeDisplayName}</strong><div className="store-nav-actions"><span>Account</span><span>Cart (0)</span></div></nav>
                  <div><p className="eyebrow">{previewProfile.storeTagline}</p><h1>{previewProfile.storefrontTitle}</h1><p>{previewProfile.storefrontDescription}</p></div>
                </header>
                <div className="shop-band">
                  {previewProfile.announcementEnabled && previewProfile.announcementText && <div className="announcement-banner">{previewProfile.announcementText}</div>}
                  {previewSections.includes("collections") && <div className={`collection-strip section-layout-${previewProfile.sectionLayouts?.collections ?? "grid"}`}><span><strong>New arrivals</strong><small>Freshly shelved titles</small></span><span><strong>Local favorites</strong><small>Reader-approved picks</small></span></div>}
                  {previewSections.includes("staff_picks") && <section className="storefront-section"><div className="section-heading"><div><p className="eyebrow">Staff picks</p><h2>Recommended by the store</h2></div></div></section>}
                  {previewSections.includes("store_info") && <section className={`storefront-section store-info-section section-layout-${previewProfile.sectionLayouts?.store_info ?? "grid"}`}><div><p className="eyebrow">Visit the store</p><h2>{previewProfile.storeDisplayName}</h2><p>{previewProfile.storeDescription}</p></div><div>{previewProfile.publicPhone && <span>{previewProfile.publicPhone}</span>}{previewProfile.publicEmail && <span>{previewProfile.publicEmail}</span>}</div></section>}
                  {previewSections.includes("loyalty") && <section className={`storefront-section loyalty-callout section-layout-${previewProfile.sectionLayouts?.loyalty ?? "grid"}`}><div><p className="eyebrow">Reader rewards</p><h2>Keep your bookstore account close</h2></div><button type="button">Open account</button></section>}
                  {previewSections.includes("catalog") && <section className="storefront-catalog-section"><div className="section-heading"><div><p className="eyebrow">Catalog</p><h2>Browse the shelves</h2></div></div><div className={`store-grid catalog-grid section-layout-${previewProfile.sectionLayouts?.catalog ?? previewProfile.catalogLayout ?? "grid"}`}>{samplePreviewProducts.map((item) => <article className="store-card" key={item.variation_id}><div className="storefront-preview-cover"><BookOpen /></div><div><h2>{item.title}</h2><p>{item.contributors ?? "Independent author"}</p><p>{item.format_name ?? "Book"}</p><strong>{formatMoney({ amount: item.default_selling_price_amount ?? 0, currency: "USD" })}</strong><button type="button">Add to cart</button></div></article>)}</div></section>}
                </div>
              </div>
            </div>
          </div>
          <footer className="modal-actions"><span className="muted">Preview reflects the current form. Save settings to publish it.</span><button type="button" onClick={() => setPreviewOpen(false)}>Close</button></footer>
        </section>
      </div>}
      <section className="panel">
        <div className="section-heading"><h2>Inventory publishing</h2><input aria-label="Search publishing inventory" value={publishingFilter} onChange={(event) => setPublishingFilter(event.target.value)} placeholder="Search title, author, format, ID" /></div>
        <div className="toolbar compact-toolbar" aria-label="Batch publishing controls">
          <button onClick={() => setSelectedPublishingIds([...new Set([...selectedPublishingIds, ...visiblePublishingItems.map((item) => item.variation_id)])])} disabled={visiblePublishingItems.length === 0 || visibleSelectedCount === visiblePublishingItems.length}>Select visible</button>
          <button onClick={() => setSelectedPublishingIds([])} disabled={selectedPublishingIds.length === 0}>Clear selection</button>
          <label>Batch scope<select value={publishingBatchDraft.scope} onChange={(event) => setPublishingBatchDraft({ ...publishingBatchDraft, scope: event.target.value as PublishingBatchDraft["scope"] })}><option value="selected">Selected items</option><option value="filtered">All filtered items</option></select></label>
          <BatchPublishingSelect label="Online" value={publishingBatchDraft.visibleOnline} onChange={(value) => setPublishingBatchDraft({ ...publishingBatchDraft, visibleOnline: value })} />
          <BatchPublishingSelect label="Pickup" value={publishingBatchDraft.pickupEnabled} onChange={(value) => setPublishingBatchDraft({ ...publishingBatchDraft, pickupEnabled: value })} />
          <BatchPublishingSelect label="Shipping" value={publishingBatchDraft.shippingEnabled} onChange={(value) => setPublishingBatchDraft({ ...publishingBatchDraft, shippingEnabled: value })} />
          <BatchPublishingSelect label="Featured" value={publishingBatchDraft.featured} onChange={(value) => setPublishingBatchDraft({ ...publishingBatchDraft, featured: value })} />
          <button className="primary" onClick={() => void applyPublishingBatch()} disabled={publishingBatchPending || batchTargetCount === 0 || !batchHasChanges}>{publishingBatchPending ? "Updating..." : `Apply to ${batchTargetCount}`}</button>
        </div>
        <p className="empty-inline">{selectedPublishingIds.length} selected / {filteredPublishingItems.length} match the current filter. Batch updates only change fields set to Enable or Disable.</p>
        <DataTable empty="No catalog items found for publishing." headers={["Select", "Title", "Author", "Format", "Available", "Online", "Pickup", "Shipping", "Featured"]} rows={visiblePublishingItems.map((item) => [
          <input key={`${item.variation_id}-select`} aria-label={`Select ${item.title ?? item.variation_id} for batch publishing`} type="checkbox" checked={selectedPublishingIds.includes(item.variation_id)} onChange={(event) => togglePublishingSelection(item.variation_id, event.target.checked)} />,
          item.title ?? item.variation_id,
          item.contributors ?? "-",
          item.format_name ?? "-",
          item.available_quantity ?? 0,
          <input key={`${item.variation_id}-online`} aria-label={`Publish ${item.title ?? item.variation_id} online`} type="checkbox" checked={Boolean(item.visible_online)} onChange={(event) => void savePublishing(item, { visible_online: event.target.checked ? 1 : 0 })} />,
          <input key={`${item.variation_id}-pickup`} aria-label={`Allow pickup for ${item.title ?? item.variation_id}`} type="checkbox" checked={Boolean(item.pickup_enabled)} onChange={(event) => void savePublishing(item, { pickup_enabled: event.target.checked ? 1 : 0 })} />,
          <input key={`${item.variation_id}-shipping`} aria-label={`Allow shipping for ${item.title ?? item.variation_id}`} type="checkbox" checked={Boolean(item.shipping_enabled)} onChange={(event) => void savePublishing(item, { shipping_enabled: event.target.checked ? 1 : 0 })} />,
          <input key={`${item.variation_id}-featured`} aria-label={`Feature ${item.title ?? item.variation_id}`} type="checkbox" checked={Boolean(item.featured)} onChange={(event) => void savePublishing(item, { featured: event.target.checked ? 1 : 0 })} />,
        ])} />
      </section>
      <div className="split">
        <section className="panel">
          <h2>Collections</h2>
          <div className="form-grid"><label>Name<input value={collectionDraft.name} onChange={(event) => setCollectionDraft({ ...collectionDraft, name: event.target.value, slug: slugify(event.target.value) })} /></label><label>Slug<input value={collectionDraft.slug} onChange={(event) => setCollectionDraft({ ...collectionDraft, slug: event.target.value })} /></label><label>Starts<input type="datetime-local" value={collectionDraft.startsAt} onChange={(event) => setCollectionDraft({ ...collectionDraft, startsAt: event.target.value })} /></label><label>Ends<input type="datetime-local" value={collectionDraft.endsAt} onChange={(event) => setCollectionDraft({ ...collectionDraft, endsAt: event.target.value })} /></label><label className="wide">Description<textarea value={collectionDraft.description} onChange={(event) => setCollectionDraft({ ...collectionDraft, description: event.target.value })} /></label><label className="wide">Variation IDs<input value={collectionDraft.variationIds} onChange={(event) => setCollectionDraft({ ...collectionDraft, variationIds: event.target.value })} /></label><label>Rule category<input value={collectionDraft.ruleCategoryId} onChange={(event) => setCollectionDraft({ ...collectionDraft, ruleCategoryId: event.target.value })} /></label><label>Rule author<input value={collectionDraft.ruleAuthor} onChange={(event) => setCollectionDraft({ ...collectionDraft, ruleAuthor: event.target.value })} /></label><label>Rule publisher<input value={collectionDraft.rulePublisher} onChange={(event) => setCollectionDraft({ ...collectionDraft, rulePublisher: event.target.value })} /></label><label>Rule condition<input value={collectionDraft.ruleCondition} onChange={(event) => setCollectionDraft({ ...collectionDraft, ruleCondition: event.target.value })} /></label><label className="check-row"><input type="checkbox" checked={collectionDraft.ruleSignedOnly} onChange={(event) => setCollectionDraft({ ...collectionDraft, ruleSignedOnly: event.target.checked })} /> Signed only</label><button className="primary" onClick={saveCollectionDraft} disabled={!collectionDraft.name || !collectionDraft.slug}>Save collection</button></div>
          <DataTable empty="No collections configured." headers={["Name", "Slug", "Status", "Featured"]} rows={collections.map((row) => [row.name, row.slug, row.publicStatus, row.featured ? "Yes" : "No"])} />
        </section>
        <section className="panel">
          <h2>Staff picks</h2>
          <div className="form-grid"><label>Variation ID<input value={pickDraft.variationId} onChange={(event) => setPickDraft({ ...pickDraft, variationId: event.target.value })} /></label><label>Display name<input value={pickDraft.staffDisplayName} onChange={(event) => setPickDraft({ ...pickDraft, staffDisplayName: event.target.value })} /></label><label className="wide">Recommendation<textarea value={pickDraft.recommendationText} onChange={(event) => setPickDraft({ ...pickDraft, recommendationText: event.target.value })} /></label><button className="primary" onClick={savePick} disabled={!pickDraft.variationId || !pickDraft.staffDisplayName || !pickDraft.recommendationText}>Save pick</button></div>
          <DataTable empty="No staff picks configured." headers={["Variation", "Staff", "Recommendation", "Featured"]} rows={staffPicks.map((row) => [row.variationId, row.staffDisplayName, row.recommendationText, row.featured ? "Yes" : "No"])} />
        </section>
      </div>
    </section>
  );
}

function OnboardingPage() {
  type Subscription = { planName: string; monthlyPriceAmount: number; setupFeeAmount: number; currency: string; billingStatus: string; setupFeeStatus: string; manualBillingOverride: boolean; billingNotes?: string };
  type Step = { stepKey: string; label: string; description?: string; status: string; notes?: string; responsibleUserId?: string; required: boolean };
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [progress, setProgress] = useState({ total: 0, complete: 0, blocked: 0, percent: 0 });
  const [toast, setToast] = useState("");
  const load = useCallback(() => {
    api<{ subscription: Subscription }>("/api/admin/subscription").then((data) => setSubscription(data.subscription));
    api<{ steps: Step[]; progress: typeof progress }>("/api/admin/onboarding").then((data) => { setSteps(data.steps); setProgress(data.progress); });
  }, []);
  useEffect(load, [load]);
  const saveSubscription = async () => {
    if (!subscription) return;
    await api("/api/admin/subscription", { method: "PUT", body: JSON.stringify({ ...subscription, idempotencyKey: crypto.randomUUID() }) });
    setToast("Subscription state saved");
    load();
  };
  const setStepDraft = (stepKey: string, patch: Partial<Step>) => setSteps((current) => current.map((step) => step.stepKey === stepKey ? { ...step, ...patch } : step));
  const updateStep = async (step: Step) => {
    await api(`/api/admin/onboarding/steps/${encodeURIComponent(step.stepKey)}`, { method: "PATCH", body: JSON.stringify({ status: step.status, notes: step.notes ?? "", responsibleUserId: step.responsibleUserId || undefined, idempotencyKey: crypto.randomUUID() }) });
    setToast(`${step.label} updated`);
    load();
  };
  const launchReady = progress.total > 0 && progress.complete === progress.total && progress.blocked === 0;
  return (
    <section className="page">
      <header className="page-header"><div><p className="eyebrow">Launch readiness</p><h1>Onboarding and pricing</h1></div><button onClick={saveSubscription} disabled={!subscription}><Save /> Save subscription</button></header>
      {toast && <div className="success">{toast}</div>}
      {subscription && <section className="panel">
        <h2>PaperbackOS pricing</h2>
        <div className="metric-grid"><Metric label="Monthly" value={formatMoney({ amount: subscription.monthlyPriceAmount, currency: subscription.currency })} /><Metric label="Setup fee" value={formatMoney({ amount: subscription.setupFeeAmount, currency: subscription.currency })} /><Metric label="Plan" value="One complete plan" /><Metric label="Sales surcharge" value="$0" /><Metric label="Inventory tiers" value="None" /></div>
        <p className="muted">Everything your bookstore needs. One fair price. No surprises. Payment-processing fees remain separate and are charged by the connected processor.</p>
        <div className="form-grid"><label>Status<select value={subscription.billingStatus} onChange={(event) => setSubscription({ ...subscription, billingStatus: event.target.value })}><option value="pending_setup">Pending setup</option><option value="trial">Trial</option><option value="active">Active</option><option value="past_due">Past due</option><option value="suspended">Suspended</option><option value="canceled">Canceled</option><option value="grandfathered">Grandfathered</option><option value="internal_demo">Internal/demo</option></select></label><label>Setup fee<select value={subscription.setupFeeStatus} onChange={(event) => setSubscription({ ...subscription, setupFeeStatus: event.target.value })}><option value="unpaid">Unpaid</option><option value="pending">Pending</option><option value="paid">Paid</option><option value="waived">Waived</option></select></label><label className="wide">Billing notes<textarea value={subscription.billingNotes ?? ""} onChange={(event) => setSubscription({ ...subscription, billingNotes: event.target.value })} /></label></div>
      </section>}
      <section className="panel">
        <div className="section-heading"><h2>Onboarding checklist</h2><span>{progress.percent}% complete / {progress.blocked} blocked</span></div>
        <div className="progress-track"><span style={{ width: `${progress.percent}%` }} /></div>
        <div className={launchReady ? "success" : progress.blocked ? "warning" : "empty-inline"}>{launchReady ? "Launch-ready: all required steps are complete and no blockers remain." : progress.blocked ? "Launch is blocked until blocked checklist items are resolved." : "Complete all required steps to reach launch-ready status."}</div>
        <div className="onboarding-list">{steps.map((step) => <article key={step.stepKey} className="onboarding-step"><div><strong>{step.label}</strong><p>{step.description}</p>{step.required && <span className="status-chip warn">Required</span>}</div><select value={step.status} onChange={(event) => setStepDraft(step.stepKey, { status: event.target.value })}><option value="not_started">Not started</option><option value="in_progress">In progress</option><option value="complete">Complete</option><option value="skipped">Skipped</option><option value="blocked">Blocked</option></select><label>Responsible<input value={step.responsibleUserId ?? ""} onChange={(event) => setStepDraft(step.stepKey, { responsibleUserId: event.target.value })} placeholder="User ID" /></label><label className="wide">Notes<textarea value={step.notes ?? ""} onChange={(event) => setStepDraft(step.stepKey, { notes: event.target.value })} /></label><button onClick={() => void updateStep(step)}><Save /> Save step</button></article>)}</div>
      </section>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong></div>;
}

function DataTable({ headers, rows, empty }: { headers: string[]; rows: Array<Array<React.ReactNode>>; empty: string }) {
  return <div className="table-wrap"><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}{rows.length === 0 && <tr><td colSpan={headers.length}>{empty}</td></tr>}</tbody></table></div>;
}

function slugify(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function InventoryDashboard({ onOpen, onSupplierSearch, onCustomerRequest }: { onOpen: (item: InventoryItem) => void; onSupplierSearch: (item: InventoryItem) => void; onCustomerRequest: (item: InventoryItem) => void }) {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const [itemType, setItemType] = useState<"all" | ProductType>("all");
  const [sort, setSort] = useState("title");
  const [category, setCategory] = useState("");
  const [mode, setMode] = useState<"cards" | "table">("cards");
  const [metadataRefreshing, setMetadataRefreshing] = useState<string | null>(null);
  const [metadataBatch, setMetadataBatch] = useState<MetadataBatchState | null>(null);
  const [toast, setToast] = useState("");

  const params = useMemo(() => new URLSearchParams({ q: query, sort, limit: "1000", itemType, ...(stockStatus ? { stockStatus } : {}) }), [itemType, query, sort, stockStatus]);
  const load = () => {
    setLoading(true);
    setError(null);
    api<{ items: InventoryItem[]; stats?: InventoryStats; sync: { refreshedAt: string } }>(`/api/admin/inventory?${params}`)
      .then((data) => {
        setItems(data.items);
        setStats(data.stats ?? null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [params]);

  const replaceItem = (item: InventoryItem) => setItems((current) => current.map((entry) => entry.id === item.id ? item : entry));
  const refreshItemMetadata = async (item: InventoryItem) => {
    setMetadataRefreshing(item.id);
    setToast(`Refreshing metadata for ${item.title}...`);
    setError(null);
    try {
      const result = await api<{ item: InventoryItem; changedFields: string[] }>(`/api/admin/catalog/items/${encodeURIComponent(item.id)}/metadata-refresh`, {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ mode: "replace_mapped" }),
      });
      replaceItem(result.item);
      setToast(result.changedFields.length ? `Refreshed ${result.item.title} metadata.` : `${result.item.title} already has the best available metadata.`);
    } catch (err) {
      setToast("");
      setError(err instanceof Error ? err.message : "Metadata refresh failed");
    } finally {
      setMetadataRefreshing(null);
    }
  };
  const refreshLibraryMetadata = async () => {
    setMetadataRefreshing("library");
    setMetadataBatch(null);
    setToast("Finding inventory records with missing metadata...");
    setError(null);
    try {
      const candidates: MetadataRefreshCandidate[] = [];
      const seen = new Set<string>();
      const pageSize = 250;
      for (let offset = 0; ; offset += pageSize) {
        const data = await api<{ candidates: MetadataRefreshCandidate[] }>(`/api/admin/catalog/metadata-refresh/candidates?${new URLSearchParams({ limit: String(pageSize), offset: String(offset) })}`);
        for (const candidate of data.candidates) {
          if (!seen.has(candidate.id)) {
            seen.add(candidate.id);
            candidates.push(candidate);
          }
        }
        setToast(`Found ${candidates.length} inventory records with missing metadata...`);
        if (data.candidates.length < pageSize) break;
      }
      setMetadataBatch({ total: candidates.length, processed: 0, refreshed: 0, skipped: 0, notFound: 0, failed: 0, results: [] });
      if (!candidates.length) {
        setToast("No inventory records need missing metadata filled.");
        return;
      }
      for (const [index, candidate] of candidates.entries()) {
        setMetadataBatch((current) => current ? { ...current, currentTitle: candidate.title } : current);
        try {
          const result = await api<{ item: InventoryItem; changedFields: string[] }>(`/api/admin/catalog/items/${encodeURIComponent(candidate.id)}/metadata-refresh`, {
            method: "POST",
            headers: { "idempotency-key": crypto.randomUUID() },
            body: JSON.stringify({ mode: "fill_missing" }),
          });
          replaceItem(result.item);
          setMetadataBatch((current) => addMetadataBatchResult(current, {
            title: result.item.title || candidate.title,
            status: result.changedFields.length ? "refreshed" : "skipped",
            detail: result.changedFields.length ? `Updated ${result.changedFields.length} field${result.changedFields.length === 1 ? "" : "s"}.` : "Already has the best available metadata.",
          }));
        } catch (err) {
          const notFound = err instanceof StaffApiError && err.code === "metadata_not_found";
          setMetadataBatch((current) => addMetadataBatchResult(current, {
            title: candidate.title,
            status: notFound ? "not_found" : "failed",
            detail: notFound ? "No confident metadata match was found." : staffErrorMessage(err),
          }));
        }
        if (index < candidates.length - 1) await delay(650);
      }
      setToast("Metadata refresh queue finished. Review the item-by-item results below.");
      load();
    } catch (err) {
      setToast("");
      setError(staffErrorMessage(err));
    } finally {
      setMetadataRefreshing(null);
    }
  };

  const categories = [...new Set(items.map((item) => item.category ?? "Uncategorized"))].sort((a, b) => a.localeCompare(b));
  const visibleItems = category ? items.filter((item) => (item.category ?? "Uncategorized") === category) : items;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Local catalog</p>
          <h1>Inventory</h1>
        </div>
        <div className="actions">
          <button onClick={() => void refreshLibraryMetadata()} disabled={Boolean(metadataRefreshing)} title="Fill missing metadata across the current library"><RefreshCw /> {metadataRefreshing === "library" ? "Filling metadata..." : "Fill missing metadata"}</button>
          <button className="primary" onClick={load}><RefreshCw /> Refresh inventory</button>
        </div>
      </header>
      <InventoryStatsPanel stats={stats} loading={loading} />
      <div className="toolbar" role="search">
        <label className="searchbox">
          <Search aria-hidden />
          <span className="sr-only">Search inventory</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, author, ISBN, SKU, description" />
        </label>
        <label>
          <SlidersHorizontal aria-hidden />
          <span className="sr-only">Stock status</span>
          <select value={stockStatus} onChange={(event) => setStockStatus(event.target.value)}>
            <option value="">All stock</option>
            <option value="in_stock">In stock</option>
            <option value="low_stock">Low stock</option>
            <option value="out_of_stock">Out of stock</option>
            <option value="not_tracked">Not tracked</option>
          </select>
        </label>
        <label>
          <span className="sr-only">Product type</span>
          <select value={itemType} onChange={(event) => setItemType(event.target.value as "all" | ProductType)}>
            <option value="all">All products</option>
            {productTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label>
          <span className="sr-only">Sort</span>
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="title">Title</option>
            <option value="author">Author</option>
            <option value="category">Category</option>
            <option value="stock">Stock quantity</option>
            <option value="updated">Recently updated</option>
          </select>
        </label>
        <div className="segmented" aria-label="View mode">
          <button className={mode === "table" ? "active" : ""} onClick={() => setMode("table")}><List /></button>
          <button className={mode === "cards" ? "active" : ""} onClick={() => setMode("cards")}><Grid2X2 /></button>
        </div>
      </div>
      {metadataBatch && (
        <div className="import-progress metadata-progress" role="status" aria-live="polite">
          <div className="progress-track" aria-hidden><span style={{ width: `${Math.round((metadataBatch.processed / Math.max(metadataBatch.total, 1)) * 100)}%` }} /></div>
          <strong>{metadataBatch.processed >= metadataBatch.total ? "Metadata refresh complete" : `Refreshing ${metadataBatch.currentTitle ?? "metadata"}`}</strong>
          <span>{metadataBatch.processed} of {metadataBatch.total} checked. {metadataBatch.refreshed} refreshed, {metadataBatch.skipped} unchanged, {metadataBatch.notFound} not found, {metadataBatch.failed} failed.</span>
          {metadataBatch.results.length > 0 && (
            <ul className="metadata-progress-list" aria-label="Recent metadata refresh results">
              {metadataBatch.results.slice(-6).map((result, index) => (
                <li key={`${result.title}-${index}`}>
                  <strong>{result.title}</strong>
                  <span>{result.status.replace("_", " ")} - {result.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="category-strip" aria-label="Categories">
        <button className={category === "" ? "active" : ""} onClick={() => setCategory("")}>All</button>
        {categories.map((entry) => <button className={category === entry ? "active" : ""} key={entry} onClick={() => setCategory(entry)}>{entry}</button>)}
      </div>
      {toast && <div className="success">{toast}</div>}
      {error && <div className="error">{error}</div>}
      {loading ? <Skeleton /> : visibleItems.length === 0 ? <EmptyState /> : mode === "table" ? <InventoryTable items={visibleItems} onOpen={onOpen} onSupplierSearch={onSupplierSearch} onCustomerRequest={onCustomerRequest} onRefreshMetadata={refreshItemMetadata} metadataRefreshing={metadataRefreshing} /> : <InventoryCards items={visibleItems} onOpen={onOpen} onSupplierSearch={onSupplierSearch} onCustomerRequest={onCustomerRequest} onRefreshMetadata={refreshItemMetadata} metadataRefreshing={metadataRefreshing} />}
    </section>
  );
}

function InventoryStatsPanel({ stats, loading }: { stats: InventoryStats | null; loading: boolean }) {
  const metrics = [
    { label: "Items in catalog", value: stats?.totalBooksInLibrary },
    { label: "Items in stock", value: stats?.totalTitlesInStock },
    { label: "Items out of stock", value: stats?.totalTitlesOutOfStock },
    { label: "Units in stock", value: stats?.totalBooksInStock },
  ];
  return (
    <section className="inventory-stats" aria-label="Inventory summary">
      {metrics.map((metric) => (
        <div className="inventory-stat" key={metric.label}>
          <span>{metric.label}</span>
          <strong>{loading || metric.value === undefined ? "..." : metric.value.toLocaleString()}</strong>
        </div>
      ))}
    </section>
  );
}

function InventoryTable({ items, onOpen, onSupplierSearch, onCustomerRequest, onRefreshMetadata, metadataRefreshing }: { items: InventoryItem[]; onOpen: (item: InventoryItem) => void; onSupplierSearch: (item: InventoryItem) => void; onCustomerRequest: (item: InventoryItem) => void; onRefreshMetadata: (item: InventoryItem) => void; metadataRefreshing: string | null }) {
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>Title</th><th>Type</th><th>Author</th><th>ISBN / SKU</th><th>Category</th><th>Price</th><th>Qty</th><th>Status</th><th /></tr></thead>
        <tbody>{items.map((item) => (
          <tr key={item.id}>
            <td><strong>{item.title}</strong><small>{item.updatedAt ? `Updated ${new Date(item.updatedAt).toLocaleString()}` : "Local catalog item"}</small></td>
            <td>{productTypeLabel(item.productType)}</td>
            <td>{item.author ?? "-"}</td>
            <td>{item.isbn ?? item.sku ?? "-"}</td>
            <td>{item.category}</td>
            <td>{formatMoney(item.priceMoney)}</td>
            <td>{item.availableQuantity ?? "-"}</td>
            <td><StockBadge status={item.stockStatus} /></td>
            <td className="table-actions">{canRequestCustomerCopy(item) && <button className="icon" aria-label={`Create customer request for ${item.title}`} onClick={() => onCustomerRequest(item)}><Shield /></button>}<button className="icon" aria-label={`Refresh metadata for ${item.title}`} disabled={metadataRefreshing === item.id || metadataRefreshing === "library"} onClick={() => onRefreshMetadata(item)}><RefreshCw /></button><button className="icon" aria-label={`Find copies of ${item.title} to buy`} onClick={() => onSupplierSearch(item)}><ExternalLink /></button><button className="icon" aria-label={`Open ${item.title}`} onClick={() => onOpen(item)}><ChevronRight /></button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function InventoryCards({ items, onOpen, onSupplierSearch, onCustomerRequest, onRefreshMetadata, metadataRefreshing }: { items: InventoryItem[]; onOpen: (item: InventoryItem) => void; onSupplierSearch: (item: InventoryItem) => void; onCustomerRequest: (item: InventoryItem) => void; onRefreshMetadata: (item: InventoryItem) => void; metadataRefreshing: string | null }) {
  return (
    <div className="card-grid">
      {items.map((item) => (
        <article
          aria-label={`Open ${item.title}`}
          className="item-card"
          key={item.id}
          onClick={() => onOpen(item)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onOpen(item);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Image imageUrl={item.imageUrl} />
          <div>
            <StockBadge status={item.stockStatus} />
            <h3>{item.title}</h3>
            <p>{[productTypeLabel(item.productType), item.author ?? "Unknown author"].join(" / ")}</p>
            <dl><dt>ISBN / SKU</dt><dd>{item.isbn ?? item.sku ?? "-"}</dd><dt>Qty</dt><dd>{item.availableQuantity ?? "-"}</dd><dt>Price</dt><dd>{formatMoney(item.priceMoney)}</dd></dl>
            <button
              className="supplier-card-button"
              onClick={(event) => {
                event.stopPropagation();
                onSupplierSearch(item);
              }}
            >
              <ExternalLink /> Find copies
            </button>
            <button
              className="supplier-card-button"
              disabled={metadataRefreshing === item.id || metadataRefreshing === "library"}
              onClick={(event) => {
                event.stopPropagation();
                onRefreshMetadata(item);
              }}
            >
              <RefreshCw /> {metadataRefreshing === item.id ? "Refreshing..." : "Refresh metadata"}
            </button>
            {canRequestCustomerCopy(item) && (
              <button
                className="supplier-card-button"
                onClick={(event) => {
                  event.stopPropagation();
                  onCustomerRequest(item);
                }}
              >
                <Shield /> Create request
              </button>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function canRequestCustomerCopy(item: InventoryItem): boolean {
  return item.availableQuantity === null || item.availableQuantity === undefined || item.availableQuantity <= 0;
}

function addMetadataBatchResult(current: MetadataBatchState | null, result: MetadataBatchResult): MetadataBatchState | null {
  if (!current) return current;
  return {
    ...current,
    currentTitle: undefined,
    processed: current.processed + 1,
    refreshed: current.refreshed + (result.status === "refreshed" ? 1 : 0),
    skipped: current.skipped + (result.status === "skipped" ? 1 : 0),
    notFound: current.notFound + (result.status === "not_found" ? 1 : 0),
    failed: current.failed + (result.status === "failed" ? 1 : 0),
    results: [...current.results, result],
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function importApplyChunkDelayMs(): number {
  return typeof navigator !== "undefined" && navigator.userAgent.includes("jsdom") ? 0 : 650;
}

function isTransientImportApplyError(error: unknown): boolean {
  if (!(error instanceof StaffApiError)) return false;
  if ([408, 429, 502, 503, 504].includes(error.status)) return true;
  return ["rate_limited", "service_unavailable", "request_timeout", "provider_unavailable"].includes(error.code);
}

function ItemEditor({ item, onBack }: { item: InventoryItem | null; onBack: () => void }) {
  const [title, setTitle] = useState(item?.title ?? "");
  const [author, setAuthor] = useState(item?.author ?? "");
  const [isbn, setIsbn] = useState(item?.isbn ?? item?.sku ?? item?.variations[0]?.gtin ?? item?.variations[0]?.sku ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [publisher, setPublisher] = useState("");
  const [publicationDate, setPublicationDate] = useState("");
  const [format, setFormat] = useState("");
  const [productType, setProductType] = useState<ProductType>(item?.productType ?? "book");
  const [pageCount, setPageCount] = useState("");
  const [language, setLanguage] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState(item?.imageUrl ?? "");
  const [metadataMatches, setMetadataMatches] = useState<BookMetadata[]>([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [supplierSearchOpen, setSupplierSearchOpen] = useState(false);
  const [price, setPrice] = useState(((item?.priceMoney?.amount ?? 0) / 100).toFixed(2));
  const [toast, setToast] = useState("");
  const [stockPositions, setStockPositions] = useState<ItemStockPosition[]>([]);
  const [stockLocationId, setStockLocationId] = useState("");
  const [stockCount, setStockCount] = useState("");
  const [stockReason, setStockReason] = useState("Manual count correction");
  const [stockLoading, setStockLoading] = useState(false);
  const loadStockPositions = useCallback(async () => {
    if (!item) return;
    const [stockData, settingsData] = await Promise.all([
      api<{ stockPositions: ItemStockPosition[] }>(`/api/admin/inventory/quantity/positions?variationId=${encodeURIComponent(item.id)}`),
      api<{ locations: InventoryLocation[]; defaults?: { defaultLocationId?: string } }>("/api/admin/inventory-settings"),
    ]);
    const activePositions = stockData.stockPositions.length
      ? stockData.stockPositions
      : (settingsData.locations ?? []).map((location) => ({
        variationId: item.id,
        locationId: location.id,
        locationName: location.name,
        locationCode: location.code,
        locationActive: location.active,
        onHandQuantity: 0,
        reservedQuantity: 0,
        availableQuantity: 0,
        costBasisAmount: 0,
        reorderThreshold: 0,
        reorderTarget: 0,
      }));
    setStockPositions(activePositions);
    const preferredLocationId = stockLocationId
      || settingsData.defaults?.defaultLocationId
      || activePositions.find((position) => position.locationActive)?.locationId
      || activePositions[0]?.locationId
      || "loc-main";
    setStockLocationId(preferredLocationId);
    const selected = activePositions.find((position) => position.locationId === preferredLocationId);
    setStockCount(String(selected?.onHandQuantity ?? item.availableQuantity ?? 0));
  }, [item, stockLocationId]);
  useEffect(() => {
    void loadStockPositions().catch((error) => setToast(error instanceof Error ? `Could not load inventory counts: ${error.message}` : "Could not load inventory counts"));
  }, [loadStockPositions]);
  const selectedStock = stockPositions.find((position) => position.locationId === stockLocationId);
  const parsedStockCount = Number(stockCount);
  const stockDelta = Number.isFinite(parsedStockCount) && selectedStock ? parsedStockCount - selectedStock.onHandQuantity : 0;
  const saveStockCount = async () => {
    if (!item || !stockLocationId || !Number.isInteger(parsedStockCount) || parsedStockCount < 0) {
      setToast("Enter a whole-number inventory count before saving.");
      return;
    }
    setStockLoading(true);
    setToast("");
    try {
      await api<{ stockPosition: StockPosition }>("/api/admin/inventory/quantity/adjust", {
        method: "POST",
        body: JSON.stringify({
          variationId: item.id,
          locationId: stockLocationId,
          targetQuantity: parsedStockCount,
          reason: stockReason || "Manual count correction",
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      await loadStockPositions();
      setToast(`Inventory count updated${stockDelta ? ` (${stockDelta > 0 ? "+" : ""}${stockDelta})` : ""}.`);
    } catch (error) {
      setToast(error instanceof Error ? `Inventory update failed: ${error.message}` : "Inventory update failed");
    } finally {
      setStockLoading(false);
    }
  };
  const retrieveMetadata = async () => {
    const normalizedIsbn = normalizeIsbn(isbn);
    const isBookLookup = isValidIsbn(normalizedIsbn);
    const query = isBookLookup
      ? { isbn: normalizedIsbn }
      : isbn.trim()
        ? { barcode: isbn.trim() }
        : title.trim()
          ? { title: title.trim(), keywords: title.trim() }
          : null;
    if (!query) {
      setToast("Enter an ISBN, barcode, or title before retrieving metadata");
      return;
    }
    setMetadataLoading(true);
    setToast("");
    try {
      const result = await api<{ results: BookMetadata[] }>(isBookLookup ? "/api/admin/metadata/search" : "/api/admin/metadata/product-search", { method: "POST", body: JSON.stringify(query) });
      setMetadataMatches(result.results);
      setToast(result.results.length ? `${result.results.length} metadata matches found` : "No metadata matches found");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Metadata lookup failed");
    } finally {
      setMetadataLoading(false);
    }
  };
  const applyMetadata = (metadata: BookMetadata) => {
    setTitle(metadata.title);
    setAuthor(metadata.authors.join(", "));
    setIsbn(metadata.isbn13 ?? metadata.isbn10 ?? metadata.barcode ?? isbn);
    setProductType(metadata.productType ?? productType);
    setDescription(metadata.description ?? description);
    setPublisher(metadata.publisher ?? "");
    setPublicationDate(metadata.publicationDate ?? "");
    setFormat(metadata.format ?? "");
    setPageCount(metadata.pageCount ? String(metadata.pageCount) : "");
    setLanguage(metadata.language ?? "");
    if (metadata.coverImageUrls[0]) {
      setCoverImageUrl(metadata.coverImageUrls[0]);
    }
    setToast(`Applied metadata from ${metadata.source}`);
  };
  const save = async () => {
    setToast("");
    if (!item) {
      setToast("Create new items from receiving, trade-ins, or import workflows.");
      return;
    }
    try {
      await api(`/api/admin/catalog/items/${encodeURIComponent(item.id)}`, {
        method: "PATCH",
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({
          title,
          authors: author ? [author] : [],
          description: description || undefined,
          isbn13: isbn.length === 13 ? isbn : undefined,
          isbn10: isbn.length === 10 ? isbn : undefined,
          sku: isbn || undefined,
          publisher: publisher || undefined,
          publicationDate: publicationDate || undefined,
          formatCode: format || item.categoryId || undefined,
          productType,
          pageCount: pageCount ? Number(pageCount) : undefined,
          language: language || undefined,
          priceMoney: { amount: Math.round(Number(price) * 100), currency: item?.priceMoney?.currency ?? "USD" },
          coverImageUrl: coverImageUrl || undefined,
        }),
      });
      setToast("Saved to local catalog");
    } catch (error) {
      setToast(error instanceof Error ? `Save failed: ${error.message}` : "Save failed");
    }
  };
  const archiveItem = async () => {
    if (!item) return;
    setToast("");
    try {
      await api(`/api/admin/items/${encodeURIComponent(item.id)}/archive`, {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
      });
      setToast("Item archived");
      onBack();
    } catch (error) {
      setToast(error instanceof Error ? `Archive failed: ${error.message}` : "Archive failed");
    }
  };
  if (supplierSearchOpen) {
    return (
      <SupplierSearchScreen
        title={title}
        author={author}
        isbn={isbn}
        imageUrl={coverImageUrl}
        onBack={() => setSupplierSearchOpen(false)}
      />
    );
  }
  return (
    <section className="page narrow">
      <header className="page-header"><div><p className="eyebrow">Catalog item</p><h1>{item ? "Edit item" : "New item"}</h1></div><button onClick={onBack}>Back</button></header>
      {toast && <div className="success"><Check /> {toast}</div>}
      <div className="editor">
        <Image imageUrl={coverImageUrl} />
        <div className="form-grid">
          <label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label>Author<input value={author} onChange={(event) => setAuthor(event.target.value)} /></label>
          <label>ISBN / SKU<input value={isbn} onChange={(event) => setIsbn(normalizeIsbn(event.target.value))} /></label>
          <label>Price<input inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} /></label>
          <label>Product type<select value={productType} onChange={(event) => setProductType(event.target.value as ProductType)}>{productTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          <label>Publisher<input value={publisher} onChange={(event) => setPublisher(event.target.value)} /></label>
          <label>Publication date<input value={publicationDate} onChange={(event) => setPublicationDate(event.target.value)} /></label>
          <label>Format<input value={format} onChange={(event) => setFormat(event.target.value)} /></label>
          <label>Page count<input inputMode="numeric" value={pageCount} onChange={(event) => setPageCount(event.target.value.replace(/\D/g, ""))} /></label>
          <label>Language<input value={language} onChange={(event) => setLanguage(event.target.value)} /></label>
          <p className="wide empty-inline">Metadata changes save to the local D1 catalog. They do not update Square Catalog.</p>
          {item && (
            <section className="wide inventory-count-panel">
              <div>
                <h2>Inventory count</h2>
                {selectedStock && <p>On hand {selectedStock.onHandQuantity} / Reserved {selectedStock.reservedQuantity} / Available {selectedStock.availableQuantity}</p>}
              </div>
              <div className="form-grid compact-form">
                <label>Location
                  <select value={stockLocationId} onChange={(event) => {
                    const nextLocationId = event.target.value;
                    setStockLocationId(nextLocationId);
                    const next = stockPositions.find((position) => position.locationId === nextLocationId);
                    setStockCount(String(next?.onHandQuantity ?? 0));
                  }}>
                    {stockPositions.map((position) => <option key={position.locationId} value={position.locationId}>{position.locationName}{position.locationCode ? ` (${position.locationCode})` : ""}</option>)}
                    {stockPositions.length === 0 && <option value="loc-main">Main Store</option>}
                  </select>
                </label>
                <label>Correct count<input inputMode="numeric" value={stockCount} onChange={(event) => setStockCount(event.target.value.replace(/\D/g, ""))} /></label>
                <label className="wide">Reason<input value={stockReason} onChange={(event) => setStockReason(event.target.value)} /></label>
                <div className="wide actions"><button onClick={() => void saveStockCount()} disabled={stockLoading || !stockLocationId}>{stockLoading ? "Saving..." : "Update count"}</button>{selectedStock && <span className="empty-inline">Adjustment {stockDelta > 0 ? "+" : ""}{stockDelta}</span>}</div>
              </div>
            </section>
          )}
          <label className="wide">Cover image URL<input value={coverImageUrl} onChange={(event) => setCoverImageUrl(event.target.value)} /></label>
          <label className="wide">Description<textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={6} /></label>
          <label>Image upload<input type="file" accept="image/png,image/jpeg,image/webp" /></label>
          <div className="wide actions"><button onClick={() => void retrieveMetadata()} disabled={metadataLoading}><Search /> {metadataLoading ? "Retrieving..." : "Retrieve metadata"}</button><button onClick={() => setSupplierSearchOpen(true)}><ExternalLink /> Find copies to buy</button><button className="primary" onClick={save}><Save /> Save catalog changes</button><button className="danger" onClick={() => void archiveItem()} disabled={!item}><Archive /> Archive item</button></div>
          {metadataMatches.length > 0 && (
            <section className="wide metadata-results" aria-label="Metadata matches">
              <h2>Metadata matches</h2>
              {metadataMatches.map((match) => (
                <article key={`${match.source}-${match.sourceId}`}>
                  <Image imageUrl={match.coverImageUrls[0]} />
                  <div>
                    <strong>{match.title}</strong>
                    <span>{productTypeLabel(match.productType)} / {match.authors.join(", ") || "Unknown author"} / {match.source} / {match.confidence}</span>
                    {match.description && <p>{descriptionSnippet(match.description)}</p>}
                    <dl className="metadata-fields">
                      <dt>Identifier</dt><dd>{match.isbn13 ?? match.isbn10 ?? match.barcode ?? "-"}</dd>
                      <dt>Publisher</dt><dd>{match.publisher ?? "-"}</dd>
                      <dt>Published</dt><dd>{match.publicationDate ?? "-"}</dd>
                      <dt>Format</dt><dd>{match.format ?? "-"}</dd>
                    </dl>
                    {Boolean(match.externalLinks?.length) && <ExternalMetadataLinks links={match.externalLinks ?? []} />}
                    <button onClick={() => applyMetadata(match)}>Apply metadata</button>
                  </div>
                </article>
              ))}
            </section>
          )}
        </div>
      </div>
    </section>
  );
}

function SupplierSearchScreen({ itemId, initialItem, title, author, isbn, imageUrl, onBack, onSelectOffer, onOpenPurchasing, requestFormat = "softcover", minimumPrices = defaultSettings.customerRequestMinimumPrices }: { itemId?: string; initialItem?: InventoryItem | null; title?: string; author?: string; isbn?: string; imageUrl?: string; onBack: () => void; onSelectOffer?: (result: SupplierSearchResult, pricing: CustomerRequestPricing) => void; onOpenPurchasing?: () => void; requestFormat?: ImportFormat; minimumPrices?: Record<ImportFormat, Money> }) {
  const [resolvedItem, setResolvedItem] = useState<InventoryItem | null>(initialItem ?? null);
  const effectiveTitle = resolvedItem?.title ?? title ?? "";
  const effectiveAuthor = resolvedItem?.author ?? author ?? "";
  const effectiveIsbn = resolvedItem?.isbn ?? resolvedItem?.sku ?? resolvedItem?.variations[0]?.gtin ?? resolvedItem?.variations[0]?.sku ?? isbn ?? "";
  const effectiveImageUrl = resolvedItem?.imageUrl ?? imageUrl;
  const [searchTitle, setSearchTitle] = useState(effectiveTitle);
  const [searchAuthor, setSearchAuthor] = useState(effectiveAuthor);
  const [searchIsbn, setSearchIsbn] = useState(normalizeIsbn(effectiveIsbn));
  const [results, setResults] = useState<SupplierSearchResult[]>([]);
  const [sections, setSections] = useState<{ id: NonNullable<SupplierSearchResult["section"]>; title: string; results: SupplierSearchResult[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [customerRequestId, setCustomerRequestId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [selectedDraftPoId, setSelectedDraftPoId] = useState("");
  const [draftPurchaseOrders, setDraftPurchaseOrders] = useState<PurchasingPurchaseOrder[]>([]);
  const [contextLoading, setContextLoading] = useState(false);
  const lastAutoSearchKey = useRef("");

  useEffect(() => {
    if (initialItem) setResolvedItem(initialItem);
  }, [initialItem]);

  const runSearch = useCallback(async (override?: { title: string; author: string; isbn: string; manualRefresh?: boolean }) => {
    const queryTitle = override?.title ?? searchTitle;
    const queryAuthor = override?.author ?? searchAuthor;
    const queryIsbn = override?.isbn ?? searchIsbn;
    if (!queryTitle.trim() && !queryIsbn.trim()) {
      setMessage("Enter a title or ISBN before searching suppliers.");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const data = await api<{ results: SupplierSearchResult[]; sections?: { id: NonNullable<SupplierSearchResult["section"]>; title: string; results: SupplierSearchResult[] }[]; warnings?: string[] }>("/api/admin/find-stock", {
        method: "POST",
        body: JSON.stringify({
          itemId,
          isbn: queryIsbn || undefined,
          title: queryTitle || undefined,
          author: queryAuthor || undefined,
          manualRefresh: override?.manualRefresh ?? false,
        }),
      });
      setResults(data.results);
      setSections(data.sections ?? sectionSupplierResults(data.results));
      setMessage(data.results.length ? `${data.results.length} sourcing results found${data.warnings?.length ? `. ${data.warnings.join(" ")}` : ""}` : "No sourcing results found");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Supplier search failed");
    } finally {
      setLoading(false);
    }
  }, [itemId, searchAuthor, searchIsbn, searchTitle]);

  useEffect(() => {
    api<{ purchaseOrders?: PurchasingPurchaseOrder[] }>("/api/admin/purchasing/purchase-orders?status=draft&limit=50")
      .then((data) => setDraftPurchaseOrders(data.purchaseOrders ?? []))
      .catch(() => setDraftPurchaseOrders([]));
  }, []);

  useEffect(() => {
    const titleValue = resolvedItem?.title ?? title ?? "";
    const authorValue = resolvedItem?.author ?? author ?? "";
    const isbnValue = resolvedItem?.isbn ?? resolvedItem?.sku ?? resolvedItem?.variations[0]?.gtin ?? resolvedItem?.variations[0]?.sku ?? isbn ?? "";
    setSearchTitle(titleValue);
    setSearchAuthor(authorValue);
    setSearchIsbn(normalizeIsbn(isbnValue));
  }, [author, isbn, resolvedItem, title]);

  useEffect(() => {
    if (!itemId || inventoryItemMatchesId(resolvedItem, itemId)) return;
    let cancelled = false;
    setContextLoading(true);
    setMessage("");
    const params = new URLSearchParams({ q: itemId, limit: "20", active: "all" });
    api<{ items: InventoryItem[] }>(`/api/admin/inventory?${params}`)
      .then((data) => {
        if (cancelled) return;
        const match = data.items.find((item) => item.id === itemId || item.variations.some((variation) => variation.id === itemId));
        if (match) {
          const matchIsbn = normalizeIsbn(match.isbn ?? match.sku ?? match.variations[0]?.gtin ?? match.variations[0]?.sku ?? "");
          setResolvedItem(match);
          lastAutoSearchKey.current = JSON.stringify([match.title.trim(), (match.author ?? "").trim(), matchIsbn.trim()]);
          void runSearch({ title: match.title, author: match.author ?? "", isbn: matchIsbn });
        } else setMessage("Item context was not found. Search by title, author, or ISBN.");
      })
      .catch((error) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Unable to load item context");
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, resolvedItem, runSearch]);

  useEffect(() => {
    if (contextLoading || (!searchTitle.trim() && !searchIsbn.trim())) return;
    const searchKey = JSON.stringify([searchTitle.trim(), searchAuthor.trim(), searchIsbn.trim()]);
    if (lastAutoSearchKey.current === searchKey) return;
    lastAutoSearchKey.current = searchKey;
    void runSearch({ title: searchTitle, author: searchAuthor, isbn: searchIsbn });
  }, [contextLoading, runSearch, searchAuthor, searchIsbn, searchTitle]);

  const confirmSubstitution = (result: SupplierSearchResult) => !result.requiresConfirmation || window.confirm("This supplier result includes a substitute or alternate edition. Confirm that this is the correct edition before ordering.");

  const addWholesaleToDraftPo = async (result: SupplierSearchResult, mode: "existing" | "new" | "direct" = "existing") => {
    if (!result.vendorId || !(result.capabilities?.includes("add_to_existing_po") || result.capabilities?.includes("add_to_draft_po") || result.capabilities?.includes("create_po"))) {
      setMessage("Only configured wholesale supplier results can be added to purchase orders.");
      return;
    }
    if (!confirmSubstitution(result)) return;
    const sourceDemand = customerRequestId.trim() ? [{ type: "customer_special_order", customerRequestId: customerRequestId.trim() }] : [];
    const payload = sourcingResultToPoAction(result, searchTitle, searchIsbn, sourceDemand, {
      purchaseOrderId: mode === "existing" ? selectedDraftPoId || undefined : undefined,
      directToCustomer: mode === "direct",
    });
    try {
      const endpoint = mode === "existing" && selectedDraftPoId ? "/api/admin/find-stock/actions/add-to-po" : "/api/admin/find-stock/actions/create-po";
      const data = await api<{ purchaseOrder: PurchasingPurchaseOrder }>(endpoint, { method: "POST", body: JSON.stringify(payload) });
      setMessage(`${mode === "existing" && selectedDraftPoId ? "Added to" : "Created"} ${data.purchaseOrder.poNumber} for ${result.providerName}`);
      onOpenPurchasing?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not add the supplier result to a purchase order");
    }
  };

  const createCustomerSpecialOrder = async (result: SupplierSearchResult) => {
    if (!customerId.trim()) {
      setMessage("Enter a customer ID before creating a customer special order.");
      return;
    }
    try {
      const data = await api<{ specialOrder: { id: string } }>("/api/admin/find-stock/actions/create-special-order", {
        method: "POST",
        body: JSON.stringify({
          customerId: customerId.trim(),
          title: result.title || searchTitle,
          author: result.author || searchAuthor || null,
          isbn: result.identifiers?.isbn13 ?? (searchIsbn || null),
          variationId: result.variationId ?? null,
          quantity: 1,
          fulfillmentPreference: result.directToCustomerEligible ? "ship_to_customer" : "pickup",
          estimatedDate: result.estimatedArrivalDate ?? result.expectedAvailabilityDate ?? null,
          notes: `Created from Find Stock ${result.providerName} result.`,
          idempotencyKey: `find-stock-special-${Date.now()}`,
        }),
      });
      setCustomerRequestId(data.specialOrder.id);
      setMessage(`Created customer special order ${data.specialOrder.id}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not create the customer special order");
    }
  };

  return (
    <section className="page narrow">
      <header className="page-header">
        <div><p className="eyebrow">Unified sourcing</p><h1>Find Stock</h1></div>
        <button onClick={onBack}>Back to item</button>
      </header>
      {message && <div className="success">{message}</div>}
      <div className="editor">
        <Image imageUrl={effectiveImageUrl} />
        <div className="form-grid">
          <label>Title<input value={searchTitle} onChange={(event) => setSearchTitle(event.target.value)} /></label>
          <label>Author<input value={searchAuthor} onChange={(event) => setSearchAuthor(event.target.value)} /></label>
          <label>ISBN<input value={searchIsbn} onChange={(event) => setSearchIsbn(normalizeIsbn(event.target.value))} /></label>
          <label>Customer request ID<input value={customerRequestId} onChange={(event) => setCustomerRequestId(event.target.value)} placeholder="Optional request link for PO lines" /></label>
          <label>Customer ID<input value={customerId} onChange={(event) => setCustomerId(event.target.value)} placeholder="Required for new special orders" /></label>
          <label>Draft PO<select value={selectedDraftPoId} onChange={(event) => setSelectedDraftPoId(event.target.value)}>
            <option value="">Create a new PO when needed</option>
            {draftPurchaseOrders.map((po) => <option value={po.id} key={po.id}>{po.poNumber} - {po.vendorName ?? po.vendorId}</option>)}
          </select></label>
          <p className="empty-inline">Find Stock separates owned inventory, incoming inventory, wholesale suppliers, and consumer marketplace results.</p>
          <div className="actions wide">
            <button className="primary" onClick={() => void runSearch()} disabled={loading || contextLoading}><Search /> {loading || contextLoading ? "Searching..." : "Search stock"}</button>
            <button onClick={() => void runSearch({ title: searchTitle, author: searchAuthor, isbn: searchIsbn, manualRefresh: true })} disabled={loading || contextLoading}><RefreshCw /> Refresh suppliers</button>
          </div>
        </div>
      </div>
      {sections.map((section) => (
        <section className="supplier-section" key={section.id}>
          <h2>{section.title}</h2>
          <div className="supplier-results">
            {section.results.map((result, index) => <SupplierResultCard key={`${section.id}-${result.providerId}-${result.productUrl}-${index}`} result={result} onSelectOffer={onSelectOffer} onAddToPo={addWholesaleToDraftPo} onCreateSpecialOrder={createCustomerSpecialOrder} hasSelectedDraftPo={Boolean(selectedDraftPoId)} requestFormat={requestFormat} minimumPrices={minimumPrices} />)}
            {!loading && section.results.length === 0 && <p className="empty-inline">{emptySourcingSectionCopy(section.id)}</p>}
          </div>
        </section>
      ))}
      {!loading && results.length === 0 && <p className="empty-inline">No inventory, incoming orders, supplier mappings, or online sources returned.</p>}
    </section>
  );
}

function inventoryItemMatchesId(item: InventoryItem | null | undefined, id: string): boolean {
  return Boolean(item && (item.id === id || item.variations.some((variation) => variation.id === id)));
}

function SupplierResultCard({ result, onSelectOffer, onAddToPo, onCreateSpecialOrder, hasSelectedDraftPo, requestFormat, minimumPrices }: { result: SupplierSearchResult; onSelectOffer?: (result: SupplierSearchResult, pricing: CustomerRequestPricing) => void; onAddToPo?: (result: SupplierSearchResult, mode?: "existing" | "new" | "direct") => void; onCreateSpecialOrder?: (result: SupplierSearchResult) => void; hasSelectedDraftPo: boolean; requestFormat: ImportFormat; minimumPrices: Record<ImportFormat, Money> }) {
  const pricing = calculateCustomerRequestPricing(result.priceMoney, result.shippingMoney, requestFormat, minimumPrices);
  const isMarketplace = result.providerType === "marketplace" || result.sourceCategory === "marketplace_result";
  const displayPrice = result.wholesaleCostMoney ?? result.consumerRetailPriceMoney ?? result.priceMoney;
  const capabilities = result.capabilities ?? (result.productUrl.startsWith("http") ? ["open_external_listing"] as SourcingActionCapability[] : []);
  return (
    <article className="supplier-card">
      <Image imageUrl={result.imageUrl} />
      <div>
        <div className="supplier-card-header">
          <strong>{result.title}</strong>
          <span>{result.providerName}</span>
        </div>
        {result.author && <p>{result.author}</p>}
        <dl className="metadata-fields">
          <dt>{result.wholesaleCostMoney ? "Wholesale cost" : isMarketplace ? "Retail price" : "Price"}</dt><dd>{formatMoney(displayPrice)}</dd>
          {result.listPriceMoney && <><dt>List price</dt><dd>{formatMoney(result.listPriceMoney)}</dd></>}
          <dt>Shipping</dt><dd>{formatMoney(result.shippingMoney)}</dd>
          <dt>Qty</dt><dd>{quantitySummary(result)}</dd>
          <dt>Source type</dt><dd>{sourceTypeLabel(result)}</dd>
          <dt>Condition</dt><dd>{result.condition ?? result.returnability ?? "-"}</dd>
          <dt>Status</dt><dd>{result.availability ?? (result.resultType === "search_link" ? "Search link" : "-")}</dd>
          <dt>Freshness</dt><dd>{freshnessLabel(result)}</dd>
          {result.warehouseName && <><dt>Warehouse</dt><dd>{result.warehouseName}{result.warehouseCode ? ` (${result.warehouseCode})` : ""}</dd></>}
          {result.minimumOrderQuantity && <><dt>MOQ</dt><dd>{result.minimumOrderQuantity}</dd></>}
          {result.casePackQuantity && <><dt>Case pack</dt><dd>{result.casePackQuantity}</dd></>}
          {result.estimatedArrivalDate && <><dt>Arrival</dt><dd>{result.estimatedArrivalDate}</dd></>}
          {result.backorderEligible !== undefined && result.backorderEligible !== null && <><dt>Backorder</dt><dd>{result.backorderEligible ? "Eligible" : "Not eligible"}</dd></>}
          {result.directToCustomerEligible !== undefined && result.directToCustomerEligible !== null && <><dt>Direct ship</dt><dd>{result.directToCustomerEligible ? "Available" : "Unavailable"}</dd></>}
          {result.providerConfidence && <><dt>Confidence</dt><dd>{result.providerConfidence}</dd></>}
        </dl>
        {result.recommended && <p className="square-match-note"><strong>Recommended supplier.</strong> {(result.recommendationReasons ?? []).join("; ")}</p>}
        {!result.recommended && result.recommendationReasons?.length ? <p className="square-match-note">{result.recommendationReasons.join("; ")}</p> : null}
        {result.minimumOrderGapMoney && result.minimumOrderGapMoney.amount > 0 && <p className="square-match-note">Minimum-order gap: {formatMoney(result.minimumOrderGapMoney)}</p>}
        {result.freeFreightGapMoney && result.freeFreightGapMoney.amount > 0 && <p className="square-match-note">Free-freight gap: {formatMoney(result.freeFreightGapMoney)}</p>}
        {result.openPurchaseOrderNumber && <p className="square-match-note">Open PO {result.openPurchaseOrderNumber}: {result.incomingQuantity ?? 0} incoming.</p>}
        {result.warnings?.map((warning) => <p className="square-match-note" key={warning}>{warning}</p>)}
        {pricing && isMarketplace && (
          <dl className="metadata-fields pricing-fields">
            <dt>Tax estimate</dt><dd>{formatMoney(pricing.taxMoney)}</dd>
            <dt>Landed cost</dt><dd>{formatMoney(pricing.landedCostMoney)}</dd>
            <dt>Minimum</dt><dd>{formatMoney(minimumPrices[requestFormat])}</dd>
            <dt>Customer price</dt><dd>{formatMoney(pricing.customerPriceMoney)}</dd>
          </dl>
        )}
        {result.note && <p className="square-match-note">{result.note}</p>}
        <div className="actions">
          {capabilities.includes("open_external_listing") && <a className="button-link" href={result.productUrl} target="_blank" rel="noreferrer"><ExternalLink /> Open on {result.providerName}</a>}
          {capabilities.includes("view_open_po") && <a className="button-link" href="/app/purchasing"><ClipboardList /> View PO</a>}
          {(capabilities.includes("add_to_existing_po") || capabilities.includes("add_to_draft_po")) && hasSelectedDraftPo && <button className="primary" onClick={() => onAddToPo?.(result, "existing")}><ClipboardList /> Add to existing PO</button>}
          {capabilities.includes("create_po") && <button className="primary" onClick={() => onAddToPo?.(result, "new")}><ClipboardList /> Create PO</button>}
          {capabilities.includes("direct_to_customer") && result.directToCustomerEligible && <button onClick={() => onAddToPo?.(result, "direct")}><PackagePlus /> Direct to customer</button>}
          {capabilities.includes("create_customer_special_order") && <button onClick={() => onCreateSpecialOrder?.(result)}><User /> Create special order</button>}
          {onSelectOffer && pricing && isMarketplace && <button className="primary" onClick={() => onSelectOffer(result, pricing)}><Check /> Use for customer request</button>}
        </div>
      </div>
    </article>
  );
}

function sectionSupplierResults(results: SupplierSearchResult[]): { id: NonNullable<SupplierSearchResult["section"]>; title: string; results: SupplierSearchResult[] }[] {
  const sections: { id: NonNullable<SupplierSearchResult["section"]>; title: string }[] = [
    { id: "in_your_inventory", title: "In Your Inventory" },
    { id: "at_other_locations", title: "At Other Locations" },
    { id: "already_on_order", title: "Already On Order" },
    { id: "book_suppliers", title: "Book Suppliers" },
    { id: "other_online_sources", title: "Other Online Sources" },
  ];
  return sections.map((section) => ({ ...section, results: results.filter((result) => (result.section ?? "other_online_sources") === section.id) }));
}

function emptySourcingSectionCopy(section: string): string {
  if (section === "in_your_inventory") return "No available copies in the current store.";
  if (section === "at_other_locations") return "No matching copies at other locations.";
  if (section === "already_on_order") return "No matching open purchase-order lines.";
  if (section === "book_suppliers") return "No configured wholesale supplier mapping or enabled wholesale fixture returned availability.";
  return "No consumer marketplace or search-provider results.";
}

function quantitySummary(result: SupplierSearchResult): string {
  const parts = [
    result.availableQuantity !== undefined ? `${result.approximateQuantity ? "about " : ""}${result.availableQuantity} available` : "",
    result.onHandQuantity !== undefined ? `${result.onHandQuantity} on hand` : "",
    result.reservedQuantity !== undefined ? `${result.reservedQuantity} reserved` : "",
    result.incomingQuantity !== undefined ? `${result.incomingQuantity} incoming` : "",
    result.onOrderToSupplierQuantity ? `${result.onOrderToSupplierQuantity} supplier inbound` : "",
    result.availableQuantity === undefined && result.availabilityCategory ? result.availabilityCategory.replaceAll("_", " ") : "",
  ].filter(Boolean);
  return parts.join(" / ") || "-";
}

function freshnessLabel(result: SupplierSearchResult): string {
  if (result.stale) return result.cacheExpiresAt ? `Stale since ${new Date(result.cacheExpiresAt).toLocaleString()}` : "Stale or fixture";
  if (result.dataSourceAt) return `Source ${new Date(result.dataSourceAt).toLocaleString()}`;
  return result.retrievedAt ? new Date(result.retrievedAt).toLocaleString() : "-";
}

function sourceTypeLabel(result: SupplierSearchResult): string {
  if (result.sourceCategory === "current_store_inventory") return "Owned inventory";
  if (result.sourceCategory === "other_location_inventory") return "Owned inventory";
  if (result.sourceCategory === "reserved_inventory") return "Reserved/unavailable";
  if (result.sourceCategory === "incoming_inventory") return "Incoming inventory";
  if (result.sourceCategory === "wholesale_availability") return "Wholesale supplier";
  return "Consumer marketplace";
}

function sourcingResultToPoAction(result: SupplierSearchResult, fallbackTitle: string, fallbackIsbn: string, sourceDemand: unknown[], options: { purchaseOrderId?: string; directToCustomer?: boolean }) {
  return {
    purchaseOrderId: options.purchaseOrderId,
    vendorId: result.vendorId,
    vendorAccountId: result.vendorAccountId,
    vendorProductId: result.vendorProductId,
    editionId: result.editionId,
    variationId: result.variationId,
    isbn13: result.identifiers?.isbn13 ?? (fallbackIsbn || null),
    isbn10: result.identifiers?.isbn10 ?? null,
    upcEanGtin: result.identifiers?.upcEanGtin ?? null,
    internalSku: result.identifiers?.internalSku ?? null,
    vendorSku: result.vendorSku ?? result.identifiers?.vendorSku ?? null,
    titleSnapshot: result.title || fallbackTitle,
    quantity: Math.max(1, result.minimumOrderQuantity ?? 1),
    expectedUnitCostAmount: result.wholesaleCostMoney?.amount ?? 0,
    currency: result.wholesaleCostMoney?.currency ?? "USD",
    substitutionOffered: Boolean(result.requiresConfirmation),
    substitutionConfirmed: Boolean(result.requiresConfirmation),
    directToCustomer: Boolean(options.directToCustomer),
    idempotencyKey: `find-stock-${Date.now()}`,
    sourceDemand,
  };
}

function calculateCustomerRequestPricing(foundPriceMoney?: Money, shippingMoney?: Money, requestFormat: ImportFormat = "softcover", minimumPrices: Record<ImportFormat, Money> = defaultSettings.customerRequestMinimumPrices): CustomerRequestPricing | null {
  if (!foundPriceMoney) return null;
  const currency = foundPriceMoney.currency;
  const shippingAmount = shippingMoney?.currency === currency ? shippingMoney.amount : 0;
  const taxableAmount = foundPriceMoney.amount + shippingAmount;
  const taxAmount = Math.round(taxableAmount * 0.05);
  const landedAmount = taxableAmount + taxAmount;
  const minimum = minimumPrices[requestFormat]?.currency === currency ? minimumPrices[requestFormat].amount : 0;
  return {
    foundPriceMoney,
    shippingMoney: { amount: shippingAmount, currency },
    taxMoney: { amount: taxAmount, currency },
    landedCostMoney: { amount: landedAmount, currency },
    customerPriceMoney: { amount: Math.max(Math.round(landedAmount * 1.5), minimum), currency },
  };
}

function ReceiveBooks() {
  const [input, setInput] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<SquareLocation[]>([]);
  const [rows, setRows] = useState<ReceivingScanRow[]>([]);
  const [matches, setMatches] = useState<BookMetadata[]>([]);
  const [status, setStatus] = useState("");
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [cameraOpen, setCameraOpen] = useState(false);

  useEffect(() => {
    api<{ locations: SquareLocation[] }>("/api/admin/locations")
      .then((data) => {
        const activeLocations = data.locations.filter((location) => location.status !== "INACTIVE");
        setLocations(activeLocations);
        setLocationId((current) => current || settings.defaultReceivingLocation || activeLocations[0]?.id || "");
      })
      .catch((error: Error) => setStatus(`Could not load Square locations: ${error.message}`));
  }, [settings.defaultReceivingLocation]);

  useEffect(() => {
    api<{ settings: Partial<AppSettings> }>("/api/admin/settings")
      .then((data) => setSettings(mergeSettings(data.settings)))
      .catch((error: Error) => setStatus(`Could not load settings: ${error.message}`));
  }, []);

  const scan = async (value = input) => {
    const isbn = normalizeIsbn(value);
    if (!isValidIsbn(isbn)) {
      setStatus("Enter a valid ISBN-10 or ISBN-13");
      return;
    }
    setRows((current) => aggregateScan(current, { isbn, title: isbn, quantity: 1 }));
    setInput("");
    setStatus("Added scan to pending batch");
    const result = await api<{ results: BookMetadata[] }>("/api/admin/metadata/search", { method: "POST", body: JSON.stringify({ isbn }) });
    setMatches(result.results);
    const best = bestMetadataForIsbn(isbn, result.results);
    const squareMatch = await findSquareMatch(isbn, best);
    const importFormat = inferImportFormat(best);
    const exactSquareMatch = squareMatch.exact;
    const similarSquareMatch = squareMatch.similar;
    const priceMoney = exactSquareMatch?.priceMoney ?? settings.defaultImportPrices[importFormat];
    if (best) {
      setRows((current) => current.map((row) => row.isbn === isbn ? {
        ...row,
        title: exactSquareMatch?.title ?? best.title,
        importFormat,
        priceMoney,
        squareItemId: exactSquareMatch?.id,
        squareVariationId: exactSquareMatch?.variations[0]?.id,
        squareMatchStatus: exactSquareMatch ? "existing" : "new",
        squareMatchSummary: exactSquareMatch
          ? `Found exact ISBN in Square as ${exactSquareMatch.title}`
          : similarSquareMatch
            ? `Similar Square title found (${similarSquareMatch.title}), but the ISBN differs. This will be created as a separate edition.`
            : "No matching Square ISBN found",
        squareMatchQuantity: exactSquareMatch?.availableQuantity,
        squareMatchPriceMoney: exactSquareMatch?.priceMoney,
        metadata: best,
        metadataAutoApplied: true,
      } : row));
      setStatus(exactSquareMatch ? `Matched existing Square ISBN: ${exactSquareMatch.title}` : `Auto-applied metadata for ${best.title}`);
    } else {
      setRows((current) => current.map((row) => row.isbn === isbn ? { ...row, squareMatchStatus: "new", squareMatchSummary: "No matching Square ISBN found" } : row));
    }
  };

  const handleCameraDetected = (value: string) => {
    setCameraOpen(false);
    setInput(value);
    void scan(value);
  };

  const submit = async () => {
    const body = {
      locationId,
      idempotencyKey: crypto.randomUUID(),
      rows: rows.map((row) => ({
        ...row,
        squareVariationId: row.squareVariationId,
        proposedItem: row.squareVariationId || !row.metadata ? undefined : metadataToProposedItem(row.metadata, row.isbn, row.priceMoney ?? settings.defaultImportPrices[row.importFormat ?? "new"]),
      })),
    };
    try {
      const result = await api<ReceivingBatchResponse>("/api/admin/receiving/batches", { method: "POST", body: JSON.stringify(body) });
      const returnedRows = result.rows ?? result.result?.rows ?? [];
      const completeRows = returnedRows.filter((row) => row.status === "complete");
      const failedRows = returnedRows.filter((row) => row.status !== "complete");
      if (returnedRows.length === 0) {
        setStatus("Square receiving returned no row results. No inventory was confirmed.");
      } else if (failedRows.length > 0) {
        const firstFailure = failedRows[0];
        setStatus(`${completeRows.length} rows received, ${failedRows.length} failed${firstFailure?.error ? `: ${firstFailure.error}` : ""}`);
      } else {
        setStatus(`${completeRows.length} rows received`);
      }
    } catch (error) {
      setStatus(error instanceof Error ? `Batch receiving failed: ${error.message}` : "Batch receiving failed");
    }
  };

  return (
    <section className="page">
      <header className="page-header">
        <div><p className="eyebrow">Fast receiving</p><h1>Add Stock</h1></div>
        <button className="primary" onClick={submit} disabled={rows.length === 0}><Upload /> Submit batch</button>
      </header>
      <div className="receive-panel">
        <label className="scan-input">Scan or enter ISBN<input autoFocus inputMode="numeric" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void scan(); }} placeholder="978..." /></label>
        <label>Receiving location
          <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
            <option value="">Select location</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name ?? location.id}</option>)}
          </select>
        </label>
        <button onClick={() => setCameraOpen(true)}><Camera /> Camera scan</button>
        <button onClick={() => void scan()}><PackagePlus /> Add</button>
      </div>
      {cameraOpen && <BarcodeScanner onDetected={handleCameraDetected} onClose={() => setCameraOpen(false)} />}
      {status && <div className="success">{status}</div>}
      <div className="split">
        <div className="receiving-list">
          {rows.map((row) => (
            <article className="receive-row" key={row.isbn ?? row.title}>
              <Image imageUrl={row.metadata?.coverImageUrls[0]} />
              <div>
                <div className="receive-row-header">
                  <div>
                    <h2>{row.title}</h2>
                    <p>{row.metadata?.authors.join(", ") || row.isbn || "Manual item"}</p>
                  </div>
                  <div className="row-badges">
                    {row.squareMatchStatus === "existing" && <span className="badge in_stock">exists in Square</span>}
                    {row.squareMatchStatus === "new" && <span className="badge low_stock">new to Square</span>}
                    {row.metadataAutoApplied && <span className="badge">metadata applied</span>}
                  </div>
                </div>
                {row.squareMatchSummary && <p className="square-match-note">{row.squareMatchSummary}</p>}
                {row.squareMatchStatus === "existing" && (
                  <div className="square-match-stats" aria-label="Current Square item details">
                    <span>Current stock <strong>{row.squareMatchQuantity ?? "Not tracked"}</strong></span>
                    <span>Square price <strong>{formatMoney(row.squareMatchPriceMoney)}</strong></span>
                  </div>
                )}
                {row.metadata?.description && (
                  <div className="description-snippet">
                    <strong>Description snippet</strong>
                    <p>{descriptionSnippet(row.metadata.description)}</p>
                  </div>
                )}
                <dl className="metadata-fields">
                  <dt>ISBN</dt><dd>{row.metadata?.isbn13 ?? row.metadata?.isbn10 ?? row.isbn ?? "-"}</dd>
                  <dt>Publisher</dt><dd>{row.metadata?.publisher ?? "-"}</dd>
                  <dt>Published</dt><dd>{row.metadata?.publicationDate ?? "-"}</dd>
                  <dt>Source</dt><dd>{row.metadata ? `${row.metadata.source} / ${row.metadata.confidence}` : "-"}</dd>
                </dl>
                <div className="receive-row-actions">
                  <label>Qty<input className="qty" type="number" min={1} value={row.quantity} onChange={(event) => setRows(rows.map((candidate) => candidate === row ? { ...candidate, quantity: Number(event.target.value) } : candidate))} /></label>
                  {row.squareMatchStatus !== "existing" && (
                    <>
                      <label>Type
                        <select value={row.importFormat ?? "new"} onChange={(event) => {
                          const importFormat = event.target.value as ImportFormat;
                          setRows(rows.map((candidate) => candidate === row ? { ...candidate, importFormat, priceMoney: settings.defaultImportPrices[importFormat] } : candidate));
                        }}>
                          <option value="hardcover">Hardcover</option>
                          <option value="softcover">Softcover</option>
                          <option value="new">New item</option>
                        </select>
                      </label>
                      <label>Import price
                        <input className="price-input" inputMode="decimal" value={moneyToInput(row.priceMoney ?? settings.defaultImportPrices[row.importFormat ?? "new"])} onChange={(event) => {
                          const next = inputToMoney(event.target.value, row.priceMoney?.currency ?? settings.defaultCurrency);
                          setRows(rows.map((candidate) => candidate === row ? { ...candidate, priceMoney: next } : candidate));
                        }} />
                      </label>
                    </>
                  )}
                  {row.metadata && <button onClick={() => setRows((current) => current.map((candidate) => candidate === row ? { ...candidate, metadataEditing: !candidate.metadataEditing } : candidate))}>{row.metadataEditing ? "Done editing" : "Edit metadata"}</button>}
                  {row.metadata && <button onClick={() => setRows((current) => current.map((candidate) => candidate === row ? { ...candidate, title: candidate.isbn ?? candidate.title, metadata: undefined, metadataAutoApplied: false } : candidate))}><RotateCcw /> Undo metadata</button>}
                  <button onClick={() => setRows(rows.filter((candidate) => candidate !== row))}>Remove</button>
                </div>
                {row.metadata && row.metadataEditing && (
                  <MetadataEditor
                    metadata={row.metadata}
                    onChange={(metadata) => setRows((current) => current.map((candidate) => candidate === row ? { ...candidate, title: metadata.title, metadata } : candidate))}
                  />
                )}
              </div>
            </article>
          ))}
        </div>
        <aside className="metadata-results">
          <h2>Metadata matches</h2>
          {matches.map((match) => (
            <article key={`${match.source}-${match.sourceId}`}>
              <Image imageUrl={match.coverImageUrls[0]} />
              <div>
                <strong>{match.title}</strong>
                <span>{match.authors.join(", ")} / {match.confidence}</span>
                {match.description && <p>{descriptionSnippet(match.description)}</p>}
                <button onClick={() => setRows((current) => current.map((row, index) => index === current.length - 1 ? { ...row, title: match.title, metadata: match, metadataAutoApplied: false } : row))}>Apply this match</button>
              </div>
            </article>
          ))}
        </aside>
      </div>
    </section>
  );
}

async function findSquareMatch(isbn: string, metadata?: BookMetadata): Promise<SquareMatchResult> {
  const isbnMatch = await findFirstInventoryMatch(isbn, (item) => itemHasIsbn(item, isbn));
  if (isbnMatch) return { exact: isbnMatch, similar: null };
  if (!metadata?.title) return { exact: null, similar: null };
  const similar = await findFirstInventoryMatch(metadata.title, (item) => item.title.toLowerCase() === metadata.title.toLowerCase() && !itemHasIsbn(item, isbn));
  return { exact: null, similar };
}

function BarcodeScanner({ onDetected, onClose }: { onDetected: (value: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const [message, setMessage] = useState("Starting camera...");

  useEffect(() => {
    let active = true;
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      controlsRef.current?.stop();
      controlsRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };

    const start = async () => {
      const Detector = (globalThis as typeof globalThis & { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
      if (!navigator.mediaDevices?.getUserMedia) {
        setMessage("Camera access is not available in this browser.");
        return;
      }
      if (!Detector) {
        await startZxingFallback();
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        const detector = new Detector({ formats: ["ean_13", "ean_8", "upc_a", "upc_e"] });
        setMessage("Point the camera at the book barcode.");

        const detect = async () => {
          if (!active || !videoRef.current) return;
          try {
            const [barcode] = await detector.detect(videoRef.current);
            const rawValue = normalizeIsbn(barcode?.rawValue ?? "");
            if (isValidIsbn(rawValue)) {
              stop();
              onDetected(rawValue);
              return;
            }
          } catch {
            setMessage("Looking for a readable ISBN barcode...");
          }
          frameRef.current = requestAnimationFrame(() => { void detect(); });
        };
        void detect();
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera.");
      }
    };

    const startZxingFallback = async () => {
      const video = videoRef.current;
      if (!video) return;
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        setMessage("Point the camera at the book barcode.");
        controlsRef.current = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          video,
          (result) => {
            if (!active || !result) return;
            const rawValue = normalizeIsbn(result.getText());
            if (isValidIsbn(rawValue)) {
              stop();
              onDetected(rawValue);
            }
          },
        );
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Could not start camera barcode scanning.");
      }
    };

    void start();
    return () => {
      active = false;
      stop();
    };
  }, [onDetected]);

  return (
    <div className="scanner-panel" role="dialog" aria-label="Camera barcode scanner">
      <div className="scanner-header">
        <strong>Camera ISBN scan</strong>
        <button className="icon" aria-label="Close camera scanner" onClick={onClose}><X /></button>
      </div>
      <div className="scanner-video-wrap">
        <video ref={videoRef} className="scanner-video" muted playsInline />
        <div className="scanner-target" aria-hidden />
      </div>
      <p>{message}</p>
    </div>
  );
}

async function findFirstInventoryMatch(query: string, predicate: (item: InventoryItem) => boolean): Promise<InventoryItem | null> {
  const params = new URLSearchParams({ q: query, active: "active", limit: "10" });
  const result = await api<{ items: InventoryItem[] }>(`/api/admin/inventory?${params}`);
  return result.items.find(predicate) ?? null;
}

function bestMetadataForIsbn(isbn: string, results: BookMetadata[]): BookMetadata | undefined {
  return results.find((metadata) => metadata.confidence === "exact_isbn" && metadataHasIsbn(metadata, isbn)) ?? results[0];
}

function metadataHasIsbn(metadata: BookMetadata, isbn: string): boolean {
  return [metadata.isbn13, metadata.isbn10].some((value) => normalizeIsbn(value ?? "") === isbn);
}

function itemHasIsbn(item: InventoryItem, isbn: string): boolean {
  return [
    item.isbn,
    item.sku,
    ...item.variations.flatMap((variation) => [variation.sku, variation.gtin]),
  ].some((value) => normalizeIsbn(value ?? "") === isbn);
}

function newTradeInLine(): TradeInDraftLine {
  return { id: crypto.randomUUID(), title: "", authors: "", isbn: "", condition: "good", intendedPrice: "10.00", notes: "" };
}

function splitAuthors(value: string): string[] {
  return value.split(",").map((entry) => entry.trim()).filter(Boolean);
}

function readTradeInPendingKeys(): Record<string, string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(tradeInPendingKey) ?? "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, string> : {};
  } catch {
    return {};
  }
}

function mergeInventoryMatches(current: InventoryItem[], incoming: InventoryItem[]): InventoryItem[] {
  const byId = new Map(current.map((item) => [item.id, item]));
  for (const item of incoming) byId.set(item.id, item);
  return [...byId.values()];
}

function formatCondition(condition: TradeInCondition): string {
  return tradeInConditions.find((entry) => entry.value === condition)?.label ?? condition;
}

function formatTradeInStatus(status: TradeInStatus): string {
  return status.split("_").map((part) => part[0]?.toUpperCase() + part.slice(1)).join(" ");
}

function tradeInErrorMessage(error: unknown): string {
  if (error instanceof StaffApiError) return staffErrorMessage(error);
  if (error instanceof Error) return error.message;
  return staffErrorMessage(error);
}

function MetadataEditor({ metadata, onChange }: { metadata: BookMetadata; onChange: (metadata: BookMetadata) => void }) {
  const update = (patch: Partial<BookMetadata>) => onChange({ ...metadata, ...patch });
  return (
    <div className="metadata-editor">
      <label>Title<input value={metadata.title} onChange={(event) => update({ title: event.target.value })} /></label>
      <label>Authors<input value={metadata.authors.join(", ")} onChange={(event) => update({ authors: event.target.value.split(",").map((author) => author.trim()).filter(Boolean) })} /></label>
      <label>ISBN-13<input value={metadata.isbn13 ?? ""} onChange={(event) => update({ isbn13: normalizeIsbn(event.target.value) || undefined })} /></label>
      <label>ISBN-10<input value={metadata.isbn10 ?? ""} onChange={(event) => update({ isbn10: normalizeIsbn(event.target.value) || undefined })} /></label>
      <label>Publisher<input value={metadata.publisher ?? ""} onChange={(event) => update({ publisher: event.target.value || undefined })} /></label>
      <label>Publication date<input value={metadata.publicationDate ?? ""} onChange={(event) => update({ publicationDate: event.target.value || undefined })} /></label>
      <label className="wide">Cover image URL<input value={metadata.coverImageUrls[0] ?? ""} onChange={(event) => update({ coverImageUrls: event.target.value ? [event.target.value] : [] })} /></label>
      <label className="wide">Description<textarea rows={5} value={metadata.description ?? ""} onChange={(event) => update({ description: event.target.value || undefined })} /></label>
    </div>
  );
}

function metadataToProposedItem(metadata: BookMetadata, fallbackIsbn?: string, priceMoney: Money = defaultSettings.defaultImportPrices.new) {
  const isbn = metadata.isbn13 ?? metadata.isbn10 ?? fallbackIsbn;
  return {
    title: metadata.title,
    subtitle: metadata.subtitle,
    authors: metadata.authors,
    description: metadata.description,
    isbn13: metadata.isbn13 ?? (isbn?.length === 13 ? isbn : undefined),
    isbn10: metadata.isbn10 ?? (isbn?.length === 10 ? isbn : undefined),
    sku: isbn,
    gtin: metadata.isbn13 ?? (isbn?.length === 13 ? isbn : undefined),
    publisher: metadata.publisher,
    publicationDate: metadata.publicationDate,
    format: metadata.format,
    pageCount: metadata.pageCount,
    language: metadata.language,
    categoryIds: [],
    tags: metadata.categories,
    priceMoney,
    active: true,
    trackInventory: true,
    lowStockThreshold: 3,
    locationAvailability: [],
    imageUrl: metadata.coverImageUrls[0],
    importImage: Boolean(metadata.coverImageUrls[0]),
  };
}

function inventoryItemToRequestPrefill(item: InventoryItem): CustomerRequestPrefill {
  const isbn = item.isbn ?? item.sku ?? item.variations[0]?.gtin ?? item.variations[0]?.sku;
  const authors = item.author ? item.author.split(",").map((author) => author.trim()).filter(Boolean) : [];
  const metadata: BookMetadata = {
    source: "google_books",
    sourceId: item.id,
    confidence: "approximate",
    isbn13: isbn && isbn.length === 13 ? isbn : undefined,
    isbn10: isbn && isbn.length === 10 ? isbn : undefined,
    title: item.title,
    subtitle: item.subtitle,
    authors,
    description: item.description,
    categories: item.category ? [item.category] : [],
    coverImageUrls: item.imageUrl ? [item.imageUrl] : [],
  };
  const requestFormat = inferImportFormat(metadata);
  return {
    title: item.title,
    author: item.author,
    isbn,
    requestFormat,
    metadata,
  };
}

function requestInventoryQuery(form: { title: string; author: string; isbn: string }): string {
  const isbn = normalizeIsbn(form.isbn);
  if (isbn) return isbn;
  return [form.title.trim(), form.author.trim()].filter(Boolean).join(" ").trim();
}

function inferImportFormat(metadata?: BookMetadata): ImportFormat {
  const value = [metadata?.format, metadata?.categories.join(" "), metadata?.title].filter(Boolean).join(" ").toLowerCase();
  if (value.includes("hardcover") || value.includes("hardback")) return "hardcover";
  if (value.includes("paperback") || value.includes("softcover") || value.includes("soft cover")) return "softcover";
  return "softcover";
}

function mergeSettings(settings: Partial<AppSettings> = {}): AppSettings {
  const currency = settings.defaultCurrency ?? defaultSettings.defaultCurrency;
  return {
    ...defaultSettings,
    ...settings,
    defaultCurrency: currency,
    defaultImportPrices: {
      hardcover: normalizeMoney(settings.defaultImportPrices?.hardcover, defaultSettings.defaultImportPrices.hardcover, currency),
      softcover: normalizeMoney(settings.defaultImportPrices?.softcover, defaultSettings.defaultImportPrices.softcover, currency),
      new: normalizeMoney(settings.defaultImportPrices?.new, defaultSettings.defaultImportPrices.new, currency),
    },
    customerRequestMinimumPrices: {
      hardcover: normalizeMoney(settings.customerRequestMinimumPrices?.hardcover, defaultSettings.customerRequestMinimumPrices.hardcover, currency),
      softcover: normalizeMoney(settings.customerRequestMinimumPrices?.softcover, defaultSettings.customerRequestMinimumPrices.softcover, currency),
      new: normalizeMoney(settings.customerRequestMinimumPrices?.new, defaultSettings.customerRequestMinimumPrices.new, currency),
    },
    shippingEnabled: settings.shippingEnabled ?? defaultSettings.shippingEnabled,
    storefrontDefaultOnlineVisibility: settings.storefrontDefaultOnlineVisibility ?? defaultSettings.storefrontDefaultOnlineVisibility,
    storefrontDefaultPickupEnabled: settings.storefrontDefaultPickupEnabled ?? defaultSettings.storefrontDefaultPickupEnabled,
    storefrontDefaultShippingEnabled: settings.storefrontDefaultShippingEnabled ?? defaultSettings.storefrontDefaultShippingEnabled,
    flatRateShippingAmount: normalizeMoney(settings.flatRateShippingAmount, defaultSettings.flatRateShippingAmount, currency),
    freeShippingThresholdAmount: settings.freeShippingThresholdAmount ? normalizeMoney(settings.freeShippingThresholdAmount, settings.freeShippingThresholdAmount, currency) : undefined,
    storefrontCategoryVisibility: settings.storefrontCategoryVisibility ?? defaultSettings.storefrontCategoryVisibility,
    supplierSearchProviders: mergeSupplierProviders(settings.supplierSearchProviders),
  };
}

function normalizeMoney(value: Money | undefined, fallback: Money, currency: string): Money {
  return { amount: value?.amount ?? fallback.amount, currency: value?.currency ?? currency };
}

function moneyToInput(money: Money): string {
  return (money.amount / 100).toFixed(2);
}

function inputToMoney(value: string, currency: string): Money {
  const amount = Math.max(0, Math.round(Number(value || 0) * 100));
  return { amount: Number.isFinite(amount) ? amount : 0, currency };
}

function moveProvider(providers: SupplierSearchProvider[], from: number, to: number): SupplierSearchProvider[] {
  const next = [...providers];
  const [provider] = next.splice(from, 1);
  next.splice(to, 0, provider);
  return next;
}

function mergeSupplierProviders(providers: SupplierSearchProvider[] | undefined): SupplierSearchProvider[] {
  const stored = providers ?? [];
  const missingDefaults = defaultSettings.supplierSearchProviders.filter((provider) => !stored.some((entry) => entry.id === provider.id));
  const normalizedStored = stored.map((provider) => {
    const defaultProvider = defaultSettings.supplierSearchProviders.find((entry) => entry.id === provider.id);
    const merged = { ...defaultProvider, ...provider } as SupplierSearchProvider;
    return merged.id === "google_shopping" && merged.mode === "link" ? { ...merged, mode: "serpapi_google_shopping" as const } : merged;
  });
  return [...missingDefaults.map((provider) => ({ ...provider, capabilities: [...provider.capabilities], credentialRequirements: [...provider.credentialRequirements] })), ...normalizedStored];
}

function SettingsPage() {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [storefrontCategories, setStorefrontCategories] = useState<{ id: string; ids?: string[]; name: string; visible: boolean; duplicateCount?: number }[]>([]);
  const [toast, setToast] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  useEffect(() => { api<{ settings: Partial<AppSettings> }>("/api/admin/settings").then((data) => setSettings(mergeSettings(data.settings))); }, []);
  useEffect(() => {
    api<{ categories: { id: string; ids?: string[]; name: string; visible: boolean; duplicateCount?: number }[] }>("/api/admin/storefront/categories")
      .then((data) => setStorefrontCategories(data.categories ?? []))
      .catch((error: Error) => setToast(`Could not load storefront categories: ${error.message}`));
  }, []);
  const save = async () => {
    await api("/api/admin/settings", {
      method: "PUT",
      body: JSON.stringify({
        defaultReceivingLocation: settings.defaultReceivingLocation ?? "",
        visibleInventoryLocations: [],
        newItemLocationPolicy: settings.newItemLocationPolicy,
        newItemSelectedLocations: [],
        defaultCategoryIds: [],
        defaultLowStockThreshold: settings.defaultLowStockThreshold,
        defaultCurrency: settings.defaultCurrency,
        defaultImportPrices: settings.defaultImportPrices,
        customerRequestMinimumPrices: settings.customerRequestMinimumPrices,
        metadataProviderPriority: ["google_books", "open_library"],
        autoImportImages: settings.autoImportImages,
        storefrontVisibleLocations: [],
        storefrontCategoryVisibility: settings.storefrontCategoryVisibility,
        hideOutOfStockStorefront: true,
        shippingEnabled: settings.shippingEnabled,
        storefrontDefaultOnlineVisibility: settings.storefrontDefaultOnlineVisibility,
        storefrontDefaultPickupEnabled: settings.storefrontDefaultPickupEnabled,
        storefrontDefaultShippingEnabled: settings.storefrontDefaultShippingEnabled,
        flatRateShippingAmount: settings.flatRateShippingAmount.amount,
        freeShippingThresholdAmount: settings.freeShippingThresholdAmount?.amount ?? null,
        supplierSearchProviders: settings.supplierSearchProviders,
      }),
    });
    setToast("Settings saved");
  };
  const saveCategoryVisibility = async () => {
    await api("/api/admin/storefront/categories", {
      method: "PUT",
      body: JSON.stringify({ categories: storefrontCategories.map((category) => ({ categoryIds: category.ids ?? [category.id], name: category.name, visible: category.visible })) }),
    });
    setSettings({ ...settings, storefrontCategoryVisibility: Object.fromEntries(storefrontCategories.map((category) => [category.id, category.visible])) });
    setToast("Storefront categories saved");
  };
  const changeAdminPassword = async () => {
    await api("/api/admin/password", { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) });
    setCurrentPassword("");
    setNewPassword("");
    setToast("Password changed");
  };
  return (
    <section className="page narrow">
      <header className="page-header"><div><p className="eyebrow">Administrator controls</p><h1>Settings</h1></div><button className="primary" onClick={save}><Save /> Save</button></header>
      {toast && <div className="success">{toast}</div>}
      <div className="form-grid">
        <label>Default receiving location<input value={settings.defaultReceivingLocation ?? ""} onChange={(event) => setSettings({ ...settings, defaultReceivingLocation: event.target.value })} /></label>
        <label>Default low-stock threshold<input type="number" min={0} value={settings.defaultLowStockThreshold} onChange={(event) => setSettings({ ...settings, defaultLowStockThreshold: Number(event.target.value) })} /></label>
        <label>Default currency<input maxLength={3} value={settings.defaultCurrency} onChange={(event) => setSettings({ ...settings, defaultCurrency: event.target.value.toUpperCase() })} /></label>
        <label>New item location policy<select value={settings.newItemLocationPolicy} onChange={(event) => setSettings({ ...settings, newItemLocationPolicy: event.target.value })}><option value="receiving_location">Current receiving location</option><option value="selected">Selected locations</option><option value="all_active">All active locations</option></select></label>
        <label>Hardcover default price<input inputMode="decimal" value={moneyToInput(settings.defaultImportPrices.hardcover)} onChange={(event) => setSettings({ ...settings, defaultImportPrices: { ...settings.defaultImportPrices, hardcover: inputToMoney(event.target.value, settings.defaultCurrency) } })} /></label>
        <label>Softcover default price<input inputMode="decimal" value={moneyToInput(settings.defaultImportPrices.softcover)} onChange={(event) => setSettings({ ...settings, defaultImportPrices: { ...settings.defaultImportPrices, softcover: inputToMoney(event.target.value, settings.defaultCurrency) } })} /></label>
        <label>New item default price<input inputMode="decimal" value={moneyToInput(settings.defaultImportPrices.new)} onChange={(event) => setSettings({ ...settings, defaultImportPrices: { ...settings.defaultImportPrices, new: inputToMoney(event.target.value, settings.defaultCurrency) } })} /></label>
        <h2 className="wide">Customer request pricing minimums</h2>
        <label>Paperback customer minimum<input inputMode="decimal" value={moneyToInput(settings.customerRequestMinimumPrices.softcover)} onChange={(event) => setSettings({ ...settings, customerRequestMinimumPrices: { ...settings.customerRequestMinimumPrices, softcover: inputToMoney(event.target.value, settings.defaultCurrency) } })} /></label>
        <label>Hardcover customer minimum<input inputMode="decimal" value={moneyToInput(settings.customerRequestMinimumPrices.hardcover)} onChange={(event) => setSettings({ ...settings, customerRequestMinimumPrices: { ...settings.customerRequestMinimumPrices, hardcover: inputToMoney(event.target.value, settings.defaultCurrency) } })} /></label>
        <label>New customer minimum<input inputMode="decimal" value={moneyToInput(settings.customerRequestMinimumPrices.new)} onChange={(event) => setSettings({ ...settings, customerRequestMinimumPrices: { ...settings.customerRequestMinimumPrices, new: inputToMoney(event.target.value, settings.defaultCurrency) } })} /></label>
        <label className="wide checkbox-row"><input type="checkbox" checked={settings.autoImportImages} onChange={(event) => setSettings({ ...settings, autoImportImages: event.target.checked })} /> Automatically import cover art when creating items</label>
        <label className="wide checkbox-row"><input type="checkbox" checked={settings.shippingEnabled} onChange={(event) => setSettings({ ...settings, shippingEnabled: event.target.checked })} /> Enable shipping checkout on the storefront</label>
        <h2 className="wide">New item storefront defaults</h2>
        <label className="wide checkbox-row"><input type="checkbox" checked={settings.storefrontDefaultOnlineVisibility} onChange={(event) => setSettings({ ...settings, storefrontDefaultOnlineVisibility: event.target.checked })} /> New items are visible online by default</label>
        <label className="wide checkbox-row"><input type="checkbox" checked={settings.storefrontDefaultPickupEnabled} onChange={(event) => setSettings({ ...settings, storefrontDefaultPickupEnabled: event.target.checked })} /> New items allow pickup by default</label>
        <label className="wide checkbox-row"><input type="checkbox" checked={settings.storefrontDefaultShippingEnabled} onChange={(event) => setSettings({ ...settings, storefrontDefaultShippingEnabled: event.target.checked })} /> New items allow shipping by default</label>
      </div>
      <div className="form-grid">
        <h2 className="wide">Storefront categories</h2>
        <div className="wide category-settings">
          {storefrontCategories.map((category) => (
            <label className="checkbox-row" key={category.id}>
              <input type="checkbox" checked={category.visible} onChange={(event) => setStorefrontCategories((current) => current.map((entry) => entry.id === category.id ? { ...entry, visible: event.target.checked } : entry))} />
              <span>{category.name}{(category.duplicateCount ?? 1) > 1 ? ` (${category.duplicateCount} Square categories)` : ""}</span>
            </label>
          ))}
          {storefrontCategories.length === 0 && <p className="empty-inline">No Square categories found.</p>}
        </div>
        <div className="wide actions"><button onClick={saveCategoryVisibility}>Save category visibility</button></div>
      </div>
      <div className="form-grid">
        <h2 className="wide">Supplier searches</h2>
        <p className="wide empty-inline">Use HTTPS URL templates with placeholders such as {"{query}"}, {"{isbn}"}, {"{title}"}, or {"{author}"}. API-backed providers can return live prices when credentials are configured.</p>
        <div className="wide supplier-settings">
          {settings.supplierSearchProviders.map((provider, index) => (
            <article className="supplier-setting-row" key={provider.id}>
              <label className="checkbox-row"><input type="checkbox" checked={provider.enabled} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, enabled: event.target.checked } : entry) })} /> Enabled</label>
              <label>Name<input value={provider.name} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, name: event.target.value } : entry) })} /></label>
              <label>Mode<select value={provider.mode} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, mode: event.target.value as SupplierSearchProvider["mode"] } : entry) })}><option value="link">Search link</option><option value="serpapi_google_shopping">Google Shopping</option><option value="ebay_api">eBay API</option><option value="google_books">Google Books</option><option value="wholesale_fixture">Wholesale fixture</option></select></label>
              <label>Provider type<select value={provider.providerType} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, providerType: event.target.value as SupplierSearchProvider["providerType"] } : entry) })}><option value="marketplace">Marketplace/search</option><option value="wholesale">Wholesale book supplier</option><option value="metadata">Metadata provider</option></select></label>
              <label className="wide">Capabilities<input value={provider.capabilities.join(", ")} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, capabilities: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) as SourcingActionCapability[] } : entry) })} /></label>
              <label className="wide">Credential requirements<input value={provider.credentialRequirements.join(", ")} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, credentialRequirements: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) } : entry) })} /></label>
              <label className="wide">URL template<input value={provider.urlTemplate} onChange={(event) => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.map((entry) => entry.id === provider.id ? { ...entry, urlTemplate: event.target.value } : entry) })} /></label>
              <div className="actions wide">
                <button onClick={() => setSettings({ ...settings, supplierSearchProviders: settings.supplierSearchProviders.filter((entry) => entry.id !== provider.id) })}>Remove</button>
                <button disabled={index === 0} onClick={() => setSettings({ ...settings, supplierSearchProviders: moveProvider(settings.supplierSearchProviders, index, index - 1) })}>Move up</button>
                <button disabled={index === settings.supplierSearchProviders.length - 1} onClick={() => setSettings({ ...settings, supplierSearchProviders: moveProvider(settings.supplierSearchProviders, index, index + 1) })}>Move down</button>
              </div>
            </article>
          ))}
        </div>
        <div className="wide actions">
          <button onClick={() => setSettings({ ...settings, supplierSearchProviders: [...settings.supplierSearchProviders, { id: `supplier_${Date.now()}`, name: "New supplier", enabled: true, mode: "link", providerType: "marketplace", capabilities: ["open_external_listing"], credentialRequirements: [], urlTemplate: "https://example.com/search?q={query}" }] })}>Add supplier</button>
          <button onClick={() => setSettings({ ...settings, supplierSearchProviders: defaultSettings.supplierSearchProviders.map((provider) => ({ ...provider, capabilities: [...provider.capabilities], credentialRequirements: [...provider.credentialRequirements] })) })}>Reset supplier defaults</button>
        </div>
      </div>
      <div className="form-grid">
        <h2 className="wide">Admin login</h2>
        <label>Username<input value="bookshopadmin" disabled /></label>
        <label>Current password<input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
        <label>New password<input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
        <div className="wide actions"><button onClick={changeAdminPassword}>Change password</button></div>
      </div>
    </section>
  );
}

const retainedLegacyScreens = { ReceiveBooks, SettingsPage, PosRegister };
void retainedLegacyScreens;

type ImportJobSummary = {
  id: string;
  sourceType: string;
  importMode: string;
  status: string;
  targetLocationId: string;
  sourceFilename?: string;
  sourceSummary?: Record<string, string | number | boolean | undefined>;
  rowTotals?: Record<string, number | undefined>;
  resultCounts?: Record<string, number | undefined>;
  createdAt?: string;
};

type ImportPreviewResponse = {
  preview: {
    summary: Record<string, number>;
    rows: {
      rowNumber: number;
      status: string;
      intendedAction: string;
      normalized?: { workTitle?: string; contributors?: { displayName: string }[]; isbn13?: string; isbn10?: string; sku?: string; quantity?: number; sellingPriceAmount?: number };
      matchedEntities: Record<string, string | undefined>;
      warnings: string[];
      errors: string[];
      conflicts: { conflictType: string; fieldName?: string; suggestedResolution?: string; notes?: string }[];
      quantityDelta?: number;
    }[];
  };
};

type ImportOperation = "replace_all" | "update_counts" | "add_to_inventory";
type CatalogFieldMode = "fill_missing" | "replace_mapped";
type ImportLocation = { id: string; name: string; code?: string; active?: boolean };
type ImportMappingProfile = { id: string; name: string; sourceType: string; mappingDefinition: Record<string, string>; updatedAt?: string };
type ImportSourceDraft = { fileName: string; mimeType: string; sourceType: "generic_csv" | "generic_xlsx"; sourceText: string; headers: string[]; rows: string[][]; warning?: string; sheetName?: string; rowCount?: number; suggestedMapping?: Record<string, string> };
type ImportInspectResponse = {
  source: {
    headers: string[];
    mapping: Record<string, string>;
    sourceSummary: Record<string, unknown>;
    rows: Array<{ raw: Record<string, string>; warnings: string[]; errors: string[] }>;
    rowTotals: { totalRows: number; validRows: number; invalidRows: number };
  };
};
type ImportApplyProgress = { appliedRows: number; remainingRows: number; totalRows: number; completed: boolean; statusMessage?: string };
type ImportBackupState = { artifactId?: string; filename: string; rowCount: number };

const importFields = [
  ["workTitle", "Title", true],
  ["authors", "Author", false],
  ["isbn13", "ISBN", false],
  ["sku", "SKU", false],
  ["sourceBarcode", "Barcode", false],
  ["quantity", "Quantity", true],
  ["sellingPriceAmount", "Price", false],
  ["format", "Format", false],
  ["condition", "Condition", false],
  ["publisher", "Publisher", false],
  ["publicationDate", "Publication date", false],
  ["description", "Description", false],
  ["shelfSource", "Shelf", false],
] as const;

function ImportsPage() {
  const [step, setStep] = useState(1);
  const [operation, setOperation] = useState<ImportOperation>("add_to_inventory");
  const [catalogFieldMode, setCatalogFieldMode] = useState<CatalogFieldMode>("fill_missing");
  const [targetLocationId, setTargetLocationId] = useState("");
  const [sourceDraft, setSourceDraft] = useState<ImportSourceDraft | null>(null);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [locations, setLocations] = useState<ImportLocation[]>([]);
  const [mappingProfiles, setMappingProfiles] = useState<ImportMappingProfile[]>([]);
  const [profileName, setProfileName] = useState("");
  const [currentJob, setCurrentJob] = useState<ImportJobSummary | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse["preview"] | null>(null);
  const [history, setHistory] = useState<ImportJobSummary[]>([]);
  const [backup, setBackup] = useState<{ artifactId?: string; filename: string; rowCount: number } | null>(null);
  const [replaceText, setReplaceText] = useState("");
  const [applyKey, setApplyKey] = useState("");
  const [enrichMetadata, setEnrichMetadata] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyProgress, setApplyProgress] = useState<ImportApplyProgress | null>(null);
  const autoPreviewKey = useRef("");
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    const [historyData, settingsData, mappingsData] = await Promise.all([
      api<{ imports: ImportJobSummary[] }>("/api/admin/imports"),
      api<{ locations: ImportLocation[]; defaults?: { defaultLocationId?: string } }>("/api/admin/inventory-settings"),
      api<{ mappings: ImportMappingProfile[] }>("/api/admin/import-mappings").catch(() => ({ mappings: [] })),
    ]);
    setHistory(historyData.imports ?? []);
    const activeLocations = (settingsData.locations ?? []).filter((location) => location.active !== false);
    setLocations(activeLocations);
    setTargetLocationId((current) => current || settingsData.defaults?.defaultLocationId || activeLocations[0]?.id || "");
    setMappingProfiles(mappingsData.mappings ?? []);
  }, []);
  useEffect(() => { void loadHistory().catch(() => undefined); }, [loadHistory]);

  const importMode = operation === "add_to_inventory" ? "add_to_stock" : "replace_stock";
  const blockingConflictCount = Number(preview?.summary.blockingConflicts ?? 0);
  const hasBlockingConflicts = blockingConflictCount > 0;
  const replaceAllResumeConfirmed = operation === "replace_all" && currentJob?.status === "applying" && Boolean(backup?.artifactId);
  const replaceAllReady = operation !== "replace_all" || replaceAllResumeConfirmed || (Boolean(backup) && replaceText === "REPLACE");
  const createJob = async () => {
    if (!sourceDraft) {
      setError("Choose a CSV or XLSX file before creating an import job.");
      return;
    }
    if (!targetLocationId) {
      setError("Choose an authorized inventory location before importing.");
      return;
    }
    setError("");
    setToast("Creating import job...");
    setPreview(null);
    setBackup(null);
    setApplyKey(crypto.randomUUID());
    const data = await api<{ job: ImportJobSummary; rows: { errors: string[] }[]; rowTotals?: { totalRows?: number } }>("/api/admin/imports", {
      method: "POST",
      body: JSON.stringify({
        sourceType: sourceDraft.sourceType,
        importMode,
        targetLocationId,
        sourceText: sourceDraft.sourceText,
        filename: sourceDraft.fileName,
        mimeType: sourceDraft.mimeType,
        fieldMapping: compactFieldMapping(fieldMapping),
        conflictPolicies: { operationMode: operation, catalogFieldMode },
        idempotencyKey: crypto.randomUUID(),
      }),
    });
    setCurrentJob(data.job);
    setStep(4);
    setToast(`Parsed ${data.rowTotals?.totalRows ?? data.rows.length} rows`);
    await loadHistory();
  };

  const generatePreview = useCallback(async () => {
    if (!currentJob) return;
    setError("");
    setPreviewLoading(true);
    try {
      const data = await api<ImportPreviewResponse>(`/api/admin/imports/${currentJob.id}/preview`, {
        method: "POST",
        body: JSON.stringify({ importMode, targetLocationId, explicitReplaceStockConfirmation: true, enrichMetadata }),
      });
      setPreview(data.preview);
      setStep(5);
      setToast("Preview generated");
      await loadHistory();
    } finally {
      setPreviewLoading(false);
    }
  }, [currentJob, enrichMetadata, importMode, loadHistory, targetLocationId]);

  useEffect(() => {
    if (step !== 4 || !currentJob || previewLoading) return;
    const key = `${currentJob.id}:${importMode}:${targetLocationId}:${enrichMetadata}`;
    if (autoPreviewKey.current === key) return;
    autoPreviewKey.current = key;
    generatePreview().catch((err: unknown) => {
      setPreviewLoading(false);
      setToast("");
      setError(staffErrorMessage(err));
    });
  }, [currentJob, enrichMetadata, generatePreview, importMode, previewLoading, step, targetLocationId]);

  const generateBackup = async () => {
    if (!currentJob) return;
    const data = await api<{ backup: { artifactId?: string; filename: string; rowCount: number } }>(`/api/admin/imports/${currentJob.id}/backup`, {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }),
    });
    setBackup(data.backup);
    setToast(`Backup ready with ${data.backup.rowCount} rows`);
    await loadHistory();
  };

  const backupFromJob = (job: ImportJobSummary): ImportBackupState | null => {
    const artifactId = typeof job.sourceSummary?.backupArtifactId === "string" ? job.sourceSummary.backupArtifactId : undefined;
    const filename = typeof job.sourceSummary?.backupFilename === "string" ? job.sourceSummary.backupFilename : "";
    const rowCount = Number(job.sourceSummary?.backupRowCount ?? 0);
    return artifactId || filename ? { artifactId, filename: filename || "inventory-backup.csv", rowCount } : null;
  };

  const resumeHistoryJob = (job: ImportJobSummary) => {
    const operationMode = String(job.sourceSummary?.operationMode ?? "");
    const nextOperation: ImportOperation = operationMode === "replace_all"
      ? "replace_all"
      : job.importMode === "replace_stock"
        ? "update_counts"
        : "add_to_inventory";
    setOperation(nextOperation);
    setTargetLocationId(job.targetLocationId);
    setCurrentJob(job);
    setBackup(backupFromJob(job));
    setReplaceText("");
    setApplyKey(crypto.randomUUID());
    setPreview({
      summary: {
        ...(job.rowTotals ?? {}),
        blockingConflicts: Number(job.rowTotals?.blockingConflicts ?? 0),
      },
      rows: [],
    });
    setApplyProgress({
      appliedRows: Number(job.resultCounts?.appliedRows ?? 0),
      remainingRows: Number(job.resultCounts?.remainingRows ?? job.rowTotals?.validRows ?? 0),
      totalRows: Math.max(
        Number(job.resultCounts?.appliedRows ?? 0) + Number(job.resultCounts?.remainingRows ?? 0),
        Number(job.rowTotals?.validRows ?? 0),
        1,
      ),
      completed: false,
      statusMessage: job.status === "applying" ? "Ready to continue from the last confirmed row." : "Ready to continue this import job.",
    });
    setError("");
    setToast("");
    setStep(job.status === "applying" ? 6 : 5);
  };

  const continueParsedHistoryJob = (job: ImportJobSummary) => {
    const operationMode = String(job.sourceSummary?.operationMode ?? "");
    const nextOperation: ImportOperation = operationMode === "replace_all"
      ? "replace_all"
      : job.importMode === "replace_stock"
        ? "update_counts"
        : "add_to_inventory";
    setOperation(nextOperation);
    setTargetLocationId(job.targetLocationId);
    setCurrentJob(job);
    setPreview(null);
    setBackup(backupFromJob(job));
    setReplaceText("");
    setApplyProgress(null);
    setApplyKey(crypto.randomUUID());
    autoPreviewKey.current = "";
    setError("");
    setToast("");
    if (job.status === "parsed") {
      setStep(4);
      return;
    }
    setPreview({
      summary: {
        ...(job.rowTotals ?? {}),
        blockingConflicts: Number(job.rowTotals?.blockingConflicts ?? 0),
      },
      rows: [],
    });
    setStep(5);
  };

  const applyImport = async () => {
    if (!currentJob) return;
    if (!replaceAllReady) {
      setError("Generate the backup and type REPLACE before running a replace-all import.");
      return;
    }
    if (hasBlockingConflicts) {
      setError("Resolve blocking import conflicts before applying. Review the dry-run conflict rows, then regenerate the preview.");
      setStep(5);
      return;
    }
    setError("");
    const key = applyKey || crypto.randomUUID();
    if (!applyKey) setApplyKey(key);
    const initialTotalRows = Math.max(Number(preview?.summary.validRows ?? applyProgress?.totalRows ?? 0), 1);
    setApplyProgress((current) => ({
      appliedRows: current?.completed ? 0 : current?.appliedRows ?? 0,
      remainingRows: current?.completed ? initialTotalRows : current?.remainingRows ?? initialTotalRows,
      totalRows: initialTotalRows,
      completed: false,
      statusMessage: "Starting import application...",
    }));
    let result: Record<string, number | boolean> = { remainingRows: 1, completed: false };
    let guard = 0;
    const maxApplyRequests = Math.max(100, Math.ceil(initialTotalRows / 5) + 25);
    let transientFailures = 0;
    while (!result.completed && Number(result.remainingRows ?? 0) > 0 && guard < maxApplyRequests) {
      let data: { result: Record<string, number | boolean> };
      try {
        data = await api<{ result: Record<string, number | boolean> }>(`/api/admin/imports/${currentJob.id}/apply`, {
          method: "POST",
          body: JSON.stringify({ idempotencyKey: key, explicitReplaceStockConfirmation: true }),
        });
        transientFailures = 0;
      } catch (err) {
        if (!isTransientImportApplyError(err)) throw err;
        transientFailures += 1;
        const waitMs = Math.min(5000, transientFailures * 1500);
        const currentAppliedRows = applyProgress?.appliedRows ?? Number(result.appliedRows ?? 0);
        setApplyProgress((current) => current ? { ...current, statusMessage: transientFailures < 4 ? `Server was busy. Retrying the same chunk in ${Math.round(waitMs / 1000)} seconds...` : "Import paused after repeated server busy responses." } : current);
        if (transientFailures >= 4) {
          setError(`Import paused after ${currentAppliedRows} confirmed rows because the server stayed busy. Click Execute import again to continue from the last confirmed row with the same retry key.`);
          await loadHistory();
          return;
        }
        await delay(waitMs);
        continue;
      }
      result = data.result;
      const appliedRows = Number(result.appliedRows ?? 0);
      const remainingRows = Number(result.remainingRows ?? 0);
      const completed = Boolean(result.completed);
      setApplyProgress({
        appliedRows,
        remainingRows,
        totalRows: Math.max(appliedRows + remainingRows, appliedRows, initialTotalRows, 1),
        completed,
        statusMessage: completed ? "Import application complete." : "Applying next chunk after a short pause...",
      });
      guard += 1;
      if (!result.completed && remainingRows > 0) await delay(importApplyChunkDelayMs());
    }
    if (!result.completed && Number(result.remainingRows ?? 0) > 0) {
      setError("Import is still applying. Click Execute import again to continue from the last confirmed row.");
      return;
    }
    setToast(`Applied ${result.appliedRows ?? 0} rows`);
    setApplyProgress((current) => current ? { ...current, completed: true, remainingRows: 0, statusMessage: "Import application complete." } : current);
    setStep(7);
    await loadHistory();
  };

  const exportInventory = async () => {
    if (!targetLocationId) return;
    const text = await apiText<string>(`/api/admin/imports/export?locationId=${encodeURIComponent(targetLocationId)}`);
    downloadText(text, `paperbackos-inventory-export-${targetLocationId}.csv`, "text/csv");
  };

  const run = (action: () => Promise<void>) => {
    action().catch((err: unknown) => {
      setToast("");
      setError(staffErrorMessage(err));
    });
  };

  const chooseFile = (file?: File) => {
    if (!file) return;
    setError("");
    setToast("Reading spreadsheet...");
    run(async () => {
      const draft = await readImportFile(file);
      setSourceDraft(draft);
      setFieldMapping(draft.suggestedMapping ?? suggestMapping(draft.headers, mappingProfiles));
      setCurrentJob(null);
      setPreview(null);
      setBackup(null);
      setApplyProgress(null);
      setStep(3);
      setToast(draft.warning ?? `Loaded ${draft.rows.length} rows from ${draft.fileName}`);
    });
  };

  const saveMappingProfile = async () => {
    if (!profileName.trim()) {
      setError("Name the mapping profile before saving it.");
      return;
    }
    await api("/api/admin/import-mappings", {
      method: "POST",
      body: JSON.stringify({ name: profileName.trim(), sourceType: sourceDraft?.sourceType ?? "generic_csv", mappingDefinition: fieldMapping }),
    });
    setProfileName("");
    setToast("Mapping profile saved");
    await loadHistory();
  };
  const stepLabels = ["Operation", "Upload", "Mapping", "Validate", "Preview", "Confirm", "Results"];

  return (
    <section className="page">
      <header className="page-header">
        <div><p className="eyebrow">Inventory operations</p><h1>Spreadsheet import & export</h1></div>
        <button onClick={() => run(loadHistory)}><RefreshCw /> Refresh</button>
      </header>
      {toast && <div className="success">{toast}</div>}
      {error && <div className="error">{error}</div>}
      <ol className="trade-stepper import-stepper" aria-label="Import progress">
        {stepLabels.map((label, index) => <li key={label} className={step >= index + 1 ? "active" : ""}><span>{index + 1}</span>{label}</li>)}
      </ol>
      {step > 1 && (
        <div className="wizard-toolbar">
          <button onClick={() => setStep(Math.max(1, step - 1))}>Back</button>
          <span>Step {step} of {stepLabels.length}: {stepLabels[step - 1]}</span>
        </div>
      )}

      {step === 1 && <div className="panel import-step-panel">
        <h2>Choose operation</h2>
        <p className="muted">Start by choosing how this spreadsheet should affect inventory. The next steps will only show the controls needed for that operation.</p>
        <div className="choice-grid">
          <button className={operation === "replace_all" ? "choice-card selected" : "choice-card"} onClick={() => { setOperation("replace_all"); setStep(2); }}>
            <strong>Replace all inventory</strong>
            <span>Spreadsheet becomes the selected location's count. Missing quantity items go to zero.</span>
            <small>Example: current 8, file 3, result 3. Absent current stock becomes 0. High risk.</small>
          </button>
          <button className={operation === "update_counts" ? "choice-card selected" : "choice-card"} onClick={() => { setOperation("update_counts"); setStep(2); }}>
            <strong>Update inventory counts</strong>
            <span>Imported quantity replaces the count for matched rows only.</span>
            <small>Example: current 8, file 3, result 3. Absent items are unchanged.</small>
          </button>
          <button className={operation === "add_to_inventory" ? "choice-card selected" : "choice-card"} onClick={() => { setOperation("add_to_inventory"); setStep(2); }}>
            <strong>Add to inventory</strong>
            <span>Imported quantity is added to current stock for matched rows.</span>
            <small>Example: current 8, file 3, result 11. Negative quantities are rejected.</small>
          </button>
        </div>
        <div className="form-grid">
          <label>Inventory location<select value={targetLocationId} onChange={(event) => setTargetLocationId(event.target.value)}>
            <option value="">Choose location</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name} {location.code ? `(${location.code})` : ""}</option>)}
          </select></label>
          <label>Catalog fields<select value={catalogFieldMode} onChange={(event) => setCatalogFieldMode(event.target.value as CatalogFieldMode)}>
            <option value="fill_missing">Fill missing fields only</option>
            <option value="replace_mapped">Replace mapped fields</option>
          </select></label>
          <label className="wide checkbox-row"><input type="checkbox" checked={enrichMetadata} onChange={(event) => { setEnrichMetadata(event.target.checked); setPreview(null); autoPreviewKey.current = ""; }} /> Run metadata lookups during dry run to fill blank import fields</label>
          <div className="actions"><button onClick={() => run(exportInventory)} disabled={!targetLocationId}><Upload /> Export current inventory</button></div>
        </div>
      </div>}

      {step === 2 && <div className="panel import-step-panel">
        <h2>Upload file</h2>
        <p className="muted">Choose the spreadsheet for this {operationLabel(operation).toLowerCase()} import. Uploading a valid file moves you to column mapping.</p>
        <div className="drop-zone">
          <Upload aria-hidden />
          <strong>{sourceDraft ? sourceDraft.fileName : "Drop a CSV or XLSX file here"}</strong>
          <span>{sourceDraft ? `${sourceDraft.rowCount ?? sourceDraft.rows.length} rows, ${sourceDraft.headers.length} columns${sourceDraft.sheetName ? ` from ${sourceDraft.sheetName}` : ""}. ${sourceDraft.warning ?? ""}` : "CSV and XLSX are supported. Legacy XLS must be saved as CSV or XLSX first."}</span>
          <input type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls" onChange={(event) => chooseFile(event.target.files?.[0])} />
        </div>
        {sourceDraft && <div className="table-wrap compact-table">
          <table><thead><tr>{sourceDraft.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{sourceDraft.rows.slice(0, 5).map((row, index) => <tr key={index}>{sourceDraft.headers.map((header, headerIndex) => <td key={header}>{row[headerIndex] ?? ""}</td>)}</tr>)}</tbody></table>
        </div>}
        <div className="actions">
          <button className="primary" disabled={!sourceDraft} onClick={() => setStep(3)}><ChevronRight /> Continue to mapping</button>
        </div>
      </div>}

      {step === 3 && <div className="panel import-step-panel">
        <h2>Map columns</h2>
        {!sourceDraft ? <p className="muted">Upload a file to map columns.</p> : (
          <>
            <div className="mapping-list">
              {importFields.map(([field, label, required]) => (
                <label key={field}>{label}{required ? " *" : ""}
                  <select value={fieldMapping[field] ?? ""} onChange={(event) => setFieldMapping({ ...fieldMapping, [field]: event.target.value })}>
                    <option value="">Not mapped</option>
                    {sourceDraft.headers.map((header) => <option key={header} value={header}>{header}</option>)}
                  </select>
                  <small>{fieldMapping[field] ? `Sample: ${sampleFor(sourceDraft, fieldMapping[field])}` : "Leave unmapped"}</small>
                </label>
              ))}
            </div>
            <div className="actions">
              <label className="inline-field">Profile name<input value={profileName} onChange={(event) => setProfileName(event.target.value)} placeholder="Monthly inventory CSV" /></label>
              <label className="inline-field checkbox-row"><input type="checkbox" checked={enrichMetadata} onChange={(event) => { setEnrichMetadata(event.target.checked); setPreview(null); autoPreviewKey.current = ""; }} /> Fill blank fields from metadata</label>
              <button onClick={() => run(saveMappingProfile)}><Save /> Save mapping</button>
              <button className="primary" onClick={() => run(createJob)}><Upload /> Create import job</button>
            </div>
            {mappingProfiles.length > 0 && <p className="muted">Saved profiles available: {mappingProfiles.map((profile) => profile.name).join(", ")}</p>}
          </>
        )}
      </div>}

      {step === 4 && <div className="panel import-step-panel">
        <h2>Validate import</h2>
        <p className="muted">PaperbackOS is generating the server dry-run automatically. Validation checks matching, duplicate rows, quantities, metadata enrichment, and blocking conflicts before anything mutates.</p>
        <div className="actions">
          <button disabled={!currentJob || previewLoading} onClick={() => run(generatePreview)}><Search /> {previewLoading ? "Generating preview..." : "Regenerate dry-run preview"}</button>
        </div>
      </div>}

      {step === 5 && <div className="panel import-step-panel">
        <h2>Preview changes</h2>
        <p className="muted">Review the server-generated dry-run. If the impact looks right, continue to confirmation.</p>
        {!preview && <p className="empty-inline">Generate a dry-run preview before reviewing impact.</p>}
        {operation === "replace_all" && <div className="warning">
          <strong>Replace-all protection</strong>
          <p>Replace-all imports require a backup and destructive confirmation on the next step.</p>
        </div>}
        {preview && (
          <>
            <ImportPreviewSummary preview={preview} operation={operation} />
          </>
        )}
        <div className="actions">
          <button disabled={!currentJob} onClick={() => run(generatePreview)}><RefreshCw /> Regenerate preview</button>
          <button className="primary" disabled={!preview || hasBlockingConflicts} onClick={() => setStep(6)}><ChevronRight /> Continue to confirmation</button>
        </div>
      </div>}

      {step === 6 && <div className="panel import-step-panel">
        <h2>Confirm import</h2>
        <p className="muted">Confirm the exact operation. Execution uses the existing server-owned import job, preview, and idempotency key.</p>
        {operation === "replace_all" && <div className="warning">
          <strong>Replace-all protection</strong>
          <p>{replaceAllResumeConfirmed ? "This in-progress replace-all import already has its backup and confirmation recorded." : <>Backup must succeed before execution. Type <code>REPLACE</code> after reviewing the dry-run impact.</>}</p>
          <p>{backup ? `Backup ready: ${backup.filename} (${backup.rowCount} rows)` : "No backup generated yet."}</p>
          {!replaceAllResumeConfirmed && <div className="actions"><button disabled={!currentJob || Boolean(backup)} onClick={() => run(generateBackup)}><Upload /> Generate required backup</button></div>}
          {!replaceAllResumeConfirmed && <label>Confirmation<input value={replaceText} onChange={(event) => setReplaceText(event.target.value)} placeholder="REPLACE" /></label>}
        </div>}
        {preview && <div className="metric-grid">
          <div className="metric"><span>Operation</span><strong>{operationLabel(operation)}</strong></div>
          <div className="metric"><span>Valid rows</span><strong>{preview.summary.validRows}</strong></div>
          <div className="metric"><span>Invalid rows</span><strong>{preview.summary.invalidRows}</strong></div>
          <div className="metric"><span>New variations</span><strong>{preview.summary.newVariations}</strong></div>
          <div className="metric"><span>Blocking conflicts</span><strong>{blockingConflictCount}</strong></div>
        </div>}
        {hasBlockingConflicts && <div className="warning" role="alert">
          <strong>Import cannot run yet</strong>
          <p>{blockingConflictCount} blocking conflict{blockingConflictCount === 1 ? "" : "s"} must be resolved before execution. Return to the dry-run preview to inspect the conflict rows.</p>
        </div>}
        <div className="actions">
          <button disabled={!currentJob} onClick={() => setStep(5)}>Review preview</button>
          <button className="primary" disabled={!currentJob || !preview || hasBlockingConflicts || !replaceAllReady} onClick={() => run(applyImport)}><Check /> Execute import</button>
        </div>
        {applyProgress && <div className="import-progress" role="status" aria-live="polite">
          <div className="progress-track"><span style={{ width: `${Math.min(100, Math.round((applyProgress.appliedRows / Math.max(applyProgress.totalRows, 1)) * 100))}%` }} /></div>
          <strong>{applyProgress.completed ? "Import complete" : "Applying import"}</strong>
          <span>{applyProgress.appliedRows} of {applyProgress.totalRows} rows applied{applyProgress.remainingRows > 0 ? `, ${applyProgress.remainingRows} remaining` : ""}</span>
          {applyProgress.statusMessage && <span>{applyProgress.statusMessage}</span>}
        </div>}
      </div>}

      {step === 7 && <div className="panel import-step-panel">
        <h2>Import result</h2>
        <p className="muted">The completed job remains in history. Start a new import only after reviewing the result.</p>
        {applyProgress && <div className="import-progress" role="status" aria-live="polite">
          <div className="progress-track"><span style={{ width: `${Math.min(100, Math.round((applyProgress.appliedRows / Math.max(applyProgress.totalRows, 1)) * 100))}%` }} /></div>
          <strong>{applyProgress.completed ? "Import complete" : "Applying import"}</strong>
          <span>{applyProgress.appliedRows} of {applyProgress.totalRows} rows applied{applyProgress.remainingRows > 0 ? `, ${applyProgress.remainingRows} remaining` : ""}</span>
          {applyProgress.statusMessage && <span>{applyProgress.statusMessage}</span>}
        </div>}
        <div className="actions"><button onClick={() => { setSourceDraft(null); setCurrentJob(null); setPreview(null); setBackup(null); setReplaceText(""); setApplyProgress(null); autoPreviewKey.current = ""; setStep(1); }}>Start another import</button></div>
      </div>}
      <div className="panel import-step-panel">
        <h2>Import history</h2>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Created</th><th>File</th><th>Mode</th><th>Status</th><th>Rows</th><th>Result</th><th>Action</th></tr></thead>
            <tbody>
              {history.map((job) => (
                <tr key={job.id}>
                  <td>{job.createdAt ? new Date(job.createdAt).toLocaleString() : ""}</td>
                  <td>{job.sourceFilename ?? job.sourceType}</td>
                  <td>{String(job.sourceSummary?.operationMode ?? job.importMode).replaceAll("_", " ")}</td>
                  <td>{job.status}</td>
                  <td>{job.rowTotals?.totalRows ?? ""}</td>
                  <td>{job.resultCounts?.appliedRows ?? ""}</td>
                  <td>
                    {job.status === "applying" && <button onClick={() => resumeHistoryJob(job)}><RefreshCw /> Resume</button>}
                    {["parsed", "ready_to_apply", "preview_ready", "awaiting_resolution"].includes(job.status) && <button onClick={() => continueParsedHistoryJob(job)}><ChevronRight /> Continue</button>}
                  </td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={7}>No imports yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function ImportPreviewSummary({ preview, operation }: { preview: ImportPreviewResponse["preview"]; operation: ImportOperation }) {
  const summary = preview.summary;
  const value = (key: string) => Number(summary[key] ?? 0);
  const blockingConflicts = value("blockingConflicts");
  const attentionRows = preview.rows.filter((row) => row.errors.length > 0 || row.warnings.length > 0 || row.conflicts.length > 0).slice(0, 6);
  const status = blockingConflicts > 0 || value("invalidRows") > 0 || value("conflictRows") > 0
    ? "Needs review"
    : value("warnings") > 0
      ? "Ready with warnings"
      : "Ready to import";
  const statusTone = status === "Ready to import" ? "ok" : status === "Ready with warnings" ? "warn" : "danger";
  const sections = [
    {
      title: "Readiness",
      metrics: [
        { label: "Rows in file", value: value("totalRows") },
        { label: "Ready rows", value: value("validRows"), tone: "ok" },
        { label: "Rows needing fixes", value: value("invalidRows") + value("conflictRows"), tone: value("invalidRows") + value("conflictRows") > 0 ? "danger" : "ok" },
        { label: "Warnings", value: value("warnings"), tone: value("warnings") > 0 ? "warn" : undefined },
      ],
    },
    {
      title: "Catalog impact",
      metrics: [
        { label: "Existing items matched", value: value("matchedVariations") },
        { label: "New items", value: value("newVariations") },
        { label: "Existing editions matched", value: value("matchedEditions") },
        { label: "New editions", value: value("newEditions") },
      ],
    },
    {
      title: "Inventory impact",
      metrics: [
        { label: "Counts increasing", value: value("quantityIncreases"), tone: value("quantityIncreases") > 0 ? "ok" : undefined },
        { label: "Counts decreasing", value: value("quantityDecreases"), tone: value("quantityDecreases") > 0 ? "warn" : undefined },
        { label: "Counts unchanged", value: value("unchangedQuantities") },
        { label: "Inventory movements", value: value("expectedMovementCount") },
      ],
    },
  ];
  if (operation === "replace_all" || value("absentExistingItems") > 0 || value("absentQuantityRemoved") > 0) {
    sections.push({
      title: "Replace-all scope",
      metrics: [
        { label: "Items absent from file", value: value("absentExistingItems"), tone: value("absentExistingItems") > 0 ? "warn" : undefined },
        { label: "Quantity removed", value: value("absentQuantityRemoved"), tone: value("absentQuantityRemoved") > 0 ? "warn" : undefined },
        { label: "Blocking conflicts", value: blockingConflicts, tone: blockingConflicts > 0 ? "danger" : "ok" },
        { label: "New exact copies", value: value("newCopies") },
      ],
    });
  }

  return (
    <div className="import-preview-summary">
      <section className={`import-impact-banner ${statusTone}`} aria-label="Import preview status">
        <div>
          <span>Dry-run impact</span>
          <strong>{status}</strong>
          <p>{importPreviewGuidance(operation, statusTone)}</p>
        </div>
        <div className="import-impact-total">
          <strong>{value("validRows")}</strong>
          <span>ready of {value("totalRows")}</span>
        </div>
      </section>
      <div className="import-impact-grid">
        {sections.map((section) => (
          <section className="import-impact-section" key={section.title}>
            <h3>{section.title}</h3>
            <div className="import-metric-grid">
              {section.metrics.map((metric) => <ImportMetric key={metric.label} {...metric} />)}
            </div>
          </section>
        ))}
      </div>
      {attentionRows.length > 0 && (
        <section className="import-attention-panel" aria-label="Rows needing attention">
          <div className="section-heading">
            <h3>Rows needing attention</h3>
            <span>{preview.rows.length > attentionRows.length ? `Showing ${attentionRows.length} examples` : `${attentionRows.length} row${attentionRows.length === 1 ? "" : "s"}`}</span>
          </div>
          <div className="import-attention-list">
            {attentionRows.map((row) => (
              <article key={row.rowNumber}>
                <strong>Row {row.rowNumber}: {row.normalized?.workTitle ?? "Unparsed item"}</strong>
                <span>{rowStatusLabel(row)}</span>
                <p>{rowMessage(row)}</p>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="import-row-review">
        <div className="section-heading">
          <h3>Row review</h3>
          <span>Showing first {Math.min(preview.rows.length, 25)} of {preview.rows.length}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Row</th><th>Item</th><th>Action</th><th>Status</th><th>Current</th><th>Imported</th><th>Change</th><th>Notes</th></tr></thead>
            <tbody>
              {preview.rows.slice(0, 25).map((row) => (
                <tr key={row.rowNumber}>
                  <td>{row.rowNumber}</td>
                  <td>{row.normalized?.workTitle ?? "Unparsed"}<br /><small>{row.normalized?.contributors?.map((entry) => entry.displayName).join(", ") || row.normalized?.isbn13 || row.normalized?.sku}</small></td>
                  <td>{formatImportAction(row.intendedAction)}</td>
                  <td><span className={`status-chip ${rowStatusTone(row)}`}>{rowStatusLabel(row)}</span></td>
                  <td>{row.matchedEntities.stockOnHand ?? ""}</td>
                  <td>{row.normalized?.quantity ?? ""}</td>
                  <td>{formatQuantityDelta(row.quantityDelta)}</td>
                  <td>{rowMessage(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ImportMetric({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return <div className={`import-metric ${tone ?? ""}`}><span>{label}</span><strong>{value}</strong></div>;
}

function importPreviewGuidance(operation: ImportOperation, tone: string): string {
  if (tone === "danger") return "Resolve blocked or invalid rows before this import can run.";
  if (operation === "replace_all") return "Backup and explicit replacement confirmation are required before execution.";
  if (tone === "warn") return "Warnings will not block the import, but review them before confirming.";
  return "No inventory or catalog changes have been applied yet.";
}

function rowStatusLabel(row: ImportPreviewResponse["preview"]["rows"][number]): string {
  if (row.status === "conflict" || row.conflicts.length > 0) return "Conflict";
  if (row.status === "invalid" || row.errors.length > 0) return "Invalid";
  if (row.warnings.length > 0) return "Warning";
  return "Ready";
}

function rowStatusTone(row: ImportPreviewResponse["preview"]["rows"][number]): string {
  const label = rowStatusLabel(row);
  if (label === "Conflict" || label === "Invalid") return "danger";
  if (label === "Warning") return "warn";
  return "ok";
}

function rowMessage(row: ImportPreviewResponse["preview"]["rows"][number]): string {
  const conflict = row.conflicts.map((entry) => entry.notes ?? entry.conflictType).filter(Boolean).join("; ");
  return conflict || row.errors.join("; ") || row.warnings.join("; ") || "Ready to import.";
}

function formatImportAction(action: string): string {
  return action.replaceAll("_", " ");
}

function formatQuantityDelta(delta: number | undefined): string {
  if (delta === undefined) return "";
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

function operationLabel(operation: ImportOperation): string {
  if (operation === "replace_all") return "Replace all inventory";
  if (operation === "update_counts") return "Update inventory counts";
  return "Add to inventory";
}

function compactFieldMapping(mapping: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(mapping).filter(([, header]) => header.trim()));
}

async function readImportFile(file: File): Promise<ImportSourceDraft> {
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension === "xls") throw new StaffApiError({ status: 415, code: "xls_not_supported", message: "Legacy XLS files are not supported safely. Save the workbook as CSV or XLSX." });
  if (extension === "xlsx") {
    const sourceText = await fileToBase64(file);
    const inspected = await api<ImportInspectResponse>("/api/admin/imports/inspect", {
      method: "POST",
      body: JSON.stringify({
        sourceType: "generic_xlsx",
        sourceText,
        filename: file.name,
        mimeType: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
    });
    const headers = inspected.source.headers;
    if (headers.length === 0) throw new StaffApiError({ status: 400, code: "empty_workbook", message: "No usable rows were found in this workbook." });
    const sheetName = typeof inspected.source.sourceSummary.selectedSheet === "string" ? inspected.source.sourceSummary.selectedSheet : undefined;
    return {
      fileName: file.name,
      mimeType: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      sourceType: "generic_xlsx",
      sourceText,
      headers,
      rows: inspected.source.rows.map((row) => headers.map((header) => String(row.raw[header] ?? ""))),
      rowCount: inspected.source.rowTotals.totalRows,
      sheetName,
      suggestedMapping: inspected.source.mapping,
      warning: sheetName ? `Loaded worksheet "${sheetName}". Save a specific sheet as CSV if you need a different worksheet.` : "Workbook inspected on the server.",
    };
  }
  if (extension !== "csv") throw new StaffApiError({ status: 415, code: "unsupported_import_file", message: "Choose a CSV or XLSX file." });
  const sourceText = await fileToText(file);
  const rows = skipLeadingBlankRows(parseClientCsv(sourceText));
  return { fileName: file.name, mimeType: file.type || "text/csv", sourceType: "generic_csv", sourceText, headers: rows[0] ?? [], rows: rows.slice(1), rowCount: Math.max(0, rows.length - 1) };
}

function skipLeadingBlankRows(rows: string[][]): string[][] {
  const firstContentIndex = rows.findIndex((row) => row.some((cell) => cell.trim()));
  return firstContentIndex >= 0 ? rows.slice(firstContentIndex) : [];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function fileToText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsText(file);
  });
}

function suggestMapping(headers: string[], profiles: ImportMappingProfile[]): Record<string, string> {
  const profile = profiles.find((candidate) => Object.values(candidate.mappingDefinition).some((header) => headers.includes(String(header))));
  const mapping: Record<string, string> = profile ? Object.fromEntries(Object.entries(profile.mappingDefinition).filter(([, header]) => headers.includes(String(header)))) as Record<string, string> : {};
  for (const [field, label] of importFields) {
    if (mapping[field]) continue;
    const match = headers.find((header) => normalizedImportHeader(header) === normalizedImportHeader(label) || importAliases(field).includes(normalizedImportHeader(header)));
    if (match) mapping[field] = match;
  }
  return mapping;
}

function importAliases(field: string): string[] {
  const aliases: Record<string, string[]> = {
    workTitle: ["title", "book title", "item name", "name"],
    authors: ["author", "authors", "creator"],
    isbn13: ["isbn", "isbn13", "isbn 13", "isbn-13", "ean"],
    sourceBarcode: ["barcode", "scan code"],
    quantity: ["qty", "quantity", "count", "stock", "on hand", "current quantity dreadful", "new quantity dreadful"],
    sellingPriceAmount: ["price", "retail", "sale price", "unit price"],
    format: ["format", "binding", "book type", "variation name"],
    condition: ["condition", "book condition"],
    shelfSource: ["shelf", "bin", "location code"],
  };
  return aliases[field] ?? [];
}

function normalizedImportHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function sampleFor(source: ImportSourceDraft, header: string): string {
  const index = source.headers.indexOf(header);
  return source.rows.map((row) => row[index]).find(Boolean)?.slice(0, 80) ?? "";
}

function parseClientCsv(input: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const next = input[index + 1];
    if (quoted) {
      if (char === "\"" && next === "\"") {
        field += "\"";
        index += 1;
      } else if (char === "\"") {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === "\"") {
      quoted = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += char;
    }
  }
  row.push(field.replace(/\r$/, ""));
  if (row.length > 1 || row[0] !== "") rows.push(row);
  return rows;
}

function downloadText(text: string, filename: string, contentType: string) {
  const blob = new Blob([text], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function StockBadge({ status }: { status?: string }) {
  const normalized = status || "unknown";
  return <span className={`badge ${normalized}`}>{normalized.replaceAll("_", " ")}</span>;
}

function Image({ imageUrl }: { imageUrl?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageUrl]);
  return imageUrl && !failed
    ? <img className="cover" src={imageUrl} alt="Cover art" onError={() => setFailed(true)} />
    : <div className="cover placeholder"><BookOpen aria-hidden /></div>;
}

function Skeleton() {
  return <div className="skeleton-list">{Array.from({ length: 8 }, (_, index) => <div className="skeleton" key={index} />)}</div>;
}

function EmptyState() {
  return <div className="empty"><BookOpen /><h2>No inventory found</h2><p>Try changing filters or refresh from Square.</p></div>;
}

function formatMoney(money?: { amount: number; currency: string }) {
  if (!money) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: money.currency }).format(money.amount / 100);
}

function lowSampleLabel(sampleSize: number): string {
  return sampleSize < 3 ? `${sampleSize} low sample` : String(sampleSize);
}

function toIsoInput(value: string): string {
  return value.includes("T") && value.endsWith("Z") ? value : new Date(value).toISOString();
}

function tradeInDetailCreditMode(detail: TradeInDetail | null): string {
  return tradeInItemCreditMode(detail?.items.find((item) => item.decision === "accept"));
}

function tradeInItemCreditMode(item?: Pick<TradeInDetailItem, "ruleSnapshot">): string {
  return item?.ruleSnapshot?.creditMode === "one_for_one" ? "one_for_one" : "dollar_amount";
}

function tradeInCreditDisplay(amount: number, currency: string, creditMode: string): string {
  if (creditMode === "one_for_one") return `${amount} trade credit${amount === 1 ? "" : "s"}`;
  return formatMoney({ amount, currency });
}

function tradeInCreditLabel(creditMode: string): string {
  return creditMode === "one_for_one" ? "Trade credit" : "Store credit";
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function descriptionSnippet(value: string): string {
  const text = stripHtml(value);
  return text.length > 220 ? `${text.slice(0, 217).trim()}...` : text;
}

class AppRecoveryBoundary extends React.Component<React.PropsWithChildren, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    console.error("PaperbackOS page rendering failed", error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="page">
        <section className="panel">
          <p className="eyebrow">Recovery</p>
          <h1>The page needs to reload</h1>
          <p>Your active Add Stock batch and paired scanner are saved. Reload to return to the batch without starting over.</p>
          <div className="actions">
            <button className="primary" onClick={() => window.location.reload()}><RefreshCw /> Reload saved batch</button>
          </div>
        </section>
      </main>
    );
  }
}

const root = document.getElementById("root");
if (root) createRoot(root).render(<AppRecoveryBoundary><App /></AppRecoveryBoundary>);
